import type { LocaleFile } from './types';

/**
 * English (US) base locale
 *
 * This is the primary locale and is statically imported (bundled in main chunk).
 * All other locales are dynamically imported for code-splitting.
 *
 * Key naming convention:
 * - Flat, underscore-separated descriptive keys (e.g., "upload_modal_title", "field_required")
 * - Common terms defined as single shared keys reused across all views and components
 * - Descriptive prefixes used only when disambiguation is required (e.g., "new_resource" vs "new_collection")
 * - Brand names (MetaLark, SCORM, xAPI) are exempt from extraction
 *
 * ICU MessageFormat syntax is used for pluralization, variables, and complex formatting.
 * Example: "{count, plural, =0 {No items} one {# item} other {# items}}"
 *
 * NOTE: No type annotation here to avoid circular reference with PhraseDictionary.
 * The 'satisfies LocaleFile' at the end validates the structure.
 */
export const en = {
  languageName: 'English',
  languageCode: 'en',
  direction: 'ltr',
  phrases: {
    // Common actions
    save: 'Save',
    cancel: 'Cancel',
    delete: 'Delete',
    search: 'Search',
    edit: 'Edit',
    create: 'Create',
    add: 'Add',
    update: 'Update',
    select: 'Select',
    remove: 'Remove',
    close: 'Close',
    confirm: 'Confirm',
    areyousure: 'Are you sure?',
    submit: 'Submit',
    reset: 'Reset',
    clear: 'Clear',
    clearall: 'Clear All',
    clearselection: 'Clear selection',
    noselection: 'No Selection',
    clearfilters: 'Clear Filters',
    resetfilters: 'Reset Filters',
    loadmore: 'Load More',
    apply: 'Apply',
    back: 'Back',
    next: 'Next',
    previous: 'Previous',
    finish: 'Finish',
    continue: 'Continue',
    view: 'View',
    viewdetails: 'View Details',
    vieweditdetails: 'View/Edit Details',
    viewchangehistory: 'View Change History',
    viewreuploaditem: 'View/Reupload Item',
    resourcedetails: 'Resource Details',
    previewcontent: 'Preview Content',
    download: 'Download',
    upload: 'Upload',
    export: 'Export',
    import: 'Import',
    refresh: 'Refresh',
    copy: 'Copy',
    duplicate: 'Duplicate',
    viewall: 'View All',
    viewmore: 'View More',
    viewless: 'View Less',
    print: 'Print',
    print_or_savepdf: 'Print / Save as PDF',
    saveandclose: 'Save & Close',
    saveandview: 'Save & View',
    savechanges: 'Save Changes',
    connect: 'Connect',
    retry: 'Retry',
    browsefiles: 'Browse Files',
    browsefolders: 'Browse Folders',
    clearfilter: 'Clear Filter',
    filter: 'Filter',
    expand: 'Expand',
    collapse: 'Collapse',
    dismiss: 'Dismiss',

    // Shared admin-tooling tooltips (small, reused across feature-flags,
    // authentication-policy, daily-usage, session-timeline, etc.)
    togglestatus: 'Toggle status',
    moveenabled: 'Move to enabled',
    movedisabled: 'Move to disabled',
    removeoverride: 'Remove override',
    revokebreakglass: 'Revoke breakglass',
    clicktocopy: 'Click to copy',
    authmethod: 'Authentication method',
    exportallfilteredcsv: 'Export all filtered requests as CSV',
    firstoccurredat: 'First occurred at {time}',
    localtimetooltip: 'Local: {time}',

    // Common nouns
    items: 'Items',
    collection: 'Collection',
    collections: 'Collections',
    courses: 'Courses',
    documents: 'Documents',
    media: 'Media',
    workspace: 'Workspace',
    content: 'Content',
    filters: 'Filters',
    report: 'Report',
    repository: 'Repository',
    customertag: 'Customer Tag',
    generatedkeyword: 'Generated Keyword',
    skill: 'Skill',
    customskill: 'Custom Skill',
    skills: 'Skills',
    type: 'Type',
    status: 'Status',
    active: 'Active',
    inactive: 'Inactive',
    selected: 'Selected',
    updated: 'Updated',
    notfound: 'Not Found',
    missed: 'Missed',
    action: 'Action',
    actions: 'Actions',
    description: 'Description',
    title: 'Title',
    total: 'Total',
    size: 'Size',
    itemcount: 'Item Count',
    createddate: 'Created Date',
    pagelength: 'Page Length',
    page: 'Page',
    reason: 'Reason',
    confidence: 'Confidence',
    relevance: 'Relevance',
    metadata: 'Metadata',
    excerpt: 'Excerpt',
    json: 'JSON',
    text: 'Text',
    markdown: 'Markdown',
    untitled: 'Untitled',
    name: 'Name',
    email: 'Email',
    customer: 'Customer',
    role: 'Role',
    note: 'Note',
    ok: 'OK',
    success: 'Success',
    error: 'Error',
    copiedtoclipboard: 'Copied to clipboard',
    minutes: 'minutes',
    min: 'min',
    max: 'max',
    records: 'Records',
    yes: 'Yes',
    no: 'No',
    default: 'Default',
    subject: 'Subject',
    version: 'Version',
    givenname: 'Given Name',
    familyname: 'Family Name',
    created: 'Created',
    category: 'Category',
    code: 'Code',
    visual: 'Visual',
    of: 'of',

    // Table/List terms
    all: 'All',
    more: 'More',
    less: 'Less',
    showmore: 'Show More +',
    showless: 'Show Less -',
    expandrow: 'Expand row',
    none: 'None',
    notapplicable: 'N/A',
    or: 'or',
    to: 'to',
    oftotal: 'of Total',
    na: 'N/A',
    unknown: 'Unknown',
    adminonly: 'Admin Only',
    on: 'On',
    off: 'Off',
    addall: 'Add All',
    nocollections: 'No Collections',
    nocontentfound: 'No Content Found',

    // Field/Column names
    itemname: 'Item Name',
    filename: 'File Name',
    skillname: 'Skill Name',
    uploaddate: 'Upload Date',
    updatedate: 'Update Date',
    authoringtool: 'Authoring Tool',
    authoringtools: 'Authoring Tools',
    languages: 'Languages',
    resourceid: 'Resource ID',
    externalsource: 'External Source',
    externalid: 'External ID',
    origin: 'Origin',

    // Features/Actions
    missingmetadata: 'Missing Metadata',
    missingmetadata_tooltip: 'Only include content items that have empty titles or descriptions.',
    includesemanticresults: 'Include Semantic Results',
    includesemanticresults_tooltip: 'Include content items that match your search based on phrase similarity.',
    showexcerpts: 'Show Excerpts',
    printresults: 'Print Results',
    report_print_preparing: 'Preparing report…',
    report_print_building: 'Building report…',
    report_print_items: '{done} of {total} items',

    // Search Types
    searchtype_fulltext: 'Full Text',
    searchtype_semantic: 'Semantic',
    searchtype_metadata: 'Metadata',

    resourcestatus_active: 'Active',
    resourcestatus_inactive: 'Inactive',
    resourcestatus_archived: 'Archived',
    resourcestatus_deleted: 'Deleted',

    resourceaction_retain: 'Retain',
    resourceaction_refresh: 'Refresh',
    resourceaction_retire: 'Retire',
    resourceaction_reuse: 'Reuse',
    resourceaction_review: 'Review',

    // Common messages
    no_results: 'No results found',
    unsaved_changes_warning: 'You have unsaved changes. Are you sure you want to leave this page?',
    loading: 'Loading...',
    saving: 'Saving...',
    testing: 'Testing...',
    warning: 'Warning',
    info: 'Information',
    error_component_mount: 'Error mounting component. Check console for details.',

    // Footer
    footer_poweredby: 'Powered by',

    // Status terms
    connected: 'Connected',
    disconnected: 'Disconnected',
    error_status: 'Error',

    // Form validation
    field_required: 'This field is required',
    invalid_email: 'Invalid email address',
    invalid_url: 'Invalid URL',
    invalid_date: 'Invalid date',
    invalid_number: 'Invalid number',
    min_length: 'Must be at least {min} characters',
    max_length: 'Must be no more than {max} characters',
    must_match: 'Fields must match',

    // Modal titles
    upload_modal_title: 'Upload Content',
    delete_modal_title: 'Confirm Delete',
    edit_modal_title: 'Edit',
    create_modal_title: 'Create New',

    // Pluralization examples (ICU MessageFormat)
    item_count: '{count, plural, =0 {No items} one {# item} other {# items}}',
    file_count: '{count, plural, =0 {No files} one {# file} other {# files}}',
    result_count: '{count, plural, =0 {No results} one {# result} other {# results}}',

    // Confirmation messages
    confirm_delete: 'Are you sure you want to delete this item?',
    confirm_cancel: 'Are you sure you want to cancel? Unsaved changes will be lost.',
    unsaved_changes: 'You have unsaved changes',

    // Status messages
    saved_successfully: 'Saved successfully',
    deleted_successfully: 'Deleted successfully',
    updated_successfully: 'Updated successfully',
    created_successfully: 'Created successfully',
    operation_failed: 'Operation failed',

    // Alert messages - Permission errors
    alert_permission_lock_account: 'You do not have permission to lock user accounts.',
    alert_permission_unlock_account: 'You do not have permission to unlock user accounts.',
    alert_permission_revoke_sessions: 'You do not have permission to revoke user sessions.',
    alert_permission_manage_mfa: 'You do not have permission to manage MFA settings.',
    alert_permission_manage_passkeys: 'You do not have permission to manage passkeys.',
    alert_permission_reset_eula: 'You do not have permission to reset Subscription Agreement acceptance.',
    alert_permission_delete_users: 'You do not have permission to delete users.',

    // Alert messages - User management
    alert_user_lock_reason_required: 'Please provide a reason for locking this account.',
    alert_user_locked_success: 'Account has been successfully locked.',
    alert_user_locked_failed: 'Failed to lock account',
    alert_user_unlocked_success: 'Account has been successfully unlocked.',
    alert_user_unlocked_failed: 'Failed to unlock account',
    alert_user_sessions_revoked: 'All sessions have been successfully revoked. The user will need to log in again.',
    alert_user_sessions_revoke_failed: 'Failed to revoke sessions',
    alert_user_mfa_disabled_success: 'MFA has been successfully disabled for this user.',
    alert_user_mfa_disabled_failed: 'Failed to disable MFA',
    alert_user_update_failed: 'Failed to update user',
    alert_user_deleted_success: 'User has been permanently deleted.',
    alert_user_deleted_failed: 'Failed to delete user',
    alert_user_disconnected_success: 'User has been removed from this customer.',
    alert_user_disconnected_and_deleted_success: 'User has been removed from this customer. They had no other access on MetaLark, so their account has been deleted.',
    alert_user_disconnected_failed: 'Failed to remove user from this customer',
    alert_user_load_failed: 'Failed to load users',
    alert_user_verification_email_sent: 'Verification email sent successfully to {email}',
    alert_user_verification_email_sent_failed: 'Failed to send verification email',

    // Alert messages - Passkey management
    alert_passkey_nickname_updated: 'Passkey nickname updated successfully!',
    alert_passkey_nickname_update_failed: 'Failed to update passkey nickname',
    alert_passkey_revoke_reason_required: 'Please provide a reason for revoking this passkey.',
    alert_passkey_revoked_success: 'Passkey has been successfully revoked.',
    alert_passkey_revoked_failed: 'Failed to revoke passkey',

    // Alert messages - EULA
    alert_eula_reset_success: 'EULA acceptance has been successfully reset. The user will need to accept the EULA again.',
    alert_eula_reset_failed: 'Failed to reset EULA',

    // Alert messages - Role management
    alert_role_no_roles_to_change: 'User has no roles to change',
    alert_role_assigned_success: 'Role assigned successfully',
    alert_role_assigned_failed: 'Failed to assign role',
    alert_role_changed_success: 'Role changed successfully',
    alert_role_changed_failed: 'Failed to change role',
    alert_role_no_roles_to_revoke: 'User has no roles to revoke',
    alert_role_revoked_success: 'Role revoked successfully',
    alert_role_revoked_failed: 'Failed to revoke role',

    // Alert messages - OAuth/Authorization
    alert_oauth_timeout: 'OAuth authorization timed out. Please try again.',
    alert_oauth_failed: 'OAuth authorization failed',
    alert_oauth_url_failed: 'Failed to get authorization URL',
    alert_oauth_reauth_failed: 'Failed to re-authorize',

    // Alert messages - File/SharePoint selection
    alert_sharepoint_select_required: 'Please select a Shared Drive',
    alert_sharepoint_save_failed: 'Failed to save selection',
    alert_sharepoint_select_failed: 'Failed to select SharePoint site',
    alert_sharepoint_load_files_failed: 'Failed to load files',
    alert_sharepoint_select_file_required: 'Please select at least one file',
    alert_sharepoint_drive_id_missing: 'SharePoint drive ID is missing. Please select a site again or refresh the page.',

    // Alert messages - Form validation
    alert_form_required_fields: 'Please fill in all required fields',

    // Alert messages - Session/Impersonation
    alert_session_expired: 'Impersonation session has expired',
    alert_session_ended: 'Impersonation session ended',
    alert_session_end_failed: 'Failed to end impersonation session',
    alert_session_renew_failed: 'Failed to renew your session. Please log in again.',

    // Alert messages - Email templates
    alert_template_no_changes: 'No changes to save',
    alert_template_updated_success: 'Template updated successfully',
    alert_template_update_failed: 'Failed to save template',

    // Alert messages - Email suppression
    alert_suppression_load_failed: 'Failed to load suppression list. Check console for details.',
    alert_suppression_email_reason_required: 'Email and reason are required',
    alert_suppression_added_success: 'Email added to suppression list successfully',
    alert_suppression_add_failed: 'Failed to add email to suppression list. Check console for details.',
    alert_suppression_removed_success: 'Email removed from suppression list successfully',
    alert_suppression_remove_failed: 'Failed to remove email from suppression list. Check console for details.',
    alert_suppression_export_failed: 'Failed to export suppression list. Check console for details.',

    // Alert messages - Customer deletion
    alert_deletion_extended_success: 'Deletion date extended by {days} days successfully!',
    alert_deletion_extend_failed: 'Failed to extend deletion date',
    alert_deletion_cancelled_success: 'Deletion canceled successfully!',
    alert_deletion_cancel_failed: 'Failed to cancel deletion',

    // Alert messages - Collections
    alert_collection_delete_failed: 'Failed to delete collection. Please try again.',

    // Alert messages - Quota
    alert_quota_load_failed: 'Failed to load quota data. Check console for details.',
    alert_quota_health_load_failed: 'Failed to load quota health data. Check console for details.',

    // Alert messages - Security settings
    alert_security_reset_success: 'Configuration reset to defaults successfully.',
    alert_security_reset_failed: 'Failed to reset configuration',

    // Button text
    button_cancel: 'Cancel',
    button_close: 'Close',
    button_ok: 'OK',
    button_save: 'Save',
    button_delete: 'Delete',
    button_add: 'Add',
    button_edit: 'Edit',
    button_remove: 'Remove',
    button_update: 'Update',
    button_submit: 'Submit',
    button_confirm: 'Confirm',
    button_back: 'Back',
    button_next: 'Next',
    button_previous: 'Previous',
    button_yes: 'Yes',
    button_no: 'No',

    // General messages
    message_no_results_found: 'No results found',
    message_failed_load_workspaces: 'Failed to load workspaces. Please try again.',

    // File picker
    filepicker_selected_drive: 'Selected Drive',
    filepicker_my_drive: 'My Drive',
    filepicker_my_onedrive: 'My OneDrive',
    filepicker_change_drive: 'Change Drive',
    filepicker_selected_site: 'Selected Site',
    filepicker_sharepoint_site: 'SharePoint Site',
    filepicker_change_site: 'Change Site',
    filepicker_search_placeholder: 'Search files...',
    filepicker_search_scope_recursive: 'Search includes the current folder and all subfolders.',
    filepicker_search_scope_current_folder_only: 'Search only matches items directly inside the current folder, not subfolders.',
    filepicker_refresh: 'Refresh',
    filepicker_all: 'All',
    filepicker_clear_filter: 'Clear Filter',
    filepicker_clear_selection: 'Clear Selection',
    filepicker_no_files_found: 'No files or folders found',
    filepicker_no_search_results: 'No files match "{query}"',
    filepicker_folders_count: 'Folders ({count})',
    filepicker_files_count: 'Files ({count})',
    filepicker_preview_tooltip: 'Open in {provider}',
    filepicker_loading_more: 'Loading more...',
    filepicker_selected_files: 'Selected Files',
    filepicker_total_size: 'Total Size',
    filepicker_import_files: 'Import Files',
    filepicker_import_file: 'Import File',
    filepicker_selected_file: 'Selected File',
    filepicker_import_files_count: 'Import Files ({count})',
    filepicker_connection_expired: 'Repository Connection Expired',
    filepicker_reauthorize_now: 'Re-authorize Now',
    filepicker_root: 'Root',
    filepicker_use_current_folder: 'Select this folder ({name})',
    filepicker_no_subfolders: 'No subfolders here.',
    filepicker_repository: 'Repository',

    // Admin - Debug session banner
    admin_debug_session_info: 'Session Debug Info',
    admin_debug_renew_session: 'Renew Session',
    admin_debug_session_countdown: 'Session Countdown',
    admin_debug_expires_at: 'Expires At',
    admin_debug_last_activity: 'Last Activity',
    admin_debug_idle_time: 'Idle Time',
    admin_debug_session_status: 'Session Status',
    admin_debug_minutes_remaining: 'Minutes Remaining',
    admin_debug_auto_renewals: 'Auto Renewals',
    admin_debug_session_duration: 'Session Duration',
    admin_debug_renew_before_expiry: 'Renew Before Expiry',
    admin_debug_check_interval: 'Check Interval',
    admin_debug_accelerate_to_end: 'Accelerate to end',
    admin_debug_15s: '15s',
    admin_debug_status_notinitialized: 'Not Initialized',
    admin_debug_status_expired: 'Expired',
    admin_debug_status_expiringsoon: 'Expiring Soon',

    // Admin - IDP modal
    admin_idp_cognito_native: 'Cognito Native',

    // Admin - Rules builder
    admin_rules_untitled: 'Untitled Rule',
    admin_rules_value_placeholder: 'Value',
    admin_rules_field_extension: 'File Extension',
    admin_rules_field_path: 'Path Contains',
    admin_rules_field_name: 'Filename Contains',
    admin_rules_field_size: 'File Size',
    admin_rules_field_modified: 'Modified Date',
    admin_rules_operator_equals: 'Equals',
    admin_rules_operator_contains: 'Contains',
    admin_rules_operator_starts: 'Starts With',
    admin_rules_operator_ends: 'Ends With',
    admin_rules_operator_matches: 'Matches (regex)',
    admin_rules_operator_gt: 'Greater Than',
    admin_rules_operator_lt: 'Less Than',
    admin_rules_operator_gte: 'Greater or Equal',
    admin_rules_operator_lte: 'Less or Equal',
    admin_rules_operator_between: 'Between',
    admin_rules_test_results: 'Rule Test Results',
    admin_rules_delete_rule: 'Delete rule',
    admin_rules_delete_condition: 'Delete condition',
    admin_rules_invert_condition: 'Invert this condition',

    // Admin - Repository activity
    admin_repo_activity_title: 'File Activity History',
    admin_repo_status_approved: 'Approved',
    admin_repo_status_rejected: 'Rejected',
    admin_repo_status_rule_matched: 'Rule Matched',
    admin_repo_status_rule_excluded: 'Rule Excluded',
    admin_repo_status_webhook: 'Webhook Discovered',
    admin_repo_status_sync_triggered: 'Sync Triggered',
    admin_repo_status_sync_completed: 'Sync Completed',
    admin_repo_col_repository: 'Repository',
    admin_repo_col_file: 'File',
    admin_repo_col_action: 'Action',
    admin_repo_col_reason: 'Reason',
    admin_repo_col_performer: 'Performed By',
    admin_repo_col_timestamp: 'Timestamp',
    admin_repo_error_loading: 'Error Loading Activity',
    admin_repo_search_filename: 'Search by filename...',
    admin_repo_search_performer: 'Search by performer...',
    admin_repo_reset_filters: 'Reset all filters',

    // Admin - S3 credentials form
    admin_s3_security_practices: 'Security Best Practices',
    admin_s3_security_list: '<li><strong>Create a dedicated IAM user</strong> - Don\'t use root AWS credentials</li><li><strong>Grant only the permissions you need</strong> - Read-only (s3:GetObject, s3:ListBucket) is enough to import content into MetaLark; add write (s3:PutObject) only if you will migrate or export content into the bucket</li><li><strong>Limit to specific bucket</strong> - Use IAM policies to restrict access to your bucket only</li><li><strong>Rotate credentials regularly</strong> - Deactivate old access keys and create new ones</li><li><strong>Enable MFA</strong> - Add multi-factor authentication to your AWS account</li><li><strong>Monitor access</strong> - Use CloudTrail to audit S3 access logs</li>',
    admin_s3_security_intro: '<strong>Important:</strong> S3 uses AWS IAM credentials instead of OAuth. Follow these security best practices:',
    admin_s3_setup_guide: 'Setup Guide',
    admin_s3_enter_credentials: 'Enter Your Credentials',
    admin_s3_credentials_encrypted: 'Your credentials are encrypted',
    admin_s3_credentials_notice: 'Credentials are sent over HTTPS and stored encrypted at rest. Your secret access key is never shown again once saved; it can only be replaced.',

    // Admin - Pending files review
    admin_pending_title: 'Pending Files',
    admin_pending_status_pending: 'Pending',
    admin_pending_col_repository: 'Repository',
    admin_pending_col_filename: 'Filename',
    admin_pending_col_path: 'Path',
    admin_pending_col_size: 'Size',
    admin_pending_col_modified: 'Modified',
    admin_pending_col_selection_status: 'Selection Status',
    admin_pending_col_ingestion_status: 'Ingestion Status',
    admin_pending_col_actions: 'Actions',
    admin_pending_search_filename: 'Search by filename...',
    admin_pending_approve: 'Approve this file',
    admin_pending_reject: 'Reject this file',

    // Admin - Customer security settings
    admin_security_session_config: 'Session Security Configuration',

    // Admin - Saved searches
    admin_saved_searches: 'Saved Searches',

    // Admin - Log histogram
    admin_log_successful: 'Successful',
    admin_log_failed: 'Failed',

    // Admin - Email logs section
    admin_emaillogs_error_loading: 'Error Loading Logs',
    admin_emaillogs_col_recipient: 'Recipient',
    admin_emaillogs_col_subject: 'Subject',
    admin_emaillogs_col_template: 'Template',
    admin_emaillogs_col_status: 'Status',
    admin_emaillogs_col_actions: 'Actions',
    admin_emaillogs_details_title: 'Email Log Details',
    admin_emaillogs_recipient_info: 'Recipient Information',
    admin_emaillogs_email_content: 'Email Content',
    admin_emaillogs_delivery_status: 'Delivery Status',
    admin_emaillogs_error_details: 'Error Details',
    admin_emaillogs_tab_text: 'Text',
    admin_emaillogs_trigger_info: 'Trigger Information',

    // Admin - User management section
    admin_users_col_email: 'Email',
    admin_users_col_name: 'Name',
    admin_users_col_current_role: 'Current Role',
    admin_users_col_customer: 'Customer',
    admin_users_col_slice: 'Slice',
    admin_users_col_actions: 'Actions',

    // Admin - Email templates section
    admin_templates_title: 'Email Templates',
    admin_templates_btn_refresh: 'Refresh',
    admin_templates_loading: 'Loading email templates...',
    admin_templates_error_loading: 'Error Loading Templates',
    admin_templates_col_name: 'Name',
    admin_templates_col_category: 'Category',
    admin_templates_col_subject: 'Subject',
    admin_templates_col_status: 'Status',
    admin_templates_col_version: 'Version',
    admin_templates_col_actions: 'Actions',
    admin_templates_status_active: 'Active',
    admin_templates_status_inactive: 'Inactive',
    admin_templates_default_badge: 'Default',
    admin_templates_btn_view_tooltip: 'View template',
    admin_templates_btn_edit: 'Edit',
    admin_templates_btn_previous: 'Previous',
    admin_templates_pagination: 'Page {currentPage} of {totalPages}',
    admin_templates_btn_next: 'Next',
    admin_templates_no_templates: 'No email templates found.',
    admin_templates_modal_title_edit: 'Edit Template',
    admin_templates_modal_title_view: 'View Template',
    admin_templates_label_name: 'Name',
    admin_templates_label_category: 'Category',
    admin_templates_label_subject: 'Subject',
    admin_templates_label_description: 'Description',
    admin_templates_label_html_body: 'HTML Body',
    admin_templates_label_text_body: 'Text Body',
    admin_templates_label_active: 'Active',
    admin_templates_label_default: 'Default',
    admin_templates_metadata_id: 'ID',
    admin_templates_metadata_version: 'Version',
    admin_templates_metadata_created: 'Created',
    admin_templates_metadata_updated: 'Updated',
    admin_templates_btn_edit_template: 'Edit Template',
    admin_templates_btn_cancel: 'Cancel',
    admin_templates_btn_save: 'Save Changes',
    admin_templates_btn_close: 'Close',

    // Admin - Impersonation section
    admin_impersonation_search_workspaces: 'Search workspaces...',

    // Admin - User management section
    admin_user_mgmt_title: 'User Management',
    admin_user_assign_role: 'Assign Role',
    admin_user_col_email: 'Email',
    admin_user_col_name: 'Name',
    admin_user_col_current_role: 'Current Role',
    admin_user_col_customer: 'Customer',
    admin_user_col_slice: 'Slice',
    admin_user_col_actions: 'Actions',
    admin_user_no_role: 'No Role',
    admin_user_btn_change: 'Change',
    admin_user_btn_revoke: 'Revoke',
    admin_user_no_users_found: 'No users found or you don\'t have permission to view users.',
    admin_user_confirm_revoke: 'Revoke role for {email}',

    // Role names (short forms for badges)
    role_system_admin_short: 'Sys Admin',
    role_customer_admin_short: 'Cust Admin',
    role_tenant_admin_short: 'Admin',
    role_tenant_user_short: 'User',
    role_resource_manager_short: 'Manager',
    role_resource_contributor_short: 'Contributor',
    role_tenant_viewer_short: 'Viewer',
    role_service_role_short: 'Service',

    // Role names (full forms for dropdowns)

    // Admin - Log histogram
    admin_histogram_last_24_hours: 'Last 24 Hours',
    admin_histogram_total_events: 'total events',
    admin_histogram_failed_to_load: 'Failed to load histogram data',
    admin_histogram_tooltip_success: 'Success',
    admin_histogram_tooltip_failed: 'Failed',
    admin_histogram_tooltip_total: 'Total',
    admin_histogram_tooltip_click: 'Click to drill down',
    admin_histogram_legend_successful: 'Successful',
    admin_histogram_legend_failed: 'Failed',
    admin_histogram_no_events: 'No audit log events in the last 24 hours',
    admin_histogram_title_24h: 'Activity Over Last {hours} Hours',
    admin_histogram_title_1h: 'Activity in Selected Hour',
    admin_histogram_title_5m: 'Activity in Selected 5 Minutes',
    admin_histogram_subtitle_24h: 'Event frequency by hour - click a bar to drill down',
    admin_histogram_subtitle_1h: 'Event frequency by 5-minute intervals - click to see per-minute',
    admin_histogram_subtitle_5m: 'Event frequency by minute - finest granularity',

    // Admin - Saved searches
    admin_saved_searches_title: 'Saved Searches',
    admin_saved_searches_clear: 'Clear Selection',
    admin_saved_searches_click_to_view: 'Click to view {count} saved searches',
    admin_saved_searches_active: 'Active',

    // Admin - Saved search presets (userService.getSavedSearches)
    admin_savedsearch_alladmins_name: 'All Administrators',
    admin_savedsearch_alladmins_desc: 'Users with admin roles',
    admin_savedsearch_recentlogins_name: 'Recent Logins',
    admin_savedsearch_recentlogins_desc: 'Users who logged in within the last 7 days',
    admin_savedsearch_inactive_name: 'Inactive Users',
    admin_savedsearch_inactive_desc: 'No login in the last 30 days, or never',
    admin_savedsearch_mfa_name: 'MFA Enabled',
    admin_savedsearch_mfa_desc: 'Users with MFA configured',
    admin_savedsearch_unverified_name: 'Unverified Email',
    admin_savedsearch_unverified_desc: 'Users with unverified email addresses',
    admin_savedsearch_locked_name: 'Locked Accounts',
    admin_savedsearch_locked_desc: 'Users with locked accounts',

    // Admin - Customer security settings (use generic phrases where possible)
    admin_security_config_title: 'Session Security Configuration',
    admin_security_config_subtitle_workspace: 'Configure session timeout and renewal settings for this workspace',
    admin_security_config_subtitle_customer: 'Configure session timeout and renewal settings for this customer',
    admin_security_subscription_tier: 'Subscription Tier',
    admin_security_max_duration: 'Maximum session duration',
    admin_security_duration_label: 'Session Duration (minutes)',
    admin_security_duration_help: 'How long a user session remains active before requiring re-authentication',
    admin_security_renew_label: 'Renew Before Expiry (minutes)',
    admin_security_renew_help: 'How many minutes before expiry to trigger session renewal for active users',
    admin_security_unsaved_changes: 'Unsaved Changes',
    admin_security_unsaved_message: 'Changes will apply to new sessions after saving. Active sessions will continue with their current settings.',
    admin_security_reset_defaults: 'Reset to Defaults',
    admin_security_saving: 'Saving...',
    admin_security_save_config: 'Save Configuration',
    admin_security_change_history: 'Configuration Change History',
    admin_security_view_audit_help: 'View all security configuration changes in the audit log',
    admin_security_view_audit: 'View Audit Logs',
    admin_security_reset_modal_confirm: 'Are you sure you want to reset all settings to system defaults?',
    admin_security_reset_modal_warning: 'This cannot be undone and will override any custom configurations.',
    admin_security_error_min: 'Minimum session duration is 15 minutes',
    admin_security_error_max: 'Maximum duration for your tier is {maxDuration} minutes',
    admin_security_error_renewal_range: 'Renewal window must be between 1 and 60 minutes',
    admin_security_error_renewal_less: 'Renewal window must be less than session duration',
    admin_security_success_saved: 'Configuration saved successfully. Changes will apply to new sessions.',
    admin_security_error_save: 'Failed to save configuration',

    // Admin - Audit log parser
    auditlog_changetype_created: 'Created',
    auditlog_changetype_modified: 'Modified',
    auditlog_changetype_deleted: 'Deleted',
    auditlog_entity_item: 'Item',
    auditlog_action_item_creation: 'Item Creation',
    auditlog_action_item_reupload: 'Item Reupload',
    auditlog_action_item_updated: 'Item Updated',
    auditlog_action_item_deleted: 'Item Deleted',
    auditlog_action_uploaded_file: 'Uploaded file',
    auditlog_action_status_changed: 'Status Changed',
    auditlog_action_action_changed: 'Action Changed',
    auditlog_action_name_changed: 'Name Changed',
    auditlog_action_fields_changed: 'Fields Changed: {fields}',
    auditlog_items_count: '{count} items',
    auditlog_resource_updated: 'Resource updated',
    auditlog_attribute_set: '{key} was set to {value}',
    auditlog_system: 'System',
    auditlog_computedattributeconvention_set: 'Computed attribute convention set {key} to {value}',
    auditlog_attribute_cleared: '{key} was cleared from {value}',
    auditlog_attribute_updated: '{key} was updated from {oldValue} to {newValue}',
    auditlog_attribute_removed: '{key} was removed',
    auditlog_field_added: '{fieldType} Field Added',
    auditlog_field_updated: '{fieldType} Field Updated',
    auditlog_field_removed: '{fieldType} Field Removed',
    auditlog_changed: 'Changed',

    // Admin - Impersonation banner
    admin_impersonation_label: 'Impersonating',
    admin_impersonation_returntoadmin: 'Return to Admin',
    admin_impersonation_endsession_button: 'End Session',

    // Time/date
    today: 'Today',
    yesterday: 'Yesterday',
    tomorrow: 'Tomorrow',

    // Greetings
    goodmorning: 'Good morning',
    goodafternoon: 'Good afternoon',
    goodevening: 'Good evening',

    // Media Types (Display names for content types)
    mediatype_scorm12: 'SCORM 1.2',
    mediatype_scorm2004: 'SCORM 2004',
    mediatype_xapi: 'xAPI',
    mediatype_cmi5: 'cmi5',
    mediatype_aicc: 'AICC',
    mediatype_video: 'Video',
    mediatype_audio: 'Audio',
    mediatype_pdf: 'PDF',
    mediatype_epub: 'EPUB',
    mediatype_word: 'Word',
    mediatype_powerpoint: 'PowerPoint',
    mediatype_html: 'HTML',
    mediatype_rtf: 'RTF',
    mediatype_image: 'Image',
    mediatype_zip: 'Zip',
    mediatype_vtt: 'VTT Caption File',
    mediatype_text: 'Plain Text',
    mediatype_markdown: 'Markdown',

    // Authoring Tools
    authoringtool_adapt: 'Adapt',
    authoringtool_captivate: 'Captivate',
    authoringtool_dominknow: 'dominKnow',
    authoringtool_ispring: 'iSpring',
    authoringtool_lectora: 'Lectora',
    authoringtool_rise: 'Rise',
    authoringtool_storyline: 'Storyline',
    authoringtool_gomo: 'GOMO',
    authoringtool_easyg: 'EasyG',
    authoringtool_claro: 'Claro',
    authoringtool_camtasia: 'Camtasia',
    authoringtool_knowbe4: 'KnowBe4',
    authoringtool_percipio: 'Percipio',
    authoringtool_generic: 'Unknown',
    authoringtool_unknown: 'Unknown',

    // File Sizes
    filesize_bytes: 'Bytes',
    filesize_kb: 'KB',
    filesize_mb: 'MB',
    filesize_gb: 'GB',
    filesize_tb: 'TB',
    filesize_pb: 'PB',
    filesize_eb: 'EB',

    // Repository Providers
    provider_googledrive: 'Google Drive',
    provider_sharepoint: 'SharePoint',
    provider_dropbox: 'Dropbox',
    provider_amazons3: 'Amazon S3',
    provider_box: 'Box',

    // Upload Errors
    upload_limit_title: 'Upload Limit',
    upload_limit_message: 'You may only upload up to {maxFiles} files at a time.',
    keyfile_generation_failed: 'Key File Generation Failed',
    keyfile_generation_error: 'An error occurred while generating the key file.',

    // Upload Files Modal
    preupload_title: 'Upload Files',
    preupload_subtitle: 'You may provide metadata and assign a collection for your files before uploading. All fields are optional.',
    preupload_collection_create_error: 'Failed to create the collection. Please try again or proceed without one.',
    preupload_create_collection_button: 'Create Collection',
    upload_inprogress: 'Upload in Progress',
    upload_complete: 'Upload Complete',
    upload_nofiles: 'No files uploaded.',
    upload_viewqueue: 'Would you like to view the processing queue?',
    upload_downloadkeyfile: 'Download Key File',
    upload_filesuploaded: '{complete} of {total} {total, plural, one {File} other {Files}} Uploaded',
    upload_filesfailedtoupload: 'The following files failed to upload:',
    upload_completemessage: 'The files below have been uploaded to your Processing Queue:',
    upload_viewprocessing: 'View Processing Queue',

    // Subscription Banner
    subscription_day: 'day',
    subscription_days: 'days',
    subscription_daysleft: '{count, plural, one {# day} other {# days}} left',
    subscription_graceperiod_progress: 'Grace period: {remaining} of {total} days remaining',
    subscription_dismiss: 'Dismiss',
    subscription_managebilling: 'Manage Billing',

    // Trial Status
    subscription_trial_ended_title: 'Trial Ended',
    subscription_trial_ended_msg: 'Upgrade now to restore access. Your data will be permanently deleted in {days} {days, plural, one {day} other {days}} if you do not subscribe.',
    subscription_trial_ending_title: 'Trial Ending in {days} {days, plural, one {Day} other {Days}}',
    subscription_trial_ending_msg: 'Upgrade now to avoid interruption.',
    subscription_upgradenow: 'Upgrade Now',

    // Grace Period Status
    subscription_grace_finalday_title: 'Grace Period',
    subscription_grace_finalday_pill: 'Final day',
    subscription_grace_finalday_msg: 'Renew now to restore full access and prevent data deletion.',
    subscription_grace_remaining_title: 'Grace Period',
    subscription_grace_remaining_msg: 'Renew your subscription to restore full access and prevent data deletion. Until then your account is read-only.',
    subscription_grace_expired_title: 'Grace Period Expired',
    subscription_grace_expired_msg: 'Renew immediately to restore access. Your data will be permanently deleted in {days} {days, plural, one {day} other {days}}.',
    subscription_cancelled_title: 'Subscription Canceled',
    subscription_cancelled_msg: 'Renew your subscription to restore full access. Until then your account is read-only.',
    subscription_renewsub: 'Renew Subscription',
    subscription_renewnow: 'Renew Now',

    // Payment Status
    subscription_paymentfailed_title: 'Payment Failed',
    subscription_paymentfailed_msg: 'Update your payment method to maintain access.',
    subscription_updatepayment: 'Update Payment',

    // Active Subscription
    subscription_ending_title: 'Subscription Ending Soon',
    subscription_ending_msg: 'Your subscription will end in {days} {days, plural, one {day} other {days}}. Renew now to continue uninterrupted access.',
    subscription_reactivate: 'Reactivate',
    subscription_ending_today_title: 'Subscription Ends Today',
    subscription_ending_today_msg: 'Reactivate now to keep full access.',
    subscription_ended_title: 'Subscription Ended',
    subscription_ended_msg: 'Reactivate now to restore full access.',
    subscription_renewalfailed_title: 'Renewal Failed',
    subscription_renewalfailed_msg: 'Check your billing details to restore full access.',
    subscription_checkbilling: 'Check Billing',
    subscription_renewalin_title: 'Renewal in {days} {days, plural, one {Day} other {Days}}',
    subscription_renewalin_msg: 'No action is needed. Review your billing details if your payment method has changed.',
    subscription_viewbilling: 'View Billing',

    // Expired Status
    subscription_expired_title: 'Subscription Expired',
    subscription_expired_msg: 'Renew now to continue accessing your data.',

    // Subscription Modal
    subscription_required: 'Subscription Required',
    subscription_graceactive: 'Grace Period Active',
    subscription_daysremaining: '{count, plural, one {# day} other {# days}} remaining',
    subscription_until: 'Until {date}',
    subscription_blockedop: 'Blocked operation',
    subscription_grace_viewonly: 'You can still view your data during the grace period, but modifications are not allowed.',
    subscription_tocontinue: 'To continue using this feature',
    subscription_modal_renew: 'Renew your subscription',
    subscription_modal_updatepayment: 'Update your payment method if needed',
    subscription_modal_chooseplan: 'Choose a new plan if desired',
    subscription_gotobilling: 'Go to Billing',

    // Item Audit Modal
    itemaudit_title: 'Audit Log',
    itemaudit_filteredby: 'Filtered by',
    itemaudit_change: 'Change',
    itemaudit_analyst: 'Analyst',
    itemaudit_datetime: 'Date & Time',
    itemaudit_reason: 'Reason',
    itemaudit_nochanges: 'No changes found',
    itemaudit_norecords: 'There are no change records for this item yet.',
    itemaudit_loadfailed: 'Failed to load change history. Please try again.',

    // Accessibility
    loading_aria: 'Loading content',
    menu_aria: 'Menu',
    close_aria: 'Close',
    search_aria: 'Search',

    // User Roles (reusable across app). Note: TENANT_ADMIN is the internal
    // enum name; users see "Workspace Administrator" because the product
    // term for a tenant is "workspace" (see admin_role_tenantadmin and the
    // "(Acting)" variants below for consistency).
    SYSTEM_ADMIN: 'System Administrator',
    SERVICE_ROLE: 'Service Account',
    PARTNER_ADMIN: 'Partner Administrator',
    CUSTOMER_ADMIN: 'Customer Administrator',
    TENANT_ADMIN: 'Workspace Administrator',
    RESOURCE_MANAGER: 'Resource Manager',
    TENANT_USER: 'User',
    RESOURCE_CONTRIBUTOR: 'Content Contributor',
    TENANT_VIEWER: 'Viewer',

    // "Acting" labels shown in the navbar while impersonating. These reflect
    // the downgraded effective role (see MultiTenantAuthMiddleware), not the
    // admin's real role, so support reviewers can tell at a glance they're
    // seeing the target's view. TENANT_ADMIN is rebranded to "Workspace
    // Administrator" here because the impersonation UX uses "workspace"
    // everywhere else.
    role_acting_customer_admin: '(Acting) Customer Administrator',
    role_acting_tenant_admin: '(Acting) Workspace Administrator',

    // Navbar - Main Navigation
    nav_home: 'Home',
    nav_upload: 'Upload',
    nav_content: 'Content',
    nav_skills: 'Skills',
    nav_reports: 'Reports',
    nav_migrate: 'Migrate',
    nav_metadata: 'Metadata',
    nav_attributemanager: 'Customer Attributes',
    nav_customertags: 'Customer Tags',
    nav_customerskills: 'Customer Skills',
    nav_contentrepositories: 'Content Repositories',
    nav_computedattributeconvention: 'Computed Attribute Convention',
    contentrepositories_title: 'Content Repositories',
    nav_configuration: 'Configuration',

    // Navbar - Settings Menu
    nav_settingsdivider: 'Settings',
    nav_admindashboard: 'Admin Dashboard',
    nav_selectworkspace: 'Select Workspace',
    nav_support: 'Support',
    nav_processingcredits: 'Processing Credits',
    nav_billing: 'Billing',
    nav_settings: 'Settings',
    nav_logout: 'Logout',
    // METALARK-835: pending-invitation badge + cog dropdown entry
    nav_pending_invitations: 'Invitations',
    navbar_pending_invitations_aria: 'You have pending organization invitations',
    navbar_customer_logo_alt: '{name} Logo',
    navbar_open_menu: 'Open menu',

    // Navbar - User Info
    nav_welcome: 'Welcome, ',
    // Shown in the header during an active access session (impersonation or
    // direct-role workspace access) in place of the user/role line.
    nav_workspace_label: 'Workspace',
    nav_return_to_admin: 'Return to Admin',
    nav_exit_workspace_session: 'Exit Workspace Session',
    // "Your Workspaces" card on admin landing — direct-role workspaces only
    admin_my_workspaces_title: 'Your Workspaces',
    admin_my_workspaces_subtitle: 'Workspaces where you hold a direct role. Enter as your own tier — no impersonation needed.',
    // Impersonation re-auth prompt — shown only for password-auth callers
    admin_impersonate_confirm_password: 'Confirm your password',
    admin_impersonate_confirm_password_placeholder: 'Re-enter your password to continue',
    admin_impersonate_password_required: 'Re-enter your password to continue.',
    admin_impersonate_password_invalid: 'Incorrect password. Please try again.',
    admin_my_workspaces_enter: 'Enter Workspace',
    admin_my_workspaces_resume: 'Resume Session',
    admin_my_workspaces_entering: 'Entering…',
    admin_my_workspaces_active_session_label: 'Active',
    admin_my_workspaces_active_session_hint: 'You have a live session in this workspace — clicking Resume reuses it.',
    admin_my_workspaces_loaderror: 'Failed to load your workspaces',
    admin_my_workspaces_enterfailed: 'Failed to enter workspace',

    // Settings - Common
    settings: 'Settings',

    // Settings - Page
    settings_subtitle: 'Manage your account settings and preferences',
    settings_togglemenu: 'Toggle settings menu',
    settings_closemenu: 'Close menu',
    settings_searchplaceholder: 'Search settings...',
    settings_nosettingsfound: 'No settings found',

    // Settings - Profile
    settings_profile_title: 'Profile Information',
    settings_profile_subtitle: 'Update your personal information and email address',
    settings_profile_firstname: 'First Name',
    settings_profile_lastname: 'Last Name',
    settings_profile_email: 'Email Address',
    settings_profile_email_hint: 'We\'ll send a verification email if you change this',
    settings_profile_email_idp_managed_hint: 'Your email is managed by your identity provider and can only be changed there.',
    settings_profile_updated: 'Profile updated successfully',
    settings_profile_updatefailed: 'Failed to update profile. Please try again.',

    // Settings - Customer
    settings_customer_title: 'Customer Information',
    settings_customer_subtitle: 'Update customer information including logo and contact information.',
    settings_customer_loading: 'Loading customer data...',
    settings_customer_name_required: 'Required: Please enter a customer name.',
    settings_customer_contactemail: 'Contact Email',
    settings_customer_email_required: 'Required: Please enter a valid email address.',
    settings_customer_logo: 'Logo',
    settings_customer_currentlogo: 'Current Logo',
    settings_customer_deletelogo: 'Delete Logo',
    settings_customer_logourl: 'Logo URL',
    settings_customer_logourl_placeholder: 'https://example.com/logo.png',
    settings_customer_importing: 'Importing...',
    settings_customer_importfromurl: 'Import from URL',
    settings_customer_uploadfile: 'Or upload a file',
    settings_customer_uploading: 'Uploading...',
    settings_customer_logo_filetypes: 'Accepted: JPEG, PNG, SVG, WebP. Max size: 4MB',
    settings_customer_logo_processing: 'Logo is being processed. This may take a moment...',
    settings_customer_updated: 'Customer information updated successfully',
    settings_customer_updatefailed: 'Failed to update customer information. Please try again.',
    settings_customer_logo_sizelimit: 'Logo file size must be less than 4MB',
    settings_customer_logo_formatrequired: 'Logo must be JPEG, PNG, SVG, or WebP format',
    settings_customer_logo_uploaded: 'Logo uploaded and processed successfully',
    settings_customer_logo_timeout: 'Logo uploaded but processing timed out. Please refresh the page.',
    settings_customer_logo_uploadfailed: 'Failed to upload logo. Please try again.',
    settings_customer_invalidurl: 'Please enter a valid URL',
    settings_customer_logo_imported: 'Logo imported and processed successfully',
    settings_customer_logo_importtimeout: 'Logo imported but processing timed out. Please refresh the page.',
    settings_customer_logo_importfailed: 'Failed to import logo from URL. Please try again.',
    settings_customer_logo_deleted: 'Logo deleted successfully',
    settings_customer_logo_deletefailed: 'Failed to delete logo. Please try again.',
    settings_customer_defaultlanguage: 'Organization Language',
    settings_customer_defaultlanguage_description: 'Set the default language for all users in your organization',
    settings_customer_defaultlanguage_save_success: 'Language updated successfully',
    settings_customer_defaultlanguage_save_error: 'Failed to update language',

    // Language Reconciliation Modal
    language_reconciliation_title: 'Language Preference Mismatch',
    language_reconciliation_message: 'Your selected language ({userLanguage}) differs from your organization\'s default ({customerLanguage}). Would you like to update the organization default to {userLanguage}?',
    language_reconciliation_yes: 'Yes, Update Organization',
    language_reconciliation_no: 'No, Keep Current',
    language_reconciliation_error: 'Failed to update organization language. Please try again.',
    selectlanguage: 'Select language',

    // Settings - Security / Password
    settings_security_title: 'Security',
    settings_security_subtitle: 'Manage your password and passkeys for secure authentication',
    settings_security_idp_managed_title: 'Managed by your identity provider',
    settings_security_idp_managed_body: 'Your sign-in to MetaLark is handled by your organization’s identity provider. Passwords and passkeys are managed there, not here. Contact your administrator if you need to change how you authenticate.',
    settings_security_password: 'Password',
    settings_security_passkeyverified: 'Passkey Verified Successfully',
    settings_security_passkeyverified_description: 'Your session has been upgraded to passkey authentication. You can now change your password using the form below.',
    settings_security_changepassword_description: 'Change your account password',
    settings_password_currentpassword: 'Current Password',
    settings_password_hidepassword: 'Hide password',
    settings_password_showpassword: 'Show password',
    settings_password_newpassword: 'New Password',
    settings_password_requirements: 'Password Requirements:',
    settings_password_requirements_length: 'At least 12 characters',
    settings_password_requirements_uppercase: 'One uppercase letter (A-Z)',
    settings_password_requirements_lowercase: 'One lowercase letter (a-z)',
    settings_password_requirements_number: 'One number (0-9)',
    settings_password_requirements_special: 'One special character (@$!%*?& etc.)',
    settings_password_confirmnewpassword: 'Confirm New Password',
    settings_password_changingpassword: 'Changing Password...',
    settings_password_changepassword: 'Change Password',
    settings_password_confirmpassword: 'Confirm Password',
    settings_password_settingpassword: 'Setting Password...',
    settings_password_setpassword: 'Set Password',
    settings_password_changedsuccessfully: 'Password Changed Successfully',
    settings_password_changedsuccess_message: 'Your password has been updated successfully.',
    settings_password_signouteverywhere: 'Sign me out everywhere else',
    settings_password_signouteverywhere_description: 'Revoke all other sessions and API tokens for added security. Your current session will remain active.',
    settings_password_signouteverywhere_recommendation: 'Recommended if you suspect unauthorized access to your account',
    settings_password_confirmsignout: 'Confirm & Sign Out Others',
    settings_password_staysignedin: 'Stay Signed in Everywhere',
    settings_password_changed: 'Password changed successfully',
    settings_password_sessionsrevoked: 'and all other sessions have been signed out',
    settings_password_changed_revokefailed: 'Password changed, but failed to sign out other sessions. You can manually revoke them in Security settings.',
    settings_password_changefailed: 'Failed to change password. Please check your current password and try again.',
    settings_password_set: 'Password set successfully',
    settings_password_setfailed: 'Failed to set password. Please try again.',
    settings_password_setwithpasskey: 'Password set successfully using passkey verification!',
    settings_security_verificationrequired: 'Security Verification Required',
    settings_security_verification_message: 'To {action} your password, please verify your identity using one of these methods:',
    settings_security_verifywithpasskey: 'Verify with Passkey (Recommended)',
    settings_security_verifywithemail: 'Send Verification Email Instead',
    settings_security_verification_info: 'Passkey verification is instant and more secure. Email verification may take a few minutes.',
    settings_security_signouteverywhere_confirm: 'Sign Out Everywhere?',
    settings_security_signouteverywhere_description: 'This will immediately sign you out of all devices and sessions, including:',
    settings_security_signouteverywhere_browsers: 'All active browser sessions',
    settings_security_signouteverywhere_mobile: 'Mobile apps and tablets',
    settings_security_signouteverywhere_tokens: 'Any generated API tokens',
    settings_security_signouteverywhere_current: 'This current session',
    settings_security_signouteverywhere_warning: 'You will need to sign in again after this action. Use this if you suspect unauthorized access to your account.',
    settings_security_signingout: 'Signing Out...',
    settings_security_signouteverywhere_confirm_button: 'Yes, Sign Out Everywhere',
    settings_security_verificationrequired_title: 'Verification Required',
    settings_security_loggedinwith: 'You logged in with {authMethod}.',
    settings_security_forsecurity: 'For security,',
    settings_security_verifytochange: 'To change your password, please verify with your passkey (recommended & instant) or email.',
    settings_security_continueverification: 'Continue with Verification',
    settings_security_emailverificationrequired: 'Email Verification Required',
    settings_security_emailverification_description: 'For security, we require verification before changing your password. We\'ll send you an email with a link to complete the change.',
    settings_security_sendingemail: 'Sending Email...',
    settings_security_sendverificationemail: 'Send Verification Email',
    settings_security_nopassword_message: 'You don\'t have a password set. Add one for an alternative sign-in method.',
    settings_security_secureauthverified: 'Secure Authentication Verified',
    settings_security_passkeylogin_message: 'You logged in with a passkey (cryptographically secure). You can set a password directly without verification.',
    settings_security_verifytoset: 'To set a password, please verify with your passkey (recommended & instant) or email.',
    settings_security_emailsetup_description: 'For security, we\'ll send you a verification email with a link to set your password.',
    settings_security_sessionmanagement: 'Session Management',
    settings_security_signouteverywhere: 'Sign Out Everywhere',
    settings_security_invalidatesessions: 'Immediately invalidate all active sessions and API tokens. This will:',
    settings_security_signout_devices: 'Sign you out on all devices (phones, tablets, computers)',
    settings_security_signout_tokens: 'Revoke all generated API tokens',
    settings_security_signout_device: 'Require you to sign in again on this device',
    settings_security_signout_warning: 'Use this if you suspect someone has unauthorized access to your account or if you\'ve lost a device.',
    settings_security_verificationemail_setup: 'Verification email sent! Please check your inbox and click the link to set your password.',
    settings_security_verificationemail_failed: 'Failed to send verification email. Please try again.',
    settings_security_verificationemail_change: 'Verification email sent! Please check your inbox and click the link to change your password.',
    settings_security_authenticatepasskey: 'Please authenticate with your passkey...',
    settings_security_passkeyverificationfailed: 'Passkey verification failed. Please try email verification instead.',
    settings_security_allsessionsrevoked: 'All sessions have been signed out. Redirecting to login...',
    settings_security_revokesessionsfailed: 'Failed to sign out all sessions. Please try again.',

    // Settings - Privacy
    settings_privacy_title: 'Privacy & Data',
    settings_privacy_subtitle: 'Control how your data is stored and used',
    settings_privacy_rememberme: 'Remember Me',
    settings_privacy_rememberme_description: 'Choose whether to be remembered as a recent user after logout for quick sign-in.',
    settings_privacy_rememberbrowser: 'Remember on this browser',
    settings_privacy_rememberbrowser_description: 'Show your profile for quick sign-in on this device',
    settings_privacy_neverremember: 'Never remember me',
    settings_privacy_neverremember_description: 'Always show full login form (more secure for shared devices)',
    settings_privacy_localstorage_info: 'This preference is stored locally in your browser only.',

    // Settings - Billing
    settings_billing_title: 'Billing & Usage',
    settings_billing_subtitle: 'Manage your subscription and view credit usage',
    settings_billing_subscription: 'Subscription',
    settings_billing_active: 'Active',
    settings_billing_trial: 'Trial',
    settings_billing_cancelled: 'Canceled',
    settings_billing_pastdue: 'Past Due',
    settings_billing_inactive: 'Inactive',
    settings_billing_noplan: 'No active plan',
    settings_billing_daysremaining: '{count, plural, one {# day} other {# days}} remaining',
    settings_billing_trialends: 'Trial ends:',
    settings_billing_renews: 'Renews:',
    settings_billing_ends: 'Ends:',
    settings_billing_managesubscription: 'Manage Subscription',
    settings_billing_comingsoon: 'Full billing management coming soon',
    settings_billing_credits: 'Credits',
    settings_billing_creditsavailable: 'credits available',
    settings_billing_purchasecredits: 'Purchase Credits',
    settings_billing_creditpurchase_comingsoon: 'Credit purchases coming soon',
    settings_billing_recentusage: 'Recent Usage (Last 7 Days)',
    settings_billing_today: 'Today',
    settings_billing_last7days: 'Last 7 Days',
    settings_billing_last30days: 'Last 30 Days',
    settings_billing_remaining: 'Remaining',
    settings_billing_viewdashboard: 'View Full Billing Dashboard',
    settings_billing_purchasemorecredits: 'Purchase More Credits',
    settings_billing_both_comingsoon: 'Billing dashboard and credit purchases are coming soon.',
    settings_billing_dashboard_comingsoon: 'Full billing dashboard coming soon.',
    settings_billing_purchases_comingsoon: 'Credit purchases coming soon.',
    settings_billing_loading: 'Loading billing data...',
    settings_billing_interval_monthly: 'Monthly',
    settings_billing_interval_yearly: 'Yearly',
    settings_billing_interval_annual: 'Annual',
    settings_billing_interval_quarterly: 'Quarterly',
    settings_billing_interval_weekly: 'Weekly',

    // Settings - Section Navigation
    settings_section_profile: 'Profile',
    settings_section_profile_subtitle: 'Personal information',
    settings_section_customer: 'Customer',
    settings_section_customer_subtitle: 'Customer Information',
    settings_section_security: 'Security',
    settings_section_security_subtitle: 'Password & passkeys',
    settings_section_privacy: 'Privacy & Data',
    settings_section_privacy_subtitle: 'Data preferences',
    settings_section_billing: 'Billing & Usage',
    settings_section_billing_subtitle: 'Subscription & credits',
    settings_section_notifications: 'Notification Preferences',
    settings_section_notifications_subtitle: 'Email subscriptions',

    // Settings - Other
    settings_theme_updatefailed: 'Failed to update theme. Please try again.',
    settings_preference_updatefailed: 'Failed to update preference. Please try again.',

    // Passkey Manager Component
    passkey_title: 'Passkeys',
    passkey_description: 'Passkeys let you sign in quickly and securely and provide passwordless authentication using your device\'s biometrics or security key.',
    passkey_notsupported: 'Passkeys are not supported in this browser. Please use Chrome, Safari, or Edge.',
    passkey_none_title: 'No passkeys configured',
    passkey_none_description: 'Add a passkey to enable quick sign-in',
    passkey_add: 'Add Passkey',
    passkey_nickname_placeholder: 'Enter nickname',
    passkey_rename: 'Rename',
    passkey_remove: 'Remove',
    passkey_devicetype_created: 'Created:',
    passkey_devicetype_platform: 'Platform authenticator',
    passkey_devicetype_crossplatform: 'External security key',
    passkey_devicetype_multidevice: 'Multi-device (synced)',
    passkey_devicetype_unknown: 'Unknown',
    passkey_devicetype_lastused: 'Last used:',
    passkey_modal_add_title: 'Add New Passkey',
    passkey_modal_add_description: 'Your passkey will be automatically named based on your device and authenticator (e.g., "iCloud Keychain (iPhone)").',
    passkey_modal_customize: 'Customize nickname',
    passkey_modal_nickname_label: 'Custom Nickname',
    passkey_modal_nickname_placeholder: 'e.g., MacBook Pro',
    passkey_modal_nickname_hint: 'Or leave blank to auto-generate',
    passkey_modal_autogenerate: 'Use auto-generated name',
    passkey_modal_remove_title: 'Confirm Passkey Removal',
    passkey_modal_remove_question: 'Are you sure you want to remove {nickname}?',
    passkey_modal_remove_consequence_title: 'This action will:',
    passkey_modal_remove_consequence_1: 'Permanently remove this passkey from your account',
    passkey_modal_remove_consequence_2: 'Prevent you from using it to log in',
    passkey_modal_remove_consequence_3: 'Cannot be undone',
    passkey_modal_remove_button: 'Remove Passkey',
    passkey_success_added: 'Passkey added successfully!',
    passkey_success_updated: 'Nickname updated!',
    passkey_success_removed: 'Passkey removed successfully!',
    passkey_error_load: 'Failed to load passkeys',
    passkey_error_update: 'Failed to update nickname',
    passkey_error_remove: 'Failed to remove passkey',
    passkey_error_no_selection: 'No passkey selected',
    passkey_error_webauthn_unavailable: 'WebAuthn not available in this browser',
    passkey_error_no_platform_authenticator: 'No platform authenticator (Face ID/Touch ID/Windows Hello) available',
    passkey_error_nickname_too_long: 'Nickname must be 100 characters or less',
    passkey_error_registration_challenge: 'Failed to get registration challenge',
    passkey_error_creation_cancelled: 'Passkey creation canceled by user',
    passkey_error_registration_failed: 'Passkey registration failed',
    passkey_error_register_generic: 'Failed to register passkey',
    passkey_error_auth_challenge: 'Failed to get authentication challenge',
    passkey_error_auth_cancelled: 'Authentication canceled by user',
    passkey_error_auth_failed: 'Authentication failed',
    passkey_error_fetch_failed: 'Failed to fetch passkeys',
    passkey_error_update_failed: 'Failed to update passkey',
    passkey_error_revoke_failed: 'Failed to revoke passkey',
    passkey_device_mac: 'My Mac',
    passkey_device_iphone: 'My iPhone',
    passkey_device_ipad: 'My iPad',
    passkey_device_windows: 'My Windows PC',
    passkey_device_android: 'My Android Phone',
    passkey_device_linux: 'My Linux Computer',
    passkey_device_generic: 'My Device',
    passkey_time_never: 'Never',
    passkey_time_justnow: 'Just now',
    passkey_time_minutesago: '{minutes}m ago',
    passkey_time_hoursago: '{hours}h ago',
    passkey_time_daysago: '{days}d ago',

    // Admin - API Token Management
    admin_apitoken_title: 'API Token Management',
    admin_apitoken_launchtokentester: 'Launch Token Tester',
    admin_apitoken_testlaunchtokens: 'Test LMS launch tokens',
    admin_apitoken_subtitle: 'Generate long-lived API tokens for third-party system integration',
    admin_apitoken_unavailable_title: 'API Token Generation Unavailable',
    admin_apitoken_unavailable_message: 'API token generation is only available for paid accounts. Upgrade your subscription to access programmatic API integration.',
    admin_apitoken_description_label: 'Description',
    admin_apitoken_description_hint: 'Describe the purpose of this API key',
    admin_apitoken_description_placeholder: 'e.g., Production API Integration',
    admin_apitoken_expiration_label: 'Expiration Date',
    admin_apitoken_expiration_hint: 'Default: 90 days from now. Maximum: 1 year from now.',
    admin_apitoken_generating: 'Generating...',
    admin_apitoken_generate: 'Generate API Key',
    admin_apitoken_warning_title: 'Important: Save Your API Key Now!',
    admin_apitoken_warning_message: 'This API key will only be shown once. After you leave this page, you will not be able to view it again. Make sure to copy and save it in a secure location.',
    admin_apitoken_yourkey: 'Your API Key',
    admin_apitoken_hide: 'Hide',
    admin_apitoken_show: 'Show',
    admin_apitoken_copy: 'Copy to clipboard',
    admin_apitoken_copied: 'Copied to clipboard',
    admin_apitoken_stat_description: 'Description',
    admin_apitoken_stat_expiresat: 'Expires At',
    admin_apitoken_stat_sliceid: 'Slice ID',
    admin_apitoken_generateanother: 'Generate Another Key',
    admin_apitoken_error_future: 'Expiration date must be in the future',
    admin_apitoken_error_maxdays: 'Expiration date cannot be more than 365 days in the future',
    admin_apitoken_error_failed: 'Failed to generate API key',

    // Admin - Audit Logs
    admin_auditlogs_title: 'Audit Log Viewer',
    admin_auditlogs_description: 'Monitor authentication and authorization events',
    admin_auditlogs_eventsbycategory: 'Events by Category',
    admin_auditlogs_clicktofilter: '(click to filter)',
    admin_auditlogs_clearfilter: 'Clear Filter',
    admin_auditlogs_ofevents: 'of events',
    admin_auditlogs_activitytimeline: 'Activity Timeline',
    admin_auditlogs_last24hours: 'Last 24 Hours',
    admin_auditlogs_last96hours: 'Last 96 Hours',
    admin_auditlogs_events: 'events',
    admin_auditlogs_successful: 'Successful',
    admin_auditlogs_failed: 'Failed',
    admin_auditlogs_totalevents: 'Total Events',
    admin_auditlogs_incurrentview: 'In current view',
    admin_auditlogs_successrate: 'Success Rate',
    admin_auditlogs_successfuloperations: 'Successful operations',
    admin_auditlogs_failedevents: 'Failed Events',
    admin_auditlogs_requiresattention: 'Requires attention',
    admin_auditlogs_currentpage: 'Current Page',
    admin_auditlogs_showingitems: 'Showing {count} items',
    admin_auditlogs_failedtoload: 'Failed to load audit logs',
    admin_auditlogs_exportcomplete: 'Export completed successfully',
    admin_auditlogs_timestamp: 'Timestamp',
    admin_auditlogs_event: 'Event',
    admin_auditlogs_user: 'User',
    admin_auditlogs_status: 'Status',
    admin_auditlogs_ipaddress: 'IP Address',
    admin_auditlogs_details: 'Details',
    admin_auditlogs_success: 'Success',
    admin_auditlogs_exporttocsv: 'Export to CSV',
    admin_auditlogs_exporting: 'Exporting',
    admin_auditlogs_failedtoexport: 'Failed to export audit logs',
    admin_auditlogs_logdetails: 'Audit Log Details',
    admin_auditlogs_eventinformation: 'Event Information',
    admin_auditlogs_category: 'Category',
    admin_auditlogs_eventtype: 'Event Type',
    admin_auditlogs_mfamethod: 'MFA Method',
    admin_auditlogs_errormessage: 'Error Message',
    admin_auditlogs_userinformation: 'User Information',
    admin_auditlogs_useruuid: 'User UUID',
    admin_auditlogs_email: 'Email',
    admin_auditlogs_name: 'Name',
    admin_auditlogs_customerid: 'Customer ID',
    admin_auditlogs_sliceid: 'Slice ID',
    admin_auditlogs_networkinformation: 'Network Information',
    admin_auditlogs_useragent: 'User Agent',
    admin_auditlogs_identityprovider: 'Identity Provider',
    admin_auditlogs_providerid: 'Provider ID',
    admin_auditlogs_providername: 'Provider Name',
    admin_auditlogs_additionaleventdata: 'Additional Event Data',
    admin_auditlogs_eventdatafield: 'Field',
    admin_auditlogs_eventdatavalue: 'Value',
    admin_auditlogs_eventdatarawjson: 'Show raw JSON',
    admin_auditlogs_eventdataempty: 'No structured event data to display',
    admin_auditlogs_metadata: 'Metadata',
    admin_auditlogs_logid: 'Log ID',
    admin_auditlogs_system: 'System',
    admin_auditlogs_filters: 'Filters',
    admin_auditlogs_clearall: 'Clear All',
    admin_auditlogs_from: 'From',
    admin_auditlogs_to: 'To',
    admin_auditlogs_show: 'Show',
    admin_auditlogs_allevents: 'All Events',
    admin_auditlogs_successfulonly: 'Successful Only',
    admin_auditlogs_failedonly: 'Failed Only',
    admin_auditlogs_filterbycategory: 'Filter by Category',
    admin_auditlogs_all: 'All {category}',
    admin_auditlogs_cat_authentication: 'All Authentication & Sessions',
    admin_auditlogs_cat_accesscontrol: 'All Access Control',
    admin_auditlogs_cat_userlifecycle: 'All User Lifecycle',
    admin_auditlogs_cat_mfa: 'All Multi-Factor Authentication',
    admin_auditlogs_cat_rolemanagement: 'All Role Management',
    admin_auditlogs_cat_identityprovider: 'All Identity Provider',
    admin_auditlogs_cat_dataprivacy: 'All Data & Privacy',
    admin_auditlogs_cat_configuration: 'All Configuration',
    admin_auditlogs_cat_compliance: 'All Compliance',
    admin_auditlogs_cat_impersonation: 'All Impersonation',
    admin_auditlogs_catgroup_authentication: 'Authentication & Sessions',
    admin_auditlogs_catgroup_accesscontrol: 'Access Control',
    admin_auditlogs_catgroup_userlifecycle: 'User Lifecycle',
    admin_auditlogs_catgroup_mfa: 'Multi-Factor Authentication',
    admin_auditlogs_catgroup_rolemanagement: 'Role Management',
    admin_auditlogs_catgroup_identityprovider: 'Identity Provider',
    admin_auditlogs_catgroup_dataprivacy: 'Data & Privacy',
    admin_auditlogs_catgroup_configuration: 'Configuration',
    admin_auditlogs_catgroup_compliance: 'Compliance (GDPR/CCPA)',
    admin_auditlogs_catgroup_impersonation: 'Impersonation',
    admin_auditlogs_catgroup_passkey: 'Passkey Management',
    admin_auditlogs_evt_login: 'Login',
    admin_auditlogs_evt_logout: 'Logout',
    admin_auditlogs_evt_loginfailed: 'Login Failed',
    admin_auditlogs_evt_sessionexpired: 'Session Expired',
    admin_auditlogs_evt_apitokengenerated: 'API Token Generated',
    admin_auditlogs_evt_apitokenrevoked: 'API Token Revoked',
    admin_auditlogs_evt_apitokenexpired: 'API Token Expired',
    admin_auditlogs_evt_passwordresetrequested: 'Password Reset Requested',
    admin_auditlogs_evt_passwordresetcompleted: 'Password Reset Completed',
    admin_auditlogs_evt_passwordchanged: 'Password Changed',
    admin_auditlogs_evt_emailverificationsent: 'Email Verification Sent',
    admin_auditlogs_evt_emailverified: 'Email Verified',
    admin_auditlogs_evt_accessdenied: 'Access Denied',
    admin_auditlogs_evt_permissiondenied: 'Permission Denied',
    admin_auditlogs_evt_ratelimitexceeded: 'Rate Limit Exceeded',
    admin_auditlogs_evt_usercreated: 'User Created',
    admin_auditlogs_evt_userupdated: 'User Updated',
    admin_auditlogs_evt_userdeleted: 'User Deleted',
    admin_auditlogs_evt_usersuspended: 'User Suspended',
    admin_auditlogs_evt_userreactivated: 'User Reactivated',
    admin_auditlogs_evt_userinvited: 'User Invited',
    admin_auditlogs_evt_accountlocked: 'Account Locked',
    admin_auditlogs_evt_accountunlocked: 'Account Unlocked',
    admin_auditlogs_evt_mfachallenge: 'MFA Challenge',
    admin_auditlogs_evt_mfasuccess: 'MFA Success',
    admin_auditlogs_evt_mfafailed: 'MFA Failed',
    admin_auditlogs_evt_mfasetup: 'MFA Setup',
    admin_auditlogs_evt_mfadisabled: 'MFA Disabled',
    admin_auditlogs_evt_roleassigned: 'Role Assigned',
    admin_auditlogs_evt_rolerevoked: 'Role Revoked',
    admin_auditlogs_evt_idpcreated: 'IDP Created',
    admin_auditlogs_evt_idpupdated: 'IDP Updated',
    admin_auditlogs_evt_idpdeleted: 'IDP Deleted',
    admin_auditlogs_evt_idpenabled: 'IDP Enabled',
    admin_auditlogs_evt_idpdisabled: 'IDP Disabled',
    admin_auditlogs_evt_sensitivedataaccessed: 'Sensitive Data Accessed',
    admin_auditlogs_evt_dataexported: 'Data Exported',
    admin_auditlogs_evt_reportgenerated: 'Report Generated',
    admin_auditlogs_evt_securitysettingschanged: 'Security Settings Changed',
    admin_auditlogs_evt_integrationenabled: 'Integration Enabled',
    admin_auditlogs_evt_integrationdisabled: 'Integration Disabled',
    admin_auditlogs_evt_consentgranted: 'Consent Granted',
    admin_auditlogs_evt_consentrevoked: 'Consent Revoked',
    admin_auditlogs_evt_datadeletionrequested: 'Data Deletion Requested',
    admin_auditlogs_evt_datadeletioncompleted: 'Data Deletion Completed',
    admin_auditlogs_evt_impersonationstarted: 'Impersonation Started',
    admin_auditlogs_evt_impersonationended: 'Impersonation Ended',
    admin_auditlogs_catgroup_campaign: 'Campaigns',
    admin_auditlogs_catgroup_webhooks: 'Webhooks',
    admin_auditlogs_evt_eulaaccepted: 'EULA Accepted',
    admin_auditlogs_evt_jwtexchangesuccess: 'JWT Exchange Success',
    admin_auditlogs_evt_jwtexchangefailed: 'JWT Exchange Failed',
    admin_auditlogs_evt_jituserprovisioned: 'JIT User Provisioned',
    admin_auditlogs_evt_jituserrejecteddomain: 'JIT User Rejected (Domain)',
    admin_auditlogs_evt_campaigncreated: 'Campaign Created',
    admin_auditlogs_evt_campaignupdated: 'Campaign Updated',
    admin_auditlogs_evt_campaignactivated: 'Campaign Activated',
    admin_auditlogs_evt_campaignpaused: 'Campaign Paused',
    admin_auditlogs_evt_campaignarchived: 'Campaign Archived',
    admin_auditlogs_evt_webhooksubscriptioncreated: 'Webhook Subscription Created',
    admin_auditlogs_evt_webhooksubscriptionupdated: 'Webhook Subscription Updated',
    admin_auditlogs_evt_webhooksubscriptiondeleted: 'Webhook Subscription Deleted',
    admin_auditlogs_evt_webhookurlrotated: 'Webhook URL Rotated',
    admin_auditlogs_evt_webhookhmacrotated: 'Webhook Secret Rotated',
    admin_auditlogs_evt_webhookrulecreated: 'Webhook Rule Created',
    admin_auditlogs_evt_webhookruleupdated: 'Webhook Rule Updated',
    admin_auditlogs_evt_webhookruledeleted: 'Webhook Rule Deleted',
    admin_auditlogs_evt_webhooktestfired: 'Webhook Test Fired',
    admin_auditlogs_evt_webhookautodisabled: 'Webhook Auto-Disabled',
    admin_auditlogs_eventsearch: 'Search event types',
    admin_auditlogs_eventnoresults: 'No matching event types',
    admin_auditlogs_eventsselected: '{count} events',
    admin_auditlogs_nologsfound: 'No audit logs found matching your criteria',
    admin_auditlogs_fromdate: 'From Date',
    admin_auditlogs_todate: 'To Date',
    admin_auditlogs_useremail: 'User Email',
    admin_auditlogs_histogram_activityoverlast: 'Activity Over Last {hours} Hours',
    admin_auditlogs_histogram_activityselectedhour: 'Activity in Selected Hour',
    admin_auditlogs_histogram_activityselected5min: 'Activity in Selected 5 Minutes',
    admin_auditlogs_histogram_frequencybyhour: '{label} frequency by hour - click a bar to drill down',
    admin_auditlogs_histogram_frequencyby5min: '{label} frequency by 5-minute intervals - click to see per-minute',
    admin_auditlogs_histogram_frequencybyminute: '{label} frequency by minute - finest granularity',
    admin_auditlogs_histogram_clicktodrilldown: 'Click to drill down',
    admin_auditlogs_histogram_noeventsinperiod: 'No events in this period',
    admin_auditlogs_histogram_total: 'Total',
    generic_histogram_total: 'Total',
    generic_histogram_clicktodrilldown: 'Click to drill down',
    generic_histogram_empty: 'No data in this period',
    generic_histogram_loadfailed: 'Failed to load histogram data',
    admin_auditlogs_histogram_failedtoload: 'Failed to load histogram data',
    admin_auditlogs_histogram_last: 'Last {hours} Hours',
    admin_auditlogs_activefilter_timerange: 'Time Range',
    admin_auditlogs_activefilter_category: 'Category',
    admin_auditlogs_placeholder_email: 'Search by email',
    admin_auditlogs_placeholder_ip: 'Search by IP',
    admin_auditlogs_placeholder_useragent: 'Search by user agent',
    admin_auditlogs_placeholder_search: 'Search email, IP, or user agent',
    admin_auditlogs_search: 'Search',

    // Content - Summary Page
    content_summary_header: 'Content: Summary',
    content_summary_detailedsummary: 'Detailed Summary',
    content_summary_placeholder: 'Enter summary content...',
    content_summary_preview: 'Summary Preview',
    content_summary_error_save: 'Failed to save summary. Please try again.',
    summary_tab_summary: 'Text Summary',
    summary_tab_detail: 'Resource Detail',
    summary_tab_blooms: 'Bloom\'s Taxonomy',
    resourcedetail_blooms_unavailable: 'Bloom\'s Taxonomy analysis is not yet available for this resource.',
    resourcedetail_metadata: 'Resource Metadata',
    resourcedetail_attributes: 'Attributes',
    resourcedetail_externalfields: 'External Fields',
    resourcedetail_aisummary: 'AI-Generated Summary',
    resourcedetail_tags: 'Customer Tags',
    resourcedetail_keywords: 'Generated Keywords',
    resourcedetail_skills: 'Skills',
    resourcedetail_skills_esco: 'ESCO Standard Skills',
    resourcedetail_skills_customer: 'Customer-Defined Skills',
    resourcedetail_collections: 'Collections',
    resourcedetail_blooms: 'Bloom\'s Taxonomy',
    resourcedetail_blooms_overview: 'Overview',
    resourcedetail_blooms_levels: 'Cognitive levels',
    resourcedetail_blooms_audience: 'Target audience',
    resourcedetail_blooms_cognitiveprofile: 'Cognitive profile',
    resourcedetail_blooms_reasoning: 'Reasoning',
    resourcedetail_blooms_evidence: 'Evidence',
    resourcedetail_blooms_score: 'score',
    summary_tab_extractquality: 'Extract Quality',
    resourcedetail_eq_error: 'Couldn\'t load extract quality results. Please try again.',
    resourcedetail_eq_pending: 'Extract quality scores are not available for this item.',
    resourcedetail_eq_overview: 'Overview',
    resourcedetail_eq_tests: 'Quality tests',
    resourcedetail_eq_label_type: 'Test Type',
    resourcedetail_eq_label_severity: 'Severity',
    resourcedetail_eq_no_issues: 'No issues detected for this test.',
    resourcedetail_eq_reasoning: 'Reasoning',
    resourcedetail_eq_score: 'score',
    resourcedetail_eq_outcome: 'Pipeline outcome',
    resourcedetail_eq_outcome_complete: 'Complete',
    resourcedetail_eq_outcome_review: 'Needs Review',
    resourcedetail_eq_outcome_quit: 'Blocked',
    resourcedetail_eq_outcome_pending: 'Pending',
    content_col_quality: 'Quality',
    content_extractquality: 'Extract Quality',
    content_section_resourcemetadata: 'Resource Metadata',
    content_filters_title: 'Content Filters',
    content_filters_close: 'Close Content Filters',
    content_morefilters: 'More Filters',
    content_applied: 'Applied',
    content_filters_pending: 'Filter changes pending. Activate Apply to update results.',
    content_section_external: 'External Fields',
    content_section_dates: 'Dates',
    content_section_options: 'Options',
    content_daterange_from: 'From',
    content_daterange_to: 'To',
    externalclassification1: 'External Classification 1',
    externalclassification2: 'External Classification 2',
    externalpublicationstatus: 'External Publication Status',
    externalpublisheddate: 'External Published Date',
    externalcreationdate: 'External Creation Date',
    externallatestaccess: 'External Latest Access',
    content_filter_pipelineoutcome: 'Pipeline Outcome',
    content_filter_failingtest: 'Failing Test',
    content_filter_teststatus: 'Test Status',
    content_eq_outcome_complete: 'Complete',
    content_eq_outcome_review: 'Needs Review',
    content_eq_outcome_quit: 'Blocked',
    content_eq_outcome_pending: 'Pending',
    content_eq_status_high: 'High',
    content_eq_status_pass: 'Pass',
    content_eq_status_low: 'Low',
    content_eq_status_fail: 'Fail',
    content_eq_test_extractlength: 'Extract Length',
    content_eq_test_englishgarbledtext: 'English Garbled Text',
    content_eq_test_charactercorruption: 'Character Corruption',
    content_eq_test_tokenfragmentation: 'Text Fragmentation',
    content_eq_test_extractcoveragequality: 'Coverage Quality',
    content_eq_test_languagedetection: 'Language Detection',
    content_eq_test_structuralquality: 'Structural Quality',
    content_eq_test_packagequality: 'Package Quality',
    content_quality_needsreview_count: 'Needs Review: {count}',
    content_quality_blocked_count: 'Blocked: {count}',
    resourcedetail_eq_stat_passed: 'Tests passed',
    resourcedetail_eq_stat_critical: 'Critical failures',
    resourcedetail_eq_stat_warnings: 'Warnings',
    resourcedetail_eq_type_extract: 'Extract',
    resourcedetail_eq_type_content: 'Content',
    resourcedetail_eq_severity_critical: 'Critical',
    resourcedetail_eq_severity_high: 'High',
    resourcedetail_eq_severity_warning: 'Warning',
    resourcedetail_eq_status_high: 'High',
    resourcedetail_eq_status_pass: 'Pass',
    resourcedetail_eq_status_low: 'Low',
    resourcedetail_eq_status_fail: 'Fail',
    resourcedetail_eq_threshold_atleast: 'At Least {value}',
    resourcedetail_eq_threshold_atmost: 'At Most {value}',
    resourcedetail_eq_test_extractlength: 'Extract Length',
    resourcedetail_eq_test_englishgarbledtext: 'English Garbled Text',
    resourcedetail_eq_test_charactercorruption: 'Character Corruption',
    resourcedetail_eq_test_tokenfragmentation: 'Text Fragmentation',
    resourcedetail_eq_test_extractcoveragequality: 'Coverage Quality',
    resourcedetail_eq_test_languagedetection: 'Language Detection',
    resourcedetail_eq_test_structuralquality: 'Structural Quality',
    resourcedetail_eq_test_shellorthinclient: "Shell or Thin Client",
    resourcedetail_eq_test_packagequality: 'Package Quality',
    resourcedetail_eq_legend_high: 'Passed with a strong score.',
    resourcedetail_eq_legend_pass: 'Passed acceptably.',
    resourcedetail_eq_legend_low: 'Below expectations.',
    resourcedetail_eq_legend_fail: 'Failed the quality threshold.',
    resourcedetail_eq_legend_severity: 'Severity reflects how heavily a test is weighted, not its result.',
    resourcedetail_resourceid: 'Resource ID',
    resourcedetail_mediatype: 'Media Type',
    resourcedetail_exported: 'Exported',
    resourcedetail_customerid: 'Customer ID',
    resourcedetail_sliceid: 'Slice ID',
    resourcedetail_createddate: 'Created Date',
    resourcedetail_updateddate: 'Updated Date',
    resourcedetail_status: 'Status',
    resourcedetail_resourcefilename: 'Resource File Name',
    resourcedetail_authoringtool: 'Authoring Tool',
    resourcedetail_filesize: 'File Size',
    resourcedetail_languages: 'Languages',
    resourcedetail_action: 'Action',

    // Content - Item Page
    content_item_header: 'Content: Edit',
    content_item_notfound: 'Content item not found.',
    content_item_processfailed: 'This content item failed to process.',
    content_item_processfailed_reasons: 'This content item failed to process correctly due to the following reasons',
    content_item_processing: 'This item is currently being processed.',
    content_item_reuploadcontent: 'Reupload Content',
    content_item_savedetails: 'Save Details',
    content_item_editdetails: 'Edit Details',
    content_item_previewcontent: 'Preview Content',
    content_item_resourcedetails: 'Resource Details',
    content_item_extractviewer: 'Element Viewer',
    content_item_opensinnewtab: '(opens in a new tab)',
    content_item_generatetest: 'Generate Test',
    content_item_reviewtest: 'Review Test',
    content_collection_generatetest: 'Generate Test',
    content_collection_reviewtest: 'Review Test',
    content_item_regeneratetitle: 'Regenerate Title',
    content_item_regeneratedescription: 'Regenerate Description',
    content_item_regeneratekeywords: 'Regenerate Keywords',
    content_item_externalsource: 'External Source',
    content_item_externalid: 'External ID',
    content_item_externaltab: 'External',
    content_item_externalclassification1: 'External Classification 1',
    content_item_externalclassification2: 'External Classification 2',
    content_item_externalpublicationstatus: 'External Publication Status',
    content_item_externalpublisheddatelatest: 'External Published Date (Latest)',
    content_item_externalcreationdate: 'External Creation Date',
    content_item_externallatestaccess: 'External Latest Access',
    content_item_externalaccesshistory: 'External Access History',
    content_item_authoringtool: 'Authoring Tool',
    content_item_filename: 'File Name',
    content_item_languages: 'Languages',
    content_item_filesize: 'File Size',
    content_item_resourceid: 'Resource ID',
    content_item_uploaded: 'Uploaded',
    content_item_lastupdated: 'Last Updated',
    content_item_itemhistory: 'Item History',
    content_item_collections: 'Collections',
    content_item_nocollections: 'No Collections',
    content_item_keywords: 'Keywords ({count})',
    content_item_generatedkeywords: 'Generated Keywords ({count})',
    content_item_customertags: 'Customer Tags ({count})',
    content_item_tagsandkeywords: 'Customer Tags & Generated Keywords ({count})',
    content_item_skills: 'Skills ({count})',
    content_item_confirm_delete: 'This item will be permanently deleted.',
    content_item_confirm_removecollection: 'This item will be removed from <strong>{name}</strong>.',
    content_item_addcollection_select: 'Select Collection',
    content_item_addcollection_create: 'Create New Collection',
    content_item_nocollections_available: 'No Collections Available',
    content_item_addcollection_title_placeholder: 'Enter a title',
    content_item_addcollection_desc_placeholder: 'Enter a description',
    content_item_addcollection_back: 'Back to Select Collection',
    content_item_addcollection_button: 'Add to Collection',
    content_item_addcollection_savecreate: 'Save & Create',
    content_item_addcollection_error_title: 'Please enter a title.',
    content_item_addcollection_error_desc: 'Please enter a description.',
    content_item_editkeyword: 'Edit Keyword',
    content_item_addkeyword: 'Add Keyword',
    content_item_modal_descriptions: 'Descriptions',
    content_item_modal_descriptions_text: 'Select a previously reviewed description to modify',
    content_item_modal_titles: 'Titles',
    content_item_modal_titles_text: 'Please select from the generated selections or provide your own text in the field directly.',
    content_item_modal_keywords: 'Keywords',
    content_item_modal_keywords_applied: 'You have applied all generated keywords.<br>You may click the Add button to add a custom keyword.',
    content_item_modal_keywords_reviewed: 'Below are your previously reviewed keywords.<br>You may also click the Add button to add a custom keyword.',
    content_item_reupload_complete: 'Reupload Complete',
    content_item_reupload_message: 'This item\'s file has been reuploaded.',
    content_item_error_unsupported: 'Unsupported File',
    content_item_error_unsupported_message: 'The selected file is not supported.',

    // Content - Test Generation Page
    testgen_header: 'Content: Test Generation',
    testgen_loading: 'Checking for existing tests...',
    testgen_generating: 'Generating test questions...',
    testgen_generating_wait: 'This may take a few moments',
    testgen_questions_count: 'Test Questions ({count})',
    testgen_saving: 'Saving...',
    testgen_removed_show: '+ Show',
    testgen_removed_hide: '- Hide',
    testgen_removed_questions: 'Removed Questions ({count})',
    testgen_approve: 'Approve',
    testgen_saveall: 'Save All',
    testgen_exporting: 'Exporting...',
    testgen_downloadtest: 'Download Test',
    testgen_done: 'Done',
    testgen_error_start: 'Failed to start test generation',
    testgen_approved: 'Question approved',
    testgen_rejected: 'Question rejected',
    testgen_questiontext: 'Question text',
    testgen_answertext: 'Answer text',
    testgen_iscorrectanswer: 'Mark as correct answer',
    collectiontestgen_header: 'Collection: Test Generation',
    collectiontestgen_generating: 'Generating collection test questions...',
    collectiontestgen_generating_wait: 'This may take several minutes depending on the number of resources',
    collectiontestgen_source: 'Source',
    collectiontestgen_notestsmsg: 'No test questions were generated. The collection may not have enough extractable content.',
    collectiontestgen_backtocollection: 'Back to Collection',
    collectiontestgen_error_loaddata: 'Failed to load collection data.',
    collectiontestgen_error_failed: 'Test generation failed. Please try again.',
    collectiontestgen_error_timeout: 'Test generation timed out. Please check back later.',

    // Preferences Tab Component
    preftab_title: 'Notification Preferences',
    preftab_description: 'Manage which emails you receive from MetaLark.ai',
    preftab_error_load_title: 'Failed to Load Preferences',
    preftab_error_load_retry: 'Retry',
    preftab_badge_required: 'Required',
    preftab_badge_alwayson: 'Always On',
    preftab_badge_alwaysoff: 'Always Off',
    preftab_lastupdated: 'Last updated:',
    preftab_success_title: 'Changes Saved',
    preftab_success_description: 'Your notification preferences have been updated.',
    preftab_info_title: 'About These Settings',
    preftab_info_items: '<li><strong>Required notifications</strong> cannot be disabled for security and compliance reasons</li><li>Changes to optional preferences take effect immediately</li><li>Your preference history is tracked for auditing purposes</li>',
    preftab_empty: 'No preference groups available at this time.',
    preftab_error_update: 'Failed to update preference:',
    preftab_item_marketing_emails: 'Marketing Emails',
    preftab_item_marketing_emails_desc: 'Promotional emails and feature announcements',
    preftab_item_payment_alerts: 'Payment Alerts',
    preftab_item_payment_alerts_desc: 'Critical: Payment notifications (required)',
    preftab_item_account_security_alerts: 'Security Alerts',
    preftab_item_account_security_alerts_desc: 'Critical: Security alerts (required)',
    preftab_item_grace_period_warning: 'Grace Period Warnings',
    preftab_item_grace_period_warning_desc: 'Warnings when your account is in a grace period',
    preftab_item_subscription_renewal_reminder: 'Subscription Renewal Reminders',
    preftab_item_subscription_renewal_reminder_desc: 'Reminders about upcoming subscription renewals',
    preftab_item_trial_ending_soon: 'Trial Ending Soon',
    preftab_item_trial_ending_soon_desc: 'Reminders when your trial period is ending',
    preftab_item_quota_alerts: 'Quota & Credit Alerts',
    preftab_item_quota_alerts_desc: 'Alerts when your credit balance is low or exhausted',

    // Reports Page
    reports_title: 'Reports',
    reports_tab_content: 'Content',
    reports_tab_collections: 'Collections',
    reports_tab_skills: 'Skills',
    reports_tab_insightconcepts: 'Insight Concepts',
    reports_tab_insights: 'Insights',
    reports_dashboard_brandconsistency_title: 'Brand Consistency Explorer',
    reports_dashboard_brandconsistency_desc: 'The Brand Consistency Explorer is a dashboard that helps content administrators evaluate how well their educational materials reflect the organization\'s brand identity. It brings together all learning resources in one place, highlighting how messaging aligns with brand guidelines.',
    reports_dashboard_blooms_title: 'Bloom\'s Taxonomy Dashboard',
    reports_dashboard_blooms_desc: 'The Bloom\'s Taxonomy Dashboard provides a structured view of how learning materials support different levels of cognitive development, from foundational knowledge to higher-order thinking. It analyzes all learning resources to help educators and content designers assess the depth and progression of learning objectives.',
    reports_dashboard_osha_title: 'OSHA Compliance Dashboard',
    reports_dashboard_osha_desc: 'The OSHA Compliance Dashboard gives organizations a clear view of how their learning resources and training materials align with Occupational Safety and Health Administration (OSHA) standards. It centralizes compliance-related content so safety managers and training teams can easily monitor coverage, progress, and potential gaps.',
    reports_viewdashboard: 'View Dashboard',
    reports_content_all_title: 'All Content',
    reports_content_all_desc: 'View a report of all content items processed in your account.',
    reports_content_refresh_title: 'Content Marked as Refresh',
    reports_content_refresh_desc: 'View a report of all content items marked as Refresh.',
    reports_content_retire_title: 'Content Marked as Retire',
    reports_content_retire_desc: 'View a report of all content items marked as Retire.',
    reports_skills_all_title: 'All Skills',
    reports_skills_all_desc: 'View a report of all skills in your account.',
    reports_collections_all_title: 'All Collections',
    reports_collections_all_desc: 'View a report of all collections created in your account.',
    reports_runreport: 'Run Report',
    reports_exportdata: 'Export Data',
    reports_dashboard_jobcompletion: 'Job Completion',
    reports_dashboard_associatedskills: 'Associated Skills',
    reports_dashboard_mediatypes: 'Content Media Types',
    reports_dashboard_mediacategories: 'Content Media Categories',
    reports_dashboard_authoringtools: 'Authoring Tools',
    reports_dashboard_emptycollections: 'Empty Collections',
    reports_dashboard_noemptycollections: 'No Empty Collections',
    reports_dashboard_totalcollections: 'Total Collections Created',
    reports_dashboard_skillsorigin: 'Skills By Origin',
    reports_dashboard_topskills: 'Top Skills by Content',
    reports_dashboard_confidence: 'Confidence Score Distribution',
    reports_dashboard_largestcollections: 'Largest Collections',
    reports_dashboard_totalitems: 'Total Items',
    reports_dashboard_total: 'Total',
    reports_dashboard_nodata: 'No Data',
    reports_dashboard_deletecollection: 'Delete Collection',
    reports_dashboard_viewcollection: 'View Collection',
    reports_dashboard_deleteconfirmmessage: 'This empty collection will be permanently deleted.',

    // Report Preview Pages
    reportpreview_listempty: 'List currently empty.<br>Update filters to continue.',
    reportpreview_content_title: 'Content Report',
    reportpreview_skills_title: 'Skills Report',
    reportpreview_collections_title: 'Collections Report',

    // Export Page
    export_title: 'Export Metadata',
    export_description: 'All content items appear in the list below by default. Use the search and filters to narrow the list, then export everything shown or select just the items you want to include in the export.',
    export_listempty: 'List currently empty.<br>Search for a term or set some filters to begin.',

    // Migration Console — Targets overview
    migration_title: 'Migration',
    migration_nav: 'Migration',
    migration_newtarget: 'New Target',
    migration_newtarget_cardsub: 'Pick a platform and folder — Google Drive, SharePoint, or Amazon S3. Reuse a folder to reopen its target.',
    migration_recentactivity: 'Recent activity',
    migration_pushesrunning: '{count, plural, one {# push running} other {# pushes running}}',
    migration_stat_targets: 'Targets',
    migration_stat_pushingnow: 'Pushing Now',
    migration_stat_filestracked: 'Files Tracked',
    migration_stat_staged: 'Staged',
    migration_stat_failed: 'Failed',
    migration_overview_info: 'A target is a connected destination (Google Drive, SharePoint, Amazon S3). Open one to adjust its file list and push.',
    migration_overview_empty: 'No targets yet. Create one to start staging and pushing content.',
    migration_loaderror: 'Could not load migration targets. Please try again.',
    // Target card
    migration_card_filesinlist: '{count, plural, one {file in list} other {files in list}}',
    migration_card_staged: '{count} staged',
    migration_card_failed: '{count} failed',
    migration_card_allpushed: 'All pushed',
    migration_card_lastpush: 'Last push {date}',
    migration_card_created: 'Created {date}',
    migration_card_pushing: 'Pushing {count, plural, one {# file} other {# files}}…',
    // Status badges
    migration_badge_pushing: 'Pushing',
    migration_badge_staged: 'Ready to push',
    migration_badge_notpushed: 'Not pushed',
    migration_badge_uptodate: 'Up to date',
    migration_badge_needsretry: 'Needs retry',
    // Per-file migration status
    migration_status_staged: 'Staged',
    migration_status_pushed: 'Pushed',
    migration_status_failed: 'Failed',
    migration_status_stale: 'Needs re-push',
    migration_status_pushed_no_metadata: 'Metadata not applied',
    migration_status_pushed_no_content: 'Content not attached',
    // New Target flow
    migration_newtarget_desc: 'Pick a destination platform and the folder to migrate into. Reuse a platform + folder to reopen that target.',
    migration_platform_connected: 'Connected',
    migration_platform_notconnected: 'Not connected',
    migration_connect: 'Connect',
    migration_connect_drive: 'Authorize OnPoint to write to your Drive.',
    migration_connect_sharepoint: 'Sign in with Microsoft to connect a site.',
    migration_connect_s3: 'Add an access key or IAM role to connect.',
    migration_connecttocontinue: 'Connect {platform} to continue.',
    migration_selectfolder: 'Folder to migrate into',
    migration_selectcustomer: 'Destination account',
    migration_selectcustomer_label: 'Account in the LMS',
    migration_selectcustomer_placeholder: 'Select an account…',
    migration_selectcustomer_loading: 'Loading accounts…',
    migration_selectcustomer_note: 'Content is created in this account. An LMS has no folders to choose.',
    migration_selectcustomer_none: 'The stored credentials cannot reach any account. Reconnect this platform from Content Repositories.',
    migration_selectcustomer_failed: 'Could not load the accounts for this platform.',
    migration_nodestinations: 'No destinations connected',
    migration_nodestinations_hint: 'Connect a storage provider or an LMS before creating a migration target.',
    migration_destinations_from_connections: 'Destinations come from your connected repositories.',
    migration_managerepositories: 'Manage repositories',
    migration_account_onpoint: 'OnPoint account',
    migration_selectcategory: 'Moodle category',
    migration_selectcategory_label: 'Destination category',
    migration_selectcategory_loading: 'Loading categories…',
    migration_selectcategory_placeholder: 'Choose a category',
    migration_selectcategory_failed: 'Could not load categories.',
    migration_selectcategory_none: 'This token can reach no course categories, so there is no destination to choose.',
    migration_createcategory_open: 'Create a category',
    migration_createcategory_label: 'New category name',
    migration_createcategory_confirm: 'Create',
    migration_createcategory_note: 'Created on the Moodle site itself. MetaLark cannot remove it afterwards.',
    migration_createcategory_failed: 'Could not create the category.',
    migration_account_moodle: 'Moodle site',
    migration_account_acto: 'Acto content hub',
    migration_selectactofolder: 'Select a folder',
    migration_selectactofolder_loading: 'Loading folders…',
    migration_selectactofolder_failed: 'Could not list folders for this Acto domain.',
    migration_selectactofolder_none: 'This Acto domain has no folders this connection can reach.',
    migration_selectactofolder_leaf: 'No sub-folders. This folder can still be the destination.',
    migration_selectactofolder_hint: 'Open a folder to choose it as the destination.',
    migration_selectactofolder_chosen: 'Pushing to {path}',
    migration_actofolder_breadcrumb: 'Folder path',
    migration_destination_picker_unavailable: '{name} destinations cannot be set up in this version yet.',
    migration_placementnote: 'Files are copied into the chosen folder.',
    migration_itemtype_course: 'Course',
    migration_itemtype_nugget: 'Nugget',
    migration_itemtype_resource: 'Resource',
    migration_variant_scorm: 'SCORM package',
    migration_variant_folder: 'Folder of files',
    migration_variant_resource: 'File',
    migration_variant_page: 'Page',
    migration_variant_pdf: 'PDF',
    migration_variant_video: 'Video',
    migration_variant_audio: 'Audio',
    migration_variant_image: 'Image',
    migration_variant_office_document: 'Office document',
    migration_variant_text_content: 'Text',
    migration_itemtype_col: 'Created as',
    migration_itemtype_tip: 'What the destination created for this item',
    migration_whatgetspushed: 'Each item carries:',
    migration_whatgetspushed_title: 'title',
    migration_whatgetspushed_description: 'description',
    migration_whatgetspushed_keywords: 'keywords',
    migration_whatgetspushed_skills: 'skills',
    migration_fieldmap_step_choose: '1. Choose the kind of item',
    migration_fieldmap_step_choose_sub: 'Each kind has its own set of fields, so they are configured separately.',
    migration_fieldmap_typemapped: '{count} of {total} fields mapped',
    migration_fieldmap_type_is: 'Type: {type}',
    migration_fieldmap_type_unknown: 'Type not reported',
    migration_fieldmap_type_unknown_note: '{platform} does not report what type some of these fields hold, so MetaLark cannot check a value before sending it. A value the field will not accept is discarded at the destination without an error.',
    migration_fieldmap_type_none_note: '{platform} does not report what type any of these fields hold, so MetaLark cannot check a value before sending it. A value the field will not accept is discarded at the destination without an error.',
    migration_fieldmap_discovered_none_title: 'No custom fields to map',
    migration_fieldmap_discovered_none: '{platform} defines no custom fields for a {type}. Title, description, keywords and skills travel with every item regardless, so nothing is missing here.',
    migration_fieldmap_discovered_unknown_title: 'Custom fields could not be read',
    migration_fieldmap_discovered_unknown: 'MetaLark reads {platform}’s custom field definitions from an item that already exists at the destination, and there is none there yet. This does not mean {platform} defines no fields. Push one item, then reload this page.',
    migration_fieldmap_discovered_failed_title: '{platform} could not be reached',
    migration_fieldmap_discovered_failed: 'MetaLark could not ask {platform} which custom fields it defines, so none can be mapped right now. Everything else on this page is unaffected, and any mapping already saved is untouched.',
    migration_fieldmap_discovered_failed_credential: 'The credential stored for this connection was refused. Reconnect {platform} from the Content Repositories page.',
    migration_fieldmap_discovered_orphan_title: 'Some mappings point at fields that no longer exist',
    migration_fieldmap_discovered_orphan: '{platform} no longer reports these fields: {fields}. A mapping into a field the destination does not recognize is ignored without an error, so these values will not arrive. Remove them, or restore the fields in {platform}.',
    migration_fieldmap_slot_unnamed: 'Not named in {platform}',
    migration_fieldmap_field_required: 'Required',
    migration_fieldmap_field_maxlength: 'Up to {count} characters',
    migration_fieldmap_allowed_values: 'Allowed values: {values}',
    migration_fieldmap_type_advisory: '{platform} reports how each field is configured, not what an import checks. Nothing on its import path reads a field’s type, its required flag or its list of allowed values, so a value that does not fit is still written. Only some field sections in {platform} offer a type at all, so the rest report as text.',
    migration_fieldmap_discovered_old_title: 'This {platform} instance cannot report field names',
    migration_fieldmap_discovered_old: 'The version of {platform} this connection points at has no way to say what its custom fields are called, so they are listed by number. Every field can still be mapped. Upgrading {platform} makes the names appear here on their own.',
    migration_fieldmap_discovered_names_failed_title: 'Field names could not be read from {platform}',
    migration_fieldmap_discovered_names_failed: 'MetaLark could not ask {platform} what its custom fields are called, so they are listed by number. Every field can still be mapped, and any mapping already saved is untouched.',
    migration_fieldmap_discovered_lastknown: 'The names shown are the last ones {platform} reported, not what it says now, so a field renamed there since will still appear under its previous name. They refresh by themselves as soon as {platform} can be read again.',
    migration_fieldmap_step_map: '2. Map the {type} fields',
    migration_fieldmap_step_map_sub: 'Choose what fills each {type} field in {platform} when the object is created. Title, description, keywords and skills already travel with every item, so they need no rule here.',
    migration_term_metatags: 'metatags',
    migration_term_keywords: 'keywords',
    migration_push_tags: 'Tags',
    migration_push_tags_sub: 'MetaLark tags have no field of their own here. They can be sent as {term} instead.',
    migration_push_tags_toggle: 'Send as {term}',
    migration_push_desc_direct: 'Create {count, plural, one {# item} other {# items}} from {name} directly in {platform}, with their metadata. No mapping document is written.',
    migration_push_itemmeta: 'Title, description and keywords',
    migration_push_itemmeta_sub: 'Only values approved in MetaLark. Keywords are stored as {term} in {platform}.',
    migration_push_skills: 'Skills',
    migration_push_skills_sub: 'Created in {platform} if they are not already there, and attached to each item.',
    migration_fieldmap_configure: 'Configure fields',
    migration_fieldmap_heading: 'Destination field mapping',
    migration_fieldmap_intro: 'Choose what MetaLark puts into each of the destination\'s own fields. The destination does not report what its numbered fields are called, so give each one a name here to keep it recognizable — names are saved against the platform account, not against this migration.',
    migration_fieldmap_intro_discovered: 'Choose what MetaLark puts into each of the custom fields {platform} defines. The names and types come from the {platform} site itself, so this list changes when someone changes it there.',
    migration_fieldmap_count: '{count, plural, =0 {No fields mapped} one {# field mapped} other {# fields mapped}}',
    migration_fieldmap_types: 'Item types',
    migration_fieldmap_slot: 'Field',
    migration_fieldmap_name: 'Field rule name',
    migration_fieldmap_name_placeholder: 'e.g. Business Unit',
    migration_fieldmap_name_for: 'Name for {slot}',
    migration_fieldmap_source: 'Send',
    migration_fieldmap_source_for: 'What to send in {slot}',
    migration_fieldmap_source_none: 'Nothing',
    migration_fieldmap_source_literal: 'A fixed value',
    migration_fieldmap_source_attribute: 'An attribute',
    migration_fieldmap_source_resource: 'A resource detail',
    migration_fieldmap_value: 'Value',
    migration_fieldmap_value_for: 'Value for {slot}',
    migration_fieldmap_fallback: 'If empty',
    migration_fieldmap_fallback_for: 'Fallback for {slot}',
    migration_fieldmap_fallback_placeholder: 'Optional fallback',
    migration_fieldmap_literal_placeholder: 'Value to send every time',
    migration_fieldmap_eg: 'e.g. {value}',
    migration_fieldmap_eg_empty: '{name} has no value for this, so nothing would be sent for it — other items may have one.',
    migration_fieldmap_choose: 'Choose…',
    migration_fieldmap_notypes: 'Could not read the destination\'s field list. Reload the page to try again.',
    migration_fieldmap_notsent: 'Left untouched',
    migration_fieldmap_noattributes: 'This workspace has no attributes defined yet.',
    migration_fieldmap_incomplete: 'Finish these before saving',
    migration_fieldmap_err_literal: '{where}: enter the value to send.',
    migration_fieldmap_err_nokey: '{where}: choose what to send.',
    migration_fieldmap_unsavedrow: 'Not saved yet',
    migration_fieldmap_typefields: '{type} fields',
    migration_fieldmap_fallback_note: 'Optional. Leave empty to send nothing when the value is missing.',
    migration_fieldmap_named_heading: 'Standard destination fields',
    migration_fieldmap_named_sub: 'Fields {platform} names itself, filled the same way on every push. Most need no setting; where one is offered, it is because MetaLark cannot know the answer.',
    migration_fieldmap_named_readonly: 'Set automatically',
    migration_fieldmap_named_notapplied: 'Automatically set by {platform}. MetaLark value not accepted by API yet.',
    migration_fieldmap_named_onlyon: 'Only on {types}',
    migration_fieldmap_named_auto_from: 'Always MetaLark\'s {source}',
    migration_fieldmap_named_auto_literal: 'Always "{value}".',
    migration_fieldmap_col_sends: 'MetaLark sends',
    migration_fieldmap_col_into: 'Into {platform} field',
    migration_push_origin: 'System of origin',
    migration_push_origin_sub: '{platform} records this as where the content came from',
    migration_fieldmap_saved: 'Saved',
    migration_fieldmap_loaderror: 'Something went wrong. Your changes have not been saved — try again.',
    migration_fieldmap_preview: 'Preview',
    migration_fieldmap_preview_heading: 'What will be sent',
    migration_fieldmap_preview_scope: 'Showing {shown} of {total} staged items',
    migration_fieldmap_preview_unresolved: 'None of the {count} staged items could be read, so there is nothing to show. Reload the page, and if it persists the staged content may no longer exist.',
    migration_fieldmap_preview_empty: 'Nothing is staged on this destination yet, so there is nothing to preview. Add items first.',
    migration_fieldmap_preview_blank: 'Nothing to send',
    migration_fieldmap_preview_blanks_note: 'Fields marked "Nothing to send" are mapped but empty for that item, so they will be left untouched in the destination. Set a fallback if that is not what you want.',
    migration_fieldmap_hint: 'Applies to everything pushed to this destination',
    migration_targetname: 'Target name',
    migration_targetname_placeholder: 'e.g. Compliance Library',
    migration_targetname_hint: 'A friendly name so you can find this destination again later.',
    migration_createtarget: 'Create Target',
    migration_openexisting: 'Open Existing Target',
    migration_duplicate_title: 'This folder already has a target: {name}',
    migration_duplicate_sub: 'Open it to pick up where you left off — its file list, statuses, and push history are all there.',
    migration_created_toast: 'Target “{name}” created',
    migration_opened_toast: 'Opened existing target “{name}”',
    // Folder browser
    migration_folder_choosedrive: 'Choose a drive',
    migration_folder_choosesite: 'Choose a site',
    migration_folder_chooselibrary: 'Choose a document library',
    migration_folder_usethisfolder: 'Use this folder',
    migration_folder_root: 'Root',
    migration_folder_empty: 'No subfolders here.',
    migration_folder_loaderror: 'Could not browse this location.',
    migration_folder_selected: 'Destination: {path}',
    // Target detail
    migration_target_prefix: 'Target:',
    migration_additems: 'Add Items',
    migration_retryfailed: 'Retry Failed ({count})',
    migration_pushtotarget: 'Push to Target',
    migration_pushtotarget_count: 'Push to Target ({count})',
    migration_pushing_btn: 'Pushing…',
    migration_meta_filesinlist: 'Files in list',
    migration_meta_waiting: 'Waiting to push',
    migration_meta_lastpush: 'Last push',
    migration_meta_never: 'Never pushed',
    migration_meta_inprogress: 'In progress…',
    migration_defaultfolder: 'Default folder: {folder}',
    migration_pushidentity_you: 'Connected by you',
    migration_pushidentity_other: 'Connected by {user}',
    migration_pushidentity_unknown: 'another user',
    migration_push_takeover: 'This target currently pushes as {user}. Your push will run as you instead.',
    migration_banner_staged: '{count, plural, one {# file is staged} other {# files are staged}}',
    migration_banner_stale: '{count, plural, one {# file needs re-push} other {# files need re-push}}',
    migration_banner_no_metadata: '{count, plural, one {# file is missing its metadata} other {# files are missing their metadata}}',
    migration_banner_and: 'and',
    migration_banner_pushhint: 'Push to {platform} when ready. Already-pushed files stay put.',
    migration_banner_allpushed: 'Everything in this target’s file list has been pushed to {platform}. Add Items to stage more, then push the delta.',
    migration_banner_failedhint: '{count, plural, one {# file failed its last push} other {# files failed their last push}}. Push to Target sends new content only. Use Retry Failed to send these again.',
    migration_files_heading: 'Files in this target ({count})',
    migration_filter_all: 'All {count}',
    migration_filter_staged: 'Staged {count}',
    migration_filter_pushed: 'Pushed {count}',
    migration_filter_failed: 'Failed {count}',
    migration_filter_stale: 'Needs re-push {count}',
    migration_files_empty_title: 'No files yet',
    migration_files_empty_sub: 'Add content items to stage them for this target, then push.',
    migration_progress_pushingto: 'Pushing to {folder}',
    migration_col_migration: 'Migration',
    migration_retry: 'Retry',
    migration_removefromlist: 'Remove from list',
    migration_unstage: 'Unstage',
    migration_pushhistory: 'Push history ({count})',
    migration_pushhistory_empty: 'No pushes yet. Stage files and push to create the first run.',
    migration_run_pushed: '{count} pushed',
    migration_run_failed: '{count} failed',
    migration_run_total: '{count, plural, one {# file} other {# files}}',
    migration_pushfiles_title: 'Pushed files',
    migration_pushfiles_empty: 'No files were recorded for this push.',
    migration_pushfiles_inprogress: 'Push in progress. Files will appear here as they complete.',
    migration_pushfiles_view: 'View files',
    migration_deletetarget: 'Delete Target',
    migration_deletetarget_confirm: 'Delete this target and its staging list? Push history is preserved.',
    migration_renamed_toast: 'Target name updated',
    migration_deleted_toast: 'Target deleted',
    migration_delete_refused: 'Cannot delete: this target has push history.',
    migration_error_generic: 'Something went wrong. Please try again.',
    migration_error_needsreauth: 'Your account isn\'t connected or authorized for this destination. Connect or re-authorize your account, or update your write permissions, then try the push again.',
    migration_avail_checking: 'Checking destination…',
    migration_avail_available: 'Destination ready.',
    migration_avail_unavailable: 'Destination unavailable.',
    migration_avail_unknown: 'Destination availability unknown.',
    migration_avail_foldermissing: 'Destination folder no longer exists at the provider.',
    migration_avail_needsreauth: 'Connection needs re-authorization.',
    migration_avail_unreachable: 'Couldn\'t reach the provider. Try again.',
    migration_avail_notconfigured: 'Destination is no longer configured.',
    migration_avail_notconfigured_token: 'This connection is not set up to accept a push.',
    migration_avail_reauthorize: 'Re-authorize',
    migration_avail_recheck: 'Re-check',
    migration_avail_pushblocked: 'Pushing is blocked until the destination is available.',
    // Add Files browse
    migration_addfiles_title: 'Add Files',
    migration_selectedcount: '{count} selected',
    migration_stagefiles: 'Stage Files',
    migration_stagefiles_count: '{count, plural, one {Stage # File} other {Stage # Files}}',
    migration_addfiles_staging: 'Staging files for {name}',
    migration_addfiles_select: 'Select files to stage',
    migration_addfiles_info: 'Selected files are added to this target’s list with a Staged status. Nothing is pushed until you push to the target.',
    migration_staged_toast: '{count, plural, one {Staged # file} other {Staged # files}}',
    migration_pagelength: 'Page Length:',
    // Push modal
    migration_push_title: 'Push to {platform}',
    migration_push_desc: 'Copy the {count, plural, one {# file} other {# files}} waiting in {name} to {platform}, along with a metadata mapping document.',
    migration_push_target: 'Target',
    migration_push_account: 'Account',
    migration_push_filestopush: 'Files to push',
    migration_push_excludesfailed: '{count, plural, one {# file that failed a previous push is not included. Use Retry Failed to send it again} other {# files that failed a previous push are not included. Use Retry Failed to send them again}}',
    migration_fieldmap_pertype: '{type}: {fields}',
    migration_push_destfields_typenote: 'Which set an item gets depends on what it becomes — packaged courses become Courses, single files become Nuggets.',
    migration_push_itemsofthistype: '{count, plural, one {# item in this push} other {# items in this push}}',
    migration_push_noitemsofthistype: 'nothing in this push becomes one',
    migration_push_destfields: 'Destination fields',
    migration_push_destfields_none: 'No fields are mapped, so only the title, description and keywords are set.',
    migration_push_destfields_optional: 'Optional',
    migration_push_whatgetspushed: 'What gets pushed',
    migration_push_format: 'File format',
    migration_push_resourcefiles: 'Resource files',
    migration_push_resourcefiles_sub: 'The source file for each item, organized into folders by collection.',
    migration_push_filesbadge: '{count, plural, one {# file} other {# files}}',
    migration_push_mappingdoc: 'Mapping document',
    migration_push_mappingdoc_sub: 'One {format} record per item — basic metadata keyed by resource filename.',
    migration_push_alwaysincluded: 'Always included',
    migration_push_optionalmeta: 'Optional Metadata',
    migration_push_metaselected: '{count} of {total} selected',
    migration_push_optionalmeta_desc: 'These metadata types can be optionally included in the {format}.',
    migration_meta_skills: 'Skills',
    migration_meta_skills_sub: 'Skill tags mapped to each item',
    migration_meta_tags: 'Tags',
    migration_meta_tags_sub: 'Tags applied to each item',
    migration_meta_keywords: 'Keywords',
    migration_meta_keywords_sub: 'Extracted keywords per item',
    migration_meta_tests: 'Tests',
    migration_meta_tests_sub: 'Assessment items & questions',
    migration_push_folder: 'Folder for this push',
    migration_push_pathpreview: 'Objects are written as {pattern}',
    migration_push_downloadzip: 'Download as a .zip package instead',
    migration_push_downloadzip_sub: '{count, plural, one {# file} other {# files}} + {docs} {format} document(s) saved locally',
    migration_push_footnote: '{count, plural, one {# file} other {# files}}',
    migration_push_btn: '{count, plural, one {Push # File} other {Push # Files}}',
    migration_push_started_toast: '{count, plural, one {Pushing # file} other {Pushing # files}} to {platform}',
    migration_push_zip_toast: 'Packaging {name}',
    // Destination config fields
    migration_dest_drive: 'Drive',
    migration_dest_drivefolder: 'Drive folder',
    migration_dest_folder: 'Folder',
    migration_dest_site: 'Site',
    migration_dest_library: 'Document library',
    migration_dest_bucket: 'Bucket',
    migration_dest_prefix: 'Key prefix',
    // Provider account labels
    migration_account_google: 'Google account',
    migration_account_microsoft: 'Microsoft tenant',
    migration_account_aws: 'AWS account',
    migration_account_dropbox: 'Dropbox account',
    migration_account_box: 'Box account',
    migration_account_generic: 'Account',
    // Push progress (screen-reader live region)
    migration_live_pushstarted: 'Push started for {name}: {total} files',
    migration_live_pushprogress: '{name}: {done} of {total} files pushed, {failed} failed',
    migration_live_pushcomplete: '{name}: push complete. {succeeded} pushed, {failed} failed.',
    migration_selectaction: 'Select Action',

    // Home Page
    home_noworkspace_title: 'No Workspace Selected',
    home_noworkspace_message: 'You need to select a workspace to access the home page.',
    home_selectworkspace: 'Select Workspace',
    home_uploadcontent: 'Upload Content',
    home_viewcontent: 'View Content',
    home_runreports: 'Run Reports',
    home_exportmetadata: 'Export Metadata',
    home_searchlabel: 'Looking for something specific?',
    home_itemsuploaded: 'Items Uploaded',
    home_collectionscreated: 'Collections Created',
    home_getstarted: 'Get started with our',
    home_setupguide: 'Setup Guide',
    home_recentitems: '5 Most Recently Uploaded Items',
    home_recentcollections: '5 Most Recently Created Collections',
    home_listempty: 'List currently empty.',
    home_uploadtobegin: 'Upload some content to begin.',
    home_createcollectiontobegin: 'Create a collection to begin.',

    // Select Workspace Page
    selectworkspace_welcomeback: 'Welcome Back!',
    selectworkspace_selecttocontinue: 'Select a workspace to continue',
    selectworkspace_filterbycustomer: 'Filter by Customer',
    selectworkspace_allcustomers: 'All Customers ({count} {count, plural, one {workspace} other {workspaces}})',
    selectworkspace_customerwithcount: '{name} ({count} {count, plural, one {workspace} other {workspaces}})',
    selectworkspace_recentlyaccessed: 'RECENTLY ACCESSED',
    selectworkspace_yourroleinworkspace: 'Your role in this workspace',
    selectworkspace_nodescription: 'No description',
    selectworkspace_allworkspaces: 'ALL WORKSPACES',
    selectworkspace_selectaworkspace: 'SELECT A WORKSPACE',
    selectworkspace_noworkspacesavailable: 'No workspaces available. Please contact your administrator.',
    selectworkspace_noworkspacescurrent: 'No workspaces are currently available.',
    selectworkspace_gotocustomermanagement: 'Go to Customer Management',
    selectworkspace_activeimpersonationsession: 'Active impersonation session',
    selectworkspace_activesession: 'Active Session',
    selectworkspace_workspaceid: 'Workspace ID',
    selectworkspace_customer: 'Customer',
    selectworkspace_status: 'Status',
    selectworkspace_sessiontime: 'Session Time',
    selectworkspace_resumesession: 'Resume Session',
    selectworkspace_enterworkspace: 'Enter Workspace',
    selectworkspace_accessworkspace: 'Access Workspace',
    selectworkspace_havingtrouble: 'Having trouble?',
    selectworkspace_goto_admin_dashboard: 'Or go to Admin Dashboard',
    selectworkspace_contactsupport: 'Contact Support',
    selectworkspace_workspaceinactive: 'This workspace is currently inactive. Please contact your administrator.',
    selectworkspace_nopermission: 'You do not have permission to access this workspace',
    selectworkspace_nopermission_selection: 'You do not have permission to access workspace selection. Please contact your administrator.',

    // Billing Page
    billing_title: 'Billing & Subscription',
    billing_subtitle: 'Manage your subscription and view credit usage',
    billing_loadinginfo: 'Loading billing information...',
    billing_subscription: 'Subscription',
    billing_trialactive: 'Trial Active',
    billing_trialends: 'Ends {date}',
    billing_trialperiodactive: 'Trial period active',
    billing_trialperiod: 'Trial Period',
    billing_trialends_label: 'Trial Ends',
    billing_started: 'Started',
    billing_subscribenow: 'Subscribe Now',
    billing_pricingsummary: '${monthly}/month or ${annual}/year (save {percent}%)',
    billing_activesubscription: 'Active Subscription',
    billing_plan: 'Plan',
    billing_monthly: 'Monthly',
    billing_annual: 'Annual',
    billing_periodstart: 'Period Start',
    billing_renews: 'Renews',
    billing_cancels: 'Cancels',
    billing_managesubscriptionpayment: 'Manage Subscription & Payment',
    billing_changeplan: 'Change Plan',
    billing_paymentpastdue: 'Payment Past Due',
    billing_paymentfailed: 'Your payment failed. Please update your payment method to continue using the platform.',
    billing_datadeletionwarning: 'Data Deletion Warning',
    billing_pastdue: 'Past Due',
    billing_periodend: 'Period End',
    billing_updatepaymentmethod: 'Update Payment Method',
    billing_updatepaymentorplan: 'Update your payment method or switch to a different plan',
    billing_subscriptionunpaid: 'Subscription Unpaid',
    billing_paymentfailedresubscribe: 'Payment failed and subscription is no longer active. Please update your payment method and resubscribe.',
    billing_previousplan: 'Previous Plan',
    billing_ended: 'Ended',
    billing_updatepaymentresubscribe: 'Update Payment & Resubscribe',
    billing_choosenewplan: 'Choose New Plan',
    billing_paymentincomplete: 'Payment Incomplete',
    billing_paymentpending: 'Your subscription is pending payment confirmation. Please complete the payment process.',
    billing_incomplete: 'Incomplete',
    billing_selectedplan: 'Selected Plan',
    billing_completepayment: 'Complete Payment',
    billing_completestripe: 'Complete your payment in the Stripe Customer Portal',
    billing_subscriptionexpired: 'Subscription Expired',
    billing_paymentnotcompleted: 'Your initial payment was not completed within the required timeframe. Please subscribe again.',
    billing_expired: 'Expired',
    billing_subscriptionpaused: 'Subscription Paused',
    billing_pausedmessage: 'Your subscription is temporarily paused. Resume to continue using the platform.',
    billing_paused: 'Paused',
    billing_pausedsince: 'Paused Since',
    billing_resumesubscription: 'Resume Subscription',
    billing_subscriptioncancelled: 'Subscription Canceled',
    billing_accessuntil: 'Access until {date}',
    billing_subscriptionended: 'Subscription has ended',
    billing_dataretentionpolicy: 'Data Retention Policy',
    billing_cancelled: 'Canceled',
    billing_accessuntil_label: 'Access Until',
    billing_resubscribe: 'Resubscribe',
    billing_noactivesubscription: 'No Active Subscription',
    billing_subscribetoaccess: 'Subscribe to access the platform',
    billing_accountdeletionpolicy: 'Account Deletion Policy',
    billing_accountdeletionmessage: 'Accounts without an active subscription will be permanently deleted after 30 days. Subscribe now to activate your account and prevent data loss.',
    billing_chooseyourplan: 'Choose Your Plan',
    billing_currentplan: 'CURRENT PLAN',
    billing_billedmonthly: 'Billed monthly',
    billing_permonth: 'per month',
    billing_save: 'SAVE {percent}%',
    billing_billedyearly: 'Billed yearly',
    billing_peryear: 'per year (${perMonth}/mo)',
    billing_processingcredits: 'Processing Credits',
    billing_availablenow: 'Available Now',
    billing_credits: 'credits',
    billing_creditspending: '{count} Credits Pending',
    billing_processingavailable: 'Processing - Available in 3-5 business days',
    billing_activecreditbundles: 'Active Credit Bundles',
    billing_bundle: '{count, plural, one {bundle} other {bundles}}',
    billing_purchased: 'Purchased',
    billing_of: 'of {total}',
    billing_pendingpurchases: 'Pending Purchases',
    billing_processing: 'Processing',
    billing_last7days: 'Last 7 Days',
    billing_used: 'used',
    billing_last30days: 'Last 30 Days',
    billing_purchasemorecredits: 'Purchase More Credits',
    billing_viewusagedetails: 'View Usage Details',
    billing_lowcreditbalance: 'Low Credit Balance! Consider purchasing more credits to avoid interruptions.',
    billing_unabletoloadcredits: 'Unable to load credit balance',
    billing_usageanalytics: 'Usage Analytics',
    billing_averagedailyusage: 'Average Daily Usage',
    billing_creditsperday: 'credits per day',
    billing_totalused30days: 'Total Used (30 days)',
    billing_estimateddepletion: 'Estimated Depletion',
    billing_notavailable: 'N/A',
    billing_atcurrentrate: 'at current usage rate',
    billing_date: 'Date',
    billing_usagetype: 'Usage Type',
    billing_quickactions: 'Quick Actions',
    billing_buycredits: 'Buy Credits',
    billing_paymentmethods: 'Payment Methods',
    billing_viewinvoices: 'View Invoices',
    billing_needhelp: 'Need Help?',
    billing_helpmessage: 'Questions about billing, credits, or pricing? Our support team is here to help.',
    billing_contactsupport: 'Contact Support',
    billing_billingsettings: 'Billing Settings',
    billing_paymentsuccess: 'Payment successful! Your subscription is now active.',
    billing_failedtoload: 'Failed to load billing information. Please refresh the page.',
    billing_datarefreshed: 'Billing data refreshed',
    billing_failedcheckout: 'Failed to create checkout session. Please try again.',
    billing_settingupaccount: 'Setting up your billing account. Please try again in a moment.',
    billing_failedportal: 'Failed to open customer portal. Please refresh the page and try again.',
    billing_failedusage: 'Failed to load usage details. Please try again.',
    billing_deletion_scheduled: 'Your account is scheduled for deletion. All data will be permanently removed. Resubscribe immediately to prevent data loss.',
    billing_deletion_urgent: 'URGENT: Your account and all data will be permanently deleted in {days} {days, plural, one {day} other {days}} ({date}). Resubscribe immediately to prevent data loss.',
    billing_deletion_soon: 'Your account and all data will be permanently deleted in {days} days ({date}). Resubscribe soon to prevent data loss.',
    billing_deletion_grace: 'Your account data will be retained until {date} ({days} days). After this 30-day grace period, your account and all data will be permanently deleted. Resubscribe anytime to keep your data.',
    billing_deletion_default: 'Your account and all associated data will be permanently deleted 30 days after your subscription ends. Resubscribe now to prevent data loss.',

    // Upload Page
    upload_supportedfiletypes: 'Supported File Types:',
    upload_newcontent: 'Upload New Content',
    upload_importfromrepository: 'Import From Repository',
    reupload_reimportfromrepository: 'Reimport From Repository',
    reupload_failed: 'Reupload Failed',
    reupload_failed_message: 'We couldn\'t start the reupload for this item. Please try again, or contact support if the problem persists.',
    upload_configurerepositories: 'Configure',
    upload_connectrepository: 'Connect a content repository',
    upload_processingqueue: 'Processing Queue',
    upload_creditsused: 'Credits Used',
    upload_discoverycompleted: 'Import completed!',
    upload_filesprocessing: '{count, plural, one {# file is} other {# files are}} being processed and will appear in the Processing Queue.',
    upload_discoveryfailed: 'Import Failed',
    upload_failedtostartdiscovery: 'Failed to start import:',
    upload_failedtoopenpicker: 'Failed to open file picker:',
    upload_pickerfailed: 'File Picker Failed',
    upload_repositoryinfomissing: 'Repository information is missing. Please try again.',

    // Content Page
    content_contentconsole: 'Content Console',
    content_deepsearch: 'Deep Search',
    content_bulkedit: 'Bulk Edit',
    deepsearch_enterquery: 'Enter a query to search across your content\'s extracts and metadata.',
    deepsearch_noresultsfound: 'No results found for "{searchTerm}"',
    deepsearch_selectresult: 'Select a result to view excerpts.',
    deepsearch_excerptsfound: 'Matching Excerpts Found',
    contenttable_showingfirstexcerpts: 'Showing the first {count} excerpts.',
    contenttable_seemoreindeepsearch: 'See more in Deep Search.',
    deepsearch_metadataexcerptsfound: 'Matching Metadata & Excerpts Found',
    deepsearch_resizepane: 'Resize excerpt pane',
    content_uploadkeyfile: 'Upload Key File',
    content_generatekeyfile: 'Generate Key File',
    content_addtokeyfile: 'Add {count} to Key File',
    content_addalltokeyfile: 'Add All to Key File',
    content_exportmetadata: 'Export Metadata',
    content_processingcreditsexhausted: 'Processing Credits Exhausted',
    content_listempty: 'List currently empty.',
    content_uploadtobegin: 'Upload some content to begin.',
    content_createcollectiontobegin: 'Create a collection to begin.',

    // Bulk Content Edit Page
    bulkedit_title: 'Bulk Edit',
    bulkedit_editdetails: 'Edit Details',
    bulkedit_emptyvalues: 'Empty Values',
    bulkedit_noresources: 'No completed resources found.',
    bulkedit_updateitems: '{count, plural, =0 {Update Items} one {Update # Item} other {Update # Items}}',
    bulkedit_updatecomplete: 'Update Complete',
    bulkedit_updatesuccess: '{count, plural, one {Successfully updated # resource.} other {Successfully updated # resources.}}',
    bulkedit_updatepartialtitle: 'Update Partially Failed',
    bulkedit_updatepartialmsg: 'Updated {success} of {total} resources; {failed} failed. See console for details.',

    // Bulk Apply Page
    content_bulkapply: 'Bulk Apply',
    pagetitle_bulkapply: 'Bulk Apply',
    bulkapply_title: 'Bulk Apply',
    bulkapply_selectitems: 'Select Items',
    bulkapply_setvalues: 'Set Values',
    bulkapply_itemsselected: '{count, plural, one {# item selected} other {# items selected}}',
    bulkapply_modalheader: 'Set values to apply to all {count, plural, one {# selected item} other {# selected items}}. Leave a field unchanged to keep existing values.',
    bulkapply_nochange: '— No change —',
    bulkapply_updateitems: '{count, plural, =0 {Update Items} one {Update # Item} other {Update # Items}}',
    bulkapply_updatecomplete: 'Bulk Apply Complete',
    bulkapply_updatesuccess: '{count, plural, one {Successfully applied updates to # item.} other {Successfully applied updates to # items.}}',
    bulkapply_updatepartialtitle: 'Bulk Apply Partially Failed',
    bulkapply_updatepartialmsg: 'Applied updates to {success} of {total} items; {failed} failed. See console for details.',
    bulkapply_noresources: 'No content items found.',

    // Collection Table Component
    collectiontable_createnewcollection: 'Create New Collection',
    collectiontable_exportcollectionsdata: 'Export Collections Data',
    collectiontable_collectionname: 'Collection Name',
    collectiontable_nocollectionsfound: 'No Collections Found',
    collectiontable_entertitle: 'Enter a title',
    collectiontable_enterdescription: 'Enter a description',
    collectiontable_error_title: 'Please enter a title.',
    collectiontable_error_description: 'Please enter a description.',

    // Collection Page
    collection_label: 'Collection',
    collection_totalitems: 'Total Items:',
    collection_exportcollectionmetadata: 'Export Collection Metadata',
    collection_editcollection: 'Edit Collection',
    collection_uploadtocollection: 'Upload to Collection',
    collection_upload_processing_message: 'Your items will be added to the {name} collection when they are finished processing.',
    collection_generatekeyfile: 'Generate Key File',
    collection_addtokeyfile: 'Add {count} to Key File',
    collection_addalltokeyfile: 'Add All to Key File',
    collection_addtocollection: 'Add to Collection',
    collection_addcounttocollection: 'Add {count} to Collection',
    collection_listempty: 'List currently empty.',
    collection_searchtobegin: 'Search for a term or set some filters to begin.',
    collection_removefromcollection: 'Remove from Collection',
    collection_confirmremove_message: 'This item will be removed from the collection.',
    collection_confirmdelete_message: 'This collection will be permanently deleted.',
    collection_deletecollection: 'Delete Collection',

    // Job Table Component
    jobtable_filename: 'File Name',
    jobtable_uploadfilestobegin: 'Upload some files to begin!',
    jobtable_allfilesprocessed: 'All files processed!',
    jobtable_checkoutcontent: 'Check them out under Content.',
    jobtable_confirmdelete_message: 'This queued item will be deleted.',
    jobtable_clear: 'Clear',
    jobtable_clear_hint: 'Remove this upload from the list. Its processing history is kept.',
    jobtable_confirmdiscard_notstarted: 'This upload never started, so nothing has been processed. It will be removed and the credit returned.',
    jobtable_confirmdelete_historywarning: 'The processing history for this upload will be lost and cannot be recovered.',
    jobtable_action_failed: 'That could not be completed. Please try again.',
    jobtable_jobprocessingfailed: 'Job Processing Failed',
    jobtable_failedreason: 'This content item failed to process correctly due to the following reasons:',
    jobtable_failedgeneric: 'This content item failed to process.',
    jobtable_reupload: 'Reupload',

    // Upload Box Component
    uploadbox_maxfiles: 'Max {count} Files Per Upload.',
    uploadbox_dragfiles: 'Drag files to upload',
    uploadbox_dragfile: 'Drag file to upload',
    uploadbox_trialexpired: 'Trial expired - upgrade to continue',
    uploadbox_graceperiod: 'Subscription in grace period - renew to upload',
    uploadbox_subscriptionexpired: 'Subscription expired - renew to continue',
    uploadbox_quotaexhausted: 'Upload limit reached - no credits remaining',
    uploadbox_uploadlimitreached: 'Upload limit reached',

    // Upload Repository Component
    uploadrepository_otherusers: '+{count} other',
    uploadrepository_popupblocked: 'Popup Blocked',
    uploadrepository_popupblocked_message: 'Please allow popups for this site and try again.',
    uploadrepository_connectionfailed: 'Connection Failed',
    uploadrepository_failedtoconnect: 'Failed to connect:',

    // Skill Table Component
    skilltable_exportskillsdata: 'Export Skills Data',
    skilltable_origin: 'Origin',
    skilltable_origin_all: 'All',
    skilltable_origin_custom: 'Custom',
    skilltable_origin_esco: 'ESCO',
    skilltable_noskillsfound: 'No Skills Found',

    // Skill Page
    skill_skilltype: 'Skill Type',
    skill_reuselevel: 'Reuse Level',
    skill_totalitems: 'Total Items',

    // Attribute Reason Modal
    attributereasonmodal_reasonfor: 'Reason for',
    attributereasonmodal_update: 'Update',
    attributereasonmodal_youare: 'You are',
    attributereasonmodal_clearing: 'clearing',
    attributereasonmodal_updating: 'updating',
    attributereasonmodal_providereason: 'Please provide a reason:',
    attributereasonmodal_oncount: 'on {count, plural, one {# item} other {# items}}',
    attributereasonmodal_placeholder: 'Enter your reason here...',

    // Doc Viewer Component
    docviewer_loading: 'Loading documentation...',
    docviewer_errorloading: 'Error Loading Documentation',
    docviewer_toc: 'Table of Contents',

    // Content Exports Bar
    contentexportsbar_exports: 'Exports',
    contentexportsbar_downloadfile: 'Download File',
    contentexportsbar_downloadmetadata: 'Download Metadata',
    contentexportsbar_downloadlayoutpackage: 'Download Layout Package',
    contentexportsbar_extractpreview: 'Extract Preview',
    contentexportsbar_viewlayout: 'View Layout',
    contentexportsbar_layoutpreview: 'Layout Preview',
    contentexportsbar_layoutpreviewframe: 'Rendered layout preview',
    contentexportsbar_layoutpreviewunavailable: 'No layout preview is available for this resource.',

    // Extract Viewer
    extractviewer_appname: 'Element Viewer',
    extractviewer_loading: 'Loading extract...',
    extractviewer_pane_pages: 'Pages',
    extractviewer_pane_inspector: 'Extract inspector',
    extractviewer_tab_elements: 'Page elements',
    extractviewer_tab_assets: 'All assets',
    extractviewer_tab_package: 'Package',
    extractviewer_no_pages: 'This extract has no pages.',
    extractviewer_no_elements: 'Select a page to inspect its elements.',
    extractviewer_no_assets: 'This extract has no images or media.',
    extractviewer_no_package: 'No downloadable package is available for this resource.',
    extractviewer_nothing_here: 'Nothing to show here.',
    extractviewer_untitled_extract: 'Untitled extract',
    extractviewer_header_badges: 'Extract attributes',
    extractviewer_prev_page: 'Prev',
    extractviewer_next_page: 'Next',
    extractviewer_fit_on: 'Fit: On',
    extractviewer_fit_off: 'Fit: Off',
    extractviewer_fit_enable: 'Fit the page to the available space',
    extractviewer_fit_disable: 'Show the page at its full size',
    extractviewer_legibility_off: 'Legibility: Off',
    extractviewer_legibility_dim: 'Legibility: Dim',
    extractviewer_legibility_wash: 'Legibility: Wash',
    extractviewer_legibility_to_dim: 'Dim background images so the text over them is easier to read',
    extractviewer_legibility_to_wash: 'Dim and desaturate background images further',
    extractviewer_legibility_to_off: 'Show background images exactly as they were captured',
    extractviewer_preview_frame: 'Page preview',
    extractviewer_preview_idle: 'Select a page to preview it.',
    extractviewer_preview_unavailable: 'No layout preview was generated for this resource.',
    extractviewer_preview_error: 'Could not load the preview.',
    extractviewer_loading_preview: 'Loading preview...',
    extractviewer_load_error: 'Could not load this extract.',
    extractviewer_error_generic: 'Please try again.',
    extractviewer_error_notfound: 'This resource was not found in this workspace.',
    extractviewer_error_forbidden: 'You do not have access to this workspace.',
    extractviewer_error_session: 'Your session has ended. Sign in again and reopen this link.',
    extractviewer_schema_drift: 'This extract was written by a different schema version than this viewer expects.',
    extractviewer_back_to_app: 'Return to MetaLark',
    extractviewer_gate_session_title: 'Your session has ended',
    extractviewer_gate_session_body: 'Sign in again, then reopen this link.',
    extractviewer_gate_role_title: 'The element viewer is limited to administrators',
    extractviewer_gate_role_body: 'Only system, customer and tenant administrators can open this view.',
    extractviewer_gate_role_detail: 'Your role',
    extractviewer_gate_workspace_title: 'This link points at another workspace',
    extractviewer_gate_workspace_body: 'The resource in this link is not available in the workspace you are signed in to.',
    extractviewer_gate_link_title: 'This link is incomplete',
    extractviewer_gate_link_body: 'The element viewer needs both a workspace and a resource in the link, for example /extract-viewer/#/resource?sliceId=…&resourceId=…',

    // Extract Viewer - notices strip and degraded states
    extractviewer_notices_label: 'Extract notices',
    extractviewer_notice_schema_title: 'This extract was written by an unsupported schema version',
    extractviewer_notice_empty_title: 'This extract captured no content',
    extractviewer_notice_empty_body: 'The extract JSON parsed correctly, but its content list is empty — nothing was captured for this resource.',
    extractviewer_notice_globals_title: 'This extract has no pages',
    extractviewer_notice_globals_body: 'Its whole content is {count, plural, one {# resource-level file} other {# resource-level files}}, listed under All assets. Resource-level files carry metadata only — no download URL is generated for them.',
    extractviewer_notice_nolayout_title: 'No layout was captured for this resource',
    extractviewer_notice_nolayout_body: 'The page list and the inspector are built from the extract JSON, which does not need a layout. Only the visual preview does.',
    extractviewer_notice_nopagelayout_title: 'No page in this extract carries a captured layout',
    extractviewer_notice_nopagelayout_body: '{count, plural, one {The one page in this extract is listed and fully inspectable; only the visual preview needs a layout.} other {All # pages are listed and fully inspectable; only the visual preview needs a layout.}}',
    extractviewer_notice_mixedlayout_title: 'Some pages have no captured layout',
    extractviewer_notice_mixedlayout_body: '{missing} of {total} pages have no layout. They are listed and inspectable, but cannot be previewed.',
    extractviewer_notice_layout_unreadable_title: 'The layout preview data could not be read',
    extractviewer_notice_layout_compose_title: 'The layout preview document could not be assembled',
    extractviewer_notice_layout_error_title: 'The layout preview could not be loaded',
    extractviewer_notice_unknown_title: 'Some content entries were not recognized',
    extractviewer_notice_unknown_body: '{count, plural, one {# entry in this extract is neither a page nor an asset, and is not shown.} other {# entries in this extract are neither a page nor an asset, and are not shown.}}',
    extractviewer_no_pages_globals: 'This extract has no pages — see All assets for its resource-level files.',
    extractviewer_flag_nolayout: 'No layout',
    extractviewer_flag_nolayout_title: 'no layout was captured for this page',
    extractviewer_announce_page: '{current} / {total}: {label}',

    // Extract Viewer - preview pane
    extractviewer_preview_nocontent: 'This extract captured no content, so there is nothing to preview.',
    extractviewer_preview_nopages: 'This extract has no pages. Its resource-level files are listed under All assets.',
    extractviewer_preview_nopagelayout: 'No page in this extract carries a captured layout, so there is nothing to preview.',
    extractviewer_preview_page_no_layout: 'This page has no captured layout, so there is nothing to preview. Its text, images and panels are still listed in the inspector.',
    extractviewer_preview_frame_error: 'The preview may not be showing this page correctly.',
    extractviewer_preview_assets_missing: 'Not included in this extract',
    extractviewer_preview_assets_failed: 'asset(s) on this page could not be loaded.',

    // Extract Viewer - inspector element kinds and badges
    extractviewer_card_image: 'Image',
    extractviewer_card_video: 'Video',
    extractviewer_card_audio: 'Audio',
    extractviewer_card_media: 'Media',
    extractviewer_card_text: 'Text',
    extractviewer_card_panel: 'Panel',
    extractviewer_card_dialog: 'Dialog',
    extractviewer_card_unknown: 'Element',
    extractviewer_badge_ai: 'AI',
    extractviewer_badge_ai_full: 'Includes an AI-generated description',
    extractviewer_badge_layout_only: 'Layout',
    extractviewer_badge_layout_only_full: 'Recorded by the layout only — no content was captured',
    extractviewer_badge_resource_level: 'Resource',
    extractviewer_badge_resource_level_full: 'Attached to the resource, not to any page',

    // Extract Viewer - inspector field labels
    extractviewer_field_aidescription: 'AI description',
    extractviewer_field_embeddedtext: 'Embedded text',
    extractviewer_field_position: 'Position',
    extractviewer_field_storedfile: 'Stored file',
    extractviewer_field_storedfile_absent: 'This element records no stored file name.',
    extractviewer_field_transcript: 'Transcript',
    extractviewer_field_usedon: 'Used on',

    // Extract Viewer - inspector explanatory notes
    extractviewer_note_layout_asset: 'Only the layout recorded this file, so there is no AI description, no embedded text and no transcript for it.',
    extractviewer_note_layout_panel: 'The preview draws this panel, but the content extract captured nothing inside it. Everything shown here comes from the layout.',
    extractviewer_note_layout_text: 'Text as the layout laid it out. Any bullet or number you see is part of the captured string, not a list this viewer rebuilt.',
    extractviewer_note_no_dialog: 'No dialog was captured for this page.',
    extractviewer_note_no_layout: 'No layout was captured for this page, so there is no preview, no element positions, and no download links for its images or media. Its text, descriptions and dialog are all still readable below.',
    extractviewer_note_panel_empty: 'This panel records no text, images or media.',
    extractviewer_note_panel_unmatched: 'This panel could not be matched to a button in the preview, so it cannot be opened from here. The page records a different number of panels on each side.',
    extractviewer_note_text_empty: 'This text element records no readable lines.',
    extractviewer_note_thumb_failed: 'The image itself could not be loaded. Asset links are signed for one hour, so reload this view if it has been open a while.',
    extractviewer_note_scope_both: 'This file is attached to the resource itself as well as being placed on the page(s) above.',
    extractviewer_note_scope_resource: 'This file is attached to the resource itself rather than to any page, so it appears on no page and in no preview.',
    extractviewer_note_unmatched_panels: 'Some captured panels could not be tied to a button in the preview, so they cannot be opened from here: ',
    extractviewer_note_panel_shortfall_a: 'The preview draws',
    extractviewer_note_panel_shortfall_b: 'panels on this page but the content extract captured',
    extractviewer_note_panel_shortfall_c: 'The unmatched panels are listed below with whatever the layout recorded.',
    extractviewer_note_globals_a: 'This resource has',
    extractviewer_note_globals_b: 'resource-level file(s), which belong to the resource rather than to a page. Their metadata is shown, but the layout download links do not cover them.',

    // Extract Viewer - empty page states and page facts
    extractviewer_empty_page: 'This page has no elements. It is a genuinely blank page in the source, not a failed read.',
    extractviewer_empty_page_layoutonly: 'The layout recorded elements on this page, but none of them produced content the extract could store — no text, no images, no media.',
    extractviewer_empty_page_nolayout: 'This page captured neither a layout nor any elements.',
    extractviewer_empty_page_v1: 'Nothing could be read from this page. This extract predates schema v2, so its paragraph text is stored in a retired field that this viewer cannot read. Re-extract the resource to populate it.',
    extractviewer_no_elements_on_page: 'No elements on this page',
    extractviewer_text_empty_v1: 'No lines. This extract predates schema v2, which stored paragraph text in a field that has since been retired — re-extract the resource to read it.',
    extractviewer_page_mode: 'Layout',
    extractviewer_page_size: 'Page size',
    extractviewer_page_auto: 'auto',
    extractviewer_page_background: 'Background',
    extractviewer_value_unknown: 'unknown',

    // Extract Viewer - copy, download and cross-pane affordances
    extractviewer_copy: 'Copy',
    extractviewer_copy_all: 'Copy all',
    extractviewer_copy_line: 'Copy line',
    extractviewer_copy_cue: 'Copy dialog line',
    extractviewer_copy_transcript: 'Copy transcript',
    extractviewer_copied: 'Copied to the clipboard',
    extractviewer_copied_short: 'Copied',
    extractviewer_copy_failed: 'Could not copy. Clipboard access needs a secure connection.',
    extractviewer_download: 'Download',
    extractviewer_download_unavailable: 'Unavailable',
    extractviewer_download_global: 'Resource-level files are not included in the layout download links, so this one cannot be downloaded from here. Its description, embedded text and transcript above are the whole of what the extract captured.',
    extractviewer_download_nosource: 'This element records no stored file name, so there is nothing to download.',
    extractviewer_download_nourl: 'No download link was published for this file. That happens when the page carried no captured layout, or when the asset was outside the layout package.',
    extractviewer_open_panel: 'Show in preview',
    extractviewer_chip_goto: 'Show on',
    extractviewer_cue_from_panel: 'in',

    // Extract Viewer - All assets tab
    extractviewer_assets_none: 'No images or media',
    extractviewer_assets_deduped: 'de-duplicated',

    // Extract Viewer - Package tab
    extractviewer_package_title: 'Layout package',
    extractviewer_package_intro: 'A single ZIP holding the generated layout viewer and the media it references, so the page rendering can be opened offline.',
    extractviewer_package_file_html: 'viewer shell',
    extractviewer_package_file_data: 'extract + asset links',
    extractviewer_package_file_assets: 'images and media',
    extractviewer_package_size: 'Size',
    extractviewer_package_generated: 'Generated',
    extractviewer_package_bestteffort: 'Media inclusion is best-effort: files that were missing from storage, or that fell outside the package size budget, are left out and show as unavailable in the offline copy.',
    extractviewer_package_download: 'Download ZIP',
    extractviewer_package_preparing: 'Preparing...',
    extractviewer_package_started: 'Download started',
    extractviewer_package_failed: 'The download could not be prepared.',
    extractviewer_package_gone: 'The package is no longer available. Reload this view to re-read its status.',
    extractviewer_package_nourl: 'The server did not return a download link.',
    extractviewer_package_nocontext: 'This view has no workspace or resource to ask for a package.',
    extractviewer_package_none: 'No layout package is available for this resource, so there is nothing to download.',
    extractviewer_package_none_title: 'No package',
    extractviewer_package_none_why: 'The API reports only that none is available, not why. The usual causes are that the layout has not been generated for this resource yet, or that its source has no derivable layout at all — a legacy binary .ppt/.doc, or blocked content.',
    extractviewer_package_status_unknown: 'The package status could not be read for this resource, so we cannot say whether a download exists. This is a failed request, not an empty result — retry, and check the workspace in the link if it keeps failing.',
    extractviewer_package_status_unknown_title: 'Package status unavailable',
    extractviewer_package_summary: 'Download the generated layout package',
    extractviewer_package_summary_none: 'No package for this resource',
    extractviewer_package_summary_unknown: 'Package status not read',

    // Extract Viewer - pluralized unit words (extractviewer_unit_* dynamic family)
    extractviewer_unit_asset: 'asset',
    extractviewer_unit_assets: 'assets',
    extractviewer_unit_cue: 'cue',
    extractviewer_unit_cues: 'cues',
    extractviewer_unit_item: 'item',
    extractviewer_unit_items: 'items',
    extractviewer_unit_line: 'line',
    extractviewer_unit_lines: 'lines',
    extractviewer_unit_use: 'use',
    extractviewer_unit_uses: 'uses',

    // Metadata Console + Export Metadata By Type
    metadataconsole_title: 'Metadata Console',
    metadataconsole_exportcontentmetadata: 'Export Content Metadata',
    exportbytypemodal_title: 'Export Metadata By Type',
    exportbytypemodal_subtitle: 'Choose a metadata type to export. This export will include all the data for the selected type. To filter the list, you may do so on that type\'s specific page.',
    exportbytypemodal_tags: 'Tags',
    exportbytypemodal_skills: 'Skills',
    exportbytypemodal_collections: 'Collections',
    exportbytypemodal_attributes: 'Attributes',
    exportbytypemodal_keywords: 'Keywords',

    // Export Summary/Detail Modal
    exportdetailmodal_subtitle: 'Choose the level of detail to include in the generated file.',
    exportdetailmodal_summaryview: 'Summary View',
    exportdetailmodal_detailview: 'Detail View with Resources',
    exportdetailmodal_downloadcsv: 'Download CSV',

    // Export Metadata Modal
    exportmetadatamodal_helper: 'Choose the metadata fields to include with each content item. Each selected field becomes a column in the downloaded CSV file.',
    exportmetadatamodal_selectall: 'Select All',
    exportmetadatamodal_downloadcsv: 'Download CSV',
    export_previewcsv: 'Preview CSV',
    csvpreview_title: 'Export CSV Preview',
    csvpreview_download: 'Download CSV',
    csvpreview_empty: 'No data to preview.',
    csvpreview_rows: '{count, plural, one {# row} other {# rows}}',
    exportmetadatamodal_titles: 'Titles',
    exportmetadatamodal_titles_desc: 'AI-suggested titles generated from the content extract of each item.',
    exportmetadatamodal_descriptions: 'Descriptions',
    exportmetadatamodal_descriptions_desc: 'AI-suggested descriptions generated from the content extract of each item.',
    exportmetadatamodal_keywords: 'Keywords',
    exportmetadatamodal_keywords_desc: 'Auto-extracted keywords and search terms surfaced during processing.',
    exportmetadatamodal_tags: 'Tags',
    exportmetadatamodal_tags_desc: 'Tags defined by your organization.',
    exportmetadatamodal_skills: 'Skills',
    exportmetadatamodal_skills_desc: 'Skills mapped to each item from your skills taxonomy.',
    exportmetadatamodal_collections: 'Collections',
    exportmetadatamodal_collections_desc: 'Collections that each item belongs to.',
    exportmetadatamodal_attributes: 'Attributes',
    exportmetadatamodal_attributes_desc: 'Custom attributes and field values configured for your workspace.',

    // Extract Chunk Launch Button
    extractchunklaunchbutton_jumptotime: 'Jump to : Time',
    extractchunklaunchbutton_jumptopage: 'Jump to : Page',
    extractchunklaunchbutton_jumptomedia: 'Jump to : Media',
    extractchunklaunchbutton_jumptosection: 'Jump to : Section',
    extractchunklaunchbutton_jumptoimage: 'Jump to : Image',
    extractchunklaunchbutton_viewimage: 'View Image',

    // Common status/state
    comingsoon: 'Coming Soon!',

    // Login Page
    login_session_ended_title: 'Session Ended',
    login_session_ended_message: 'Your session has ended. Please log back in to continue.',
    login_eula_required_title: 'Subscription Agreement Required',
    login_eula_required_message: 'You must accept the Subscription Agreement to use MetaLark. Please try again if you wish to accept.',
    login_logo_alt: 'MetaLark Logo',
    login_welcome: 'Welcome to MetaLark.ai',
    login_signin: 'Sign in to continue',
    login_poweredby: 'Powered by MetaLark.ai',
    login_needhelp: 'Need help?',
    login_contactsupport: 'Contact Support',
    login_privacypolicy: 'Privacy Policy',
    login_termsofservice: 'Terms of Service',
    login_subdomain_signin: 'Sign In',
    login_loadingoptions: 'Loading sign-in options...',
    login_signinwith: 'Sign in with {provider}',
    login_otheroptions: 'Other options',
    login_noproviders: 'No sign-in methods are configured for this organization. Contact your administrator.',

    // Login Form Component
    loginform_email: 'Email',
    loginform_emailplaceholder: 'email@example.com',
    loginform_password: 'Password',
    loginform_passwordplaceholder: 'Enter your password',
    loginform_hidepassword: 'Hide password',
    loginform_showpassword: 'Show password',
    loginform_forgotpassword: 'Forgot password?',
    loginform_signin: 'Sign In',
    loginform_signinwithpasskey: 'Sign In with Passkey',
    loginform_signinwith: 'Sign In with {provider}',
    loginform_usepasswordinstead: 'Use password instead',
    login_or: 'or',
    login_signinwithpassword: 'Sign in with password',
    login_backtosso: 'Back to SSO options',
    domainbanner_title: 'Your organization\'s sign-in URL:',
    domainbanner_benefit: 'Sign in faster with your organization\'s SSO',
    domainbanner_bookmark: 'Open & bookmark',
    loginform_usepasskeyinstead: 'Use passkey instead',
    loginform_choosedifferentmethod: 'Choose different login method',
    loginform_or: 'OR',
    loginform_loadingloginoptions: 'Loading login options...',
    loginform_continuewith: 'Continue with {provider}',
    loginform_nooproviderswarning: 'No OAuth providers configured, using fallback options',
    loginform_continuewithgoogle: 'Continue with Google',
    loginform_continuewithmicrosoft: 'Continue with Microsoft',
    loginform_loginsuccessful: 'Login successful!',
    loginform_eulaaccepted: 'EULA accepted! Logging you in...',
    loginform_unabletodisplayeula: 'Unable to display EULA. Please refresh and try again.',
    loginform_startingauth: 'Starting authentication...',
    loginform_redirectingto: 'Redirecting to {provider}...',
    loginform_verifyingauth: 'Verifying authentication...',
    loginform_authsuccessful: 'Authentication successful!',
    loginform_redirectingtogoogle: 'Redirecting to Google...',
    loginform_microsoftnotavailable: 'Microsoft login is not available in fallback mode',

    // Passkey Login Button
    passkeylogin_not_available: 'Passkey Not Available',
    passkeylogin_signin_with_passkey: 'Sign in with Passkey',
    passkeylogin_authenticating: 'Authenticating...',
    passkeylogin_auth_failed: 'Passkey Authentication Failed',
    passkeylogin_not_supported: 'Passkeys are not supported on this device or browser',
    passkeylogin_auth_failed_generic: 'Authentication failed',
    passkeylogin_error_unexpected: 'An unexpected error occurred',
    passkeylogin_error_cancelled: 'Passkey authentication was canceled or not allowed',
    passkeylogin_error_no_passkeys: 'No passkeys found for this site. Please sign in another way first.',
    passkeylogin_error_security: 'Security error: Make sure you\'re accessing this site over HTTPS',
    passkeylogin_error_aborted: 'Authentication was canceled',

    // Passkey Registration Prompt
    passkeyprompt_title: 'Add Passkey for Faster Login?',
    passkeyprompt_description: 'Use Face ID, Touch ID, or Windows Hello to log in instantly next time.',
    passkeyprompt_no_passwords: 'No passwords to remember!',
    passkeyprompt_benefit_biometrics: 'One-tap login with biometrics',
    passkeyprompt_benefit_devices: 'Works on all your devices',
    passkeyprompt_benefit_secure: 'More secure than passwords',
    passkeyprompt_auto_naming_info: 'We\'ll automatically name your passkey based on your device (e.g., "iCloud Keychain (iPhone)"). You can change it later in account settings.',
    passkeyprompt_maybe_later: 'Maybe Later',
    passkeyprompt_add_passkey: 'Add Passkey',
    passkeyprompt_adding: 'Adding...',
    passkeyprompt_error_failed: 'Failed to register passkey',
    passkeyprompt_error_unexpected: 'An unexpected error occurred',
    passkeyprompt_error_cancelled: 'Passkey registration was canceled or not allowed',
    passkeyprompt_error_exists: 'A passkey already exists for this account on this device',
    passkeyprompt_error_security: 'Security error: Make sure you\'re accessing this site over HTTPS',
    passkeyprompt_error_aborted: 'Registration was canceled',

    // EULA Acceptance Modal
    eula_title: 'Subscription Agreement',
    eula_subtitle: 'Please review and accept the Subscription Agreement to continue',
    eula_loading: 'Loading Subscription Agreement...',
    eula_version: 'Version',
    eula_effective_date: 'Effective Date',
    eula_scroll_to_continue: 'Please scroll to the bottom to continue',
    eula_accept_button: 'Accept and Continue',
    eula_error_load: 'Failed to load Subscription Agreement',
    eula_error_accept: 'Failed to accept Subscription Agreement',

    // SSO JIT Consent + EULA acceptance page (/#sso-consent)
    sso_consent_title: 'Confirm your account',
    sso_consent_loading: 'Loading workspace details...',
    sso_consent_invalid_title: 'Invitation link is invalid or expired',
    sso_consent_invalid_token: 'This invitation link is no longer valid. Please sign in again to start over.',
    sso_consent_back_to_login: 'Back to sign in',
    sso_consent_inviting_intro: "You're about to be added to the following workspace:",
    sso_consent_role_label: 'Role',
    sso_consent_email_label: 'Email',
    sso_consent_eula_preamble: 'By joining, you agree to the Subscription Agreement below.',
    sso_consent_submit: 'Join workspace and accept agreement',
    sso_consent_confirm_failed: 'We could not complete your sign-in. Please try again or contact support.',

    // Accept-Invitation page (METALARK-835)
    accept_invitation_title: 'You\'ve been invited',
    accept_invitation_loading: 'Loading invitation details...',
    accept_invitation_intro: 'You\'ve been invited to join:',
    accept_invitation_role_label: 'Role',
    accept_invitation_workspace_label: 'Workspace',
    accept_invitation_inviter_label: 'Invited by',
    accept_invitation_expires_label: 'Expires',
    accept_invitation_explanation: 'Accepting will add this role to your existing MetaLark account. Your access in other organizations is unaffected. You can decline if you don\'t recognize this organization.',
    accept_invitation_accept: 'Accept Invitation',
    accept_invitation_accepting: 'Accepting...',
    accept_invitation_decline: 'Decline',
    accept_invitation_declining: 'Declining...',
    accept_invitation_continue: 'Continue',
    accept_invitation_sign_in: 'Sign in to continue',
    accept_invitation_sign_in_prompt: 'Sign in to your existing MetaLark account to accept or decline this invitation.',
    accept_invitation_action_failed: 'Something went wrong. Please try again.',
    accept_invitation_back_to_login: 'Back to sign in',
    accept_invitation_sign_out: 'Sign out',
    accept_invitation_keep_current: 'Keep current account',
    // Error states
    accept_invitation_not_found_title: 'Invitation no longer valid',
    accept_invitation_not_found_body: 'This invitation link has been canceled, already used, or never existed. Ask your administrator to send a new invitation if you still need access.',
    accept_invitation_expired_title: 'Invitation expired',
    accept_invitation_expired_body: 'This invitation has expired. Ask your administrator to resend it and use the new link.',
    accept_invitation_mismatch_title: 'Different account signed in',
    accept_invitation_mismatch_body_prefix: 'This invitation is for ',
    accept_invitation_mismatch_body_suffix: '. Sign out of your current account and sign in with that email to accept the invitation.',
    // Success states
    accept_invitation_success_accept_title: 'Welcome aboard',
    accept_invitation_success_accept_body_prefix: 'You\'re now a member of ',
    accept_invitation_success_accept_body_suffix: '. You can switch into this organization from the workspace switcher.',
    accept_invitation_success_decline_title: 'Invitation declined',
    accept_invitation_success_decline_body: 'No role has been added to your account. Your administrator has been notified.',
    // Discoverability CTA when a signed-in user lands here with a stale token
    accept_invitation_view_pending: 'View pending invitations',
    accept_invitation_other_pending_one: 'You have 1 other pending invitation.',
    accept_invitation_other_pending_other: 'You have {count} other pending invitations.',

    // Invitations list page (METALARK-835)
    invitations_page_title: 'Your invitations',
    invitations_page_subtitle: 'Pending invitations to organizations on MetaLark.ai.',
    invitations_page_loading: 'Loading your invitations...',
    invitations_page_empty_title: 'No pending invitations',
    invitations_page_empty_body: 'You don\'t have any outstanding invitations. If someone invited you and you can\'t see it here, ask them to resend the invitation email.',
    invitations_page_back_home: 'Back to home',
    invitations_page_invite_intro: 'You\'ve been invited to join:',
    invitations_page_load_failed: 'We couldn\'t load your invitations. Please try again.',

    // Password Manager
    passwordmgr_title: 'Password',
    passwordmgr_description_has: 'Change your password or add a passkey for easier sign-in.',
    passwordmgr_description_none: 'Set a password to sign in without external providers.',
    passwordmgr_info_external: 'You currently sign in with an external provider. Set a password to enable password-based sign-in.',
    passwordmgr_set_button: 'Set Password',
    passwordmgr_change_button: 'Change Password',
    passwordmgr_new_password: 'New Password',
    passwordmgr_new_password_placeholder: 'Enter new password',
    passwordmgr_confirm_password: 'Confirm Password',
    passwordmgr_confirm_new_password: 'Confirm New Password',
    passwordmgr_confirm_placeholder: 'Confirm new password',
    passwordmgr_current_password: 'Current Password',
    passwordmgr_current_password_placeholder: 'Enter current password',
    passwordmgr_strength: 'Password Strength',
    passwordmgr_requirements: 'Password must',
    passwordmgr_success_set: 'Password set successfully!',
    passwordmgr_success_changed: 'Password changed successfully!',

    // Password validation errors
    password_error_nomatch: 'Passwords do not match',
    password_error_current_required: 'Current password is required',
    password_error_new_nomatch: 'New passwords do not match',
    password_error_same_as_current: 'New password must be different from current password',
    password_error_required: 'Password is required',
    password_error_minlength: 'Password must be at least {minLength} characters long',
    password_error_uppercase: 'Password must contain at least one uppercase letter',
    password_error_lowercase: 'Password must contain at least one lowercase letter',
    password_error_number: 'Password must contain at least one number',
    password_error_special: 'Password must contain at least one special character ({specialChars})',

    // Password strength feedback
    password_strength_enter: 'Please enter a password',
    password_strength_use_chars: 'Use at least {minLength} characters',
    password_strength_add_uppercase: 'Add uppercase letters',
    password_strength_add_lowercase: 'Add lowercase letters',
    password_strength_add_numbers: 'Add numbers',
    password_strength_add_special: 'Add special characters',
    password_strength_avoid_letters_only: 'Avoid using only letters or only numbers',
    password_strength_avoid_repeating: 'Avoid repeating characters',
    password_strength_strong: 'Strong password!',

    // Password requirements list
    password_req_minlength: 'At least {minLength} characters long',
    password_req_uppercase: 'Contains at least one uppercase letter (A-Z)',
    password_req_lowercase: 'Contains at least one lowercase letter (a-z)',
    password_req_number: 'Contains at least one number (0-9)',
    password_req_special: 'Contains at least one special character ({specialChars})',

    // Password API errors
    password_api_invalid: 'Invalid password',
    password_api_incorrect: 'Current password is incorrect',
    password_api_access_denied: 'Access denied',
    password_api_previous: 'Cannot use a previous password',
    password_api_too_many: 'Too many attempts. Please try again later',
    password_api_server_error: 'Server error. Please try again',
    password_api_network_error: 'Network error. Please check your connection',
    password_api_unexpected: 'An unexpected error occurred',

    // Inactivity Warning Modal
    inactivity_title: 'Are you still there?',
    inactivity_expires_in: 'Your session will expire in',
    inactivity_action_hint: 'Click "I\'m here" or perform any action to stay logged in.',
    inactivity_im_here: 'I\'m here',

    // Returning Users
    returning_welcome: 'Welcome back! Choose an account',
    returning_last_login: 'Last login',
    returning_passkey: 'Passkey',
    returning_password: 'Password',
    returning_remove_tooltip_confirm: 'Click again to forget this user',
    returning_remove_tooltip: 'Remove from list',
    returning_choose_different: 'Choose different account',
    returning_back: 'Back',
    returning_enter_password: 'Enter your password',
    returning_signin_passkey: 'Sign in with Passkey',
    returning_or: 'OR',
    returning_error_unexpected: 'An unexpected error occurred. Please try again.',
    returning_error_passkey_failed: 'Passkey authentication failed',

    // Reset Password Page
    resetpassword_validating: 'Validating reset token...',
    resetpassword_validating_message: 'Please wait while we verify your password reset request.',
    resetpassword_title: 'Reset Your Password',
    resetpassword_subtitle: 'Enter your new password below',
    resetpassword_newpassword: 'New Password',
    resetpassword_newpassword_placeholder: 'Enter your new password',
    resetpassword_showpassword: 'Show password',
    resetpassword_hidepassword: 'Hide password',
    resetpassword_requirements: 'Password Requirements:',
    resetpassword_requirement_length: 'At least 12 characters',
    resetpassword_requirement_uppercase: 'One uppercase letter (A-Z)',
    resetpassword_requirement_lowercase: 'One lowercase letter (a-z)',
    resetpassword_requirement_number: 'One number (0-9)',
    resetpassword_requirement_special: 'One special character (@$!%*?& etc.)',
    resetpassword_confirmpassword: 'Confirm Password',
    resetpassword_confirmpassword_placeholder: 'Confirm your new password',
    resetpassword_resetting: 'Resetting Password...',
    resetpassword_resetbutton: 'Reset Password',
    resetpassword_success_title: 'Password Reset Successfully!',
    resetpassword_success_message: 'Your password has been reset successfully.',
    resetpassword_success_nextstep: 'You can now log in with your new password.',
    resetpassword_success_gotologin: 'Go to Login',
    resetpassword_failed_title: 'Reset Failed',
    resetpassword_failed_returntologin: 'Return to Login',
    resetpassword_error_notoken: 'No reset token provided. Please check your email and click the password reset link again.',
    resetpassword_error_invalidtoken: 'Invalid or expired reset token. Please request a new password reset.',
    resetpassword_error_nomatch: 'Passwords do not match',
    resetpassword_error_criteria: 'Password does not meet the required criteria',
    resetpassword_error_failed: 'Failed to reset password',
    resetpassword_error_tryagain: 'Failed to reset password. Please try again.',
    resetpassword_error_invalidrequest: 'Invalid password reset request.',

    // Password Setup Page
    passwordsetup_validating: 'Validating setup token...',
    passwordsetup_validating_message: 'Please wait while we verify your password setup request.',
    passwordsetup_title: 'Set Your Password',
    passwordsetup_subtitle: 'Create a password for your account',
    passwordsetup_newpassword: 'New Password',
    passwordsetup_newpassword_placeholder: 'Enter your new password',
    passwordsetup_confirmpassword: 'Confirm Password',
    passwordsetup_confirmpassword_placeholder: 'Confirm your new password',
    passwordsetup_setting: 'Setting Password...',
    passwordsetup_setbutton: 'Set Password',
    passwordsetup_success_title: 'Password Set Successfully!',
    passwordsetup_success_message: 'Your password has been set successfully.',
    passwordsetup_success_nextstep: 'You can now log in with your new password.',
    passwordsetup_success_gotologin: 'Go to Login',
    passwordsetup_failed_title: 'Setup Failed',
    passwordsetup_failed_returntologin: 'Return to Login',
    passwordsetup_error_notoken: 'No setup token provided. Please check your email and click the password setup link again.',
    passwordsetup_error_invalidtoken: 'Invalid or expired setup token. Please request a new password setup.',
    passwordsetup_error_nomatch: 'Passwords do not match',
    passwordsetup_error_criteria: 'Password does not meet the required criteria',
    passwordsetup_error_failed: 'Failed to set password',
    passwordsetup_error_tryagain: 'Failed to set password. Please try again.',
    passwordsetup_error_invalidrequest: 'Invalid password setup request.',

    // Password Change Page
    passwordchange_modal_title: 'Password Changed Successfully',
    passwordchange_modal_message: 'Your password has been updated successfully.',
    passwordchange_modal_revoke_label: 'Sign me out everywhere else',
    passwordchange_modal_revoke_description: 'Revoke all other sessions and API tokens for added security. Your current session will remain active.',
    passwordchange_modal_revoke_recommended: 'Recommended if you suspect unauthorized access to your account',
    passwordchange_modal_skip: 'Skip',
    passwordchange_modal_confirm_revoke: 'Confirm & Sign Out Others',
    passwordchange_modal_continue: 'Continue',
    passwordchange_validating: 'Validating change token...',
    passwordchange_validating_message: 'Please wait while we verify your password change request.',
    passwordchange_title: 'Change Your Password',
    passwordchange_subtitle: 'Enter your current password and choose a new one',
    passwordchange_currentpassword: 'Current Password',
    passwordchange_currentpassword_placeholder: 'Enter your current password',
    passwordchange_newpassword: 'New Password',
    passwordchange_newpassword_placeholder: 'Enter your new password',
    passwordchange_confirmnewpassword: 'Confirm New Password',
    passwordchange_confirmnewpassword_placeholder: 'Confirm your new password',
    passwordchange_changing: 'Changing Password...',
    passwordchange_changebutton: 'Change Password',
    passwordchange_success_title: 'Password Changed Successfully!',
    passwordchange_success_nextstep: 'All your other sessions have been logged out for security. Please log in again with your new password.',
    passwordchange_success_gotologin: 'Go to Login',
    passwordchange_failed_title: 'Change Failed',
    passwordchange_failed_returntologin: 'Return to Login',
    passwordchange_error_notoken: 'No change token provided. Please check your email and click the password change link again.',
    passwordchange_error_invalidtoken: 'Invalid or expired change token. Please request a new password change.',
    passwordchange_error_nomatch: 'Passwords do not match',
    passwordchange_error_criteria: 'Password does not meet the required criteria',
    passwordchange_error_failed: 'Failed to change password',
    passwordchange_error_tryagain: 'Failed to change password. Please try again.',
    passwordchange_error_invalidrequest: 'Invalid password change request.',
    passwordchange_error_unauthorized: 'Invalid or expired change token, or incorrect current password.',
    passwordchange_success_message: 'Your password has been changed successfully',
    passwordchange_success_revoked: ' and all other sessions have been signed out',
    passwordchange_success_revoke_failed: 'Password changed, but failed to sign out other sessions. You can manually revoke them in Security settings.',

    // Not Found Page
    notfound_title: 'Page Not Found',
    notfound_message: 'Sorry, the page you are looking for doesn\'t exist.',

    // Support Page
    support_title: 'Support Resources',
    support_subtitle: 'Everything you need to get the most out of MetaLark',
    support_setupguide_title: 'Setup Guide',
    support_setupguide_description: 'Get started with MetaLark. Learn how to configure your workspace, set up user roles, and upload your first content.',
    support_setupguide_badge: 'Getting Started',
    support_userguide_title: 'User Guide',
    support_userguide_description: 'Comprehensive guide to using MetaLark. Learn about content management, AI features, and advanced functionality.',
    support_lmsintegration_title: 'LMS Integration Guide',
    support_lmsintegration_description: 'Connect MetaLark with your Learning Management System. Supports REST API integration.',
    support_moodlesetup_title: 'Moodle Setup Guide',
    support_moodlesetup_description: 'For the Moodle administrator: enable web services, define the service MetaLark calls, and issue a token.',
    support_moodlepluginstall_title: 'Connector Plugin Installation',
    support_moodlepluginstall_description: 'For the Moodle administrator: install the MetaLark Connector Plugin and authorize its functions, so published Courses arrive with their content.',
    support_documentation_badge: 'Documentation',
    support_openapi_title: 'OpenAPI Specification',
    support_openapi_description: 'Interactive API documentation with Swagger UI. Explore all available endpoints and test API calls.',
    support_reportissue_title: 'Report an Issue',
    support_reportissue_description: 'Need help? Report bugs, request features, or contact our support team through our help center.',
    support_support_badge: 'Support',
    support_guides_badge: 'Guides',
    support_technical_badge: 'Technical',
    support_releasenotes_title: 'Release Notes',
    support_releasenotes_description: 'View the latest updates, new features, bug fixes, and improvements to the MetaLark platform.',
    support_whatsnew_badge: 'What\'s New',
    support_externalplayer_title: 'External Player Launch Guide',
    support_externalplayer_description: 'Authenticate and Launch to the MetaLark Player for an identified Resource from an external location using a temporary auth token.',
    support_platformwiki_title: 'LMS Platform Portability Wiki',
    support_platformwiki_description: 'Reference guide to how learning platforms handle content ingress and egress — what you can move in, get out, and migrate when a contract ends.',
    support_filter_label: 'Filter resources by category',
    support_filter_all: 'All',
    support_search_placeholder: 'Search support resources...',
    support_no_results: 'No resources match your filters.',

    // LMS Platform Portability page (in-site field guide chrome)
    pagetitle_lmsportability: 'LMS Platform Portability',
    lmspw_search_placeholder: 'Filter platforms…',
    lmspw_search_label: 'Filter platforms',
    lmspw_filtergroup_label: 'Filter by portability category',
    lmspw_filter_all: 'All',
    lmspw_filter_open: 'Open',
    lmspw_filter_constrained: 'Constrained',
    lmspw_filter_bydesign: 'By design',
    lmspw_filter_service: 'Not a platform',
    lmspw_nav_label: 'Platform list',
    lmspw_toggle_label: 'Browse platforms',
    lmspw_empty: 'No platforms match that filter.',
    lmspw_profiles_label: 'Platform profiles',
    lmspw_spectrum_label: 'Portability spectrum',

    // Link Account Page
    linkaccount_title: 'Link Your Account',
    linkaccount_tagline: 'Connect your OAuth provider to your account',
    linkaccount_verifying_title: 'Verifying linking request...',
    linkaccount_verifying_message: 'Please wait while we verify your account linking request.',
    linkaccount_emailverified_title: 'Email Verified!',
    linkaccount_emailverified_message: 'Your email has been verified successfully.',
    linkaccount_finalstep_title: 'Final Step: Re-authenticate with {providerName}',
    linkaccount_finalstep_message: 'To complete the account linking process, please sign in with your {providerName} account one more time.',
    linkaccount_step1: 'Click the button below to sign in with {providerName}',
    linkaccount_step2: 'You\'ll be redirected to {providerName} to authenticate',
    linkaccount_step3: 'After authentication, your accounts will be linked',
    linkaccount_continuetosignin: 'Continue to Sign In',
    linkaccount_gotosettings: 'Go to Settings',
    linkaccount_success_title: 'Account Linked Successfully!',
    linkaccount_success_message: 'Your {providerName} account has been linked to your MetaLark.ai account.',
    linkaccount_success_signinusing: 'You can now sign in using:',
    linkaccount_success_emailpassword: 'Your email and password',
    linkaccount_success_provider: 'Your {providerName} account',
    linkaccount_gotologin: 'Go to Login',
    linkaccount_failed_title: 'Linking Failed',
    linkaccount_returntologin: 'Return to Login',
    linkaccount_error_notoken: 'No linking token provided. Please check your email and click the verification link again.',
    linkaccount_error_verification: 'Verification failed. Please try again.',
    linkaccount_error_generic: 'An error occurred during verification. The token may be invalid or expired.',

    // Verify Email Page
    verifyemail_welcome: 'Welcome to MetaLark.ai',
    verifyemail_tagline: 'A Content Intelligence Toolkit',
    verifyemail_verifying_title: 'Verifying your email...',
    verifyemail_verifying_message: 'Please wait while we verify your email address.',
    verifyemail_success_title: 'Email Verified Successfully!',
    verifyemail_nextstep: 'Now let\'s set up your password to complete your account.',
    verifyemail_continue: 'Continue',
    verifyemail_setpassword_title: 'Set Your Password',
    verifyemail_setpassword_description: 'Choose a strong password for your account',
    verifyemail_password: 'Password',
    verifyemail_passwordplaceholder: 'Enter your password',
    verifyemail_showpassword: 'Show password',
    verifyemail_hidepassword: 'Hide password',
    verifyemail_requirements: 'Password Requirements:',
    verifyemail_req_length: 'At least 12 characters',
    verifyemail_req_uppercase: 'One uppercase letter (A-Z)',
    verifyemail_req_lowercase: 'One lowercase letter (a-z)',
    verifyemail_req_number: 'One number (0-9)',
    verifyemail_req_special: 'One special character (@$!%*?& etc.)',
    verifyemail_confirmpassword: 'Confirm Password',
    verifyemail_confirmpasswordplaceholder: 'Confirm your password',
    verifyemail_settingpassword: 'Setting Password...',
    verifyemail_setpasswordbutton: 'Set Password',
    verifyemail_failed_title: 'Verification Failed',
    verifyemail_requestnewlink: 'Request New Verification Link',
    verifyemail_returntologin: 'Return to Login',
    verifyemail_requestlink_title: 'Request New Link',
    verifyemail_requestlink_description: 'Enter your email address to receive a new verification link',
    verifyemail_emailaddress: 'Email Address',
    verifyemail_emailplaceholder: 'your@email.com',
    verifyemail_sending: 'Sending...',
    verifyemail_sendlink: 'Send Verification Link',
    verifyemail_cancel: 'Cancel',
    verifyemail_passwordsetsuccess_title: 'Password Set Successfully!',
    verifyemail_passwordsetsuccess_message: 'Your password has been set successfully.',
    verifyemail_passwordsetsuccess_nextstep: 'You can now log in with your new credentials.',
    verifyemail_gotologin: 'Go to Login',
    verifyemail_error_notoken: 'No verification token provided. Please check your email and click the verification link again.',
    verifyemail_error_verification: 'Verification failed. Please try again.',
    verifyemail_error_generic: 'An error occurred during verification. Please try again later.',
    verifyemail_error_passwordmismatch: 'Passwords do not match',
    verifyemail_error_passwordcriteria: 'Password does not meet the required criteria',
    verifyemail_error_setpassword: 'Failed to set password',
    verifyemail_error_setpasswordretry: 'Failed to set password. Please try again.',
    verifyemail_error_emailrequired: 'Please enter your email address',
    verifyemail_resendsuccess: 'Verification email sent! Please check your inbox.',
    verifyemail_error_resendfailed: 'Failed to send verification email',
    verifyemail_error_resendretry: 'Failed to send verification email. Please try again.',

    // Verify Email Change Page
    verifyemailchange_title: 'Email Change Verification',
    verifyemailchange_tagline: 'Verifying your email address change',
    verifyemailchange_verifying_title: 'Verifying email change...',
    verifyemailchange_verifying_message: 'Please wait while we verify your email address.',
    verifyemailchange_oldemail_verified: 'Old Email Verified!',
    verifyemailchange_newemail_verified: 'New Email Verified!',
    verifyemailchange_status_oldverified: 'Old email verified',
    verifyemailchange_status_newverified: 'New email verified',
    verifyemailchange_nextstep: 'Please check your other email inbox and click the verification link to complete the email change.',
    verifyemailchange_backtosettings: 'Back to Settings',
    verifyemailchange_success_title: 'Email Changed Successfully!',
    verifyemailchange_success_message: 'Your email address has been updated successfully.',
    verifyemailchange_newemail: 'New Email:',
    verifyemailchange_success_nextstep: 'You can now use your new email address to log in.',
    verifyemailchange_gotosettings: 'Go to Settings',
    verifyemailchange_gotologin: 'Go to Login',
    verifyemailchange_failed_title: 'Verification Failed',
    verifyemailchange_returntologin: 'Return to Login',
    verifyemailchange_error_notoken: 'No verification token provided. Please check your email and click the verification link again.',
    verifyemailchange_success_complete: 'Your email address has been changed successfully!',
    verifyemailchange_error_verification: 'Verification failed. Please try again.',
    verifyemailchange_error_generic: 'An error occurred during verification. The token may be invalid or expired.',

    // Auth Progress Steps Component
    authprogress_title: 'Authentication Progress',
    authprogress_step1: 'Start\nAuthentication',
    authprogress_step2: 'OAuth\nProvider',
    authprogress_step3: 'Verify\nIdentity',
    authprogress_step4: 'Access\nGranted',

    // Forgot Password Page
    forgotpassword_title: 'Forgot Your Password',
    forgotpassword_description: 'Enter your email address and we\'ll send you instructions to reset your password.',
    forgotpassword_emailaddress: 'Email Address',
    forgotpassword_emailplaceholder: 'your@email.com',
    forgotpassword_sending: 'Sending...',
    forgotpassword_sendbutton: 'Send Reset Instructions',
    forgotpassword_backtologin: 'Back to Login',
    forgotpassword_checkemail_title: 'Check Your Email',
    forgotpassword_checkemail_message: 'If an account exists with <strong>{email}</strong>, you will receive password reset instructions shortly.',
    forgotpassword_securitynote: 'For security reasons, we don\'t disclose whether an account exists with that email address.',
    forgotpassword_didntreceive: 'Didn\'t receive the email?',
    forgotpassword_tips: '<ul><li>Check your spam or junk folder</li><li>Make sure you entered the correct email address</li><li>The reset link expires in 1 hour</li></ul>',
    forgotpassword_returntologin: 'Return to Login',
    forgotpassword_trydifferent: 'Try Different Email',
    forgotpassword_error_required: 'Please enter your email address',
    forgotpassword_error_invalid: 'Please enter a valid email address',
    forgotpassword_error_sendfailed: 'Failed to send reset email',

    // Trial Signup Page
    trialsignup_title: 'MetaLark Free Trial',
    trialsignup_duration: '{days}-day trial',
    trialsignup_description: 'Sign up and upload up to twenty representative content items in various formats and MetaLark will process and deploy them for your review in a private, secure instance for a {days}-day evaluation.',
    trialsignup_starttrial: 'Start Your Free Trial',
    trialsignup_trialsummary: '{days} days \u2022 {credits} free credits',
    trialsignup_loading: 'Loading...',
    trialsignup_haveaccesscode: 'Do you have an access code?',
    trialsignup_accesscodedescription: 'Access codes provide free trial access without requiring a credit card. Without a code you\'ll just need to provide a credit card to begin your free trial.',
    trialsignup_yeshavecode: 'Yes, I have an access code',
    trialsignup_nostartwithoutcode: 'No, start my trial without a code',
    trialsignup_backtooptions: '← Back to options',
    trialsignup_contactdetails: 'Contact details',
    trialsignup_email: 'Email',
    trialsignup_emailplaceholder: 'email@example.com',
    trialsignup_fullname: 'Full name',
    trialsignup_fullnameplaceholder: 'Full name',
    trialsignup_businessname: 'Business name',
    trialsignup_businessnameplaceholder: 'Business name',
    trialsignup_phone: 'Phone',
    trialsignup_phoneplaceholder: '(201) 555-0123',
    trialsignup_accesscode: 'Access Code',
    trialsignup_accesscodeplaceholder: 'Enter your access code',
    trialsignup_code_checking: 'Checking code…',
    trialsignup_code_valid: 'Code accepted.',
    trialsignup_perk_trial_days: 'Trial length:',
    trialsignup_perk_trial_credits: 'Included credits:',
    trialsignup_perk_discount: 'Discount:',
    trialsignup_code_reason_unknown_code: 'Unknown access code. Please double-check and try again.',
    trialsignup_code_reason_not_active: 'This code isn\'t available right now.',
    trialsignup_code_reason_not_started: 'This code isn\'t active yet. Try again once the campaign begins.',
    trialsignup_code_reason_expired: 'This code has expired.',
    trialsignup_code_reason_depleted: 'All available redemptions for this code have been used.',
    trialsignup_code_reason_domain_not_allowed: 'This code isn\'t valid for your email domain.',
    trialsignup_code_reason_domain_denied: 'This code isn\'t valid for your email domain.',
    trialsignup_code_reason_domain_cap_reached: 'Too many signups from your email domain have already used this code.',
    trialsignup_code_reason_rate_limited: 'Too many attempts. Wait a moment and try again.',
    trialsignup_code_reason_unknown: 'Code is not valid.',
    trialsignup_noaccesscode: 'Don\'t have an access code?',
    trialsignup_signupwithcard: 'Sign up for a trial with credit card instead',
    trialsignup_billingaddress: 'Billing address',
    trialsignup_countryorregion: 'Country or region',
    trialsignup_selectcountry: 'Select country',
    trialsignup_address: 'Address',
    trialsignup_addressline2: 'Apt, suite, etc. (optional)',
    trialsignup_city: 'City',
    trialsignup_state: 'State',
    trialsignup_state_placeholder_state: 'State',
    trialsignup_state_placeholder_province: 'Province',
    trialsignup_state_placeholder_state_optional: 'State (optional)',
    trialsignup_state_placeholder_region_optional: 'Region (optional)',
    trialsignup_state_placeholder_county_optional: 'County (optional)',
    trialsignup_state_placeholder_prefecture_optional: 'Prefecture (optional)',
    trialsignup_state_placeholder_province_optional: 'Province (optional)',
    trialsignup_state_placeholder_canton_optional: 'Canton (optional)',
    trialsignup_state_placeholder_default: 'State / Province / Region (optional)',
    trialsignup_state_optional_marker: 'optional',
    trialsignup_select: 'Select',
    trialsignup_zip: 'ZIP',
    trialsignup_creatingtrial: 'Creating your trial...',
    trialsignup_startfreetrial: 'Start Free Trial',
    trialsignup_alreadyhaveaccount: 'Already have an account?',
    trialsignup_signin: 'Sign in',
    trialsignup_error_validemail: 'Please enter a valid email address',
    trialsignup_error_fullname: 'Please enter your full name',
    trialsignup_error_companyname: 'Company name is required',
    trialsignup_error_validphone: 'Please enter a valid phone number',
    trialsignup_error_streetaddress: 'Street address is required',
    trialsignup_error_city: 'City is required',
    trialsignup_error_state: '{placeholder} is required',
    trialsignup_error_validentry: 'Please provide a valid entry',
    trialsignup_error_postalcode: 'Postal code is required',
    trialsignup_error_selectcountry: 'Please select a country',
    trialsignup_error_accesscode: 'Access code is required',
    trialsignup_error_signupfailed: 'Signup failed. Please try again.',

    // Trial Success Page
    trialsuccess_welcome: 'Welcome to MetaLark.ai',
    trialsuccess_tagline: 'A Content Intelligence Toolkit',
    trialsuccess_title: 'Trial Account Created!',
    trialsuccess_message: 'Your free trial account has been created successfully.',
    trialsuccess_whatsnext: 'What\'s Next?',
    trialsuccess_step1_title: 'Check your email',
    trialsuccess_step1_message: 'We\'ve sent you a verification link to confirm your email address.',
    trialsuccess_step2_title: 'Set your password',
    trialsuccess_step2_message: 'After verifying your email, you\'ll be able to set your password.',
    trialsuccess_step3_title: 'Start exploring',
    trialsuccess_step3_message: 'Once verified, sign in and start using your {trialCredits} free credits!',
    trialsuccess_trialincludes: 'Your Trial Includes:',
    trialsuccess_daysfree: '{days} Days Free',
    trialsuccess_secureinstance: 'Access your own private, secure instance',
    trialsuccess_credits: '{credits} Credits',
    trialsuccess_freecredits: '{credits} Free Credits',
    trialsuccess_processcontent: 'Process your first content',
    trialsuccess_nocreditcard: 'No Credit Card',
    trialsuccess_nopayment: 'No payment required now',
    trialsuccess_gotologin: 'Go to Login',
    trialsuccess_resendverification: 'Resend Verification Email',
    trialsuccess_sending: 'Sending...',
    trialsuccess_emailsent: 'Verification email sent! Please check your inbox.',
    trialsuccess_notreceived: 'Didn\'t receive the email? Check your spam folder or',
    trialsuccess_contactsupport: 'contact support',

    // Onboarding Page
    onboarding_title: 'Setup Guide',
    onboarding_profileinfo_title: 'Profile Information',
    onboarding_profileinfo_description: 'Update/add customer information including logo and contact information.',
    onboarding_exploreusecases_title: 'Explore Use Cases',
    onboarding_exploreusecases_description: 'See how MetaLark\'s toolkit meets your use case.',
    onboarding_exploreusecases_intro: 'MetaLark provides a variety of different uses for our platform. Please browse through these different use cases to find how best to utilize MetaLark for your needs.',
    onboarding_connect_title: 'Connect Your Content & Services',
    onboarding_connect_description: 'Connect external repositories, grab an API key.',
    onboarding_connect_intro: 'Along with uploading content directly via our upload page, <strong>MetaLark</strong> offers the ability to connect through APIs and external repositories to streamline getting large amounts of content into the system. To set these up later, please visit the Admin Dashboard page via the Gear Icon in the menu bar.',
    onboarding_contentintel_title: 'Content Intelligence',
    onboarding_contentintel_description: 'Analyze an entire library of learning content for substance and future relevancy. A content manager with thousands of digital resources, articles and study guides faces months of manual effort to understand what all is in their legacy content. Unfortunately, not all of their content has accurate or updated metadata making review and updates tedious especially given how outdated some of the content has grown. The learning team is able to leverage MetaLark\'s content ingestion APIs to automate the capture of all legacy content assets and then use MetaLark\'s Parsing Engine to analyze thousands of summaries stored in standard DOCX and RTF formats to AI-generate full metadata for more than 5,000 content items in less than a week\'s time.',
    onboarding_skillsalignment_title: 'Skills Alignment',
    onboarding_skillsalignment_description: 'Align available content with targeted skills. Identify gaps. In their efforts to transform their culture from delivering training through structured curriculums into becoming a "skills-based organization", a leading technology company needs to document how well their training content aligns with their selected skills taxonomies. Unfortunately, the content in their enterprise LMS has either incomplete or altogether missing metadata, making it difficult to identify how their content may align with their future skills targets. MetaLark\'s Parsing Engine is able to analyze more than 7,500 content items over a long weekend, then automatically update missing metadata to help create an updated Skills Inventory that can be reviewed by their L&D leadership team to make decisions on measurable skills gaps they need to fill.',
    onboarding_searchdiscovery_title: 'Search & Discovery',
    onboarding_searchdiscovery_description: 'Find the needles in the haystack. A large manufacturing company recently merged with one of their industry competitors and now needs to update their company name in all their training courseware and reference materials. The merged L&D departments have limited resources to try and identify all the many places the name is used across the broad array of existing content originally created by scores of instructional designers, trainers, third party custom content developers and consultants. With <strong>MetaLark</strong>, the new team can quickly leverage APIs to import content into <strong>MetaLark</strong> from the legacy LMS platform and other active repositories. Once all the content is processed, they can easily perform a search for each occurrence of the current company name so that it can be replaced where appropriate. In the process, the new learning team can also take the opportunity to decide whether the content items should be "retained, refreshed or retired" prior to any data export.',
    onboarding_migration_title: 'Migration',
    onboarding_migration_description: 'Transition Content to a new Learning Platform. A learning-centric Systems Integrator is tasked with assisting a large enterprise customer with multiple legacy LMS platforms in transitioning onto a third more versatile LMS that will serve the enterprise moving forward. Besides the enormous challenge of identifying and assessing the broad array of existing content housed in the various existing platforms, is the challenge of "moving" the content items onto the new LMS platform, and potentially even from one authoring tool to another. With <strong>MetaLark</strong>, JSON-based content templates can serve as the foundation for repurposing and migrating existing legacy content into new contexts such as converting a SCORM or xAPI course created using authoring tool A into a new course editable in authoring tool B. Finally, teams can refactor text-based summaries of content extracted from their legacy library into a format that\'s easily migrated to/importable into corporate Large Language Models or provisioned for Agentic AI access if desired. MetaLark\'s Parsing Engine is able to analyze more than 15,000 content items over a week, then automatically update missing details to help create a full inventory complete with accurate metadata. Content tagged for future use can later be automatically pushed into the new go-forward LMS platform, saving substantial time and effort for the integrator\'s team.',
    onboarding_downloadtemplates_title: 'Download Templates',
    onboarding_downloadtemplates_description: 'The Content Key gives you a way to apply a title, description, or other existing metadata that you want to apply to content items that have already been uploaded and processed by MetaLark. Each time you upload a key file, MetaLark will find the content with the matching ID field value and apply the key file values to that item.',
    onboarding_s3bucket_title: 'External Repository Management',
    onboarding_s3bucket_description: 'Repository integrations allow you to connect external file storage platforms like Google Drive and SharePoint to easily sync content into your workspace. Once connected, you can seamlessly import learning materials, documents, and other resources from these external repositories for AI-powered analysis and metadata extraction. Each workspace maintains its own repository connections, giving you flexibility to configure different integrations per workspace based on your team\'s needs. Currently, MetaLark supports Google Drive and SharePoint integrations, with each connection securely scoped to your current workspace.',
    onboarding_connectrepositories: 'Connect Repositories',
    onboarding_apimanagement_title: 'API Management',
    onboarding_apidocs_title: 'Interactive API Documentation',
    onboarding_apidocs_description: 'Explore the MetaLark.ai API with interactive Swagger documentation:',
    onboarding_openapi: 'OpenAPI Specification',
    onboarding_usingtoken_title: 'Using Your Token',
    onboarding_usingtoken_instruction1: 'To authenticate with the API, always include your API token in the Authorization header of each request as a Bearer token.',
    onboarding_usingtoken_instruction2: 'Replace YOUR_API_TOKEN with your actual API token. Requests without this header or with an invalid token will be rejected. Never expose your token in URLs, request bodies, or client-side code.',
    onboarding_carousel_migration1: '<h3 class=\'font-bold mb-1 text-accent\'>Content Lifecycle Management and Workflow Actions</h3>MetaLark offers a series of conditional flags to help Analysts determine the state and desired disposition of each content item allowing for more targeted and intelligent exports and migration efforts.',
    onboarding_carousel_migration2: '<h3 class=\'font-bold mb-1 text-accent\'>Centralized Content Migration and Workflow Management</h3>The <strong>Migration Console</strong> allows teams to determine the readiness of all content marked for migration tasks as part of an enterprise content lifecycle strategy. The initial view shows a filtered list featuring assets whose action is set to <strong>Retain</strong>, which signals that an item is ready for migration. All flagged items are staged for API-level access from a connected LMS platform or enterprise Large Language Model.',
    onboarding_carousel_skills1: '<h3 class=\'font-bold mb-1 text-accent\'>Linking Skills to Content Assets</h3>Analysts have access to the full library of inferred skills as represented in their MetaLark Content Encyclopedia. Selecting a specific skill from a content item\'s record launches a hyperlink to the top-level Skills area where a list of all content items with that associated Skill will be shown in order of relevance and confidence along with an explanation of why MetaLark inferred this skill designation. Finally, Analysts are able to confirm skills from the content detail perspective and manage skill associations.',
    onboarding_carousel_search1: '<h3 class=\'font-bold mb-1 text-accent\'>Advanced Content Search and Semantic Retrieval</h3>MetaLark provides a powerful and flexible content search and retrieval function. Beyond searching by word or phrase, Analysts can search along any dimension (Type, Status, or Action) to instantly filter the list to the relevant desired content and drill down to see where the search word or phrase is actually found within the content items themselves.',
    onboarding_carousel_search2: '<h3 class=\'font-bold mb-1 text-accent\'>Deep Search</h3>The <strong>Discovery Console</strong> is a full-text and semantic search tool that presents search results with context, showing item type, associated collections, and a relevant content excerpt with the keyword highlighted. This console is crucial for administrators needing to quickly understand why an asset was returned in the search and what specific part of the content addresses the query.',
    onboarding_carousel_contentintel1: '<h3 class=\'font-bold mb-1 text-accent\'>Updating Content Metadata and Organization</h3>Effective content management starts with accurate metadata and organization. After identifying an item on the \'Content\' tab, you can access the detailed view to review extracted information like Type (PDF), File Name, and Keywords. Switching to the edit interface enables real-time changes, such as refreshing Titles, Descriptions and Keywords as well as associating the content item to one or more defined Collections which ensures the content is properly grouped and discoverable within the system.',
    onboarding_carousel_contentintel2: '<h3 class=\'font-bold mb-1 text-accent\'>Content Summary Review and Editing Capability</h3>MetaLark delivers rapid content analysis and enables administrative control over all extracted content and metadata. Analysts are able to review, print and download extracted details in a variety of digital formats. This ensures data accuracy and alignment with organizational standards.',
    onboarding_carousel_contentintel3: '<h3 class=\'font-bold mb-1 text-accent\'>Content Launch Preview</h3>Use the Preview Content feature to open and play your content when needed to verify it\'s as aligned to the metadata generated. The Preview Content feature plays back SCORM/xAPI/cmi5 packages as well as displays other digital content like videos, podcasts, PDFs and more without needing to return to your LMS or cloud repository.',

    // Admin Dashboard page
    admin_dashboard_title: 'Admin Dashboard',
    admin_dashboard_apidocs: 'Interactive API Documentation',
    admin_dashboard_explore: 'Explore the MetaLark.ai API with interactive Swagger documentation',
    admin_dashboard_openapi: 'OpenAPI Specification',
    admin_dashboard_usingtoken: 'Using Your Token',
    admin_dashboard_authenticate: 'To authenticate with the API, always include your API token in the Authorization header of each request as a Bearer token.',
    admin_dashboard_tokenwarning: 'Replace YOUR_API_TOKEN with your actual API token. Requests without this header or with an invalid token will be rejected. Never expose your token in URLs, request bodies, or client-side code.',
    admin_dashboard_built: 'Built',
    admin_dashboard_branch: 'Branch',
    admin_dashboard_environment: 'Environment',
    admin_dashboard_fullcommit: 'Full Commit',
    admin_dashboard_release: 'Release',
    admin_dashboard_build_suffix: 'Build',
    admin_dashboard_loaderror: 'Failed to load some admin data. Check console for details.',

    // Admin Navigation
    admin_nav_sysadminonly: 'System Admin Only',
    admin_nav_dashboard: 'Dashboard',
    admin_nav_activitydashboard: 'Activity Dashboard',
    admin_nav_users: 'Users',
    admin_nav_auditlogs: 'Audit Logs',
    admin_nav_customermanagement: 'Customer Management',
    admin_nav_customeroperations: 'Customer Operations',
    admin_nav_deletions: 'Deletions',
    admin_nav_sso: 'SSO',
    admin_nav_identityproviders: 'Identity Providers',
    admin_nav_verifieddomains: 'Verified Domains',
    admin_nav_logindomain: 'Login Domain',
    admin_nav_customdomains: 'Custom Domains (All)',
    admin_nav_cloudrepositories: 'Cloud Repositories',
    admin_nav_repositories: 'Repositories',
    admin_nav_fileselectionrules: 'File Selection Rules',
    admin_nav_pendingfiles: 'Pending Files',
    admin_nav_activitylog: 'Activity Log',
    admin_nav_financials: 'Financials',
    admin_nav_billing: 'Billing',
    admin_nav_purchasecredits: 'Purchase Credits',
    admin_nav_processingcredits: 'Processing Credits',
    admin_nav_quotahealth: 'Quota Health',
    admin_nav_xray: 'Processing X-Ray',
    admin_nav_email: 'Email',
    admin_nav_emailtemplates: 'Email Templates',
    admin_nav_emaillogs: 'Email Logs',
    admin_nav_notifications: 'Notifications',
    admin_nav_emailsuppression: 'Email Suppression',
    admin_nav_systemconfiguration: 'System & Configuration',
    admin_nav_featureflags: 'Feature Flags',
    admin_nav_webhooksubscriptions: 'Webhook Subscriptions',
    admin_nav_administration: 'Administration',
    admin_nav_customeradministration: 'Customer Administration',
    admin_nav_users_global: 'Users (Global)',
    admin_nav_auditlogs_global: 'Audit Logs (Global)',
    admin_nav_emaillogs_global: 'Email Logs (Global)',

    // Admin - Custom Domains
    customdomains_title: 'Custom Domains',
    customdomains_subtitle: 'Map subdomains to customers for branded SSO login pages',
    customdomains_adddomain: 'Add Domain',
    customdomains_editdomain: 'Edit Domain',
    customdomains_addcustomdomain: 'Add Custom Domain',
    customdomains_howitworks: 'How it works',
    customdomains_howitworks_desc: 'Each subdomain (e.g. acme) maps to a customer and their primary identity provider. When users visit acme.{baseDomain}, they see a branded login page with that customer\'s SSO options.',
    customdomains_totaldomains: 'Total Domains',
    customdomains_customer: 'Customer',
    customdomains_primaryprovider: 'Primary Provider',
    customdomains_defaultslice: 'Default Slice',
    customdomains_loadingdomains: 'Loading domains...',
    customdomains_emptystate: 'No custom domains configured. Click "Add Domain" to get started.',
    customdomains_domainconfiguration: 'Domain Configuration',
    customdomains_checkingavailability: 'Checking availability...',
    customdomains_subdomainavailable: 'Subdomain is available',
    customdomains_customerid: 'Customer ID',
    customdomains_customerid_help: 'The customer organization this subdomain belongs to.',
    customdomains_primaryidentityprovider: 'Primary Identity Provider',
    customdomains_primaryidentityprovider_help: 'The default sign-in provider shown on the login page. Must belong to the selected customer.',
    customdomains_defaultslice_label: 'Default Slice (optional)',
    customdomains_defaultslice_placeholder: 'Leave empty for no default',
    customdomains_defaultslice_help: 'If set, users arriving at this subdomain are placed into this workspace by default.',
    customdomains_createdomain: 'Create Domain',
    customdomains_setupguide: 'Setup Guide',
    customdomains_setupguide_desc: 'How custom domains work in MetaLark.',
    customdomains_step1_title: 'Create the mapping',
    customdomains_step1_desc: 'Choose a subdomain and link it to a customer and their primary identity provider.',
    customdomains_step2_title: 'DNS is automatic',
    customdomains_step2_desc: 'A wildcard DNS record (*.{baseDomain}) routes all subdomains to CloudFront. No per-subdomain DNS changes needed.',
    customdomains_step3_title: 'Lambda@Edge resolves it',
    customdomains_step3_desc: 'When a user visits the subdomain, Lambda@Edge looks up the mapping and injects customer context headers into the request.',
    customdomains_step4_title: 'User sees branded login',
    customdomains_step4_desc: 'The login page shows the customer\'s logo and configured identity providers. The subdomain is live immediately after creation.',
    customdomains_loadfailed: 'Failed to load custom domains',
    customdomains_requiredfields: 'Subdomain, customer ID, and primary provider are required',
    customdomains_created: 'Domain "{subdomain}" created',
    customdomains_updated: 'Domain "{subdomain}" updated',
    customdomains_savefailed: 'Failed to save: {error}',
    customdomains_deleteconfirm: 'Delete subdomain "{subdomain}"? It will become unavailable immediately.',
    customdomains_deleted: 'Domain "{subdomain}" deleted',
    customdomains_deletefailed: 'Failed to delete: {error}',

    // Admin - Login Domain
    logindomain_title: 'Login Domain',
    logindomain_subtitle: 'Give your team a branded sign-in URL with your organization\'s identity provider',
    logindomain_customdomains: 'Custom Domains',
    logindomain_trialgate: 'Custom sign-in domains become available once your trial converts to a paid subscription. Contact Metalark support if you need assistance.',
    logindomain_upgrade: 'Upgrade',
    logindomain_teamsigninurl: 'Your team\'s sign-in URL',
    logindomain_previewloginpage: 'Preview Login Page',
    logindomain_currentsettings: 'Current Settings',
    logindomain_change: 'Change',
    logindomain_primaryprovider: 'Primary Sign-in Provider',
    logindomain_defaultworkspace: 'Default Workspace',
    logindomain_removedomain: 'Remove Domain',
    logindomain_removedomain_description: 'This will immediately disable the custom login URL. Your team will need to use the standard login page instead.',
    logindomain_removecustomdomain: 'Remove Custom Domain',
    logindomain_updateyourdomain: 'Update Your Login Domain',
    logindomain_setupyourdomain: 'Set Up Your Login Domain',
    logindomain_changeproviderorworkspace: 'Change your sign-in provider or default workspace.',
    logindomain_chooseshortname: 'Choose a short name for your organization. Your team will use this URL to sign in.',
    logindomain_yourloginurl: 'Your Login URL',
    logindomain_placeholder_subdomain: 'your-company',
    logindomain_checkingavailability: 'Checking availability...',
    logindomain_isavailable: 'is available',
    logindomain_primarysigninmethod: 'Primary Sign-in Method',
    logindomain_primarysigninmethod_description: 'This will be the main button on your login page. Users can also choose from any other providers you\'ve configured.',
    logindomain_loadingproviders: 'Loading providers...',
    logindomain_noproviders: 'No identity providers configured.',
    logindomain_noproviders_hint: 'No identity providers configured yet.',
    logindomain_setupone: 'Set one up first',
    logindomain_primary_none: 'None (username + password)',
    logindomain_primary_none_hint: 'Use the branded URL without a required SSO provider.',
    logindomain_activatedomain: 'Activate Domain',
    logindomain_providertype_password: 'Password',
    logindomain_selectprovider: 'Please select a primary sign-in provider',
    logindomain_updated: 'Login domain updated',
    logindomain_activated: 'Login domain activated!',
    logindomain_failed: 'Failed: {message}',
    logindomain_confirmremove: 'Remove your custom login domain? Your team will need to use the standard login page.',
    logindomain_removed: 'Login domain removed',
    logindomain_copiedtoclipboard: 'Copied to clipboard',

    // Admin - Activity Dashboard
    activitydashboard_title: 'Activity Dashboard',
    activitydashboard_systemwide_tooltip: 'View activity across all customers (System Admin Only)',
    activitydashboard_systemwide_label: 'All customers',
    activitydashboard_timewindow_15min: '15 min',
    activitydashboard_timewindow_30min: '30 min',
    activitydashboard_timewindow_1hour: '1 hour',
    activitydashboard_timewindow_4hours: '4 hours',
    activitydashboard_timewindow_12hours: '12 hours',
    activitydashboard_timewindow_24hours: '24 hours',
    activitydashboard_timewindow_placeholder: 'min',
    activitydashboard_timewindow_unit: 'min',
    activitydashboard_timewindow_backtopresets: 'Back to presets',
    activitydashboard_timewindow_label: 'Time window',
    activitydashboard_refresh_tooltip: 'Refresh (R)',
    activitydashboard_tab_daily: 'Daily Usage',
    activitydashboard_tab_realtime: 'Real Time',
    activitydashboard_recentactivity_heading: 'Recent Activity',
    activitydashboard_section_sessionsusers: 'Sessions & users',
    activitydashboard_metric_activesessions: 'Active Sessions',
    activitydashboard_metric_activesessions_tooltip: 'Number of unique sessions with activity in the current time window',
    activitydashboard_metric_uniqueusers: 'Unique Users',
    activitydashboard_metric_uniqueusers_tooltip: 'Distinct users (by email or UUID) active in the current time window',
    activitydashboard_metric_customers: 'Customers',
    activitydashboard_metric_customers_tooltip: 'Number of distinct customers with activity in the time window',
    activitydashboard_metric_workspaces: 'Workspaces',
    activitydashboard_metric_workspaces_tooltip: 'Number of distinct workspaces (slices) with activity in the time window',
    activitydashboard_metric_totalrequests: 'Total Requests',
    activitydashboard_metric_totalrequests_tooltip: 'Total API requests in the most recent refresh period, across all token types',
    activitydashboard_metric_avgduration: 'Avg Duration',
    activitydashboard_metric_avgduration_tooltip: 'Average session duration across all active sessions in the time window',
    activitydashboard_section_requestsbytype: 'Requests by type',
    activitydashboard_metric_sessiontoken: 'Session Token',
    activitydashboard_metric_sessiontoken_tooltip: 'Requests from interactive browser sessions (password, passkey, OAuth logins)',
    activitydashboard_metric_apitoken: 'API Token',
    activitydashboard_metric_apitoken_tooltip: 'Requests from long-lived API tokens (chatbots, integrations, scripts)',
    activitydashboard_metric_launchtoken: 'Launch Token',
    activitydashboard_metric_launchtoken_tooltip: 'Requests from time-limited LMS launch tokens (embedded player access)',
    activitydashboard_metric_searches: 'Searches',
    activitydashboard_metric_searches_tooltip: 'Number of content searches performed in the time window',
    activitydashboard_collapseall: 'Collapse all',
    activitydashboard_expandall: 'Expand all',
    activitydashboard_section_apihealth: 'API health',
    activitydashboard_metric_2xxresponses: '2xx Responses',
    activitydashboard_metric_2xxresponses_tooltip: 'Successful HTTP responses (status 200-299) per refresh period',
    activitydashboard_metric_errors: 'Errors',
    activitydashboard_metric_errors_tooltip: 'Non-2xx HTTP responses (4xx client errors, 5xx server errors) per refresh period',
    activitydashboard_metric_avgresponse: 'Avg Response',
    activitydashboard_metric_avgresponse_tooltip: 'Average API response time in milliseconds across all requests in the time window',
    activitydashboard_section_processing: 'Processing',
    activitydashboard_metric_creditsused: 'Credits Used',
    activitydashboard_metric_creditsused_tooltip: 'Processing credits consumed during the current time window',
    activitydashboard_metric_jobsqueued: 'Jobs Queued',
    activitydashboard_metric_jobsqueued_tooltip: 'Background jobs currently queued or running (includes content extraction, transcription, etc.)',
    activitydashboard_metric_jobsdone: 'Jobs Done',
    activitydashboard_metric_jobsdone_tooltip: 'Background jobs completed successfully in the current time window',
    activitydashboard_metric_jobsfailed: 'Jobs Failed',
    activitydashboard_metric_jobsfailed_tooltip: 'Background jobs that failed in the current time window',
    activitydashboard_metric_avgjobtime: 'Avg Job Time',
    activitydashboard_metric_avgjobtime_tooltip: 'Average processing time for completed jobs in the time window',
    activitydashboard_section_security: 'Security',
    activitydashboard_metric_failedlogins: 'Failed Logins',
    activitydashboard_metric_failedlogins_tooltip: 'Authentication attempts that were rejected (wrong password, unknown user, etc.)',
    activitydashboard_metric_lockouts: 'Lockouts',
    activitydashboard_metric_lockouts_tooltip: 'Accounts locked due to too many failed login attempts',
    activitydashboard_metric_mfafailures: 'MFA Failures',
    activitydashboard_metric_mfafailures_tooltip: 'Multi-factor authentication challenges that failed (wrong code, expired, etc.)',
    activitydashboard_metric_impersonations: 'Impersonations',
    activitydashboard_metric_impersonations_tooltip: 'Active admin impersonation sessions (admins logged in as another user)',
    activitydashboard_metric_password: 'Password',
    activitydashboard_metric_password_tooltip: 'Users who logged in with username and password in the time window',
    activitydashboard_metric_passkey: 'Passkey',
    activitydashboard_metric_passkey_tooltip: 'Users who logged in with a passkey/security key (WebAuthn) in the time window',
    activitydashboard_metric_ssooauth: 'SSO/OAuth',
    activitydashboard_metric_ssooauth_tooltip: 'Users who logged in via SSO, OAuth, or federated identity providers',
    activitydashboard_section_databilling: 'Data & billing',
    activitydashboard_refreshbilling_tooltip: 'Refresh billing data',
    activitydashboard_metric_creditsremaining: 'Credits Remaining',
    activitydashboard_metric_creditsremaining_tooltip: 'Total unused processing credits across all active quota bundles',
    activitydashboard_metric_pendingresources: 'Pending Resources',
    activitydashboard_metric_pendingresources_tooltip: 'Resources with jobs queued or currently being processed',
    activitydashboard_metric_warmrecords: 'Warm Records',
    activitydashboard_metric_warmrecords_tooltip: 'Activity log records in the warm retention tier (older data with reduced indexes)',
    activitydashboard_metric_monthlysubs: 'Monthly Subs',
    activitydashboard_metric_monthlysubs_tooltip: 'Active subscriptions on a monthly billing cycle',
    activitydashboard_metric_annualsubs: 'Annual Subs',
    activitydashboard_metric_annualsubs_tooltip: 'Active subscriptions on an annual billing cycle',
    activitydashboard_metric_trialsubs: 'Trial Subs',
    activitydashboard_metric_trialsubs_tooltip: 'Subscriptions currently in a free trial period',
    activitydashboard_metric_renewing30d: 'Renewing (30d)',
    activitydashboard_metric_renewing30d_tooltip: 'Active subscriptions set to auto-renew within the next 30 days',
    activitydashboard_metric_canceling30d: 'Canceling (30d)',
    activitydashboard_metric_canceling30d_tooltip: 'Subscriptions marked for cancellation at the end of their current billing period within 30 days',
    activitydashboard_truncation_warning: 'Results limited to most recent 500 activities. Try a shorter time window.',
    activitydashboard_activesessions_heading: 'Active Sessions',
    activitydashboard_filter_allcustomers: 'All Customers',
    activitydashboard_filter_alltypes: 'All Types',
    activitydashboard_filter_launchtoken: 'Launch Token',
    activitydashboard_filter_systemadmin: 'System Admin',
    activitydashboard_filter_customeradmin: 'Customer Admin',
    activitydashboard_filter_tenantadmin: 'Workspace Admin',
    activitydashboard_filter_tenantuser: 'Tenant User',
    activitydashboard_timeago_never: 'never',
    activitydashboard_timeago_seconds: '{count}s ago',
    activitydashboard_timeago_minutes: '{count}m ago',
    activitydashboard_timeago_hours: '{count}h ago',
    activitydashboard_timeago_days: '{count}d ago',
    activitydashboard_error_loadfailed: 'Failed to load activity data',

    // Admin - Daily Usage Tab
    dailyusage_7days: '7 days',
    dailyusage_14days: '14 days',
    dailyusage_30days: '30 days',
    dailyusage_60days: '60 days',
    dailyusage_90days: '90 days',
    dailyusage_period: 'Time period',
    dailyusage_exportcsv: 'Export daily usage as CSV',
    dailyusage_loading: 'Loading daily usage…',
    dailyusage_dailyrequestvolume: 'Daily Request Volume',
    dailyusage_allcustomers: 'All Customers',
    dailyusage_total: 'Total',
    dailyusage_session: 'Session',
    dailyusage_api: 'API',
    dailyusage_launch: 'Launch',
    dailyusage_apitoken: 'API Token',
    dailyusage_launchtoken: 'Launch Token',
    dailyusage_clicktoview: 'Click to view requests',
    dailyusage_drilldown_hint: 'Click a day in the chart below to see individual request details for that day.',
    dailyusage_totalrequests: 'Total Requests',
    dailyusage_usagebycustomer: 'Usage by Customer',
    dailyusage_searchcustomers: 'Search customers…',
    dailyusage_exportcustomerscsv: 'Export all customers as CSV',
    dailyusage_customer: 'Customer',
    dailyusage_trend: 'Trend',
    dailyusage_nocustomerdata: 'No customer usage data for this period',
    dailyusage_alltypes: 'All types',
    dailyusage_type_apicall: 'API Call',
    dailyusage_type_pageview: 'Page View',
    dailyusage_type_resourceaccess: 'Resource Access',
    dailyusage_type_playerlaunch: 'Player Launch',
    dailyusage_type_search: 'Search',
    dailyusage_type_login: 'Login',
    dailyusage_type_logout: 'Logout',
    dailyusage_type_export: 'Export',
    dailyusage_allauthmethods: 'All auth methods',
    dailyusage_allstatuses: 'All statuses',
    dailyusage_status_success: 'Success (1xx–3xx)',
    dailyusage_status_clienterror: 'Client error (4xx)',
    dailyusage_status_servererror: 'Server error (5xx)',
    dailyusage_clearfilters: 'Clear filters',
    dailyusage_loadingrequests: 'Loading requests…',
    dailyusage_timeutc: 'Time (UTC)',
    dailyusage_authmethod: 'Auth Method',
    dailyusage_user: 'User',
    dailyusage_target: 'Target',
    dailyusage_timems: 'Time (ms)',
    dailyusage_loadingdetails: 'Loading details…',
    dailyusage_detail_sessionid: 'Session ID',
    dailyusage_detail_useruuid: 'User UUID',
    dailyusage_detail_usertype: 'User Type',
    dailyusage_detail_ipaddress: 'IP Address',
    dailyusage_detail_useragent: 'User Agent',
    dailyusage_detail_requestid: 'Request ID',
    dailyusage_detail_enduser: 'End User',
    dailyusage_detail_statuscode: 'Status Code',
    dailyusage_detail_responsetime: 'Response Time',
    dailyusage_detail_retentiontier: 'Retention Tier',
    dailyusage_detail_activitydata: 'Activity Data',
    dailyusage_norequests: 'No requests recorded on this date',
    dailyusage_hoursutc: 'Hours (UTC)',

    // Admin - Session Table
    sessiontable_customer: 'Customer',
    sessiontable_user: 'User',
    sessiontable_auth: 'Auth',
    sessiontable_activitybreakdown: 'Activity Breakdown',
    sessiontable_duration: 'Duration',
    sessiontable_lastactive: 'Last Active',
    sessiontable_unknown: 'Unknown',
    sessiontable_noactivesessions: 'No active sessions',
    sessiontable_login: 'login',
    sessiontable_tooltip_apicalls: 'API calls',
    sessiontable_tooltip_resourceviews: 'Resource views',
    sessiontable_tooltip_playerlaunches: 'Player launches',
    sessiontable_tooltip_searches: 'Searches',
    sessiontable_tooltip_login: 'Login',

    // Admin - Session Timeline Modal
    sessiontimeline_title: 'Session Timeline',
    sessiontimeline_unknownuser: 'Unknown User',
    sessiontimeline_viewuserdetails: 'View user details',
    sessiontimeline_viewuser: 'View User',
    sessiontimeline_activities: 'activities',
    sessiontimeline_sessionstart: 'Session Start',
    sessiontimeline_lastactivity: 'Last Activity',
    sessiontimeline_sessionduration: 'Session Duration',
    sessiontimeline_apicalls: 'API Calls',
    sessiontimeline_ip: 'IP',
    sessiontimeline_browser: 'Browser',
    sessiontimeline_trace: 'Trace',
    sessiontimeline_session: 'Session',
    sessiontimeline_lmsuser: 'LMS User',
    sessiontimeline_noactivities: 'No activities recorded for this session',
    sessiontimeline_lastupdated: 'Last updated',

    // Admin - Activity Feed
    activityfeed_bookmarked: 'bookmarked',
    activityfeed_norecentactivities: 'No recent activities',
    activityfeed_loggedin: 'logged in',
    activityfeed_loggedinvia: 'logged in via {method}',
    activityfeed_loggedout: 'logged out',
    activityfeed_searched: 'searched "{term}"',
    activityfeed_exporteddata: 'exported data',
    activityfeed_launched: 'launched {resource}',
    activityfeed_launchedresource: 'launched a resource',
    activityfeed_generatedtokenfor: 'generated token for {resource}',
    activityfeed_generatedtoken: 'generated a launch token',
    activityfeed_accessed: 'accessed {resource}',
    activityfeed_accessedresource: 'accessed a resource',
    activityfeed_viewed: 'viewed {target}',
    activityfeed_apage: 'a page',
    activityfeed_called: 'called {target}',
    activityfeed_loginmethod_password: 'password',
    activityfeed_loginmethod_passkey: 'passkey',
    activityfeed_loginmethod_oauth: 'OAuth',
    activityfeed_loginmethod_sso: 'SSO',
    activityfeed_loginmethod_federated: 'federated',
    activityfeed_loginmethod_api_token: 'API token',
    activityfeed_loginmethod_impersonation: 'impersonation',
    activityfeed_loginmethod_lms_launch: 'LMS launch',
    activityfeed_auth_session: 'session',
    activityfeed_auth_api_token: 'API',
    activityfeed_auth_launch_token: 'launch',
    activityfeed_source_launch_token: 'Launch token',
    activityfeed_source_search: 'from search',
    activityfeed_source_resource_detail: 'from details',
    activityfeed_source_direct: 'direct',
    activityfeed_timeago_seconds: '{count}s ago',
    activityfeed_timeago_minutes: '{count}m ago',
    activityfeed_timeago_hours: '{count}h ago',
    activityfeed_timeago_days: '{count}d ago',

    // Admin - Activity Histogram
    activityhistogram_fullwindow: 'Full Window',
    activityhistogram_totalrequests: 'total requests',
    activityhistogram_loadfailed: 'Failed to load activity histogram',
    activityhistogram_session: 'Session',
    activityhistogram_apitoken: 'API Token',
    activityhistogram_launchtoken: 'Launch Token',
    activityhistogram_total: 'Total',
    activityhistogram_clicktodrilldown: 'Click to drill down',
    activityhistogram_noactivity: 'No activity in this time window',
    activityhistogram_title_overview: 'API Usage Over Time',
    activityhistogram_title_hour: 'Activity in Selected Hour',
    activityhistogram_title_5min: 'Activity in Selected 5 Minutes',
    activityhistogram_subtitle_perminute: 'Per-minute granularity',
    activityhistogram_subtitle_drilldown: 'Requests by token type — click a bar to drill down',
    activityhistogram_subtitle_5min: '5-minute intervals — click to see per-minute',

    // Admin - Activity Timeline Item
    activitytimeline_path: 'Path',
    activitytimeline_method: 'Method',
    activitytimeline_response: 'Response',
    activitytimeline_auth: 'Auth',
    activitytimeline_time: 'Time',
    activitytimeline_offset: 'Offset',
    activitytimeline_collapse: 'Collapse',
    activitytimeline_expanddetails: 'Expand Details',
    activitytimeline_earliest: 'earliest',
    activitytimeline_identical: 'identical',
    activitytimeline_launched: 'Launched',
    activitytimeline_generatedtoken: 'Generated launch token',
    activitytimeline_accessedresource: 'Accessed resource',
    activitytimeline_searched: 'Searched',
    activitytimeline_exporteddata: 'Exported data',
    activitytimeline_loggedin: 'Logged in',
    activitytimeline_loggedout: 'Logged out',
    activitytimeline_viewedpage: 'Viewed page',

    // Admin - Identity Providers
    idp_title: 'Identity Providers',
    idp_subtitle: 'Configure SSO, OAuth, and SAML authentication',
    idp_addprovider: 'Add Provider',
    idp_editprovider: 'Edit Provider',
    idp_addidentityprovider: 'Add Identity Provider',
    idp_filter_alltypes: 'All Types',
    idp_filter_cognito: 'Cognito Native',
    idp_filter_google: 'Google OAuth',
    idp_filter_microsoft: 'Microsoft OAuth',
    idp_filter_github: 'GitHub OAuth',
    idp_filter_genericoauth: 'Generic OAuth/OIDC',
    idp_filter_okta: 'Okta SAML',
    idp_filter_azuread: 'Azure AD SAML',
    idp_filter_genericsaml: 'Generic SAML',
    idp_totalproviders: 'Total Providers',
    idp_oauthoidc: 'OAuth/OIDC',
    idp_saml: 'SAML',
    idp_provider: 'Provider',
    idp_scope: 'Scope',
    idp_default: 'Default',
    idp_loadingproviders: 'Loading providers...',
    idp_emptystate: 'No identity providers found. Click "Add Provider" to configure SSO.',
    idp_scope_slice: 'Workspace',
    idp_scope_customerwide: 'Customer-wide',
    idp_testconnection: 'Test connection',
    idp_disable: 'Disable',
    idp_enable: 'Enable',
    idp_providertype: 'Provider Type',
    idp_typecannotchange: 'Type cannot be changed after creation',
    idp_identity: 'Identity',
    idp_displayname: 'Display Name',
    idp_displayname_placeholder: 'e.g. Corporate SSO',
    idp_internalidentifier: 'Internal Identifier',
    idp_internalidentifier_placeholder: 'auto-generated',
    idp_internalidentifier_help: 'Used in URLs and API references. Auto-generated from display name.',
    idp_description_placeholder: 'Brief description of this provider\'s purpose and who should use it',
    idp_scopeoptions: 'Scope & Options',
    idp_scope_customerwide_option: 'Customer-wide',
    idp_scope_slicespecific: 'Workspace-specific',
    idp_sliceid: 'Workspace',
    idp_sliceid_placeholder: 'Enter workspace ID',
    idp_sliceid_pick: 'Select a workspace…',
    idp_defaultprovider: 'Default Provider',
    idp_defaultprovider_help: 'Shown as the primary sign-in option',
    idp_defaultprovider_replace_warning: 'Saving will replace {name} as the default — only one provider can hold this flag per customer.',
    idp_oauth_title: 'OAuth / OIDC Configuration',
    idp_oauth_subtitle: 'Configure the OAuth 2.0 or OIDC connection parameters for this provider',
    idp_oauth_clientid: 'Client ID',
    idp_oauth_clientid_placeholder: 'OAuth client ID',
    idp_oauth_clientsecret: 'Client Secret',
    idp_oauth_clientsecret_placeholder: 'OAuth client secret',
    idp_oauth_discovery: 'Discovery & Endpoints',
    idp_oauth_issuerurl: 'Issuer / Discovery URL',
    idp_oauth_authorizationendpoint: 'Authorization Endpoint',
    idp_oauth_tokenendpoint: 'Token Endpoint',
    idp_oauth_userinfoendpoint: 'UserInfo Endpoint',
    idp_oauth_jwksuri: 'JWKS URI',
    idp_oauth_jwksuri_help: 'Optional if Issuer URL is set — it will be auto-discovered. Only needed when the provider does not support OIDC discovery.',
    idp_oauth_scopes: 'Scopes',
    idp_oauth_scopes_help: 'Space-separated list of OAuth scopes to request',
    idp_oauth_issuerurl_help: 'The base URL of the identity provider. Sometimes called "Discovery URL", "OpenID Configuration URL", or "OIDC Issuer". If set, all endpoints below (authorize, token, userinfo, JWKS) are auto-discovered from the provider\'s .well-known/openid-configuration document.',
    idp_oauth_endpoints_optional: 'Issuer URL is set — the endpoints below are auto-discovered and only need to be filled in if you want to override them.',
    idp_oauth_openid_required: 'The "openid" scope must be included here AND enabled in the external provider\'s allowed scopes.',
    idp_validate_clientid_required: 'Client ID is required.',
    idp_validate_clientsecret_required: 'Client Secret is required.',
    idp_validate_issuer_or_auth_required: 'Issuer URL or Authorization Endpoint is required for generic OAuth.',
    idp_validate_openid_scope_required: 'The "openid" scope is required. Cognito needs it to receive an id_token from the provider. Make sure "openid" is also enabled in the external provider\'s allowed scopes.',
    idp_oauth_mapping_note: 'OIDC claim names are standardized. The defaults (email, given_name, family_name, name) work for most providers. Only change these if your provider uses nonstandard claim names.',
    idp_saml_title: 'SAML Configuration',
    idp_saml_subtitle: 'Configure the SAML 2.0 connection to the external identity provider',
    idp_saml_tab_metadataurl: 'Metadata URL',
    idp_saml_tab_metadataxml: 'Metadata XML',
    idp_saml_tab_manualentry: 'Manual Entry',
    idp_saml_idpmetadataurl: 'IdP Metadata URL',
    idp_saml_fetch: 'Fetch',
    idp_saml_metadataurl_help: 'The IdP\'s metadata XML endpoint. We\'ll periodically re-fetch to stay in sync.',
    idp_saml_idpmetadataxml: 'IdP Metadata XML',
    idp_saml_metadataxml_placeholder: 'Paste the IdP metadata XML here...',
    idp_saml_metadataxml_help: 'Paste the full XML document from your IdP.',
    idp_saml_parsexml: 'Parse XML',
    idp_saml_endpointscertificate: 'Endpoints & Certificate',
    idp_saml_entityid: 'Entity ID',
    idp_saml_entityid_help: 'The IdP\'s unique identifier (entityID from metadata).',
    idp_saml_ssourl: 'SSO URL',
    idp_saml_ssourl_help: 'The URL where sign-in requests are sent (SingleSignOnService).',
    idp_saml_slourl: 'SLO URL (Single Logout)',
    idp_saml_slourl_help: 'Optional. When a user logs out, we send a logout request here.',
    idp_saml_certificate: 'X.509 Signing Certificate',
    idp_saml_certificate_help: 'The public certificate the IdP uses to sign assertions.',
    idp_saml_nameidformat: 'NameID Format',
    idp_saml_nameid_email: 'Email Address',
    idp_saml_nameid_persistent: 'Persistent',
    idp_saml_nameid_transient: 'Transient',
    idp_saml_nameid_unspecified: 'Unspecified',
    idp_saml_nameidformat_help: 'How the IdP identifies the user in assertions.',
    idp_mapping_title: 'Attribute Mapping',
    idp_mapping_subtitle: 'Map attributes from the identity provider to MetaLark user fields',
    idp_mapping_note_title: 'Confirm these mappings with your identity provider',
    idp_mapping_note_body: 'The defaults below rarely match a real SAML deployment out of the box. Ask your IdP administrator which attribute names the provider actually emits and update each row before saving — a wrong source attribute silently fails authentication at login.',
    idp_mapping_metalarkfield: 'MetaLark Field',
    idp_mapping_idpattribute: 'IdP Attribute',
    idp_mapping_field_email: 'Email',
    idp_mapping_field_firstname: 'First Name',
    idp_mapping_field_lastname: 'Last Name',
    idp_mapping_field_fullname: 'Full Name',
    idp_mapping_field_username: 'Username',
    idp_mapping_field_avatarurl: 'Avatar URL',
    idp_mapping_field_tenantid: 'Tenant ID',
    idp_mapping_addmapping: 'Add Mapping',
    idp_mapping_preset: 'Preset',
    idp_mapping_preset_custom: 'Custom',
    idp_mapping_preset_hint: 'Pre-fills common attribute names. You can edit values after applying.',
    idp_mapping_placeholder: 'e.g. urn:oid:0.9.2342.19200300.100.1.3',
    idp_oauth_redirecturl: 'Redirect URL (give this to your provider)',
    idp_oauth_redirecturl_hint: 'Enter this URL in the external provider as the redirect/callback URL. It is where the provider sends the user after authentication.',
    idp_oauthguide_title: 'OAuth Setup Guide',
    idp_oauthguide_subtitle: 'Follow these steps to connect an external OAuth or OIDC provider.',
    idp_oauthguide_step1_title: 'Create an application in your provider',
    idp_oauthguide_step1_desc: 'In the external provider\'s admin console (Clerk, Auth0, Okta, etc.), create a new OAuth/OIDC application. When asked for a redirect URL, use the one shown above.',
    idp_oauthguide_step2_title: 'Copy the credentials',
    idp_oauthguide_step2_desc: 'The provider will give you a Client ID and Client Secret. Enter them in the form on the left. Also ensure the "openid" scope is enabled in the provider\'s allowed scopes — it is required for the OIDC flow.',
    idp_oauthguide_step3_title: 'Configure endpoints (Generic OAuth only)',
    idp_oauthguide_step3_desc: 'For pre-built providers (Google, Microsoft, GitHub), endpoints are configured automatically. For Generic OAuth/OIDC, enter the Issuer URL or individual endpoints from your provider\'s documentation.',
    idp_oauthguide_step4_title: 'Test the connection',
    idp_oauthguide_step4_desc: 'Save the provider, then use the Test Authorization feature to verify the full sign-in flow works end-to-end.',
    idp_jwtbearer_title: 'JWT Bearer Configuration',
    idp_jwtbearer_subtitle: 'Accept JWTs issued by your identity provider and exchange them for a MetaLark session. No redirect \u2014 the token is validated directly against your IdP\u2019s JWKS.',
    idp_jwtbearer_issuer: 'Issuer (iss claim)',
    idp_jwtbearer_issuer_help: 'The exact `iss` value your IdP writes into its tokens. Auth0: https://{tenant}.auth0.com/. Okta: your authorization server issuer. Azure AD: https://login.microsoftonline.com/{tenantId}/v2.0. Must match byte-for-byte.',
    idp_jwtbearer_jwksuri: 'JWKS URL',
    idp_jwtbearer_jwksuri_help: 'The public-keys endpoint your IdP already publishes. Usually at https://{issuer}/.well-known/jwks.json or /oauth2/v1/keys. HTTPS required.',
    idp_jwtbearer_audience: 'Audience (aud claim)',
    idp_jwtbearer_audience_help: 'Configure your IdP to include MetaLark as an audience on tokens sent here. Pick a stable value (e.g. "metalark-{your-org}") and set it as both an API identifier in your IdP and here.',
    idp_jwtbearer_cachettl: 'JWKS cache TTL (seconds)',
    idp_jwtbearer_cachettl_help: 'How long MetaLark caches your IdP\u2019s public keys between refetches. Default 600. Keys are always refetched on `kid` miss, so key rotation works without any action here.',
    idp_jwtbearer_claims_title: 'Claim Mapping',
    idp_jwtbearer_claims_help: 'Which claim on the incoming JWT maps to each MetaLark user field. The defaults match standard OIDC \u2014 leave them blank unless your IdP uses non-standard claim names.',
    idp_jwtbearer_claim_email: 'Email claim',
    idp_jwtbearer_claim_externalid: 'External ID claim',
    idp_jwtbearer_claim_displayname: 'Display name claim',
    idp_jwtbearer_origins_title: 'Allowed Browser Origins',
    idp_jwtbearer_origins_help: 'Browser origins permitted to POST to /api/v1/auth/jwt-exchange for this provider. CORS will echo back a matching origin with credentials allowed so the session cookie can be set.',
    idp_jwtbearer_origins_hint: 'One per line. Bare origin only (scheme + host + optional port). HTTPS required except for localhost.',
    idp_jwtbearer_mode_title: 'Identity Assertion Mode',
    idp_jwtbearer_mode_verified_title: 'Verified Domain (default)',
    idp_jwtbearer_mode_verified_desc: 'Email is the identity key across all providers. Requires a verified-domain allowlist for JIT. Use this when your users sign in with email addresses on domains your organization owns.',
    idp_jwtbearer_mode_trusted_title: 'Trusted IdP',
    idp_jwtbearer_mode_trusted_desc: 'Your IdP is trusted to assert identities on any email domain. Users are keyed by (provider, external ID). Use this when your application authenticates end users whose email domains you do not control \u2014 for example, customers of a product you build on top of MetaLark.',
    idp_jwtbearer_mode_trusted_warning: 'Trusted-IdP mode lets this provider create MetaLark user accounts for any email address it asserts. Setup requires an identity-attestation agreement with MetaLark \u2014 contact your account team before enabling this.',
    idp_jwtbearer_mode_trusted_restricted: 'Trusted-IdP mode is managed by MetaLark staff. Contact your account team to request this configuration.',
    idp_jwtbearer_guide_title: 'JWT Bearer Setup',
    idp_jwtbearer_guide_subtitle: 'Connect your existing identity provider \u2014 or a custom identity system you operate \u2014 so its JWTs are accepted by MetaLark.',
    idp_jwtbearer_guide_step1_title: 'Locate your JWKS URL and issuer',
    idp_jwtbearer_guide_step1_desc: 'Commercial IdPs publish these already. Auth0: Settings \u2192 Endpoints \u2192 JSON Web Key Set URL. Okta: your authorization server \u2192 Metadata URI. Azure AD: app registration \u2192 Endpoints \u2192 OpenID Connect metadata. For a self-built system, expose a JWKS document over HTTPS listing your public keys \u2014 only asymmetric keys (RSA, EC, EdDSA) are accepted.',
    idp_jwtbearer_guide_step2_title: 'Configure the audience on your side',
    idp_jwtbearer_guide_step2_desc: 'In your IdP, register MetaLark as an API / audience so the tokens your application receives include the `aud` value you entered on the left. Keep token lifetimes short (around 10 minutes is typical).',
    idp_jwtbearer_guide_step3_title: 'Exchange the token for a MetaLark session',
    idp_jwtbearer_guide_step3_desc: 'From your application, POST the JWT to /api/v1/auth/jwt-exchange with Authorization: Bearer <token>. MetaLark verifies the signature against your JWKS, checks issuer and audience, and returns a session cookie for the user.',
    idp_guide_title: 'SAML Setup Guide',
    idp_guide_subtitle: 'Follow these steps to establish a trust relationship with the external IdP.',
    idp_guide_step1_title: 'Download our SP metadata',
    idp_guide_step1_desc: 'Give this to the external IdP administrator to set up the trust.',
    idp_guide_spmetadataurl: 'SP Metadata URL',
    idp_guide_copy: 'Copy',
    idp_guide_acsurl: 'ACS URL (Assertion Consumer Service)',
    idp_guide_acsurl_domain: 'ACS URL (Cognito Domain)',
    idp_guide_both_acs_hint: 'Both ACS URLs must be registered in your IdP. The SSO flow uses the Cognito domain URL.',
    idp_guide_entityid: 'Entity ID',
    idp_guide_downloadspmetadata: 'Download SP Metadata XML',
    idp_guide_loadspmetadata: 'Load SP Metadata',
    idp_guide_step2_title: 'Configure the external IdP',
    idp_guide_step2_desc: 'In the external IdP admin console, create a new SAML application using our SP metadata.',
    idp_guide_step3_title: 'Get the IdP metadata',
    idp_guide_step3_desc: 'Copy the IdP metadata URL or download the metadata XML file.',
    idp_guide_step4_title: 'Map attributes',
    idp_guide_step4_desc: 'Configure attribute mapping so user fields are correctly passed.',
    idp_guide_step5_title: 'Test the connection',
    idp_guide_step5_desc: 'Save the provider, then use the Test Authorization button to verify.',
    idp_createprovider: 'Create Provider',
    idp_detail_configuration: 'Configuration',
    idp_detail_type: 'Type:',
    idp_detail_status: 'Status:',
    idp_detail_default: 'Default:',
    idp_detail_scope: 'Scope:',
    idp_detail_tenantid: 'Tenant ID',
    idp_detail_metadata: 'Metadata',
    idp_detail_created: 'Created:',
    idp_detail_updated: 'Updated:',
    idp_detail_cognitoprovider: 'Cognito Provider:',
    idp_detail_record: 'Record',
    idp_detail_internal: 'Internal',
    idp_detail_hidden: 'Hidden',
    idp_detail_viewxml: 'View XML',
    idp_detail_providerid: 'Provider ID',
    idp_detail_cognitoname: 'Cognito Name',
    idp_detail_clientid: 'Client ID',
    idp_detail_clientsecret: 'Client Secret',
    idp_detail_scopes: 'Scopes',
    idp_detail_issuerurl: 'Issuer URL',
    idp_detail_authendpoint: 'Authorization Endpoint',
    idp_detail_tokenendpoint: 'Token Endpoint',
    idp_detail_userinfoendpoint: 'UserInfo Endpoint',
    idp_detail_jwksuri: 'JWKS URI',
    idp_detail_idpentityid: 'IdP Entity ID',
    idp_detail_ssourl: 'SSO URL',
    idp_detail_slourl: 'SLO URL',
    idp_detail_metadataurl: 'Metadata URL',
    idp_detail_nameidformat: 'NameID Format',
    idp_detail_signingcert: 'Signing Certificate',
    idp_detail_cert_provided: 'Provided',
    idp_detail_cert_notprovided: 'Not provided',
    idp_detail_idpmetadataxml: 'IdP Metadata XML',
    idp_detail_spvalues: 'Our SP Values (for the external IdP)',
    idp_detail_download: 'Download',
    idp_detail_oauthconfig: 'OAuth / OIDC Configuration',
    idp_detail_oauth_subtitle: 'Redirects users to your OAuth / OIDC provider, exchanges the authorization code for tokens, and provisions them into MetaLark.',
    idp_detail_oauth_section_client: 'Client Credentials',
    idp_detail_oauth_section_endpoints: 'Provider Endpoints',
    idp_detail_oauth_section_redirect: 'Redirect URL (configure in your IdP)',
    idp_detail_samlconfig: 'SAML Configuration',
    idp_detail_saml_subtitle: 'Accepts SAML assertions from your identity provider via a browser redirect and provisions the authenticated user into MetaLark.',
    idp_detail_saml_section_idp: 'IdP Configuration',
    idp_detail_jwtconfig: 'JWT Bearer Configuration',
    idp_detail_jwt_subtitle: 'Accepts JWTs signed by your identity provider and exchanges them for a MetaLark session.',
    idp_detail_jwt_section_core: 'Core Configuration',
    idp_detail_jwt_seconds: 'seconds',
    idp_detail_jwt_claim_source: 'Source',
    idp_detail_jwt_default: 'Default',
    idp_detail_jwt_custom: 'Custom',
    idp_detail_jwt_exchange_title: 'JSON Exchange',
    idp_detail_jwt_exchange_tag: 'Shared-domain',
    idp_detail_jwt_exchange_when: 'Use when your portal and MetaLark share a parent domain.',
    idp_detail_jwt_launch_title: 'Browser Launch',
    idp_detail_jwt_launch_tag: '3P-cookie safe',
    idp_detail_jwt_launch_when: 'Use when your portal and MetaLark are on unrelated domains.',
    idp_detail_jwt_issuer: 'Issuer (iss claim)',
    idp_detail_jwt_audience: 'Audience (aud claim)',
    idp_detail_jwt_cachettl: 'JWKS Cache TTL',
    idp_detail_jwt_mode_trusted_hint: 'This provider is trusted to assert identities on any email. Users are keyed by (provider, external ID).',
    idp_detail_jwt_mode_verified_hint: 'Email is the identity key across providers. Requires a verified-domain allowlist for JIT.',
    idp_detail_jwt_endpoints: 'Exchange Endpoints',
    idp_detail_jwt_exchange_url: 'JSON Exchange URL (background fetch from your portal)',
    idp_detail_jwt_exchange_flow: 'Your portal mints a signed JWT for the user and calls this endpoint with Authorization: Bearer <jwt> via fetch(…, { credentials: "include" }). MetaLark pulls the iss claim, locates this provider by matching issuer, verifies the signature against the configured JWKS, checks audience and expiry, and runs JIT provisioning if needed. On success MetaLark returns { success, userUuid, email, provisioned } and sets the session cookie on the response; your portal then redirects the browser to MetaLark and the user is signed in. Use this when your portal and MetaLark share a parent domain — the cookie is first-party. If the domains are unrelated, privacy-focused browsers (Brave, Safari ITP, Chrome with Tracking Protection) drop the cookie silently; use the browser launch URL below instead.',
    idp_detail_jwt_launch_url: 'Browser Launch URL (first-party redirect for 3P-cookie-blocking browsers)',
    idp_detail_jwt_launch_flow: 'Your portal renders an auto-submitting HTML form that POSTs the JWT as a form field named "token" directly to this URL in a top-level navigation. Because the browser is navigating to MetaLark\'s origin, cookies MetaLark sets are first-party from the browser\'s perspective — no 3P-cookie blockers apply. MetaLark validates the JWT exactly the same way as the JSON exchange (JWKS signature, issuer, audience, expiry, JIT provisioning), sets the session cookie, and 302-redirects to the returnTo path (validated as a same-origin relative path; defaults to "/"). Pass an optional returnTo form field to drop the user straight onto a specific MetaLark page. Use this flow when your portal and MetaLark are on unrelated domains — which is most customer integrations.',
    idp_detail_attrmapping: 'Attribute Mapping',
    idp_yes: 'Yes',
    idp_no: 'No',
    idp_testauthorization: 'Test Authorization',
    idp_test_title: 'Test',
    idp_test_subtitle: 'Perform a test authorization to verify configuration and inspect the provider\'s assertion/token response',
    idp_test_connectiontest: 'Connection Test',
    idp_test_connectiontest_desc: 'Tests that the provider endpoint is reachable and responds correctly.',
    idp_test_runconnectiontest: 'Run Connection Test',
    idp_test_connectionsuccess: 'Connection successful',
    idp_test_connectionfailed: 'Connection failed',
    idp_test_authorizationtest: 'Authorization Test',
    idp_test_authorizationtest_desc: 'Initiates a real authorization flow. A popup window will open to the IdP login page. After authentication, the assertion/token response will be displayed below.',
    idp_test_startauthtest: 'Start Authorization Test',
    idp_test_popupnotice: 'Opens a popup to the IdP. Complete the login to see the assertion.',
    idp_test_jwt_title: 'Validate a JWT',
    idp_test_jwt_desc: 'Paste a sample JWT your portal would mint for an end user. We validate it against this provider\'s JWKS + issuer + audience and show the parsed claims — no MetaLark session is created.',
    idp_test_jwt_paste_label: 'JWT (signed by your IdP)',
    idp_test_jwt_paste_hint: 'Header.Payload.Signature (three dot-separated segments). Sign with one of the configured JWKS keys.',
    idp_test_jwt_validate_btn: 'Validate Token',
    idp_test_jwt_result_success: 'Token is valid',
    idp_test_jwt_result_failed: 'Token validation failed',
    idp_test_jwt_result_valid: 'VALID',
    idp_test_jwt_mapped_title: 'Mapped Identity',
    idp_test_jwt_raw_claims: 'Decoded claims',
    idp_test_jwt_role_claim: 'Role claim',
    idp_test_samlassertion: 'SAML Assertion',
    idp_test_tokenresponse: 'Token Response',
    idp_test_copyjson: 'Copy JSON',
    idp_test_receivedattributes: 'Received Attributes',
    idp_test_attributename: 'Attribute Name',
    idp_test_value: 'Value',
    dailyusage_clicktoclear: 'Click to clear',
    dailyusage_clicktofilter: 'Click to filter',

    // Admin Impersonation Section
    admin_impersonation_activesessions: 'Your Active Impersonation Sessions',
    admin_impersonation_workspaceid: 'Workspace ID',
    admin_impersonation_started: 'Started',
    admin_impersonation_expires: 'Expires',
    admin_impersonation_remaining: 'Remaining',
    admin_impersonation_resume: 'Resume',
    admin_impersonation_end: 'End',
    admin_impersonation_customerworkspaces: 'Customer Workspaces',
    admin_impersonation_selectworkspace: 'Select a workspace to begin an impersonation session',
    admin_impersonation_allcustomers: 'All Customers',
    admin_impersonation_searchplaceholder: 'Search workspaces...',
    admin_impersonation_showingresults: 'Showing {range} of {total} workspaces',
    admin_impersonation_filteredbycustomer: '(filtered by customer)',
    admin_impersonation_enter: 'Enter',
    admin_impersonation_access: 'Access',
    admin_impersonation_noworkspaces: 'No workspaces found. Try adjusting your search or filters.',
    admin_impersonation_endsession: 'End Impersonation Session',
    admin_sessions_endsession: 'End Session',
    admin_impersonation_confirm_end: 'Are you sure you want to end the impersonation session for {workspace}? This will permanently terminate the session.',
    admin_impersonation_error_start: 'Failed to start impersonation',
    admin_impersonation_error_resume: 'Failed to resume session',
    admin_impersonation_error_end: 'Failed to end impersonation session. Please try again.',
    admin_impersonation_error_access: 'Failed to access workspace',

    // Slim active-impersonation card (shown on the SYSTEM_ADMIN dashboard
    // and on Customer 360's Content / Workspaces subtab). Kept in the
    // main locale because CUSTOMER_ADMIN also owns active sessions — the
    // system-admin chunk would never reach them.
    admin_dashboard_active_impersonations_title: 'Active Impersonations',
    admin_dashboard_active_sessions_title: 'Active Sessions',
    admin_dashboard_active_impersonations_empty: 'No active sessions.',
    admin_dashboard_active_impersonations_remaining: 'remaining',
    admin_dashboard_session_kind_impersonation: 'Impersonation',
    admin_dashboard_session_kind_workspace_access: 'Direct',

    // Dedicated modal that replaces the generic confirm-modal for
    // ending an impersonation. Pause keeps the session alive on the
    // server so it can be resumed from the Active Impersonations card.
    impersonation_end_modal_pause: 'Pause Session',
    impersonation_end_modal_pause_explainer: 'Pause returns you to your normal session. The impersonation stays active on the server and can be resumed from the Active Impersonations card. End terminates it permanently.',

    // Admin Identity Provider Section
    admin_idp_title: 'Identity Providers',
    admin_idp_addprovider: 'Add Provider',
    admin_idp_scope: 'Scope',
    admin_idp_default: 'Default',
    admin_idp_slicespecific: 'Slice-specific',
    admin_idp_customerwide: 'Customer-wide',
    admin_idp_enabled: 'Enabled',
    admin_idp_disabled: 'Disabled',
    admin_idp_testconnection: 'Test connection',
    admin_idp_disable: 'Disable',
    admin_idp_enable: 'Enable',
    admin_idp_noproviders: 'No identity providers configured. Add one to enable SSO authentication.',
    admin_idp_success_updated: 'Identity provider updated successfully',
    admin_idp_success_created: 'Identity provider created successfully',
    admin_idp_error_save: 'Failed to save identity provider',
    admin_idp_confirm_disable: 'Are you sure you want to disable {name}?',
    admin_idp_confirm_enable: 'Are you sure you want to enable {name}?',
    admin_idp_success_disabled: 'Identity provider disabled successfully',
    admin_idp_success_enabled: 'Identity provider enabled successfully',
    admin_idp_error_disable: 'Failed to disable identity provider',
    admin_idp_error_enable: 'Failed to enable identity provider',
    admin_idp_success_test: 'Connection test successful!',
    admin_idp_error_test: 'Connection test failed',
    admin_idp_error_testfailed: 'Failed to test identity provider',
    admin_idp_confirm_delete: 'Are you sure you want to delete {name}? This action cannot be undone.',
    admin_idp_delete_title: 'Delete Identity Provider',
    admin_idp_delete_description: 'You are about to permanently delete {name}.',
    admin_idp_delete_warning_title: 'This action cannot be undone.',
    admin_idp_delete_warning_body: 'Users who authenticate through this provider may lose access if no other enabled providers are available to them.',
    admin_idp_delete_confirm: 'Delete Provider',
    admin_idp_success_deleted: 'Identity provider deleted successfully',
    admin_idp_error_delete: 'Failed to delete identity provider',
    admin_idp_error_load: 'Failed to load identity providers',
    admin_idp_success_metadata: 'Metadata fetched and parsed',
    admin_idp_error_metadata: 'Failed to fetch metadata',
    admin_idp_error_popupblocked: 'Popup blocked. Please allow popups for this site.',

    // Admin - Identity Providers - JIT (Just-In-Time) provisioning (METALARK-730)
    idp_jit_title: 'Just-In-Time Provisioning',
    idp_jit_subtitle_verified_prefix: 'Automatically create user accounts the first time someone authenticates through this provider. Requires at least one',
    idp_jit_subtitle_verified_link: 'verified domain',
    idp_jit_subtitle_verified_suffix: '.',
    idp_jit_subtitle_trusted: 'Trusted-IdP mode: the identity provider is contractually trusted to assert arbitrary identities. Users are keyed by (provider, external id) \u2014 email is a display attribute and is not cross-referenced with other providers.',
    idp_jit_enable: 'Enable JIT provisioning',
    idp_jit_warning_verified: 'Users authenticating via this provider whose email matches a verified domain below AND who are not yet in MetaLark will be granted access. Existing MetaLark users must be invited manually. Every new user will confirm the organization they\u2019re joining before access is granted.',
    idp_jit_warning_trusted: 'Trusted-IdP users cannot set passwords or passkeys, and are bound to the default workspace only. Email addresses are marked \u201casserted\u201d (not verified). Revocation flows through the IdP, not MetaLark.',
    idp_jit_default_role: 'Default role for auto-provisioned users',
    idp_jit_default_role_placeholder: 'Select a role\u2026',
    idp_jit_default_workspace: 'Default workspace (required)',
    idp_jit_default_workspace_placeholder: 'Select a workspace\u2026',
    idp_jit_allowed_domains: 'Allowed email domains (required)',
    idp_jit_no_verified_domains: 'No verified domains available.',
    idp_jit_add_verify_domain_link: 'Add and verify a domain first.',
    idp_jit_required_trusted: 'Required when Trusted IdP is enabled.',
    idp_jit_error_no_verified_domain: 'JIT provisioning requires at least one verified domain. Add and verify a domain first.',
    idp_jit_default_workspace_scoped_hint: 'Workspace-scoped providers always provision into their own workspace.',
    idp_jit_error_default_role_required: 'Select a default role for auto-provisioned users.',
    idp_jit_error_default_workspace_required: 'Select a default workspace for auto-provisioned users.',
    idp_jit_error_allowlist_empty: 'Select at least one allowed email domain.',
    idp_detail_jit: 'JIT provisioning:',
    idp_detail_jit_default_role: 'JIT default role:',
    idp_detail_jit_default_workspace: 'JIT default workspace:',
    idp_detail_jit_allowed_domains: 'JIT allowed domains:',

    // Admin - Authentication Policy (session revocation)
    admin_authpolicy_revoke_title: 'Revoke User Sessions',
    admin_authpolicy_revoke_description: 'Force a user to re-authenticate by invalidating all their active sessions. The user can log back in immediately.',
    admin_authpolicy_revoke_user_label: 'User UUID',
    admin_authpolicy_revoke_user_placeholder: 'Enter user UUID to revoke sessions',
    admin_authpolicy_revoke_button: 'Revoke Sessions',
    admin_authpolicy_revoke_confirm_title: 'Confirm Session Revocation',
    admin_authpolicy_revoke_confirm_message: 'This will immediately invalidate all active sessions for user {userUuid}. They will need to log in again.',
    admin_authpolicy_revoke_success: 'All sessions have been revoked. The user will need to log in again.',
    admin_authpolicy_revoke_error: 'Failed to revoke sessions. Please try again.',

    // Admin EULA Section
    admin_eula_title: 'Subscription Agreement',
    admin_eula_currentversion: 'Current Subscription Agreement Version: {version}',
    admin_eula_effectivedate: 'Effective Date',
    admin_eula_content: 'Subscription Agreement Content',
    admin_eula_viewfull: 'View Full Subscription Agreement',
    admin_eula_updateinfo: 'To update the Subscription Agreement content or version, modify the api/controllers/EulaController.ts file. Changes require a backend deployment.',
    admin_eula_loading: 'Loading Subscription Agreement information...',
    admin_eula_error: 'Error Loading Subscription Agreement',
    admin_eula_version: 'Version',

    // Admin Users Page
    admin_users_title: 'User Management',
    admin_users_subtitle: 'Manage users, roles, and permissions',
    admin_users_createuser: 'Create User',
    admin_users_totalusers: 'Total Users',
    admin_users_incurrentview: 'In current view',
    admin_users_verifiedusers: 'Verified Users',
    admin_users_percentverified: '{percent}% verified',
    admin_users_mfaenabled: 'MFA Enabled',
    admin_users_percentofusers: '{percent}% of users',
    admin_users_currentpage: 'Current Page',
    admin_users_showingitems: 'Showing {count} items',
    admin_users_assignrole: 'Assign Role to Existing User',
    admin_users_createnewuser: 'Create New User with Role',
    admin_users_emailaddress: 'Email Address',
    admin_users_emailplaceholder: 'user@example.com',
    admin_users_searching: 'Searching for user...',
    admin_users_userexists: 'User already exists',
    admin_users_assignrolevisible: 'Assign them a role in your scope to make them visible.',
    admin_users_changeemail: 'Change Email',
    admin_users_firstname: 'First Name',
    admin_users_firstnameplaceholder: 'John',
    admin_users_lastname: 'Last Name',
    admin_users_lastnameplaceholder: 'Doe',
    admin_users_sendwelcomeemail: 'Send Cognito welcome email with temporary password',
    admin_users_generatetemppassword: 'Generate temporary password (will be shown after creation)',
    admin_users_emailverificationlink: 'User will receive an email verification link and must set their own password.',
    admin_users_roleassignment: 'Role Assignment (Required)',
    admin_users_selectrole: 'Select Role',
    admin_users_chooserole: 'Choose a role...',
    admin_users_customer: 'Customer',
    admin_users_autofilled: 'auto-filled',
    admin_users_selectcustomer: 'Select a customer...',
    admin_users_selectworkspace: 'Select a workspace...',
    admin_users_selectcustomerfirst: 'Select customer first...',
    admin_users_usercreatedsuccess: 'User created successfully!',
    admin_users_temporarypassword: 'Temporary Password',
    admin_users_savepassword: 'Save this password - it will not be shown again!',
    admin_users_assigningrole: 'Assigning Role...',
    admin_users_creatinguser: 'Creating User...',
    admin_users_assignrolebutton: 'Assign Role',
    admin_users_createuserbutton: 'Create User with Role',

    // Admin Role Names (user-friendly versions for forms)
    admin_role_tenantadmin: 'Workspace Admin',
    admin_role_tenantuser: 'Workspace User',
    admin_role_tenantviewer: 'Workspace Viewer',
    admin_role_resourcemanager: 'Resource Manager',
    admin_role_resourcecontributor: 'Resource Contributor',
    admin_role_customeradmin: 'Customer Admin',
    admin_role_systemadmin: 'System Admin',
    admin_role_partneradmin: 'Partner Admin',
    admin_role_servicerole: 'Service Role',

    // Saved Searches Component
    admin_users_quick_label: 'Quick Filters',
    admin_users_quick_clear: 'Clear Quick Filter',
    clearsearch: 'Clear Search',

    // User Filters Component
    user_filter_searchbyname: 'Search by name...',
    user_filter_searchbyemail: 'Search by email...',
    user_filter_useruuid: 'User UUID',
    user_filter_searchbyuuid: 'Search by UUID...',
    user_filter_allroles: 'All Roles',
    user_filter_searchplaceholder: 'Search by name, email, or UUID\u2026',
    user_filter_allcustomers: 'All Customers',
    user_filter_anystatus: 'Any Status',
    user_filter_noeula: 'No EULA',
    user_filter_system: 'System',
    user_filter_servicerole: 'Service Role',
    user_filter_customer: 'Customer',
    user_filter_tenant: 'Tenant',
    user_filter_tenantslice: 'Tenant/Slice',
    user_filter_resource: 'Resource',
    user_filter_resources: 'Resources',
    user_filter_emailstatus: 'Email Status',
    user_filter_mfastatus: 'MFA Status',
    user_filter_accountstatus: 'Account Status',
    user_filter_eulastatus: 'EULA Status',
    user_filter_allstatuses: 'All Statuses',
    user_filter_verified: 'Verified',
    user_filter_unverified: 'Unverified',
    user_filter_asserted: 'Asserted',
    user_table_idp_managed: 'IdP-managed',
    user_table_idp_managed_tooltip: 'This account is managed by a Trusted-IdP identity provider. Passwords, passkeys, and workspace role changes must flow through the IdP, not MetaLark.',
    user_filter_locked: 'Locked',
    user_filter_unlocked: 'Unlocked',
    user_filter_enabled: 'Enabled',
    user_filter_disabled: 'Disabled',
    user_filter_all: 'All',
    user_filter_withmfa: 'With MFA',
    user_filter_withoutmfa: 'Without MFA',
    user_filter_withpasskey: 'With Passkey',
    user_filter_withoutpasskey: 'Without Passkey',
    user_filter_accepted: 'Accepted',
    user_filter_notaccepted: 'Not Accepted',
    user_filter_lastloginfrom: 'Last Login From',
    user_filter_lastloginto: 'Last Login To',
    user_filter_searchcustomers: 'Search customers...',
    user_filter_sliceid: 'Slice ID',
    user_filter_filterbyslice: 'Filter by slice...',
    user_filter_activefilters: 'Active Filters',
    user_filter_clearfilters: 'Clear Filters',
    user_filter_account: 'Account',
    user_filter_loginfrom: 'Login From',
    user_filter_loginto: 'Login To',
    user_filter_slice: 'Slice',

    // User Table Component
    user_table_col_user: 'User',
    user_table_col_workspace_role: 'Customer / Workspace & Role',
    user_table_col_auth: 'Auth',
    user_table_roles: 'Roles',
    user_table_lastlogin: 'Last Login',
    user_table_uuid: 'UUID',
    user_table_noroles: 'No Roles',
    user_table_never: 'Never',
    user_table_mfa: 'MFA',
    user_table_passkey: 'Passkey',
    user_table_eula: 'EULA',
    user_table_noeula: 'No EULA',
    user_table_nousers: 'No users found matching your criteria.',

    // Export Button Component
    exporting: 'Exporting...',
    exportcsv: 'Export CSV',
    generatingcsv: 'Generating CSV...',
    export_jobfailed: 'The export could not be completed. Please try again.',
    export_jobtimeout: 'The export is taking longer than expected. Please try again.',
    export_users_error: 'Failed to export users',

    // User Detail Component
    user_detail_title: 'User Details',
    user_detail_profileinfo: 'Profile Information',
    user_detail_deleteuser: 'Delete User',
    user_detail_resendverification: 'Resend Verification Email',
    user_detail_userlogin: 'User Login',
    user_detail_roleassignments: 'Role Assignments',
    user_detail_manageroles: 'Manage Roles',
    user_detail_context: 'Context',
    user_detail_assigneddate: 'Assigned Date',
    user_detail_global: 'Global',
    user_detail_active: 'Active',
    // Pending-invitation states surfaced in the user-detail role table (METALARK-835)
    user_detail_invited: 'Invited',
    user_detail_invitation_expires: 'Expires',
    user_detail_invitation_expired: 'Expired',
    user_detail_noroles: 'No role assignments found for this user.',
    user_detail_mfa: 'Multi-Factor Authentication',
    user_detail_disablemfa: 'Disable MFA',
    user_detail_configured: 'Configured',
    user_detail_enabled: 'Enabled',
    user_detail_disabled: 'Disabled',
    user_detail_nomfa: 'MFA is not configured for this user.',
    user_detail_passkeys: 'Passkeys',
    user_detail_enternickname: 'Enter nickname',
    user_detail_revoked: 'Revoked',
    user_detail_created: 'Created',
    user_detail_lastused: 'Last used',
    user_detail_rename: 'Rename',
    user_detail_revoke: 'Revoke',
    user_detail_nopasskeys: 'No passkeys configured for this user.',
    user_detail_accountsecurity: 'Account Security',
    user_detail_revokesessions: 'Revoke Sessions',
    user_detail_unlockaccount: 'Unlock Account',
    user_detail_lockaccount: 'Lock Account',
    user_detail_accountislocked: 'Account is Locked',
    user_detail_locked: 'Locked',
    user_detail_reason: 'Reason',
    user_detail_lockedby: 'Locked by',
    user_detail_accountactive: 'Account is active and unlocked',
    user_detail_subagreement: 'Subscription Agreement',
    user_detail_resetsubagreement: 'Reset Subscription Agreement',
    user_detail_subagreementaccepted: 'Subscription Agreement Accepted',
    user_detail_version: 'Version',
    user_detail_accepted: 'Accepted',
    user_detail_subagreementnotaccepted: 'Subscription Agreement Not Accepted',
    user_detail_mustacceptsubagreement: 'User must accept the Subscription Agreement to activate their account.',
    user_detail_notificationprefs: 'Notification Preferences',
    user_detail_viewhistory: 'View History',
    user_detail_required: 'Required',
    user_detail_readonly: 'Read-Only',
    user_detail_editprefsinsettings: 'Edit preferences in the Settings tab',
    user_detail_lastupdated: 'Last updated',
    user_detail_changes: 'change(s)',
    user_detail_nopreferences: 'No notification preferences configured. Edit preferences in the Settings tab.',
    user_detail_metadata: 'Metadata',
    user_detail_createddate: 'Created Date',
    user_detail_emailverified: 'Email Verified',
    user_detail_mfastatus: 'MFA Status',
    user_detail_accountstatus: 'Account Status',
    user_detail_confirmmfadisable: 'Confirm MFA Disable',
    user_detail_confirmdisablemfafor: 'Are you sure you want to disable Multi-Factor Authentication for',
    user_detail_thisactionwill: 'This action will',
    user_detail_removeallmfa: 'Remove all MFA configurations for this user',
    user_detail_allowloginwithoutmfa: 'Allow the user to login without MFA verification',
    user_detail_reducesecurity: 'Reduce the security of this account',
    user_detail_usercanreenablemfa: 'The user will be able to re-enable MFA from their account settings if needed.',
    user_detail_disabling: 'Disabling...',
    user_detail_confirmaccountlock: 'Confirm Account Lock',
    user_detail_confirmlockfor: 'Are you sure you want to lock the account for',
    user_detail_preventlogin: 'Prevent the user from logging in with new sessions',
    user_detail_blockapirequests: 'Block all API requests from this user immediately',
    user_detail_requireadmin: 'Require admin intervention to unlock',
    user_detail_reasonforlocking: 'Reason for locking (required)',
    user_detail_enterreasonlock: 'Enter reason for locking this account...',
    user_detail_locking: 'Locking...',
    user_detail_confirmaccountunlock: 'Confirm Account Unlock',
    user_detail_confirmunlockfor: 'Are you sure you want to unlock the account for',
    user_detail_allowloginagain: 'This will allow the user to log in again.',
    user_detail_originallockreason: 'Original Lock Reason',
    user_detail_unlocking: 'Unlocking...',
    user_detail_confirmsessionrevoke: 'Confirm Session Revocation',
    user_detail_confirmrevokefor: 'Are you sure you want to revoke all sessions for',
    user_detail_forceloginalldevices: 'Force the user to log in again on all devices',
    user_detail_invalidatetokens: 'Invalidate all current JWT tokens',
    user_detail_effectimmediately: 'Take effect immediately',
    user_detail_notsameaslocking: 'This is NOT the same as locking the account',
    user_detail_canloginagainafter: 'The user can log in again immediately after re-authentication',
    user_detail_usefulforpermissions: 'Useful for applying permission changes or responding to security concerns',
    user_detail_lessdisruptive: 'Less disruptive than a full account lock',
    user_detail_reasonforrevoking: 'Reason for session revocation (optional)',
    user_detail_enterreasonrevoke: 'Enter reason for revoking sessions (optional)...',
    user_detail_revoking: 'Revoking...',
    user_detail_revokeallsessions: 'Revoke All Sessions',
    user_detail_confirmpasskeyrevoke: 'Confirm Passkey Revocation',
    user_detail_confirmrevokepasskey: 'Are you sure you want to revoke the passkey {nickname} for {email}?',
    user_detail_permanentlyrevoke: 'Permanently revoke this passkey',
    user_detail_preventusingpasskey: 'Prevent the user from using it to log in',
    user_detail_cannotbeundone: 'Cannot be undone',
    user_detail_reasonforrevocation: 'Reason for revocation (required)',
    user_detail_enterreasonrevokepasskey: 'Enter reason for revoking this passkey...',
    user_detail_revokepasskey: 'Revoke Passkey',
    user_detail_confirmeulareset: 'Confirm EULA Reset',
    user_detail_confirmreseteulafor: 'Are you sure you want to reset the EULA acceptance for',
    user_detail_markeulanotaccepted: 'Mark the EULA as not accepted',
    user_detail_clearacceptancedate: 'Clear the acceptance date and version',
    user_detail_changestatuspendingeula: "Change account status to 'pending_eula'",
    user_detail_requireaccepteulaagain: 'Require the user to accept the EULA again before using the system',
    user_detail_usefulfortesting: 'This is useful for testing purposes or when requiring users to re-accept updated terms.',
    user_detail_resetting: 'Resetting...',
    user_detail_reseteula: 'Reset EULA',
    user_detail_confirmuserdeletion: 'Confirm User Deletion',
    user_detail_confirmdeleteuserfor: 'Are you sure you want to permanently delete the user',
    user_detail_actionpermanent: 'This action is PERMANENT and will',
    user_detail_deleteuserfromcognito: 'Delete the user from Cognito',
    user_detail_removeallroles: 'Remove all role assignments',
    user_detail_deleteallmfaconfigs: 'Delete all MFA configurations',
    user_detail_revokeallpasskeys: 'Revoke all passkeys',
    user_detail_preserveauditlogs: 'Preserve audit logs (orphaned records for compliance)',
    user_detail_toconfirmtypeuuid: "To confirm, type the user's UUID",
    user_detail_pasteuuid: 'Paste or type the UUID here...',
    user_detail_deleting: 'Deleting...',
    user_detail_deleteuserpermanently: 'Delete User Permanently',
    user_detail_delete_customer_admin_note: 'This will revoke every role this user holds in your customer. If they have role assignments in other customers, those remain and their MetaLark account stays — but the list above may not show every customer they belong to. If this customer is their only access on MetaLark, their account will be deleted entirely.',
    user_detail_remove_from_customer: 'Remove from customer',
    user_detail_disconnect_title: 'Remove user from this customer',
    user_detail_disconnect_body_prefix: 'You\'re about to revoke every role ',
    user_detail_disconnect_body_suffix: ' holds in this customer.',
    user_detail_disconnect_bullet_revoke: 'Every role this user holds in this customer will be revoked.',
    user_detail_disconnect_bullet_other_customers: 'Role assignments in other customers (if any) are not affected. Customer admins may not see every customer the user belongs to.',
    user_detail_disconnect_bullet_no_cognito: 'If this customer is their only access on MetaLark, the user\'s account will be deleted entirely. If they have roles in other customers, the account stays and only this customer\'s roles are revoked.',
    user_detail_disconnect_confirm: 'Remove from this customer',
    user_detail_disconnect_in_progress: 'Removing...',
    user_detail_prefchangehistory: 'Preference Change History',
    user_detail_showing: 'Showing',
    user_detail_prefchangesfor: 'preference change(s) for',
    user_detail_noprefchanges: 'No preference changes recorded yet.',

    // Role Management Component
    role_mgmt_currentroles: 'Current Role Assignments',
    role_mgmt_assigned: 'assigned',
    role_mgmt_workspace: 'Workspace',
    role_mgmt_globalscope: 'Global scope',
    role_mgmt_editroleassignment: 'Edit role assignment',
    role_mgmt_removeroleassignment: 'Remove this role assignment',
    role_mgmt_assignnewrole: 'Assign New Role',
    role_mgmt_idp_managed_banner: 'This user is managed by a Trusted-IdP identity provider. Their workspace binding is controlled by the provider — role assignments cannot be added, edited, or removed from here.',
    role_mgmt_selectrole: 'Select Role',
    role_mgmt_systemroles: 'System Roles',
    role_mgmt_customerroles: 'Customer Roles',
    role_mgmt_tenantroles: 'Workspace Roles',
    role_mgmt_contentroles: 'Content Roles',
    role_mgmt_basicroles: 'Basic Roles',
    role_mgmt_rolescope: 'Role Scope',
    role_mgmt_systemwide: 'System-wide',
    role_mgmt_systemwidedesc: 'Global role that applies across all customers and workspaces',
    role_mgmt_customerwide: 'Customer-wide',
    role_mgmt_customerwidedesc: 'Role that applies across all workspaces within a customer',
    role_mgmt_workspacespecific: 'Workspace-specific',
    role_mgmt_workspacespecificdesc: 'Role scoped to a specific workspace',
    role_mgmt_tenantadmin_workspace_required: 'Tenant admins must be granted per workspace, not customer-wide. Assign each workspace separately.',
    role_mgmt_role_locked_homogeneity: 'This user already holds the {role} role. Admin-tier users must hold a single role across all assignments, so new assignments must match.',
    role_mgmt_searchcustomers: 'Search customers...',
    role_mgmt_selectworkspace: 'Select a workspace...',
    role_mgmt_selectcustomerfirst: 'Select customer first...',
    role_mgmt_loadingworkspaces: 'Loading workspaces...',
    role_mgmt_pleaseselectcustomer: 'Please select a customer first',
    role_mgmt_updating: 'Updating...',
    role_mgmt_assigning: 'Assigning...',
    role_mgmt_updaterole: 'Update Role',
    role_mgmt_assignrole: 'Assign Role',
    role_mgmt_confirmrevocation: 'Confirm Role Revocation',
    role_mgmt_confirmrevokerole: 'Are you sure you want to revoke the',
    role_mgmt_forcustomer: 'for customer',
    role_mgmt_andworkspace: 'and workspace',
    role_mgmt_revokerole: 'Revoke Role',
    role_mgmt_confirmrolechange: 'Confirm Role Change',
    role_mgmt_rolemodwillrevoke: 'Role modification will revoke and reassign',
    role_mgmt_ensureaudittrail: 'To ensure a complete audit trail, the system will:',
    role_mgmt_revokeexisting: 'Revoke the existing role assignment',
    role_mgmt_createnewrole: 'Create a new role assignment with your changes',
    role_mgmt_currentassignment: 'Current Assignment',
    role_mgmt_changing: 'Changing',
    role_mgmt_willbecome: 'WILL BECOME',
    role_mgmt_newassignment: 'New Assignment',
    role_mgmt_new: 'New',
    role_mgmt_changedfields: 'Changed fields are highlighted in yellow above. This ensures proper audit logging and permissions are correctly applied.',

    // Modal Components - invalid-files-modal
    modal_invalidfiles_title: 'Unsupported File Type',
    modal_invalidfiles_description: 'The following files are not supported',

    // Modal Components - key-file-upload-modal
    modal_keyfileupload_title: 'Upload Content Key File',
    modal_keyfileupload_description: 'The Content Key gives you a way to apply a title, description, or other existing metadata that you want to apply to content items that have already been uploaded and processed by MetaLark. Each time you upload a key file, MetaLark will find the content with the matching ID field value and apply the key file values to that item.',
    modal_keyfileupload_supportedfields: 'The Content Key file supports the following metadata fields',
    modal_keyfileupload_field_itemname: 'Item Name',
    modal_keyfileupload_field_description: 'Description',
    modal_keyfileupload_field_externalid: 'External ID',
    modal_keyfileupload_field_externalsource: 'External Source',
    modal_keyfileupload_field_customerattributes: 'Customer Attributes',
    modal_keyfileupload_computednote: 'Computed attributes are read-only. Their column is ignored on import and is never updated from the file.',
    modal_keyfileupload_selectcsvfile: 'Select CSV File',
    modal_keyfileupload_uploadbutton: 'Upload Key File',
    modal_keyfileupload_processing: 'Processing key file...',
    modal_keyfileupload_pleasewait: 'Please wait while we update your resources',
    modal_keyfileupload_totalrows: 'Total Rows',
    modal_keyfileupload_viewidsnotfound: 'View IDs not found',
    modal_keyfileupload_viewskippedvalues: 'View values not applied',
    modal_keyfileupload_error_selectcsv: 'Please select a CSV file',
    modal_keyfileupload_error_uploadfailed: 'Failed to upload key file. Please try again.',

    // Modal Components - quota-limit-modal
    modal_processinglimit_title: 'Processing Limit Reached',
    modal_processinglimit_description: 'You have reached your processing limit ({totalCredits} Credits).',
    modal_processinglimit_description_nocredits: 'You have reached your processing limit.',
    modal_processinglimit_purchasecredits: 'Purchase Credits',

    // Components - sharepoint-site-selector
    sharepoint_selectsite_title: 'Select a SharePoint Site',
    sharepoint_selectsite_description: 'Choose which SharePoint site to browse. Only files from the selected site will be available.',
    sharepoint_selectsite_description_folder: 'Choose which SharePoint site to browse. Only folders from the selected site will be available.',
    sharepoint_loadingsites: 'Loading available sites...',
    sharepoint_searchsites_placeholder: 'Search sites...',
    sharepoint_myonedrive: 'My OneDrive',
    sharepoint_teamsite: 'Team Site',
    sharepoint_nositeselected: 'No site selected',
    sharepoint_browsefiles: 'Browse Files',
    sharepoint_oauth_connectionexpired: 'Repository Connection Expired',
    sharepoint_oauth_reauthorizenow: 'Re-authorize Now',
    sharepoint_nosites_error: 'No SharePoint sites available. Please check your permissions.',
    sharepoint_nosearchresults: 'No sites match "{searchQuery}"',
    sharepoint_error_pleaseselect: 'Please select a SharePoint site',
    sharepoint_error_nodriveid: 'Failed to get drive ID for selected site. Please try again or contact support.',
    sharepoint_error_savefailed: 'Failed to save selection',
    sharepoint_oauth_defaultmessage: 'Your repository connection has expired. Please re-authorize to continue.',
    sharepoint_oauth_error_nourl: 'Failed to get authorization URL',
    sharepoint_oauth_error_timeout: 'OAuth authorization timed out. Please try again.',
    sharepoint_oauth_error_failed: 'OAuth authorization failed',
    sharepoint_oauth_error_reauth: 'Failed to re-authorize',

    // Components - workspace-switcher
    workspace_switcher_title: 'Switch Workspace',
    workspace_switcher_searchplaceholder: 'Search workspaces...',
    workspace_switcher_impersonationsessions: 'Impersonation Sessions',
    workspace_switcher_noreason: 'No reason',
    workspace_switcher_loadmore: 'Load More',
    workspace_switcher_returntoselection: 'Return to Workspace Selection',
    workspace_switcher_adminconsole: 'Admin Console',
    workspace_switcher_impersonating: 'Impersonating...',
    workspace_switcher_unknown: 'Unknown',
    workspace_switcher_of: 'of',

    // Workspace switcher role abbreviations
    workspace_role_sysadmin: 'Sys Admin',
    workspace_role_custadmin: 'Cust Admin',
    workspace_role_admin: 'Admin',
    workspace_role_user: 'User',
    workspace_role_manager: 'Manager',
    workspace_role_contributor: 'Contributor',
    workspace_role_viewer: 'Viewer',
    workspace_role_service: 'Service',

    // Workspace switcher error messages
    workspace_error_switch: 'Failed to switch workspace',
    workspace_error_session: 'Failed to switch to impersonation session',
    workspace_error_clear: 'Failed to clear workspace',
    workspace_error_impersonation: 'Failed to start impersonation',

    // Admin - Repository Activity Page
    admin_repositoryactivity_title: 'Repository Activity',
    admin_repositoryactivity_description: 'View file actions and synchronization history',
    admin_repositoryactivity_repository: 'Repository',
    admin_repositoryactivity_selectrepository: 'Select a repository...',
    admin_repositoryactivity_norepositoryselected: 'No Repository Selected',
    admin_repositoryactivity_selecttoview: 'Select a repository to view its activity history.',

    // Admin - Customers
    admin_customers_title: 'Customer Management',
    admin_customers_subtitle: 'Manage customers and their workspaces',
    admin_customers_newcustomer: 'New Customer',
    admin_customers_totalcustomers: 'Total Customers',
    admin_customers_incurrentview: 'In current view',
    admin_customers_tier_standard: 'Standard',
    admin_customers_tier_professional: 'Professional',
    admin_customers_tier_enterprise: 'Enterprise',
    admin_customers_currentpage: 'Current Page',
    admin_customers_showingitems: 'Showing {count} items',
    admin_customers_createnewcustomer: 'Create New Customer',
    admin_customers_displayname: 'Display Name',
    admin_customers_contactemail: 'Contact Email',
    admin_customers_billingemail: 'Billing Email',
    admin_customers_subscriptiontier: 'Subscription Tier',
    admin_customers_maxslices: 'Max Slices',
    admin_customers_createcustomer: 'Create Customer',
    admin_customers_displayname_description_required: 'Display Name and Description are required',
    admin_customers_loadfailed: 'Failed to load customers',
    admin_customers_createfailed: 'Failed to create customer',

    // Session security toggle (still used by customer-security-settings.tag,
    // which is embedded directly in Customer 360's Settings tab).
    admin_customer_detail_use_defaults: 'Use System Defaults',
    admin_customer_detail_using_defaults: 'Customer is using default security settings. Uncheck to customize.',
    admin_customer_detail_custom_settings: 'Customer has custom security settings configured.',

    // Admin - Customer Workspaces Card
    admin_customer_workspaces_title: 'Workspaces',
    admin_customer_workspaces_new: 'New Workspace',
    admin_customer_workspaces_search_placeholder: 'Search by name, description, or ID...',
    admin_customer_workspaces_clear_search: 'Clear search',
    admin_customer_workspaces_name: 'Name',
    admin_customer_workspaces_id: 'Workspace ID',
    admin_customer_workspaces_status: 'Status',
    admin_customer_workspaces_created: 'Created',
    admin_customer_workspaces_resources: 'Resources',
    admin_customer_workspaces_repositories: 'Repositories',
    admin_customer_workspaces_users: 'Users',
    admin_customer_workspaces_actions: 'Actions',
    admin_customer_workspaces_nodescription: 'No description',
    admin_customer_workspaces_active: 'Active',
    admin_customer_workspaces_inactive: 'Inactive',
    admin_customer_workspaces_delete_title: 'Delete workspace',
    admin_customer_workspaces_no_results: 'No workspaces match your search.',
    admin_customer_workspaces_empty: 'No workspaces found for this customer. Create one to get started.',
    admin_customer_workspaces_delete_confirm_title: 'Confirm Delete',
    admin_customer_workspaces_delete_confirm_message: 'Are you sure you want to delete workspace',
    admin_customer_workspaces_delete_warning: 'This action cannot be undone.',
    admin_customer_workspaces_modal_create: 'Create New Workspace',
    admin_customer_workspaces_modal_edit: 'Edit Workspace',
    admin_customer_workspaces_displayname: 'Display Name',
    admin_customer_workspaces_description: 'Description',
    admin_customer_workspaces_isactive: 'Active',
    admin_customer_workspaces_create: 'Create',
    admin_customer_workspaces_save: 'Save',
    admin_customer_workspaces_delete_failed: 'Failed to delete workspace',
    admin_customer_workspaces_save_failed: 'Failed to save workspace',

    // Admin - Customer Quota Card keys live in the system-admin locale
    // chunk because the card now only renders inside the SYSTEM_ADMIN-only
    // Advanced Edit modal on Customer 360. Lambda@Edge gates the chunk in
    // production so customer-admin sessions never download these strings.


    // Admin - Customer Table
    admin_customer_table_logo: 'Logo',
    admin_customer_table_customername: 'Customer Name',
    admin_customer_table_contactemail: 'Contact Email',
    admin_customer_table_subscriptiontier: 'Subscription Tier',
    admin_customer_table_subscriptionstatus: 'Subscription Status',
    admin_customer_table_quotacredits: 'Quota Credits',
    admin_customer_table_usedcredits: 'Used Credits',
    admin_customer_table_createddate: 'Created Date',
    admin_customer_table_actions: 'Actions',
    admin_customer_table_protected: 'Protected from automatic deletion',
    admin_customer_table_id: 'ID',
    admin_customer_table_exhausted: 'exhausted',
    admin_customer_table_clicktoview: 'Click row to view detailed quota breakdown',
    admin_customer_table_view: 'View',
    admin_customer_table_nocustomers: 'No customers found matching your criteria.',
    admin_customer_table_status_unknown: 'Unknown',
    admin_customer_table_status_trial: 'Trial',
    admin_customer_table_status_active: 'Active',
    admin_customer_table_status_canceled: 'Canceled',
    admin_customer_table_status_expired: 'Expired',
    admin_customer_table_status_suspended: 'Suspended',
    admin_customer_table_status_none: 'None',

    // Admin - Customer Filters
    admin_customer_filters_clearall: 'Clear All',
    admin_customer_filters_search_placeholder: 'Search name, email, customer ID...',
    admin_customer_filters_tier_all: 'All Tiers',
    admin_customer_filters_tier_standard: 'Standard',
    admin_customer_filters_tier_professional: 'Professional',
    admin_customer_filters_tier_enterprise: 'Enterprise',
    admin_customer_filters_status_all: 'All Statuses',
    admin_customer_filters_status_trial: 'Trial',
    admin_customer_filters_status_active: 'Active',
    admin_customer_filters_status_canceled: 'Canceled',
    admin_customer_filters_status_pastdue: 'Past Due',
    admin_customer_filters_status_unpaid: 'Unpaid',
    admin_customer_filters_status_none: 'No Subscription',
    admin_customer_filters_quota_all: 'All Quota Amounts',
    admin_customer_filters_quota_exhausted: 'Exhausted (0)',
    admin_customer_filters_quota_low: 'Low (1-10)',
    admin_customer_filters_quota_medium: 'Medium (11-100)',
    admin_customer_filters_quota_high: 'High (101+)',

    // Admin - Email Logs
    admin_emaillogs_title: 'Email Logs',
    admin_emaillogs_description: 'View email delivery logs and status',
    admin_emaillogs_emailaddress: 'Email Address',
    admin_emaillogs_searchemail: 'Search email',
    admin_emaillogs_placeholder_search: 'Search recipient, subject, or name…',
    admin_emaillogs_fromdate: 'From Date',
    admin_emaillogs_todate: 'To Date',
    admin_emaillogs_unknowncustomer: 'Unknown customer',
    admin_emaillogs_customerdeleted: 'Deleted customer',
    admin_emaillogs_customersystem: 'System',
    admin_emaillogs_customerpartner: 'Partner',
    admin_emaillogs_customersystemtooltip: 'Platform-scoped email with no owning customer',
    admin_emaillogs_customerpartnertooltip: 'Partner-scoped email with no owning customer',
    admin_emaillogs_referenceid: 'Reference ID',
    admin_emaillogs_placeholder_referenceid: 'Email Reference ID (em_…)',
    admin_emaillogs_allstatuses: 'All Statuses',
    admin_emaillogs_queued: 'Queued',
    admin_emaillogs_sent: 'Sent',
    admin_emaillogs_delivered: 'Delivered',
    admin_emaillogs_failed: 'Failed',
    admin_emaillogs_bounced: 'Bounced',
    admin_emaillogs_customer: 'Customer',
    admin_emaillogs_allcustomers: 'All Customers',
    admin_emaillogs_datefrom: 'Date From',
    admin_emaillogs_activitytimeline: 'Activity Timeline',
    admin_emaillogs_last24hours: 'Last 24 Hours',
    admin_emaillogs_last96hours: 'Last 96 Hours',
    admin_emaillogs_totalemails: 'total emails',
    admin_emaillogs_sentdelivered: 'Sent/Delivered',
    admin_emaillogs_failedbounced: 'Failed/Bounced',
    admin_emaillogs_loadinglogs: 'Loading email logs',
    admin_emaillogs_errorloadinglogs: 'Error Loading Logs',
    admin_emaillogs_recipient: 'Recipient',
    admin_emaillogs_subject: 'Subject',
    admin_emaillogs_template: 'Template',
    admin_emaillogs_retries: 'Retries',
    admin_emaillogs_provider: 'Provider',
    admin_emaillogs_justnow: 'Just now',
    admin_emaillogs_minutesago: '{minutes}m ago',
    admin_emaillogs_hoursago: '{hours}h ago',
    admin_emaillogs_daysago: '{days}d ago',
    admin_emaillogs_nologsfound: 'No email logs found',
    admin_emaillogs_logdetails: 'Email Log Details',
    admin_emaillogs_emailreferenceid: 'Email Reference ID',
    admin_emaillogs_recipientinfo: 'Recipient Information',
    admin_emaillogs_useruuid: 'User UUID',
    admin_emaillogs_customerid: 'Customer ID',
    admin_emaillogs_emailcontent: 'Email Content',
    admin_emaillogs_templatename: 'Template',
    admin_emaillogs_templateid: 'Template ID',
    admin_emaillogs_viewemailbody: 'View Email Body',
    admin_emaillogs_html: 'HTML',
    admin_emaillogs_nobodycontent: 'No body content available',
    admin_emaillogs_deliverystatus: 'Delivery Status',
    admin_emaillogs_providermessageid: 'Provider Message ID',
    admin_emaillogs_retrycount: 'Retry Count',
    admin_emaillogs_timeline: 'Timeline',
    admin_emaillogs_opened: 'Opened',
    admin_emaillogs_clicked: 'Clicked',
    admin_emaillogs_complained: 'Complained',
    admin_emaillogs_bounceinfo: 'Bounce/Complaint Information',
    admin_emaillogs_bouncetype: 'Bounce Type',
    admin_emaillogs_bouncesubtype: 'Bounce SubType',
    admin_emaillogs_complainttype: 'Complaint Type',
    admin_emaillogs_feedbacktype: 'Feedback Type',
    admin_emaillogs_diagnosticcode: 'Diagnostic Code',
    admin_emaillogs_errordetails: 'Error Details',
    admin_emaillogs_message: 'Message',
    admin_emaillogs_code: 'Code',
    admin_emaillogs_triggerinfo: 'Trigger Information',
    admin_emaillogs_triggertype: 'Trigger Type',
    admin_emaillogs_context: 'Context',
    admin_emaillogs_logid: 'Log ID',
    admin_emaillogs_logidentifiers: 'Log Identifiers',
    admin_emaillogs_metadatafield: 'Field',
    admin_emaillogs_metadatavalue: 'Value',
    admin_emaillogs_metadatarawjson: 'Show raw JSON',
    admin_emaillogs_metadataempty: 'No structured metadata to display',
    admin_emaillogs_created: 'Created',
    admin_emaillogs_resendemail: 'Resend Email',
    admin_emaillogs_notemplate: 'No template',
    admin_emaillogs_col_sent_at: 'Sent At',
    admin_emaillogs_btn_view_details: 'View details',

    // Admin - Email Templates
    admin_emailtemplates_title: 'Email Templates',
    admin_emailtemplates_subtitle: 'Manage and customize email templates for system notifications',
    admin_emailtemplates_loading: 'Loading email templates',
    admin_emailtemplates_error_loading: 'Error Loading Email Templates',
    admin_emailtemplates_error_loadfailed: 'Failed to load email templates',
    admin_emailtemplates_col_name: 'Name',
    admin_emailtemplates_col_category: 'Category',
    admin_emailtemplates_col_subject: 'Subject',
    admin_emailtemplates_col_status: 'Status',
    admin_emailtemplates_col_version: 'Version',
    admin_emailtemplates_col_actions: 'Actions',
    admin_emailtemplates_viewtemplate: 'View Template',
    admin_emailtemplates_notemplates: 'No email templates found',
    admin_emailtemplates_templatepreview: 'Template Preview',
    admin_emailtemplates_notextbody: 'No plain text body available',
    admin_emailtemplates_defaulttemplate: 'Default Template',
    admin_emailtemplates_basicinfo: 'Basic Information',
    admin_emailtemplates_subjectline: 'Subject Line',
    admin_emailtemplates_htmlbody: 'HTML Body',
    admin_emailtemplates_plaintextbody: 'Plain Text Body',
    admin_emailtemplates_editingunsaved: 'Editing mode - unsaved changes',
    admin_emailtemplates_enteremailcontent: 'Enter email content...',
    admin_emailtemplates_alert_nochanges: 'No changes made to template',
    admin_emailtemplates_alert_updatesuccess: 'Template updated successfully',
    admin_emailtemplates_alert_updatefailed: 'Failed to update template',

    // Admin - Pending Files
    admin_pendingfiles_title: 'Pending Files Review',
    admin_pendingfiles_showingfilesfrom: 'Showing files from',
    admin_pendingfiles_allrepositories: 'All Repositories',
    admin_pendingfiles_howtouse: 'How to Use Pending Files Review',
    admin_pendingfiles_reviewingfrom: 'Reviewing files from the selected repository.',
    admin_pendingfiles_showingall: 'Showing pending files from all repositories. Use the dropdown to filter by a specific repository.',
    admin_pendingfiles_filestatuses: 'File Statuses',
    admin_pendingfiles_statuslist: '<li><span class="font-medium">Pending:</span> Awaiting your decision</li><li><span class="font-medium">Rule Matched:</span> Matches a business rule, ready to approve</li><li><span class="font-medium">Rule Excluded:</span> Doesn\'t match your configured rules</li><li><span class="font-medium">Approved:</span> Queued for ingestion into your workspace</li><li><span class="font-medium">Rejected:</span> Won\'t be ingested</li>',
    admin_pendingfiles_workflowrules: 'Workflow & Business Rules',
    admin_pendingfiles_workflowlist: '<li>Files appear here after discovery from your repository</li><li><strong>Rule-matched</strong> files have been pre-vetted by your selection rules</li><li>Use <strong>filters</strong> to focus on specific status groups</li><li><strong>Approve/Reject</strong> individually or use <strong>bulk actions</strong></li><li>Modify rules in <strong>File Selection Rules</strong> to change matching criteria</li>',

    // Admin - Quota Health
    admin_quotahealth_title: 'Quota Health Monitor',
    admin_quotahealth_dryrunmode: 'Dry Run Mode',
    admin_quotahealth_totalcustomers: 'Total Customers',
    admin_quotahealth_healthy: 'Healthy',
    admin_quotahealth_warning: 'Warning',
    admin_quotahealth_critical: 'Critical',
    admin_quotahealth_overallsystemhealth: 'Overall System Health',
    admin_quotahealth_orphanedrecords: 'orphaned records',
    admin_quotahealth_creditsoutofsync: 'credits out of sync',
    admin_quotahealth_topproblems: 'Top Problems',
    admin_quotahealth_noproblems: 'No problems detected! System quota is healthy.',
    admin_quotahealth_health: 'Health',
    admin_quotahealth_cachedbalance: 'Cached Balance',
    admin_quotahealth_bundlebalance: 'Bundle Balance',
    admin_quotahealth_discrepancy: 'Discrepancy',
    admin_quotahealth_verify: 'Verify',
    admin_quotahealth_verifytooltip: 'Check if credits are properly allocated',
    admin_quotahealth_repairing: 'Repairing...',
    admin_quotahealth_repair: 'Repair',
    admin_quotahealth_recentfailures: 'Recent Reconciliation Failures',
    admin_quotahealth_nofailures: 'No outstanding failures. All usage records are properly reconciled.',
    admin_quotahealth_failures_title: 'These usage records were debited but never linked to bundles',
    admin_quotahealth_failures_description: 'The customer\'s cache was reduced, but the bundle tracking failed. Use the repair function above to fix these.',
    admin_quotahealth_jobid: 'Job ID',
    admin_quotahealth_credits: 'Credits',
    admin_quotahealth_createddate: 'Created Date',
    admin_quotahealth_info_title: 'What is Quota Health?',
    admin_quotahealth_info_description: 'The system monitors whether customer quota caches match their actual bundle balances. Issues are indicated when reconciliation fails.',
    admin_quotahealth_autorepair_title: 'Auto Repair',
    admin_quotahealth_autorepair_description: 'Click \'Repair\' to automatically link orphaned usage records to bundles and correct the customer\'s cache balance.',
    admin_quotahealth_verification_title: 'Bundle Usage Alignment Report',
    admin_quotahealth_aligned: 'Aligned',
    admin_quotahealth_misaligned: 'Misaligned',
    admin_quotahealth_totalusage: 'Total usage',
    admin_quotahealth_bundleused: 'Bundle used',
    admin_quotahealth_bundlebreakdown: 'Bundle Breakdown',
    admin_quotahealth_bundleid: 'Bundle ID',
    admin_quotahealth_usedcredits: 'Used Credits',
    admin_quotahealth_creditsfromrecords: 'Credits from Records',
    admin_quotahealth_match: 'Match',
    admin_quotahealth_unallocatedrecords: 'Unallocated Records',
    admin_quotahealth_unallocatedrecords_description: 'These records have no bundle assigned and represent orphaned usage',
    admin_quotahealth_recordid: 'Record ID',
    admin_quotahealth_job: 'Job',
    admin_quotahealth_runrepairnow: 'Run Repair Now',
    admin_quotahealth_testrepair: 'Test Repair (Dry Run)',
    admin_quotahealth_confirmrepair: 'Confirm Repair',
    admin_quotahealth_dryrun_description: 'This will show what would be fixed without making any changes.',
    admin_quotahealth_repair_description: 'This will link orphaned records to bundles and fix misaligned bundle metrics.',
    admin_quotahealth_dryrun_safe: 'Safe: No database changes will be made',
    admin_quotahealth_repair_warning: 'This action will modify your quota data',
    admin_quotahealth_rundryrun: 'Run Dry Run',
    admin_quotahealth_proceedwithrepair: 'Proceed with Repair',
    admin_quotahealth_dryrunresults: 'Dry Run Results',
    admin_quotahealth_repairresults: 'Repair Results',
    admin_quotahealth_bundlesfixed: 'Bundles Fixed',
    admin_quotahealth_recordslinked: 'Records Linked',
    admin_quotahealth_skipped: 'Skipped',
    admin_quotahealth_bundlesfixed_explanation: 'Bundles where the stored used credits didn\'t match the actual sum of linked usage records',
    admin_quotahealth_recordslinked_explanation: 'Orphaned usage records (with no bundle assigned) that were successfully linked to bundles',
    admin_quotahealth_dryrunmode_title: 'Dry Run Mode',
    admin_quotahealth_dryrunmode_description: 'No changes were made. Turn off "Dry Run Mode" and run repair again to apply fixes.',
    admin_quotahealth_details: 'Details',
    admin_quotahealth_bundlefixed: 'Bundle Fixed',
    admin_quotahealth_recordlinked: 'Record Linked',
    admin_quotahealth_gotit: 'Got it',
    admin_quotahealth_repairfailed: 'Repair Failed',

    // Admin - Quota Management
    admin_quotamanagement_title: 'Processing Credits',
    admin_quotamanagement_current_balance: 'Current Balance',
    admin_quotamanagement_credits: 'credits',
    admin_quotamanagement_active: 'Active',
    admin_quotamanagement_exhausted: 'Exhausted',
    admin_quotamanagement_total_credits: 'Total Credits',
    admin_quotamanagement_all_time_allocated: 'All time allocated',
    admin_quotamanagement_remaining_credits: 'Remaining Credits',
    admin_quotamanagement_available_for_use: 'Available for use',
    admin_quotamanagement_used_credits: 'Used Credits',
    admin_quotamanagement_resources_processed: 'Resources processed',
    admin_quotamanagement_quota_bundles: 'Quota Bundles',
    admin_quotamanagement_no_bundles_found: 'No quota bundles found',
    admin_quotamanagement_bundle_name: 'Bundle Name',
    admin_quotamanagement_type: 'Type',
    admin_quotamanagement_total: 'Total',
    admin_quotamanagement_used: 'Used',
    admin_quotamanagement_remaining: 'Remaining',
    admin_quotamanagement_status: 'Status',
    admin_quotamanagement_priority: 'Priority',
    admin_quotamanagement_created: 'Created',
    admin_quotamanagement_usage_history: 'Usage History',
    admin_quotamanagement_no_usage_records_found: 'No usage records found',
    admin_quotamanagement_date: 'Date',
    admin_quotamanagement_user: 'User',
    admin_quotamanagement_auth: 'Auth',
    admin_quotamanagement_resource: 'Resource',
    admin_quotamanagement_description: 'Description',
    admin_quotamanagement_bundle: 'Bundle',
    admin_quotamanagement_usage_type_base: 'Base',
    admin_quotamanagement_usage_type_media: 'Media',
    admin_quotamanagement_usage_type_additional: 'Additional',
    admin_quotamanagement_auth_type_session: 'Session',
    admin_quotamanagement_auth_type_api: 'API',
    admin_quotamanagement_auth_type_service: 'Service',
    admin_quotamanagement_priority_title: 'Credit Consumption Priority',
    admin_quotamanagement_priority_description: 'Trial credits (priority 1) are consumed before prepaid credits (priority 100).',
    admin_quotamanagement_credit_types_title: 'Credit Types',
    admin_quotamanagement_base_extraction: 'Base Extraction',
    admin_quotamanagement_base_extraction_desc: '1 credit per resource upload',
    admin_quotamanagement_media_transcription: 'Media Transcription',
    admin_quotamanagement_media_transcription_desc: 'Additional credits based on media duration',

    // Admin - Repository Rules
    admin_repositoryrules_title: 'File Selection Rules',
    admin_repositoryrules_selectrepository: 'Select a repository...',
    admin_repositoryrules_howtheywork: 'How File Selection Rules Work',
    admin_repositoryrules_explanation: '<p><strong>Purpose:</strong> Define which files from your repository should be automatically approved for ingestion.</p><p><strong>File Matching:</strong> Configure conditions based on file extension, path, name, and size. Files matching your rules will be marked as "Rule Matched".</p><p><strong>Multiple Rules:</strong> You can create multiple rules. Files matching ANY enabled rule will be auto-approved.</p><p><strong>Conditions:</strong> Within each rule, you can combine conditions with AND (all must match) or OR (at least one must match).</p><p><strong>Example:</strong> Create a rule like "Extension equals PDF AND Path contains Training" to auto-approve all PDF files in your Training folder.</p>',
    admin_repositoryrules_tip: 'Tip: Test your rules against sample files before enabling them in your repository sync settings.',
    admin_repositoryrules_norepositoryselected: 'No Repository Selected',
    admin_repositoryrules_pleaseselectrepository: 'Please select a repository to manage its file selection rules.',

    // Admin - Email Suppression List
    admin_emailsuppression_title: 'Email Suppression List',
    admin_emailsuppression_description: 'Manage bounced, complained, and manually suppressed email addresses',
    admin_emailsuppression_export_csv: 'Export CSV',
    admin_emailsuppression_add_email: 'Add Email',
    admin_emailsuppression_no_entries: 'No suppressed emails found',
    admin_emailsuppression_email_address: 'Email Address',
    admin_emailsuppression_reason: 'Reason',
    admin_emailsuppression_added_date: 'Added Date',
    admin_emailsuppression_modal_add_title: 'Add Email to Suppression List',
    admin_emailsuppression_placeholder_email: 'email@example.com',
    admin_emailsuppression_select_reason: 'Select reason',
    admin_emailsuppression_reason_bounce: 'Bounce',
    admin_emailsuppression_reason_complaint: 'Complaint',
    admin_emailsuppression_reason_manual: 'Manual',
    admin_emailsuppression_placeholder_notes: 'Optional notes...',
    admin_emailsuppression_adding: 'Adding...',
    admin_emailsuppression_modal_details_title: 'Suppression Details',
    admin_emailsuppression_bounce_type: 'Bounce Type',
    admin_emailsuppression_complaint_type: 'Complaint Type',
    admin_emailsuppression_alert_load_failed: 'Failed to load suppression list',
    admin_emailsuppression_alert_email_reason_required: 'Email and reason are required',
    admin_emailsuppression_alert_added_success: 'Email added to suppression list',
    admin_emailsuppression_alert_add_failed: 'Failed to add email to suppression list',
    admin_emailsuppression_alert_removed_success: 'Email removed from suppression list',
    admin_emailsuppression_alert_remove_failed: 'Failed to remove email from suppression list',
    admin_emailsuppression_alert_export_failed: 'Failed to export suppression list',
    admin_emailsuppression_confirm_delete: 'Are you sure you want to remove this email from the suppression list?',

    // Admin - Repositories
    admin_repositories_title: 'Cloud Repository Integrations',
    admin_repositories_subtitle: 'Connect external file repositories to sync content into your workspace.',
    admin_repositories_addintegration: 'Add Integration',
    admin_repositories_refresh: 'Refresh',
    admin_repositories_nointegrations: 'No Repository Integrations',
    admin_repositories_nointegrations_desc: 'Connect to external repositories like Google Drive or SharePoint to automatically sync files into your workspace.',
    admin_repositories_addfirstintegration: 'Add Your First Integration',
    admin_repositories_menu_label: 'More actions',
    admin_repositories_menu_editsettings: 'Edit Settings',
    admin_repositories_menu_editcredentials: 'Edit Credentials',
    admin_repositories_menu_testconnection: 'Test Connection',
    admin_repositories_menu_syncnow: 'Sync Now',
    admin_repositories_menu_disconnect: 'Disconnect',
    admin_repositories_menu_delete: 'Delete',
    admin_repositories_yourstatus: 'Your Status:',
    admin_repositories_status: 'Status:',
    admin_repositories_connectionerror: 'Connection Error',
    admin_repositories_otherusersconnected: '{count, plural, one {# other user} other {# other users}} connected',
    admin_repositories_otherusersdisconnected: '{count, plural, one {# other user} other {# other users}} disconnected',
    admin_repositories_lastsync: 'Last sync: {date}',
    admin_repositories_status_connected: 'Connected',
    admin_repositories_status_error: 'Error',
    admin_repositories_status_disconnected: 'Disconnected',
    admin_repositories_status_configured: 'Configured',
    admin_repositories_status_unknown: 'Unknown',
    admin_repositories_connect: 'Connect',
    admin_repositories_retryconnection: 'Retry Connection',
    admin_repositories_browsefiles: 'Browse Files',
    admin_repositories_history: 'History',
    admin_repositories_info_workspacescoped_title: 'Workspace Scoped',
    admin_repositories_info_workspacescoped_desc: 'Repository integrations are scoped to your current workspace. Each workspace can have its own connections.',
    admin_repositories_info_secureauth_title: 'Secure Authentication',
    admin_repositories_info_secureauth_desc: 'OAuth providers use secure token-based authentication. S3 credentials are encrypted and stored securely. Your sensitive data is protected.',
    admin_repositories_lms_addplatform: 'Add Platform',
    admin_repositories_lms_legacy_empty: 'No Legacy LMS/LXP Platforms have been added.',
    admin_repositories_lms_target_empty: 'No Target LMS/LXP Platforms have been added.',
    admin_repositories_lms_legacy_subtitle: 'Connect a legacy LMS/LXP platform as a content source.',
    admin_repositories_lms_target_subtitle: 'Connect a target LMS/LXP platform as a publishing destination.',
    admin_repositories_lms_real_subtitle: 'Connect an LMS as a real publishing destination for content migration.',
    admin_repositories_lms_real_empty: 'No LMS destinations connected yet.',
    admin_onpoint_configure: 'Configure',
    admin_onpoint_platform_choose: 'Choose a platform',
    admin_onpoint_platform_unsupported: '{name} cannot be connected from this version yet.',
    admin_onpoint_credentials_encrypted: 'These credentials are stored encrypted',
    admin_onpoint_credentials_notice: 'The username and password are sent over HTTPS and stored encrypted at rest. They are never logged, and the password is never shown again after saving.',
    admin_onpoint_title_config: 'Connect OnPoint Digital',
    admin_onpoint_title_update: 'Update OnPoint credentials',
    admin_onpoint_subtitle_config: 'Content pushed here is created as Courses and Nuggets in OnPoint.',
    admin_onpoint_subtitle_update: 'Replace the stored credentials for this OnPoint connection.',
    admin_onpoint_sa_requirement: 'OnPoint requires an active super-admin (SA) account. A standard user account cannot be used.',
    admin_onpoint_apiurl: 'API base URL',
    admin_onpoint_apiurl_help: 'The root of the OnPoint REST API for this instance. Must use https.',
    admin_onpoint_username: 'Username',
    admin_onpoint_password: 'Password',
    admin_onpoint_password_replace_help: 'Enter the password again to replace the stored credentials.',
    admin_onpoint_testconnection: 'Test connection',
    admin_onpoint_save: 'Save connection',
    admin_onpoint_test_success: 'Signed in to OnPoint successfully.',
    admin_onpoint_test_failed: 'Could not connect to OnPoint.',
    admin_onpoint_customers_found: '{count, plural, one {# customer reachable} other {# customers reachable}}',
    admin_onpoint_customers_note: 'Which customer a push goes to is chosen when you create the migration target.',
    admin_moodle_title_config: 'Connect Moodle',
    admin_moodle_title_update: 'Update Moodle token',
    admin_moodle_subtitle_config: 'Content pushed here is created as courses in Moodle.',
    admin_moodle_subtitle_update: 'Replace the stored token for this Moodle connection.',
    admin_moodle_prereq: 'Moodle needs web services enabled and a token issued for an external service that includes the functions MetaLark calls.',
    admin_moodle_prereq_help: 'Read the Moodle setup guide',
    admin_moodle_siteurl: 'Moodle site address',
    admin_moodle_siteurl_help: 'The root of the Moodle site. Must use https.',
    admin_moodle_token: 'Web service token',
    admin_moodle_token_help: 'Generated in Moodle under Site administration, Server, Web services, Manage tokens.',
    admin_moodle_token_replace_help: 'Enter the token again to replace the stored one.',
    admin_moodle_credentials_notice: 'The token is sent over HTTPS and stored encrypted at rest. It is never logged, and it is not shown again after saving.',
    admin_lms_secret_stored_help: 'A value is already stored. Leave this blank to keep it, or enter a new one to replace it.',
    admin_lms_secret_stored_kept: 'Stored value kept',
    admin_moodle_test_success: 'Connected to Moodle successfully.',
    admin_moodle_test_failed: 'Could not connect to Moodle.',
    admin_acto_title_config: 'Connect Acto',
    admin_acto_title_update: 'Update Acto token',
    admin_acto_subtitle_config: 'Content pushed here is created as resources in an Acto Content Hub folder.',
    admin_acto_subtitle_update: 'Replace the stored token for this Acto connection.',
    admin_acto_prereq: 'Acto needs a Personal Access Token from an account with Content Hub permissions, and the domain needs public API access enabled by Acto.',
    admin_acto_hostname: 'Acto domain address',
    admin_acto_hostname_help: 'The root of the Acto domain. Must use https.',
    admin_acto_token: 'Personal Access Token',
    admin_acto_token_help: 'Generated in Acto under Admin, Manage API.',
    admin_acto_token_replace_help: 'Enter the token again to replace the stored one.',
    admin_acto_credentials_notice: 'The token is sent over HTTPS and stored encrypted at rest. It is never logged, and it is not shown again after saving.',
    admin_acto_test_success: 'Connected to Acto successfully.',
    admin_acto_test_failed: 'Could not connect to Acto.',
    admin_acto_reachable: '{count, plural, one {# folder reachable} other {# folders reachable}}',
    admin_acto_folders_note: 'Which folder a push goes to is chosen when you create the migration target.',
    admin_acto_no_folders: 'Connected, but no folders are reachable.',
    admin_acto_no_folders_detail: 'The token works, but it can see no Content Hub folders — so there is nowhere to push to yet. Check the account has access to at least one folder.',
    admin_moodle_mode_full: 'Full content push available.',
    admin_moodle_mode_full_detail: 'The MetaLark connector plugin is reachable, so pushed files are attached as course content a learner can open.',
    admin_moodle_mode_coreonly: 'Metadata-only push available.',
    admin_moodle_mode_coreonly_detail: 'Courses will be created with their titles, descriptions and tags, but the file cannot be attached, so learners would see an empty course. Either the MetaLark connector plugin is not installed on this Moodle site, or this token belongs to an external service that does not list its functions.',
    admin_moodle_plugin_authorize_hint: 'If the plugin is already installed, add those functions to the external service this token was issued against. The token does not need to be reissued.',
    admin_moodle_plugin_missing_functions: 'Functions not reachable through this token: {functions}',
    admin_moodle_plugin_install_help: 'How to install the connector plugin',
    admin_moodle_token_incomplete: 'This token cannot push to Moodle yet',
    admin_moodle_token_limited: 'This token can push, with limits',
    admin_moodle_token_gap_core_webservice_get_site_info: 'The site cannot be read at all.',
    admin_moodle_token_gap_core_course_get_categories: 'Destination categories cannot be listed, so there is nothing to point a migration at.',
    admin_moodle_token_gap_core_course_create_courses: 'Courses cannot be created, so no push will place anything.',
    admin_moodle_token_gap_core_course_get_courses_by_field: 'An existing course cannot be found again, so re-pushing would create a duplicate instead of updating.',
    admin_moodle_token_gap_core_course_update_courses: 'Metadata cannot be corrected on a course that already exists, so a re-push cannot fix a title or description that has changed in MetaLark.',
    admin_moodle_token_gap_core_course_create_categories: 'Categories cannot be created from MetaLark. Choose one that already exists in Moodle instead.',
    admin_moodle_token_gap_generic: 'This function is not available to the token: {fn}',
    admin_moodle_token_fix_title: 'To fix this in Moodle:',
    admin_moodle_token_fix_step1: 'Go to Site administration, then Server, then Web services, then External services.',
    admin_moodle_token_fix_step2: 'Edit the service this token was issued for and open its Functions list.',
    admin_moodle_token_fix_step3: 'Add each function listed above, then save.',
    admin_moodle_token_fix_step4: 'Return here and test the connection again. The token itself does not need to be reissued.',
    admin_moodle_maxupload: 'Largest file this site accepts: {size}',
    admin_moodle_maxupload_unknown: 'This site did not report an upload limit, so a large file may be refused at push time.',
    admin_moodle_categories_found: '{count, plural, one {# category reachable} other {# categories reachable}}',
    admin_moodle_categories_note: 'Which category content goes to is chosen when the migration target is created.',
    admin_repositories_lms_legacy_title: 'Legacy LMS/LXP Platforms',
    admin_repositories_lms_target_title: 'Target LMS/LXP Platforms',
    admin_repositories_lms_modal_title: 'Connect LMS/LXP Platform',
    admin_repositories_lms_modal_legacy_title: 'Connect Legacy LMS/LXP Platform',
    admin_repositories_lms_modal_target_title: 'Connect Target LMS/LXP Platform',
    admin_repositories_lms_modal_intro: 'Select an LMS/LXP platform to connect.',
    admin_repositories_lms_create: 'Create',
    admin_repositories_lms_cancel: 'Cancel',
    admin_repositories_lms_menu_remove: 'Remove',
    admin_repositories_modal_addtitle: 'Add Repository Integration',
    admin_repositories_modal_edittitle: 'Edit External Connection',
    admin_repositories_modal_connectiondetails: 'Connection Details',
    admin_repositories_modal_provider: 'Provider',
    admin_repositories_modal_integrationid: 'Integration ID',
    admin_repositories_modal_chooseprovider: 'Choose which repository service to connect',
    admin_repositories_modal_integrationsettings: 'Integration Settings',
    admin_repositories_modal_selectprovider: 'Select a provider above to configure the integration',
    admin_repositories_modal_integrationname: 'Integration Name',
    admin_repositories_modal_integrationname_placeholder: 'e.g., Marketing Files, Project Documents',
    admin_repositories_modal_syncmode: 'Sync Mode',
    admin_repositories_modal_syncmode_manual: 'Manual',
    admin_repositories_modal_syncmode_manual_desc: 'Sync only when triggered',
    admin_repositories_modal_syncmode_scheduled: 'Scheduled',
    admin_repositories_modal_syncmode_scheduled_desc: 'Auto-sync on a schedule',
    admin_repositories_modal_syncmode_webhook: 'Webhook',
    admin_repositories_modal_syncmode_webhook_desc: 'Real-time updates',
    admin_repositories_modal_workflowmode: 'Workflow Mode',
    admin_repositories_modal_workflowmode_auto: 'Auto Process',
    admin_repositories_modal_workflowmode_auto_desc: 'Files ingested immediately',
    admin_repositories_modal_workflowmode_review: 'Review & Approve',
    admin_repositories_modal_workflowmode_review_desc: 'Admin approval required',
    admin_repositories_modal_enableautosync: 'Enable Automatic Sync',
    admin_repositories_modal_enableautosync_desc: 'Files will be automatically discovered and processed based on your sync mode schedule.\nLeave this off to only manually select files.',
    admin_repositories_modal_businessrules_title: 'How Business Rules Apply',
    admin_repositories_modal_businessrules_syncmode_title: 'Sync Mode Discovery',
    admin_repositories_modal_businessrules_syncmode_desc: 'Files are discovered based on your selected sync mode: manual (click to sync), scheduled (automatic at set times), or webhook (real-time).',
    admin_repositories_modal_businessrules_workflow_title: 'Workflow Processing',
    admin_repositories_modal_businessrules_workflow_desc: 'Auto Process: Files matching business rules are automatically ingested. Review & Approve: Matching files are flagged for admin review first.',
    admin_repositories_modal_businessrules_configure_title: 'Configure Rules',
    admin_repositories_modal_businessrules_configure_desc: 'Set up business rules in the File Selection Rules tab to automatically filter and categorize files.',
    admin_repositories_modal_s3credentials: 'S3 Credentials',
    admin_repositories_modal_updates3credentials: 'Update S3 Credentials',
    admin_repositories_modal_nextcredentials: 'Next: Enter Credentials',
    admin_repositories_modal_create: 'Create',
    admin_repositories_modal_save: 'Save',
    admin_repositories_synchistory_title: 'Sync History',
    admin_repositories_synchistory_nohistory: 'No sync history available',
    admin_repositories_synchistory_status: 'Status',
    admin_repositories_synchistory_started: 'Started',
    admin_repositories_synchistory_duration: 'Duration',
    admin_repositories_synchistory_files: 'Files',
    admin_repositories_synchistory_details: 'Details',
    admin_repositories_synchistory_discovered: 'Discovered: {count}',
    admin_repositories_synchistory_processed: 'Processed: {count}',
    admin_repositories_synchistory_skipped: 'Skipped: {count}',
    admin_repositories_synchistory_errors: '{count, plural, one {# error} other {# errors}}',
    admin_repositories_filepicker_title: '{name} - Browse Files',
    admin_repositories_filepicker_subtitle: 'Select files to import and prepare for ingestion',
    admin_repositories_delete_title: 'Confirm Delete',
    admin_repositories_delete_confirm: 'Are you sure you want to delete the integration "{name}"?',
    admin_repositories_delete_warning: 'This will disconnect the repository and stop all syncing. This action cannot be undone.',
    admin_repositories_delete_button: 'Delete',
    admin_repositories_capability_readonly: "Read-only",
    admin_repositories_capability_readwrite: "Read & write",
    admin_repositories_enablewrite: "Enable write access",
    admin_repositories_writeaccess_connect_intro: "MetaLark connects to {name} with read-only access by default.",
    admin_repositories_writeaccess_intro: "Grant write access so MetaLark can export and migrate content into {name}.",
    admin_repositories_writeaccess_what: "Write access lets MetaLark upload and update files in the folders you choose, and write a migration manifest alongside them.",
    admin_repositories_writeaccess_reassure: "Write permission is only requested when you choose a feature that needs it — you can stay read-only and upgrade later.",
    admin_repositories_writeaccess_grantnow: "Also grant write access now (optional)",
    admin_repositories_disconnect_title: 'Disconnect Repository',
    admin_repositories_disconnect_confirm: 'Are you sure you want to disconnect "{name}"?',
    admin_repositories_disconnect_thisdoes: 'This will:',
    admin_repositories_disconnect_stopsyncing: 'Stop automatic syncing immediately',
    admin_repositories_disconnect_disconnectoauth: 'Disconnect the OAuth connection',
    admin_repositories_disconnect_notdelete: 'Not delete already synced files',
    admin_repositories_disconnect_button: 'Disconnect',
    admin_repositories_alert_default: 'Notice',
    admin_repositories_confirm: 'Confirm',
    admin_repositories_alert_loadfailed_title: 'Load Failed',
    admin_repositories_alert_loadfailed: 'Failed to load integrations: {error}',
    admin_repositories_alert_savefailed_title: 'Save Failed',
    admin_repositories_alert_savefailed: 'Failed to save integration: {error}',
    admin_repositories_alert_popupblocked_title: 'Popup Blocked',
    admin_repositories_alert_popupblocked: 'Please allow popups for this site and try again.',
    admin_repositories_alert_connectionfailed_title: 'Connection Failed',
    admin_repositories_alert_connectionfailed: 'Failed to connect: {error}',
    admin_repositories_alert_connectsuccess_title: 'Connection Successful',
    admin_repositories_alert_connectsuccess: 'Successfully connected to {name}!',
    admin_repositories_alert_securityfailed: 'Security verification failed',
    admin_repositories_alert_connected_title: 'Connected',
    admin_repositories_alert_connected: 'Successfully connected to {name}!',
    admin_repositories_alert_disconnected_title: 'Disconnected',
    admin_repositories_alert_disconnected: 'Successfully disconnected from "{name}".',
    admin_repositories_alert_disconnectfailed_title: 'Disconnect Failed',
    admin_repositories_alert_disconnectfailed: 'Failed to disconnect: {error}',
    admin_repositories_alert_testsuccess_title: 'Test Successful',
    admin_repositories_alert_testsuccess: 'Connection test successful!\n\n{message}',
    admin_repositories_alert_connectionverified: 'Connection verified',
    admin_repositories_alert_testfailed_title: 'Test Failed',
    admin_repositories_alert_testfailed: 'Connection test failed',
    admin_repositories_alert_testfailed_error: 'Test failed: {error}',
    admin_repositories_alert_syncstarted_title: 'Sync Started',
    admin_repositories_alert_syncstarted: 'Sync triggered successfully!\n\nJob ID: {jobId}',
    admin_repositories_alert_syncfailed_title: 'Sync Failed',
    admin_repositories_alert_syncfailed: 'Failed to trigger sync: {error}',
    admin_repositories_alert_historyfailed: 'Failed to load history: {error}',
    admin_repositories_alert_syncerrors_title: 'Sync Errors',
    admin_repositories_alert_syncerrors: 'Sync Errors:\n\n{errors}',
    admin_repositories_alert_filepickerfailed_title: 'File Picker Failed',
    admin_repositories_alert_filepickerfailed: 'Failed to open file picker: {error}',
    admin_repositories_alert_discoveryfailed_title: 'Discovery Failed',
    admin_repositories_alert_discoveryfailed: 'Failed to start discovery: {error}',
    admin_repositories_alert_reposelectioncleared: 'Repository selection was cleared. Please try again.',
    admin_repositories_alert_discovery_title: 'Discovery {status}',
    admin_repositories_alert_discovery: 'Discovery {status}!\n\nSelected files: {count}\nTotal size: {size}\n\n{message}',
    admin_repositories_discovery_completed: 'completed',
    admin_repositories_discovery_started: 'started',
    admin_repositories_alert_unknownerror: 'Unknown error occurred',
    admin_repositories_alert_deletefailed_title: 'Delete Failed',
    admin_repositories_alert_deletefailed: 'Failed to delete: {error}',
    admin_repositories_alert_s3connected_title: 'S3 Connected',
    admin_repositories_alert_s3connected: 'Successfully configured S3 repository!',
    admin_repositories_alert_s3connected_named: 'Successfully configured S3 repository "{name}"!',

    // Admin - Resend Email Modal
    admin_resendemail_title: 'Resend Email',
    admin_resendemail_about: 'You are about to resend the following email',
    admin_resendemail_recipient: 'Recipient',
    admin_resendemail_name: 'Name',
    admin_resendemail_subject: 'Subject',
    admin_resendemail_originalstatus: 'Original Status',
    admin_resendemail_template: 'Template',
    admin_resendemail_ratelimit: 'Rate Limit: Maximum 3 resends per hour per recipient',
    admin_resendemail_newentry: 'This will create a new email log entry with trigger type \'admin_resend\'',
    admin_resendemail_resending: 'Resending...',
    admin_resendemail_button: 'Resend Email',
    admin_resendemail_confirm: 'Are you sure you want to resend this email to {email}?',
    admin_resendemail_invaliddata: 'Invalid email log data',
    admin_resendemail_failed: 'Failed to resend email',
    admin_resendemail_ratelimit_exceeded: 'Rate limit exceeded. Maximum 3 resends per hour. Please wait before trying again.',

    // Admin - Change Role Modal
    admin_changerole_title: 'Change User Role',
    admin_changerole_about: 'You are about to change the role for',
    admin_changerole_user: 'User',
    admin_changerole_currentrole: 'Current Role',
    admin_changerole_newrole: 'New Role',
    admin_changerole_warning: 'Role changes are immediate and will affect user permissions',
    admin_changerole_button: 'Change Role',
    admin_changerole_confirm: 'Are you sure you want to change this user\'s role?',
    admin_changerole_invaliddata: 'Invalid user data',
    admin_changerole_tier_locked: 'This user holds an admin-tier role in another workspace — role assignments must stay on the same tier.',

    // Admin - Assign Role Modal
    admin_assignrole_title: 'Assign Role to User',
    admin_assignrole_description: 'Assign a role to a user within your authorized scope.',
    admin_assignrole_useruuid: 'User Email or UUID',
    admin_assignrole_useruuid_placeholder: 'user@example.com or user-uuid',
    admin_assignrole_useruuid_hint: 'Enter the email or UUID of the user',
    admin_assignrole_role: 'Role',
    admin_assignrole_selectrole: 'Select Role',
    admin_assignrole_role_hint: 'You can only assign roles at or below your level',
    admin_assignrole_customerid: 'Customer ID',
    admin_assignrole_customerid_placeholder: 'customer-id',
    admin_assignrole_customerid_hint: 'Required for customer-scoped roles',
    admin_assignrole_sliceid: 'Slice ID (optional)',
    admin_assignrole_sliceid_placeholder: 'slice-id',
    admin_assignrole_sliceid_hint: 'Optional - for slice-specific roles like TENANT_ADMIN',
    admin_assignrole_button: 'Assign Role',
    admin_assignrole_required: 'Please fill in all required fields',

    // Admin - File Picker Modal
    admin_filepicker_browsefiles: 'Browse Files',
    admin_filepicker_selectfiles: 'Select files to import and prepare for ingestion. You may optionally assign a collection to your files before importing.',
    admin_filepicker_selectfile: 'Select a file to import and prepare for ingestion.',
    admin_filepicker_whatdoes: 'What does "Import Files" do?',
    admin_filepicker_help_list: '<li><strong>Scans</strong> your selected files from the repository</li><li><strong>Prepares</strong> them for processing and ingestion</li><li><strong>Queues</strong> them for import into your workflow (auto-process or manual review)</li><li>Files will appear in <strong>Pending Files Review</strong> for final approval</li>',

    // Admin - Identity Provider Modal
    admin_idp_title_edit: 'Edit Identity Provider',
    admin_idp_title_create: 'Create Identity Provider',
    admin_idp_modal_subtitle: 'Configure authentication provider for your organization',
    admin_idp_displayname: 'Display Name',
    admin_idp_displayname_placeholder: 'e.g., Company Google OAuth',
    admin_idp_internalname: 'Internal Name (no spaces)',
    admin_idp_internalname_placeholder: 'e.g., company-google',
    admin_idp_providertype: 'Provider Type',
    admin_idp_selecttype: 'Select Type',
    admin_idp_type_cannot_change: 'Provider type cannot be changed after creation',
    admin_idp_scope_customer: 'Customer-wide',
    admin_idp_scope_slice: 'Slice-specific',
    admin_idp_sliceid: 'Slice ID',
    admin_idp_sliceid_placeholder: 'slice-id',
    admin_idp_description: 'Description (optional)',
    admin_idp_description_placeholder: 'Provider description',
    admin_idp_defaultprovider: 'Default Provider',
    admin_idp_config_info: 'Provider configuration (OAuth keys, SAML certificates, etc.) will be managed through AWS Cognito Console or Secrets Manager',
    admin_idp_button_update: 'Update Provider',
    admin_idp_button_create: 'Create Provider',

    // Admin - Impersonation Modal
    admin_impersonate_title: 'Begin Impersonation',
    admin_impersonate_about: 'You are about to impersonate the following workspace',
    admin_impersonate_slice: 'Workspace',
    admin_impersonate_id: 'ID',
    admin_impersonate_customer: 'Customer',
    admin_impersonate_duration: 'Duration (hours)',
    admin_impersonate_maxhours: 'Max: 8 hours',
    admin_impersonate_reason: 'Reason (recommended)',
    admin_impersonate_reason_placeholder: 'e.g., Support ticket #12345',
    admin_impersonate_ticket: 'Ticket Number (optional)',
    admin_impersonate_ticket_placeholder: 'e.g., TICKET-12345',
    admin_impersonate_starting: 'Starting...',
    admin_impersonate_button: 'Begin Impersonation',

    // Admin - Feature Flags
    admin_featureflags_title: 'Feature Flags',
    admin_featureflags_description: 'Control feature availability with targeting rules and progressive rollouts',
    admin_featureflags_statusguide: 'Status Guide',
    admin_featureflags_statusactive: 'Feature is ON and evaluated per targeting rules',
    admin_featureflags_statusinactive: 'Feature is OFF for everyone',
    admin_featureflags_statusarchived: 'No longer in use',
    admin_featureflags_createflag: 'Create Flag',
    admin_featureflags_totalflags: 'Total Flags',
    admin_featureflags_systemwidefeatures: 'System-wide features',
    admin_featureflags_activeflags: 'Active Flags',
    admin_featureflags_percentactive: '{percent}% active',
    admin_featureflags_permanent: 'Permanent',
    admin_featureflags_accountrestrictions: 'Account restrictions',
    admin_featureflags_development: 'Development',
    admin_featureflags_temporaryflags: 'Temporary flags',
    admin_featureflags_key: 'Key',
    admin_featureflags_targeting: 'Targeting',
    admin_featureflags_rolloutpercent: 'Rollout %',
    admin_featureflags_loadingflags: 'Loading flags',
    admin_featureflags_noflags: 'No feature flags found',
    admin_featureflags_accesssummary: 'Access Summary',
    admin_featureflags_targetingrules: 'Targeting Rules',
    admin_featureflags_accessoverrides: 'Access Overrides',
    admin_featureflags_addoverride: 'Add Override',
    admin_featureflags_nodescription: 'No description',
    admin_featureflags_rollout: 'Rollout',
    admin_featureflags_displayname: 'Display Name',
    admin_featureflags_progressiverollout: 'Progressive Rollout',
    admin_featureflags_currentoverrides: 'Current Overrides',
    admin_featureflags_lastupdated: 'Last Updated',
    admin_featureflags_modalcreate: 'Create Feature Flag',
    admin_featureflags_modaledit: 'Edit Feature Flag',
    admin_featureflags_modalview: 'Feature Flag Details',
    admin_featureflags_edit: 'Edit',
    admin_featureflags_toggle: 'Toggle',
    admin_featureflags_customer: 'Customer',
    admin_featureflags_user: 'User',
    admin_featureflags_searchcustomers: 'Search customers...',
    admin_featureflags_searchusers: 'Search users...',
    admin_featureflags_enable: 'Enable',
    admin_featureflags_disable: 'Disable',
    admin_featureflags_overridehelp: 'Add explicit access control for specific customers or users. Overrides take precedence over all other rules. Type to search or enter ID directly.',
    admin_featureflags_notypesfound: 'No {type}s found',
    admin_featureflags_enabledcustomers: 'Enabled Customers ({count})',
    admin_featureflags_noenabledcustomers: 'No enabled customer overrides',
    admin_featureflags_disabledcustomers: 'Disabled Customers ({count})',
    admin_featureflags_nodisabledcustomers: 'No disabled customer overrides',
    admin_featureflags_enabledusers: 'Enabled Users ({count})',
    admin_featureflags_noenabledusers: 'No enabled user overrides',
    admin_featureflags_disabledusers: 'Disabled Users ({count})',
    admin_featureflags_nodisabledusers: 'No disabled user overrides',
    admin_featureflags_created: 'Created:',
    admin_featureflags_by: 'by',
    admin_featureflags_keyrequired: 'Key *',
    admin_featureflags_keyhelp: 'Use snake_case (e.g., my_feature)',
    admin_featureflags_descriptionplaceholder: 'Describe what this flag controls...',
    admin_featureflags_typepermanent: 'Permanent',
    admin_featureflags_typedevelopment: 'Development',
    admin_featureflags_typerollout: 'Rollout',
    admin_featureflags_typedescrpermanent: 'Account restrictions',
    admin_featureflags_typedescrdevelopment: 'Temporary flag',
    admin_featureflags_typedescrrollout: 'Progressive release',
    admin_featureflags_statusinactive_option: 'Inactive',
    admin_featureflags_statusactive_option: 'Active',
    admin_featureflags_statusarchived_option: 'Archived',
    admin_featureflags_rollouthelp: 'Gradually roll out to {percent}% of users. Hash-based distribution ensures consistency.',
    admin_featureflags_help: 'Help',
    admin_featureflags_targetingformat: 'JSON format. Leave empty for no targeting restrictions.',
    admin_featureflags_overridesummary_enabled: 'Enabled:',
    admin_featureflags_overridesummary_overrides: 'override(s)',
    admin_featureflags_overridesummary_disabled: 'Disabled:',
    admin_featureflags_overridesummary_help: 'Manage detailed overrides after saving by viewing the flag',
    admin_featureflags_savesuccess: 'Flag saved successfully!',
    admin_featureflags_cancel: 'Cancel',
    admin_featureflags_saving: 'Saving...',
    admin_featureflags_savechanges: 'Save Changes',
    admin_featureflags_createflag_button: 'Create Flag',
    admin_featureflags_close: 'Close',
    admin_featureflags_targetinghelp_title: 'Targeting Rules',
    admin_featureflags_targetinghelp_description: 'Targeting rules determine which users can access a feature:',
    admin_featureflags_targetinghelp_accounttypes: 'Account Types:',
    admin_featureflags_targetinghelp_use: 'Use',
    admin_featureflags_targetinghelp_subscriptionstatuses: 'Subscription Statuses:',
    admin_featureflags_targetinghelp_userroles: 'User Roles:',
    admin_featureflags_targetinghelp_combined: 'Combined Example:',
    admin_featureflags_ok: 'OK',
    admin_featureflags_confirm: 'Confirm',
    admin_featureflags_accessdenied_title: 'Access Denied',
    admin_featureflags_accessdenied_message: 'Feature flag management requires SYSTEM_ADMIN role.',
    admin_featureflags_loadfailed_title: 'Load Failed',
    admin_featureflags_loadfailed_message: 'Failed to load feature flags:',
    admin_featureflags_togglestatus_title: 'Toggle Flag Status',
    admin_featureflags_togglestatus_message: 'Change {name} status to {status}?',
    admin_featureflags_togglestatus_success: 'Flag {name} is now {status}',
    admin_featureflags_togglestatus_change: 'Change',
    admin_featureflags_norestrictions: 'No restrictions',
    admin_featureflags_customrules: 'Custom rules',
    admin_featureflags_noflag_title: 'No Flag',
    admin_featureflags_noflag_message: 'Please create or edit a flag first',
    admin_featureflags_targetingrule_applied: 'Targeting rule "{type}" applied',
    admin_featureflags_invalidexample_title: 'Invalid Example',
    admin_featureflags_invalidexample_message: 'Failed to parse example: {error}',
    admin_featureflags_inputrequired_title: 'Input Required',
    admin_featureflags_inputrequired_message: 'Please enter a customer ID or user UUID',
    admin_featureflags_overrideadded: 'Override added: {action} {type}',
    admin_featureflags_error: 'Error',
    admin_featureflags_overrideadd_failed: 'Failed to add override:',
    admin_featureflags_removeoverride_title: 'Remove Override',
    admin_featureflags_removeoverride_message: 'Remove override for {type} {id}?',
    admin_featureflags_removeoverride_success: 'Override removed successfully',
    admin_featureflags_removeoverride_failed: 'Failed to remove override:',
    admin_featureflags_removeoverride_button: 'Remove',
    admin_featureflags_swapoverride_success: 'Override {action} successfully',
    admin_featureflags_swapoverride_failed: 'Failed to swap override:',
    admin_featureflags_togglemodal_title: 'Toggle Flag Status',
    admin_featureflags_togglemodal_failed: 'Failed to toggle flag:',
    admin_featureflags_accesssummary_disabled: 'Feature is completely disabled for all users. Toggle status to "active" to enable evaluation.',
    admin_featureflags_accesssummary_archived: 'Feature is archived and disabled for all users. This flag is kept for historical records only.',
    admin_featureflags_accesssummary_enabledfor: 'Explicitly enabled for {count} override(s)',
    admin_featureflags_accesssummary_disabledfor: 'Explicitly disabled for {count} override(s)',
    admin_featureflags_accesssummary_restricted: 'Restricted to: {restrictions}',
    admin_featureflags_accesssummary_rollout_zero: 'Rollout at 0% - no users in rollout (except overrides)',
    admin_featureflags_accesssummary_rollout_progressive: 'Progressive rollout: {percent}% of eligible users',
    admin_featureflags_accesssummary_all: 'Feature is enabled for ALL users without restrictions.',
    admin_featureflags_accesssummary_default: 'Feature is enabled for all users who match the targeting criteria.',
    admin_featureflags_accesssummary_warning: 'Enabled overrides must also match targeting restrictions to access feature.',

    // Admin - Notification Dashboard
    admin_notificationdashboard_title: 'Scheduled Notification Dashboard',
    admin_notificationdashboard_testcondition: 'Test Condition',
    admin_notificationdashboard_scheduled: 'Scheduled',
    admin_notificationdashboard_sent24h: 'Sent (24h)',
    admin_notificationdashboard_failed24h: 'Failed (24h)',
    admin_notificationdashboard_skipped24h: 'Skipped (24h)',
    admin_notificationdashboard_upcomingtitle: 'Upcoming Notifications (Next 7 Days)',
    admin_notificationdashboard_scheduledfor: 'Scheduled For',
    admin_notificationdashboard_customerid: 'Customer ID',
    admin_notificationdashboard_recipient: 'Recipient',
    admin_notificationdashboard_template: 'Template',
    admin_notificationdashboard_condition: 'Condition',
    admin_notificationdashboard_none: 'None',
    admin_notificationdashboard_noupcoming: 'No upcoming notifications',
    admin_notificationdashboard_failedtitle: 'Failed Notifications (Last 24h)',
    admin_notificationdashboard_retries: 'Retries',
    admin_notificationdashboard_reason: 'Reason',
    admin_notificationdashboard_unknown: 'Unknown',
    admin_notificationdashboard_recenttitle: 'Recent Activity (Last 24h)',
    admin_notificationdashboard_status: 'Status',
    admin_notificationdashboard_sent: 'Sent',
    admin_notificationdashboard_customer: 'Customer',
    admin_notificationdashboard_viewdetails: 'View email details',
    admin_notificationdashboard_view: 'View',
    admin_notificationdashboard_testmodaltitle: 'Test Notification Condition',
    admin_notificationdashboard_conditiontype: 'Condition Type',
    admin_notificationdashboard_selectcondition: 'Select condition type...',
    admin_notificationdashboard_loadingcustomers: 'Loading customers...',
    admin_notificationdashboard_selectcustomer: 'Select a customer...',
    admin_notificationdashboard_foundmatching: 'Found {count} matching customers',
    admin_notificationdashboard_andmore: '... and {count} more',
    admin_notificationdashboard_testing: 'Testing...',
    admin_notificationdashboard_failedload: 'Failed to load dashboard data',
    admin_notificationdashboard_failedconditiontypes: 'Failed to load condition types',
    admin_notificationdashboard_failedtestcondition: 'Failed to test condition',
    admin_notificationdashboard_confirmcancel: 'Are you sure you want to cancel this notification?',
    admin_notificationdashboard_canceledbyadmin: 'Canceled by admin',
    admin_notificationdashboard_failedcancel: 'Failed to cancel notification',
    admin_notificationdashboard_invalid: 'Invalid',

    // Page Titles (browser tabs)
    pagetitle_home: 'Home',
    pagetitle_login: 'Login',
    pagetitle_selectworkspace: 'Select Workspace',
    pagetitle_settings: 'Settings',
    pagetitle_skills: 'Skills',
    pagetitle_skilldetails: 'Skill Details',
    pagetitle_content: 'Content Console',
    pagetitle_collection: 'Collection',
    pagetitle_reports: 'Reports',
    pagetitle_report: 'Report',
    pagetitle_export: 'Metadata Console',
    pagetitle_upload: 'Upload',
    pagetitle_summary: 'Summary',
    pagetitle_item: 'Item',
    pagetitle_deepsearch: 'Deep Search',
    pagetitle_migration: 'Migration',
    pagetitle_bulkedit: 'Bulk Edit',
    pagetitle_verifyemail: 'Verify Email',
    pagetitle_resetpassword: 'Reset Password',
    pagetitle_forgotpassword: 'Forgot Password',
    pagetitle_trialsignup: 'Trial Signup',
    pagetitle_trialcreated: 'Trial Created',
    pagetitle_users: 'Users',
    pagetitle_customers: 'Customers',
    pagetitle_customer360: 'Customer View',
    pagetitle_auditlogs: 'Audit Logs',
    pagetitle_emaillogs: 'Email Logs',
    pagetitle_emailtemplates: 'Email Templates',
    pagetitle_emailsuppressionlist: 'Email Suppression List',
    pagetitle_billingsubscription: 'Billing & Subscription',
    pagetitle_purchasecredits: 'Purchase Credits',
    pagetitle_notfound: 'Page Not Found',
    pagetitle_releasenotes: 'Release Notes',
    pagetitle_lmsintegration: 'LMS Integration',
    pagetitle_userguide: 'User Guide',
    pagetitle_externalplayer: 'External Player Launch Guide',
    pagetitle_customertags: 'Customer Tags',
    pagetitle_customerskills: 'Customer Skills',
    pagetitle_computedattributeconvention: 'Computed Attribute Convention',
    pagetitle_sessionexpired: 'Session Expired',
    pagetitle_loggedout: 'Logged Out',

    // Custom Tags Admin Page
    customertags_title: 'Customer Tags',
    customertags_scansremaining: '{count} of {max} Scans Remaining ({year})',
    customertags_lastrescan: 'Last Rescan: {date}',
    customertags_scanning: 'Scanning...',
    customertags_rescan_background_note: 'You can navigate away — the rescan will continue in the background.',
    customertags_rescancontent: 'Rescan Content',
    customertags_importcsv: 'Import CSV',
    customertags_newtag: 'New Tag',
    customertags_edittag: 'Edit Tag',
    customertags_deletetag: 'Delete Tag',
    customertags_createnewtag: 'Create New Tag',
    customertags_tagname: 'Tag Name',
    customertags_tagdescription: 'Tag Description',
    customertags_entertagname: 'Enter a tag name',
    customertags_entertagdescription: 'Enter a tag description',
    customertags_tagnamerequired: 'Tag name is required',
    customertags_tagdescriptionrequired: 'Tag description is required',
    customertags_createtag: 'Create Tag',
    customertags_deletemessage: 'The tag "{name}" will be permanently deleted.',
    customertags_deletelinked: 'This tag is linked to {count} resource(s).',
    customertags_importfromcsv: 'Import Tags from CSV',
    customertags_importinstructions: 'Upload a CSV file to import or update tags. The CSV should have the following columns:',
    customertags_importcolumns: '<li><strong>tagId</strong> (optional) - If provided, the tag will be updated. Leave blank to create a new tag.</li><li><strong>displayName</strong> (required) - The name of the tag.</li><li><strong>description</strong> (required) - A description of the tag.</li>',
    customertags_selectcsvfile: 'Select CSV File',
    customertags_downloadsamplecsv: 'Download Sample CSV',
    customertags_uploadcsv: 'Upload CSV',
    customertags_selectcsvfileerror: 'Please select a CSV file',
    customertags_importerror: 'Failed to import CSV. Please check the file format and try again.',
    customertags_createerror: 'Failed to create tag. Please try again.',
    customertags_updateerror: 'Failed to update tag. Please try again.',
    customertags_deleteerror: 'Failed to delete tag. Please try again.',
    customertags_scanerror: 'Failed to start content scan. Please try again.',
    customertags_scanlimitreached: 'Annual tag scan limit reached.',

    // Customer Skills Admin Page
    customerskills_title: 'Customer Skills',
    customerskills_scansremaining: '{count} of {max} Scans Remaining ({year})',
    customerskills_lastrescan: 'Last Rescan: {date}',
    customerskills_scanning: 'Scanning...',
    customerskills_rescan_background_note: 'You can navigate away — the rescan will continue in the background.',
    customerskills_rescancontent: 'Rescan Content',
    customerskills_importcsv: 'Import CSV',
    customerskills_newskill: 'New Skill',
    customerskills_exportcustomerskillsdata: 'Export Customer Skills Data',
    customerskills_editskill: 'Edit Skill',
    customerskills_deleteskill: 'Delete Skill',
    customerskills_createnewskill: 'Create New Skill',
    customerskills_skillname: 'Skill Name',
    customerskills_skilldescription: 'Skill Description',
    customerskills_enterskillname: 'Enter a skill name',
    customerskills_enterskilldescription: 'Enter a skill description',
    customerskills_skillnamerequired: 'Skill name is required',
    customerskills_skilldescriptionrequired: 'Skill description is required',
    customerskills_createskill: 'Create Skill',
    customerskills_deletemessage: 'The skill "{name}" will be permanently deleted.',
    customerskills_deletelinked: 'This skill is linked to {count} resource(s).',
    customerskills_importfromcsv: 'Import Skills from CSV',
    customerskills_importinstructions: 'Upload a CSV file to import or update skills. The CSV should have the following columns:',
    customerskills_importcolumns: '<li><strong>skillId</strong> (optional) - If provided, the skill will be updated. Leave blank to create a new skill.</li><li><strong>displayName</strong> (required) - The name of the skill.</li><li><strong>description</strong> (required) - A description of the skill.</li>',
    customerskills_selectcsvfile: 'Select CSV File',
    customerskills_downloadsamplecsv: 'Download Sample CSV',
    customerskills_uploadcsv: 'Upload CSV',
    customerskills_selectcsvfileerror: 'Please select a CSV file',
    customerskills_importerror: 'Failed to import CSV. Please check the file format and try again.',
    customerskills_createerror: 'Failed to create skill. Please try again.',
    customerskills_updateerror: 'Failed to update skill. Please try again.',
    customerskills_deleteerror: 'Failed to delete skill. Please try again.',
    customerskills_scanerror: 'Failed to start content scan. Please try again.',
    customerskills_scanlimitreached: 'Annual skill scan limit reached.',

    // Computed Attribute Convention builder (METALARK-1108)
    computedattributeconvention_title: 'Computed Attribute Convention',
    computedattributeconvention_section_pattern: 'Pattern',
    computedattributeconvention_section_applyto: 'Apply pattern to',
    computedattributeconvention_section_emptyattr: 'When an attribute is empty',
    computedattributeconvention_section_availablevalues: 'Available Values',
    computedattributeconvention_section_livepreview: 'Live preview',
    computedattributeconvention_group_resourceattributes: 'Resource Attributes',
    computedattributeconvention_group_basefields: 'Base Fields',
    computedattributeconvention_group_custom: 'Custom',
    computedattributeconvention_separator_label: 'Separator',
    computedattributeconvention_separator_dash: 'Dash',
    computedattributeconvention_separator_pipe: 'Pipe',
    computedattributeconvention_separator_underscore: 'Underscore',
    computedattributeconvention_separator_space: 'Space',
    computedattributeconvention_separator_custom: 'Custom',
    computedattributeconvention_separator_custom_placeholder: 'e.g. &',
    computedattributeconvention_separator_custom_label: 'Custom separator',
    computedattributeconvention_target_title: 'Title',
    computedattributeconvention_titlewarning: 'Saving will overwrite existing item titles, including titles a user has manually edited or approved from an AI suggestion.',
    computedattributeconvention_target_attribute: 'Attribute',
    computedattributeconvention_attributepicker_placeholder: 'Select an attribute',
    computedattributeconvention_attributepicker_label: 'Target attribute',
    computedattributeconvention_computedhint: 'The generated value is written to this computed attribute, which is read-only wherever else it appears.',
    computedattributeconvention_createcomputed: 'Create Computed Attribute',
    computedattributeconvention_createcomputed_title: 'Create Computed Attribute',
    computedattributeconvention_createcomputed_help: 'This attribute holds the value the computed attribute convention generates.\nIts value cannot be edited manually.',
    computedattributeconvention_createcomputed_name: 'Name',
    computedattributeconvention_createcomputed_label: 'Label',
    computedattributeconvention_createcomputed_description: 'Description',
    computedattributeconvention_createcomputed_save: 'Create',
    computedattributeconvention_createcomputed_required: 'Name and label are required.',
    computedattributeconvention_createcomputed_error: 'Couldn\'t create the attribute. Please try again.',
    computedattributeconvention_texttypehint: 'Only text-input attributes can be targeted.',
    computedattributeconvention_attributewarning: 'Saving will overwrite the existing values for this attribute on every resource.',
    computedattributeconvention_existing_attr: 'Existing {label}',
    computedattributeconvention_generated_attr: 'Generated {label}',
    computedattributeconvention_confirm_field_attribute: 'The {label} value of every matching resource will be overwritten.',
    computedattributeconvention_confirm_allitems: 'This computed attribute convention will be applied to all existing and future content items in this workspace.',
    computedattributeconvention_confirm_irreversible: 'This change cannot be reversed. There is no undo, and no rollback of the re-apply run.',
    computedattributeconvention_confirm_acknowledge: 'I understand this will overwrite content and cannot be undone.',
    computedattributeconvention_empty_skip: 'Skip it — leave the gap out',
    computedattributeconvention_empty_fallback: 'Use a default value',
    computedattributeconvention_defaults_sublabel: 'Default when empty',
    computedattributeconvention_default_placeholder: 'General',
    computedattributeconvention_default_aria: 'Default value for {label}',
    computedattributeconvention_basefield_uploadyear: 'Upload Year',
    computedattributeconvention_basefield_contenttype: 'Content Type',
    computedattributeconvention_customtext: 'Custom text',
    computedattributeconvention_customtext_placeholder: 'text',
    computedattributeconvention_customtext_label: 'Custom text value',
    computedattributeconvention_noattributes: 'No resource attributes are defined for this workspace.',
    computedattributeconvention_manageattributes: 'Manage Customer Attributes',
    computedattributeconvention_addtoken: 'Add {label} to pattern',
    computedattributeconvention_removetoken: 'Remove {label}',
    computedattributeconvention_emptypattern: 'No attributes yet',
    computedattributeconvention_samplecount: '{count, plural, one {# Sample Resource} other {# Sample Resources}}',
    computedattributeconvention_existingtitle: 'Existing Title',
    computedattributeconvention_generatedtitle: 'Generated Title',
    computedattributeconvention_badge_updated: 'Updated',
    computedattributeconvention_badge_unchanged: 'Unchanged',
    computedattributeconvention_badge_novalue: 'No value',
    computedattributeconvention_save: 'Save Convention',
    computedattributeconvention_confirm_title: 'Save computed attribute convention?',
    computedattributeconvention_confirm_count: '{count, plural, one {The # resource in this workspace will be checked and updated if its generated value changes.} other {All # resources in this workspace will be checked and updated where the generated value changes.}}',
    computedattributeconvention_confirm_field_title: 'The Title of every matching resource will be overwritten.',
    computedattributeconvention_confirm_future: 'This convention also applies to future uploads and re-applies when a participating attribute is added or edited. Both are picked up by a daily run, so a value can take up to 24 hours to update.',
    computedattributeconvention_confirm_apply: 'Save & Apply',
    computedattributeconvention_applying: 'Applying...',
    computedattributeconvention_runstatus_running: 'Applying to existing resources…',
    computedattributeconvention_runstatus_completed: 'Apply run completed.',
    computedattributeconvention_runstatus_summary: '{updated} updated · {unchanged} unchanged · {novalue} with no value',
    computedattributeconvention_run_background_note: 'You can navigate away. The run will continue in the background.',
    computedattributeconvention_lastapplied: 'Last applied: {date}',
    computedattributeconvention_loaderror: 'Failed to load the computed attribute convention. Please try again.',
    computedattributeconvention_saveerror: 'Failed to save the computed attribute convention. Please try again.',
    computedattributeconvention_previewerror: 'Failed to generate the preview. Please try again.',

    // Attribute Manager Page
    attributemanager_title: 'Customer Attributes',
    // Shared attribute keys (definition + computed, used across attribute surfaces)
    attribute_key_label: 'Key',
    attribute_computed_badge: 'Computed',
    attribute_computed_locktip: 'This value is generated by a computed attribute convention and cannot be edited.',
    attribute_computed_configlocktip: 'The value of this computed attribute is generated by the Computed Attribute Convention, so its configuration cannot be edited.',
    attribute_computed_reserved: 'That name belongs to a computed attribute, which is read-only.',
    attributemanager_searchplaceholder: 'Search attributes...',
    attributemanager_addattribute: 'Add Attribute',
    attributemanager_exportattributesdata: 'Export Attributes Data',
    attributemanager_emptytitle: 'No attribute definitions found',
    attributemanager_emptydescription: 'Create attribute definitions to add custom metadata fields to your resources.',
    attributemanager_createfirst: 'Create Your First Attribute',
    attributemanager_typeselect: 'Select List',
    attributemanager_typetext: 'Text Input',
    attributemanager_deletetitle: 'Delete Attribute',
    attributemanager_deletemessage: 'Are you sure you want to delete "{name}"? This cannot be undone.',
    attributemanager_edittitle: 'Edit Attribute',
    attributemanager_createtitle: 'Create Attribute',
    attributemanager_nameplaceholder: 'e.g. department',
    attributemanager_label: 'Label',
    attributemanager_labelplaceholder: 'e.g. Department',
    attributemanager_descriptionplaceholder: 'Describe this attribute...',
    attributemanager_typelabel: 'Attribute Type',
    attributemanager_options: 'Options',
    attributemanager_optionplaceholder: 'Option value',
    attributemanager_addoption: 'Add Option',
    attributemanager_save: 'Save Attribute',
    attributemanager_savefailed: 'Failed to save attribute',

    // Resource Attributes (per-resource attribute editor on item detail)
    resourceattributes_noattributes: 'No attributes associated with this item',
    resourceattributes_edittoaddhint: 'Edit Details to add attributes',
    resourceattributes_addbuttonhint: 'Use the Add button below to add attributes',
    resourceattributes_addcustomvalue: '+ Add Custom Value',
    resourceattributes_nodefinitions: 'No definitions available',
    resourceattributes_custommenuitem: '+ Custom Attribute',
    resourceattributes_modaltitle: 'Add Custom Attribute',
    resourceattributes_value: 'Value',
    resourceattributes_valueplaceholder: 'Enter value...',
    resourceattributes_duplicateerror: 'An attribute with this name already exists on this item.',
    resourceattributes_attributestab: 'Attributes',
    resourceattributes_addattribute: 'Add Attribute',
    resourceattributes_nameplaceholder: 'e.g. department',
    resourceattributes_label: 'Label',
    resourceattributes_labelplaceholder: 'e.g. Department',

    // Job status values (raw API values: queued, pending, pending_upload, running, completed, failed)
    jobstatus_queued: 'Queued',
    jobstatus_pending: 'Pending',
    jobstatus_pending_upload: 'Pending Upload',
    jobstatus_running: 'Running',
    jobstatus_completed: 'Completed',
    jobstatus_failed: 'Failed',
    jobstatus_review: 'Review',

    // Launch Token Tester (SYSTEM_ADMIN modal)
    launchtoken_title: 'Launch Token Tester',
    launchtoken_desc_insights: 'Generate a test insights launch token (slice-wide dashboard access).',
    launchtoken_desc_player: 'Generate a test LMS launch token and preview the player flow.',
    launchtoken_tokentype: 'Token Type',
    launchtoken_player: 'Player',
    launchtoken_insights: 'Insights',
    launchtoken_randomdata_info: 'Form is pre-filled with random test data. Click Random Data to shuffle.',
    launchtoken_resource: 'Resource',
    launchtoken_loadingresources: 'Loading resources…',
    launchtoken_searchresources: 'Search resources by name or ID…',
    launchtoken_noresources: 'No resources available',
    launchtoken_noresources_hint: 'Make sure the API is running.',
    launchtoken_nomatching: 'No matching resources found.',
    launchtoken_endusername: 'End User Name',
    launchtoken_enduseremail: 'End User Email',
    launchtoken_enduserid: 'End User ID',
    launchtoken_enduserlogin: 'End User Login',
    launchtoken_expires_minutes: 'Expires In (minutes)',
    launchtoken_min_short: '{count} min',
    launchtoken_randomdata: 'Random Data',
    launchtoken_generating: 'Generating…',
    launchtoken_generatetoken: 'Generate Token',
    launchtoken_insights_generated: 'Insights launch token generated successfully.',
    launchtoken_player_generated: 'Launch token generated successfully.',
    launchtoken_token_label: 'Token',
    launchtoken_showtoken: 'Show token',
    launchtoken_hidetoken: 'Hide token',
    launchtoken_copytoken: 'Copy token',
    launchtoken_launchurl: 'Launch URL',
    launchtoken_copyurl: 'Copy URL',
    launchtoken_expiresat: 'Expires At',
    launchtoken_sliceid: 'Slice ID',
    launchtoken_generateanother: 'Generate Another',
    launchtoken_launch_insights: 'Launch Insights',
    launchtoken_launch_player: 'Launch Player',

    // Verified Domains admin page / customer-360 tab
    verifieddomains_description: 'Prove ownership of your organization\'s email domains via DNS TXT records. Verified domains are required for JIT (Just-In-Time) user provisioning.',
    verifieddomains_adddomain: 'Add Domain',
    verifieddomains_empty_title: 'No verified domains yet',
    verifieddomains_empty_hint: 'Add your organization\'s email domain to enable JIT provisioning.',
    verifieddomains_verifiedat: 'Verified {date}',
    verifieddomains_show_dns_instructions: 'Show DNS instructions',
    verifieddomains_dns_record: 'DNS record',
    verifieddomains_verify: 'Verify',
    verifieddomains_remove_domain: 'Remove domain',
    verifieddomains_modal_addtitle: 'Add Email Domain',
    verifieddomains_emaildomain: 'Email domain',
    verifieddomains_dns_instruction_intro: 'Add the following DNS TXT record to prove ownership of {domain}.',
    verifieddomains_recordtype: 'Record type:',
    verifieddomains_host: 'Host:',
    verifieddomains_value: 'Value:',
    verifieddomains_copyvalue: 'Copy value',
    verifieddomains_publicly_queryable: 'This TXT record is publicly queryable. If you need a non-DNS verification method, contact support.',
    verifieddomains_done: 'Done',
    verifieddomains_addedrecord_verify: 'I\'ve added the record — Verify now',
    verifieddomains_dns_instructions_title: 'DNS Verification Instructions',
    verifieddomains_dns_zone_for: 'Add this TXT record to your DNS zone for {domain}:',
    verifieddomains_dns_zone_keep: 'Keep this TXT record in your DNS zone for {domain} to stay verified:',
    verifieddomains_status_verified: 'Verified',
    verifieddomains_status_failed: 'Failed',
    verifieddomains_status_pending: 'Pending',
    verifieddomains_error_load: 'Failed to load domains',
    verifieddomains_error_enterdomain: 'Enter a domain',
    verifieddomains_error_add: 'Failed to add domain',
    verifieddomains_error_verify: 'Verification failed',
    verifieddomains_verify_success: 'Domain {domain} verified successfully.',
    verifieddomains_confirm_remove: 'Remove {domain}? This cannot be undone.',
    verifieddomains_error_delete: 'Failed to delete domain',

    // Support page titles (architecture is the only one missing)
    support_architecture_title: 'Architecture Summary',

    // Tag Table
    tagtable_tagname: 'Tag Name',
    tagtable_itemcount: 'Item Count',
    tagtable_createddate: 'Created Date',
    tagtable_updateddate: 'Updated Date',
    tagtable_exporttagsdata: 'Export Tags Data',
    tagtable_notagsfound: 'No Tags Found',

    // Tag Content Table
    tagcontent_itemname: 'Item Name',
    tagcontent_reason: 'Reason',
    tagcontent_confidence: 'Confidence',
    tagcontent_type: 'Type',
    tagcontent_nocontentfound: 'No Content Found',

    // Customer Tag Detail Page
    customertag_customertaglabel: 'Customer Tag',
    customertag_created: 'Created',
    customertag_totalitems: 'Total Items',

    // Credits Page
    credits_loading: 'Loading credit information...',
    credits_title: 'Purchase Processing Credits',
    credits_description: 'Processing content in MetaLark requires credits. Credits are used for each newly uploaded content item and when an existing content item is uploaded again (in the case of significant updates for example). If processing fails for any reason, the credit is returned to your account.',
    credits_available_now: 'Available Now',
    credits_label: 'credits',
    credits_recent_usage: 'Recent Usage (7 days)',
    credits_used: 'credits used',
    credits_pending: '{count} Credits Pending',
    credits_bank_transfer_notice: 'Bank transfer processing - Available in 3-5 business days',
    credits_pending_purchases: 'Pending Credit Purchases',
    credits_pending_alert_title: 'Bank transfer payments typically take 3-5 business days to clear.',
    credits_pending_alert_description: 'Your credits will be automatically added to your account once the payment is confirmed. We\'ll send you an email notification when they\'re available.',
    credits_bundle_count: '{count} credits',
    credits_purchased: 'Purchased',
    credits_processing: 'Processing',
    credits_subscription_required: 'Active Subscription Required',
    credits_subscription_have_credits: 'You have {count} credits, but need an active subscription to use them.',
    credits_subscription_need_subscription: 'You need an active subscription to purchase and use credits.',
    credits_view_subscription: 'View Subscription',
    credits_choose_amount: 'Choose Amount',
    credits_number_of_credits: 'Number of Credits',
    credits_enter_quantity_placeholder: 'Enter quantity (min: {min})',
    credits_minimum_purchase: 'Minimum purchase',
    credits_bank_transfer_required: 'Bank Transfer Required',
    credits_price_per_credit: 'Price per Credit',
    credits_save_percent: 'Save {percent}% vs. base price',
    credits_total_price: 'Total Price',
    credits_payment_methods: 'Payment methods',
    credits_payment_bank_transfer: 'Bank transfer (ACH) or purchase order',
    credits_payment_all_methods: 'Credit card, bank transfer, or purchase order',
    credits_request_purchase_order: 'Request Purchase Order',
    credits_btn_processing: 'Processing...',
    credits_btn_purchase: 'Purchase {count} Credits',
    credits_btn_enter_quantity: 'Enter Quantity',
    credits_pricing_guide: 'Pricing Guide',
    credits_table_range: 'Credit Amount Range',
    credits_table_cost_per: 'Cost per Credit',
    credits_table_savings: 'Savings',
    credits_table_save_badge: 'SAVE {percent}%',
    credits_enterprise_title: 'Enterprise Pricing',
    credits_enterprise_description: 'Need 200,000+ credits? Contact our sales team for custom enterprise pricing and payment options including Purchase Orders.',
    credits_enterprise_benefits_label: 'Enterprise benefits',
    credits_enterprise_benefits: 'Volume discounts up to 50% • Flexible payment terms • Dedicated support',
    credits_request_quote: 'Request Quote',
    credits_recent_usage_title: 'Recent Usage',
    credits_po_modal_title: 'Purchase Order Request',
    credits_po_modal_description: 'We\'re happy to accept purchase orders for standard purchases. For contracts or agreement-based requests, please contact our Sales team at sales@metalark.ai directly to assist you.',
    credits_company_name: 'Company Name',
    credits_enter_name_placeholder: 'Enter your name',
    credits_enter_company_placeholder: 'Enter company name',
    credits_send_purchase_order: 'Send Purchase Order',
    credits_purchase_success: 'Credit purchase successful! Your credits have been added to your account.',
    credits_quota_exhausted: 'Your credit balance is exhausted. Purchase more credits to continue extracting content.',
    credits_error_load: 'Failed to load credit information. Please refresh the page.',
    credits_bank_transfer_message: 'Transactions over ${limit} require bank transfer to avoid high credit card fees. Credit cards are only available for the lowest pricing tier.',
    credits_error_minimum: 'Minimum purchase is {min} credits.',
    credits_confirm_bank_transfer: 'This purchase requires bank transfer (ACH) payment. Credit cards are not available for purchases over ${limit}.\n\nDo you want to proceed?',
    credits_bank_transfer_title: 'Bank transfer required',
    credits_bank_transfer_over_limit: 'Purchases over {limit} must use bank transfer (ACH). Credit cards aren\'t available above this threshold because card processing fees are disproportionate at higher amounts.',
    credits_bank_transfer_credits: 'Credits',
    credits_bank_transfer_amount: 'Total',
    credits_bank_transfer_method: 'Method',
    credits_bank_transfer_ach: 'Bank transfer (ACH)',
    credits_bank_transfer_timing: 'Credits are applied once funds clear — typically 3–5 business days after the transfer initiates. You\'ll receive a confirmation email at each step.',
    credits_bank_transfer_continue: 'Continue to checkout',
    credits_error_checkout: 'Failed to create checkout session. Please try again.',
    credits_email_enterprise_subject: 'Enterprise Credit Purchase Inquiry',
    credits_email_enterprise_body: 'I am interested in purchasing 50,000+ credits. Please contact me with enterprise pricing options.',
    credits_email_po_subject: 'Purchase Order Request - Processing Credits',
    credits_email_po_title: 'Purchase Order Request',
    credits_email_pricing_details: 'Pricing Details',
    credits_email_po_footer: 'Please send a purchase order for the above credit quantity.',
    credits_email_thank_you: 'Thank you!',

    // S3 Credentials Form (admin/integrations)
    admin_s3_title_update: 'Update S3 Credentials',
    admin_s3_title_config: 'AWS S3 Repository Configuration',
    admin_s3_subtitle_update: 'Update your AWS S3 bucket credentials',
    admin_s3_subtitle_config: 'Connect your AWS S3 bucket for file discovery and ingestion',
    admin_s3_step1_title: 'Create IAM User',
    admin_s3_step1_desc: 'Go to AWS IAM Console and create a new user for CloudCore S3 integration',
    admin_s3_capability_note: "Amazon S3 access is set by the IAM user's permissions, not by MetaLark. Read-only permissions (s3:GetObject, s3:ListBucket) are enough to import content into MetaLark; migrating or exporting content into the bucket also requires write permissions (s3:PutObject). The policy you attach below determines whether this connection is read-only or read-write.",
    admin_s3_step2_title: "Attach an S3 access policy",
    admin_s3_step2_desc: "Attach a read-only policy (AmazonS3ReadOnlyAccess) to import content, or a read-write policy to also migrate content into the bucket",
    admin_s3_step3_title: 'Generate Access Keys',
    admin_s3_step3_desc: 'Create access keys for the IAM user',
    admin_s3_step3_warning: 'Save the Access Key ID and Secret Access Key immediately - you won\'t be able to see the secret again!',
    admin_s3_step4_title: '(Optional) Restrict to Specific Bucket',
    admin_s3_step4_desc: 'For enhanced security, create a custom IAM policy with the following permissions',
    admin_s3_bucketname: 'S3 Bucket Name',
    admin_s3_bucketname_placeholder: 'my-bucket',
    admin_s3_bucketname_help: 'The name of your S3 bucket (e.g., my-company-documents)',
    admin_s3_region: 'AWS Region',
    admin_s3_region_placeholder: 'Select a region...',
    admin_s3_region_useast1: 'US East (N. Virginia)',
    admin_s3_region_useast2: 'US East (Ohio)',
    admin_s3_region_uswest1: 'US West (N. California)',
    admin_s3_region_uswest2: 'US West (Oregon)',
    admin_s3_region_euwest1: 'EU (Ireland)',
    admin_s3_region_eucentral1: 'EU (Frankfurt)',
    admin_s3_region_apsoutheast1: 'Asia Pacific (Singapore)',
    admin_s3_region_apsoutheast2: 'Asia Pacific (Sydney)',
    admin_s3_region_apnortheast1: 'Asia Pacific (Tokyo)',
    admin_s3_region_cacentral1: 'Canada (Central)',
    admin_s3_region_help: 'The AWS region where your bucket is located',
    admin_s3_prefix: 'Prefix (Optional)',
    admin_s3_prefix_placeholder: 'documents/',
    admin_s3_prefix_help: 'Limit file discovery to objects with this prefix (e.g., documents/ for a specific folder)',
    admin_s3_accesskeyid: 'AWS Access Key ID',
    admin_s3_accesskeyid_placeholder: 'AKIAIOSFODNN7EXAMPLE',
    admin_s3_accesskeyid_help: 'Your IAM user\'s Access Key ID (starts with AKIA)',
    admin_s3_secretaccesskey: 'AWS Secret Access Key',
    admin_s3_secretaccesskey_placeholder: 'wJalrXUtnFEMI/K7MDENG/bPxRfiCYEXAMPLEKEY',
    admin_s3_secretaccesskey_help: 'Your IAM user\'s Secret Access Key (keep this secret!)',
    admin_s3_secretaccesskey_show: 'Show secret access key',
    admin_s3_secretaccesskey_hide: 'Hide secret access key',
    admin_s3_testconnection: 'Test Connection',
    admin_s3_saveconfiguration: 'Save Configuration',
    admin_s3_success_connected: 'Successfully connected to S3 bucket!',
    admin_s3_error_connection: 'Failed to connect to S3 bucket',
    admin_s3_error_message: 'Connection error: {message}',
    admin_s3_success_updated: 'S3 credentials updated successfully!',
    admin_s3_success_configured: 'S3 repository configured successfully!',
    admin_s3_error_save: 'Failed to save configuration',
    admin_s3_error_save_message: 'Save error: {message}',

    // Shared Drive Selector (admin/integrations)
    admin_drive_select_title: 'Select a Shared Drive',
    admin_drive_select_subtitle: 'Choose which Shared Drive to browse. Only files from the selected drive will be available.',
    admin_drive_select_subtitle_folder: 'Choose which Shared Drive to browse. Only folders from the selected drive will be available.',
    admin_drive_loading: 'Loading available drives...',
    admin_drive_nodrivesavailable: 'No Shared Drives available. You only have access to "My Drive".',
    admin_drive_nodriveselected: 'No drive selected',
    admin_drive_mydrive: 'My Drive',
    admin_drive_search_placeholder: 'Search drives...',
    admin_drive_capabilities_copy: 'Can Copy',
    admin_drive_capabilities_download: 'Can Download',
    admin_drive_capabilities_edit: 'Can Edit',

    // Rules Builder (admin/repository-integrations)
    admin_rules_testrules: 'Test Rules',
    admin_rules_addrule: 'Add Rule',
    admin_rules_rule: 'Rule',
    admin_rules_matchall: 'Match ALL conditions',
    admin_rules_matchany: 'Match ANY condition',
    admin_rules_conditions: 'Conditions',
    admin_rules_addcondition: 'Add Condition',
    admin_rules_preview: 'Preview',
    admin_rules_norulesconfigured: 'No rules configured yet.',
    admin_rules_emptystatedescription: 'Add a rule to enable auto-approval based on file properties.',
    admin_rules_createfirstrule: 'Create First Rule',
    admin_rules_saverules: 'Save Rules',
    admin_rules_reevaluate: 'Re-evaluate Pending Files',
    admin_rules_totalfilestested: 'Total Files Tested',
    admin_rules_filesmatching: 'Files Matching',
    admin_rules_matchpercentage: 'Match Percentage',
    admin_rules_detailedresults: 'Detailed Results',
    admin_rules_range_to: 'to',
    admin_rules_not: 'NOT',

    // Pending Files Review (admin/repository-integrations)
    admin_pending_statistics_pending: 'Pending',
    admin_pending_statistics_approved: 'Approved',
    admin_pending_statistics_rejected: 'Rejected',
    admin_pending_statistics_rulematched: 'Rule Matched',
    admin_pending_statistics_ruleexcluded: 'Rule Excluded',
    admin_pending_filter_allrepositories: 'All Repositories',
    admin_pending_filter_allstatus: 'All Status',
    admin_pending_bulkactions_approveselected: 'Approve Selected',
    admin_pending_bulkactions_rejectselected: 'Reject Selected',
    admin_pending_bulkactions_clearselection: 'Clear Selection',
    admin_pending_selectall: 'Select All ({count})',
    admin_pending_empty_title: 'No files to review',
    admin_pending_empty_description: 'All files have been reviewed or there are no pending files.',

    // Repository Activity (admin/repository-integrations)
    admin_repo_filter_repository: 'Repository',
    admin_repo_filter_action: 'Action',
    admin_repo_filter_allactions: 'All Actions',
    admin_repo_loading: 'Loading activity history...',
    admin_repo_empty_title: 'No activity history found',
    admin_repo_empty_description: 'File actions will appear here as they are performed',
    admin_repo_resource_label: 'Resource',
    admin_repo_action_approved: 'Approved',
    admin_repo_action_rejected: 'Rejected',
    admin_repo_action_rule_matched: 'Rule Matched',
    admin_repo_action_rule_excluded: 'Rule Excluded',
    admin_repo_action_webhook_discovered: 'Webhook Discovered',
    admin_repo_action_sync_triggered: 'Sync Triggered',
    admin_repo_action_sync_completed: 'Sync Completed',
    repoactivity_justnow: 'just now',
    repoactivity_minutesago: '{count}m ago',
    repoactivity_hoursago: '{count}h ago',
    repoactivity_daysago: '{count}d ago',
    repoactivity_showing: 'Showing',
    repoactivity_records: 'records',

    // Identity Provider Types (admin/modals/idp-modal)
    admin_idp_type_googleoauth: 'Google OAuth',
    admin_idp_type_microsoftoauth: 'Microsoft OAuth',
    admin_idp_type_githuboauth: 'GitHub OAuth',
    admin_idp_type_genericoauth: 'Generic OAuth',
    admin_idp_type_oktasaml: 'Okta SAML',
    admin_idp_type_azureadsaml: 'Azure AD SAML',
    admin_idp_type_genericsaml: 'Generic SAML',

    // Customer 360 page (METALARK-694)
    customer360_breadcrumb_customers: 'Customers',
    customer360_quickswitch_tooltip: 'Switch to another customer',
    customer360_quickswitch_placeholder: 'Search name, email, customer ID…',
    customer360_quickswitch_no_results: 'No customers match that search.',
    customer360_quickswitch_hint: 'Press Enter to open the top match; Esc to close.',
    customer360_quickswitch_end: 'No more results',
    customer360_switch_no_slice_found: 'Could not find a workspace to enter in that customer. Try the workspace switcher instead.',
    customer360_missing_customer_id: 'No customer selected. Pass a customerId in the URL.',
    customer360_error_load_failed: 'Failed to load customer.',
    customer360_header_created: 'Customer since {date}',
    customer360_header_mrr: 'MRR',
    customer360_header_mrr_unit: 'monthly',
    customer360_header_lifetime: 'Lifetime',
    customer360_header_lifetime_days: '{count, plural, one {# day} other {# days}}',
    customer360_header_lifetime_since: 'since trial start',
    customer360_header_credits: 'Credits',
    customer360_header_credits_remaining: 'remaining',
    customer360_view_as_customer: 'View as customer',
    customer360_view_as_customer_tooltip: 'Hide all admin-only UI and see this page the way a customer admin would.',

    customer360_impersonate_title: 'Impersonate a user in {customer}',
    customer360_impersonate_subtitle: 'Start a time-limited session in one of the workspaces below. Your access will be scoped to that workspace as an administrator would see it. Every action is audited.',
    customer360_impersonate_slice_label: 'Workspace',
    customer360_impersonate_duration_label: 'Duration',
    customer360_impersonate_duration_15: '15 minutes',
    customer360_impersonate_duration_30: '30 minutes',
    customer360_impersonate_duration_60: '1 hour',
    customer360_impersonate_duration_120: '2 hours',
    customer360_impersonate_reason_label: 'Reason',
    customer360_impersonate_reason_placeholder: 'Why are you impersonating? (required for audit)',
    customer360_impersonate_ticket_label: 'Support ticket',
    customer360_impersonate_ticket_placeholder: 'e.g., SUP-4821 (optional)',
    customer360_impersonate_audit_note: 'Every action taken during this session is logged with your identity and the reason you provided.',
    customer360_impersonate_start: 'Start impersonation',
    customer360_impersonate_no_slices: 'This customer has no active workspaces to impersonate into.',
    customer360_impersonate_failed: 'Failed to start impersonation',
    customer360_action_impersonate: 'Impersonate',
    customer360_action_enter_workspace: 'Enter Workspace',
    customer360_action_entering: 'Entering…',
    customer360_workspace_selector_label: 'Select a workspace to enter',
    customer360_action_open_stripe: 'Open in Stripe',
    customer360_tab_overview: 'Overview',
    customer360_tab_billing: 'Billing',
    customer360_tab_users: 'Users & Access',
    customer360_tab_content: 'Workspaces',
    customer360_tab_audit: 'Audit',
    customer360_tab_settings: 'Settings',
    customer360_tab_api: 'API',

    // DisplayedSubscriptionState labels
    displayed_state_trial_active: 'Trial Active',
    displayed_state_trial_ending_soon: 'Trial Ending Soon',
    displayed_state_trial_expired: 'Trial Expired',
    displayed_state_active: 'Active',
    displayed_state_active_scheduled_cancel: 'Scheduled Cancel',
    displayed_state_active_plan_change_pending: 'Plan Change Pending',
    displayed_state_payment_pending: 'Payment Pending',
    displayed_state_past_due: 'Past Due',
    displayed_state_unpaid: 'Unpaid',
    displayed_state_incomplete: 'Incomplete',
    displayed_state_canceled_in_grace: 'Canceled (Grace)',
    displayed_state_expired: 'Expired',
    displayed_state_pending_deletion: 'Pending Deletion',
    displayed_state_soft_deleted: 'Soft Deleted',
    displayed_state_no_subscription: 'No Subscription',
    displayed_state_paused: 'Paused',

    // Overview tab
    customer360_overview_activity_title: 'Activity Timeline',
    customer360_overview_activity_subtitle: 'Recent audit events: API tokens, user/role changes, and security activity. Subscription lifecycle events live on the Billing tab.',
    customer360_overview_filter_all: 'All',
    customer360_overview_filter_api_tokens: 'API Tokens',
    customer360_overview_filter_users: 'Users',
    customer360_overview_filter_audit: 'Audit',
    customer360_overview_no_events: 'No recent activity.',
    customer360_overview_timeline_end: 'End of activity',
    customer360_overview_event_unknown: 'Event',
    customer360_overview_billing_title: 'Billing',
    customer360_overview_billing_plan: 'Plan',
    customer360_overview_billing_renews: 'Renews',
    customer360_overview_billing_cancels: 'Cancels',
    customer360_overview_billing_reason: 'Reason',
    customer360_overview_trial_remaining_label: 'Trial remaining',
    customer360_overview_trial_remaining_days: '{count, plural, =0 {Ends today} one {# day left} other {# days left}}',
    customer360_overview_credits: 'Credits',
    customer360_overview_credits_remaining: 'remaining',
    customer360_overview_bundles_summary: '{count} bundles ({detail})',
    customer360_overview_bundle_exhausted: 'exhausted',
    customer360_overview_no_quota: 'No quota configured for this customer.',
    customer360_overview_users_title: 'Users ({count})',
    customer360_overview_users_subtitle: 'Team members and role assignments.',
    customer360_overview_workspaces_title: 'Workspaces ({count})',
    customer360_overview_workspaces_subtitle: 'Isolated tenant environments.',
    customer360_overview_workspace_metrics: '{resources} resources · {users} users',
    customer360_overview_workspace_direct_hint: 'You hold a direct role — click to enter',
    customer360_overview_workspace_impersonate_hint: 'No direct role — click to impersonate',
    customer360_overview_manage: 'Manage',
    customer360_overview_manage_subscription: 'Manage subscription',
    customer360_overview_manage_credits: 'Manage credits',

    // Billing tab
    customer360_billing_jumpto: 'Jump to',
    customer360_billing_section_subscription: 'Subscription',
    customer360_billing_view_credits: 'Credits',
    customer360_billing_view_usage: 'Usage',
    customer360_billing_view_subscription: 'Subscription',
    customer360_billing_view_purchase: 'Purchase Credits',
    customer360_billing_credits_card_title: 'Credits',
    customer360_billing_credits_available: 'Available',
    customer360_billing_credits_used_last_30: 'Used in last 30 days',
    customer360_billing_purchase_complete_title: 'Purchase complete',
    customer360_billing_purchase_complete_body: 'Your credits will appear in the balance shortly.',
    customer360_billing_subscription_complete_title: 'Subscription started',
    customer360_billing_subscription_complete_body: 'Your subscription is being activated; the billing tab will refresh in a moment.',
    customer360_billing_usage_daily_title: 'Daily credit usage',
    customer360_billing_usage_daily_empty: 'No credit usage in this period',
    customer360_billing_usage_period_7d: '7d',
    customer360_billing_usage_period_14d: '14d',
    customer360_billing_usage_period_30d: '30d',
    customer360_billing_usage_period_60d: '60d',
    customer360_billing_usage_period_90d: '90d',
    customer360_billing_section_credits: 'Credits & Quota',
    customer360_billing_current_subscription: 'Current Subscription',
    customer360_billing_state: 'State',
    customer360_billing_plan: 'Plan',
    customer360_billing_renews: 'Renews',
    customer360_billing_period_ends: 'Current period ends',
    customer360_billing_billing_mode: 'Billing',
    customer360_billing_billing_mode_autopay: 'Automatic card',
    customer360_billing_billing_mode_contract: 'Contract (invoiced)',
    customer360_billing_next_invoice: 'Next invoice',
    customer360_billing_trial_end: 'Trial ends',
    customer360_billing_cancels_at: 'Cancels at',
    customer360_billing_cancel_reason: 'Reason',
    customer360_billing_actions: 'Quick Actions',
    customer360_billing_action_change_plan: 'Change Plan',
    customer360_billing_action_start_subscription: 'Start subscription',
    customer360_billing_action_update_payment: 'Update payment method',
    customer360_billing_action_resume: 'Resume subscription',
    customer360_billing_update_payment_failed: 'Failed to open payment portal',
    customer360_billing_portal_failed: 'Failed to open billing portal',
    customer360_billing_banner_past_due: 'Payment past due',
    customer360_billing_banner_past_due_detail: '{count, plural, one {# day overdue. Service may be suspended if payment isn\'t resolved.} other {# days overdue. Service may be suspended if payment isn\'t resolved.}}',
    customer360_billing_banner_unpaid: 'Unpaid — access at risk',
    customer360_billing_banner_unpaid_detail: 'Multiple payment attempts have failed. Update the payment method immediately to restore service.',
    customer360_billing_banner_incomplete: 'Subscription setup incomplete',
    customer360_billing_banner_incomplete_detail: 'Initial payment didn\'t finish. Complete the setup to activate the subscription.',
    customer360_billing_banner_paused: 'Subscription paused',
    customer360_billing_banner_paused_detail: 'Access is suspended until the subscription is resumed.',
    customer360_billing_banner_pending_deletion: 'Pending deletion',
    customer360_billing_banner_pending_deletion_detail: '{count, plural, =0 {Account will be permanently deleted today.} one {Account will be permanently deleted in # day.} other {Account will be permanently deleted in # days.}}',
    customer360_billing_banner_plan_change_pending: 'Plan change pending',
    customer360_billing_banner_plan_change_pending_detail: 'A plan change is scheduled and will take effect at the next billing cycle.',
    customer360_billing_banner_payment_pending: 'Payment processing',
    customer360_billing_banner_payment_pending_detail: 'Awaiting confirmation from Stripe — usually completes within a few minutes.',
    customer360_billing_active_since: 'Active since',
    customer360_billing_active_since_days: '{count, plural, one {# day} other {# days}}',
    customer360_billing_stripe_id: 'Stripe',
    customer360_billing_manage_in_stripe: 'Billing portal',
    customer360_billing_manage_in_stripe_action: 'Manage in Stripe',
    customer360_billing_payment_method: 'Payment method',
    customer360_billing_payment_on_file: 'On file',
    customer360_billing_payment_missing: 'Missing',
    customer360_billing_payment_show: 'Show payment method',
    customer360_billing_payment_loading: 'Loading…',
    customer360_billing_payment_load_error: 'Could not load — retry',
    customer360_billing_payment_none: 'None on file',
    customer360_billing_payment_expires: '(expires {month}/{year})',
    customer360_billing_payment_refresh_title: 'Refresh from Stripe',
    customer360_billing_next_charge: 'Next charge',
    customer360_billing_next_charge_display: '{amount} on {date}',
    customer360_billing_whats_next: "What's next",
    customer360_billing_next_trial_ends_in: '{count, plural, one {Trial ends tomorrow ({date})} other {Trial ends in # days ({date})}}',
    customer360_billing_next_trial_ends_today: 'Trial ends today',
    customer360_billing_next_trial_hint: 'Add a payment method to convert to a paid plan and keep access without interruption.',
    customer360_billing_next_trial_expired: 'Trial expired',
    customer360_billing_next_grace_ends_in: '{count, plural, one {Grace ends tomorrow ({date})} other {Grace ends in # days ({date})}}',
    customer360_billing_next_trial_expired_hint: 'Subscribe before grace ends to avoid data being scheduled for deletion.',
    customer360_billing_next_cancel_in: '{count, plural, =0 {Subscription cancels today ({date})} one {Subscription cancels tomorrow ({date})} other {Subscription cancels in # days ({date})}}',
    customer360_billing_next_cancel_hint: 'Reactivate before this date to keep the subscription running.',
    customer360_billing_next_plan_change: 'Plan change takes effect at next billing cycle',
    customer360_billing_next_plan_change_hint: 'Effective {date}. Current plan continues until then.',
    customer360_billing_next_payment_pending: 'Awaiting Stripe confirmation',
    customer360_billing_next_payment_pending_hint: 'Usually completes within a few minutes. If stuck, try "Force Sync from Stripe".',
    customer360_billing_next_payment_pending_hint_customer: 'Usually completes within a few minutes. Refresh to check for updates.',
    customer360_billing_next_past_due: 'Payment past due',
    customer360_billing_next_past_due_days: '{count, plural, one {# day overdue} other {# days overdue}}',
    customer360_billing_next_past_due_hint: 'Stripe retries automatically. Service may be suspended if the issue persists.',
    customer360_billing_next_unpaid: 'Service suspension imminent',
    customer360_billing_next_unpaid_hint: 'Multiple payment attempts have failed. Update the payment method to restore access.',
    customer360_billing_next_incomplete: 'Setup incomplete',
    customer360_billing_next_incomplete_hint: 'Stripe waits up to 23 hours for the initial payment; after that the subscription expires.',
    customer360_billing_next_access_ends_in: '{count, plural, =0 {Access ends today ({date})} one {Access ends tomorrow ({date})} other {Access ends in # days ({date})}}',
    customer360_billing_next_access_ended: 'Access has ended',
    customer360_billing_next_deletion_hint: 'When access ends, the account is fully suspended (logins, API calls, and workspaces go offline) and scheduled for permanent deletion {count} days later.',
    customer360_billing_next_deletion_scheduled: 'Account scheduled for deletion',
    customer360_billing_next_deletion_in: '{count, plural, =0 {Permanent deletion today} one {Permanent deletion tomorrow} other {Permanent deletion in # days}}',
    customer360_billing_next_deletion_hint2: 'Contact support to halt deletion if the customer returns.',
    customer360_billing_next_deletion_hint2_customer: 'Contact support immediately to restore your account before permanent deletion.',
    customer360_billing_next_deletion_imminent: 'Deletion imminent',
    customer360_billing_next_deletion_imminent_hint: 'The customer has passed the deletion threshold. All data will be permanently removed on the next deletion sweep.',
    customer360_billing_next_deletion_imminent_hint_customer: 'Your account has passed the deletion threshold. All data will be permanently removed on the next deletion sweep. Contact support immediately if this is in error.',
    customer360_billing_next_paused: 'Subscription paused — no upcoming charges',
    customer360_billing_next_paused_hint: 'Resume the subscription to restore billing and access.',
    customer360_billing_next_no_sub: 'No active subscription',
    customer360_billing_next_no_sub_hint: 'Start a subscription to begin billing and unlock paid features.',

    customer360_billing_section_history: 'Event History',
    customer360_billing_history_empty: 'No subscription events recorded yet.',
    customer360_billing_history_end: 'End of history',
    customer360_billing_history_col_date: 'Date',
    customer360_billing_history_col_event: 'Event',
    customer360_billing_history_col_details: 'Details',
    customer360_billing_history_admin_tooltip: 'Admin-initiated event',
    customer360_billing_history_admin_actor: 'by {actor}',

    // METALARK-789: friendly event-title strings rendered in the timeline
    // by eventTitle(). Customer-mode and system-mode both use these.
    customer360_billing_event_trial_started: '{trialDays}-day trial started',
    customer360_billing_event_trial_started_no_days: 'Trial started',
    customer360_billing_event_trial_converted: 'Trial converted to paid plan',
    customer360_billing_event_trial_expired: 'Trial ended',
    customer360_billing_event_trial_extended: 'Trial extended by {days} days',
    customer360_billing_event_trial_extended_no_days: 'Trial extended',
    customer360_billing_event_subscription_created: 'Subscription started',
    customer360_billing_event_renewed_with_interval: 'Renewed for another {interval}',
    customer360_billing_event_renewed: 'Subscription renewed',
    customer360_billing_event_plan_changed_with_plans: 'Plan changed: {from} → {to}',
    customer360_billing_event_plan_changed: 'Plan changed',
    customer360_billing_event_payment_failed: 'Payment failed — please update your card',
    customer360_billing_event_payment_recovered: 'Payment recovered',
    customer360_billing_event_cancel_scheduled: 'Cancellation scheduled',
    customer360_billing_event_canceled: 'Subscription canceled',
    customer360_billing_event_reactivated: 'Subscription reactivated',
    customer360_billing_event_refunded: 'Refund issued',
    customer360_billing_event_refund_failed: 'Refund failed',
    customer360_billing_event_view_receipt: 'View receipt',
    customer360_billing_event_paused: 'Subscription paused',
    customer360_billing_event_resumed: 'Subscription resumed',
    customer360_billing_event_credits_granted: '{credits} credits added',
    customer360_billing_event_credits_granted_no_count: 'Credits added',
    customer360_billing_event_credits_purchased: '{credits} credits purchased',
    customer360_billing_event_credits_purchased_no_count: 'Credits purchased',
    customer360_billing_event_credits_purchase_pending: '{credits} credits — bank transfer pending',
    customer360_billing_event_credits_purchase_pending_no_count: 'Credits purchase — bank transfer pending',
    customer360_billing_event_grace_extended: 'Grace period extended',
    customer360_billing_event_sync_forced: 'Synced with Stripe',
    customer360_billing_event_stripe_ids_updated: 'Stripe identifiers updated',
    customer360_billing_event_payment_grace_expired: 'Account access ended after payment failure',
    customer360_billing_event_grace_period_elapsed: 'Account access ended',
    customer360_billing_event_deletion_grace_elapsed: 'Scheduled for deletion',
    customer360_billing_event_soft_delete_executed: 'Account soft-deleted',
    customer360_billing_event_scheduled_cancel_completed: 'Cancellation took effect',
    customer360_billing_event_incomplete_abandoned: 'Incomplete signup expired',
    customer360_billing_event_payment_pending_window_elapsed: 'Signup payment timed out',
    // METALARK-789: SubscriptionEvent.reason values (mirror TransitionReason
    // union in api/services/SubscriptionStateMachine.ts). Rendered inside the
    // event-detail summary line; falls back to humanized snake_case when a
    // particular reason has no locale entry.
    customer360_billing_event_reason_trial_ended_no_payment: 'no payment method on file',
    customer360_billing_event_reason_trial_converted: 'converted to paid',
    customer360_billing_event_reason_subscription_canceled: 'subscription canceled',
    customer360_billing_event_reason_subscription_paused: 'paused by Stripe',
    customer360_billing_event_reason_subscription_resumed: 'resumed',
    customer360_billing_event_reason_cancel_scheduled: 'cancellation scheduled',
    customer360_billing_event_reason_cancel_unscheduled: 'cancellation undone',
    customer360_billing_event_reason_payment_failed: 'payment failed',
    customer360_billing_event_reason_payment_recovered: 'payment recovered',
    customer360_billing_event_reason_invoice_paid: 'invoice paid',
    customer360_billing_event_reason_subscription_created_trial: 'trial started',
    customer360_billing_event_reason_subscription_created_active: 'paid plan started',
    customer360_billing_event_reason_subscription_created_incomplete: 'signup incomplete',
    customer360_billing_event_reason_payment_pending_window_elapsed: 'payment window elapsed',
    customer360_billing_event_reason_incomplete_abandoned: 'signup abandoned',
    customer360_billing_event_reason_payment_grace_expired: 'payment grace expired',
    customer360_billing_event_reason_scheduled_cancel_completed: 'cancellation took effect',
    customer360_billing_event_reason_grace_period_elapsed: 'grace period elapsed',
    customer360_billing_event_reason_deletion_grace_elapsed: 'deletion grace elapsed',
    customer360_billing_event_reason_soft_delete_executed: 'soft delete executed',
    customer360_billing_event_reason_admin_force_cancel: 'force-canceled by admin',
    customer360_billing_event_reason_admin_force_expire: 'expired by admin',
    customer360_billing_event_reason_admin_reactivation: 'reactivated by admin',
    customer360_billing_event_reason_admin_undelete: 'restored by admin',
    customer360_billing_event_reason_customer_resubscribe: 'resubscribed',
    customer360_billing_event_reason_card_declined: 'card declined',
    // Renewal interval labels (used inside the renewed event title)
    customer360_billing_event_interval_year: 'year',
    customer360_billing_event_interval_month: 'month',
    // System-mode chrome
    customer360_billing_event_show_details: 'Show details',
    customer360_billing_event_hide_details: 'Hide details',
    customer360_billing_event_metadata_label: 'Metadata',
    customer360_billing_event_no_metadata: 'No additional metadata.',

    customer360_billing_section_invoices: 'Invoices',
    customer360_billing_invoices_empty: 'No invoices yet.',
    customer360_billing_invoices_col_number: 'Invoice',
    customer360_billing_invoices_type_subscription: 'Subscription',
    customer360_billing_invoices_type_credits: 'Credits',
    customer360_billing_invoices_type_other: 'Invoice',
    customer360_billing_invoices_col_date: 'Date',
    customer360_billing_invoices_col_amount: 'Amount',
    customer360_billing_invoices_col_status: 'Status',
    customer360_billing_invoices_col_link: 'Link',
    customer360_billing_invoices_view: 'View',

    admin_only_badge: 'Admin',
    customer360_billing_start_hint_trial: '{count, plural, one {# day} other {# days}} left in trial',
    customer360_billing_start_hint_expired: 'Subscription has ended. Start a new one to restore access.',
    customer360_billing_start_hint_none: 'No active subscription.',
    customer360_billing_switch_to_stripe_title: 'Switch to automatic card billing',
    customer360_billing_switch_to_stripe_body: 'Your subscription is currently billed by invoice or purchase order. You can move to automatic card payments at any time — your subscription terms stay the same.',
    customer360_billing_switch_to_stripe_button: 'Set up autopay with Stripe',
    customer360_billing_switch_to_stripe_failed: 'Could not start Stripe checkout',
    customer360_billing_switch_to_stripe_confirm: 'Continue to Stripe',
    customer360_billing_stripe_not_configured: 'Stripe isn\u2019t configured for this environment. Autopay isn\u2019t available here — contact support if you need to complete this switch.',
    customer360_billing_action_reactivate: 'Reactivate',
    customer360_billing_action_cancel: 'Cancel subscription…',
    customer360_billing_credits_remaining: 'remaining',
    customer360_billing_credits_total: 'total',
    customer360_billing_bundles_title: 'Quota Bundles',
    customer360_billing_bundle_name: 'Name',
    customer360_billing_bundle_type: 'Type',
    customer360_billing_bundle_total: 'Total',
    customer360_billing_bundle_used: 'Used',
    customer360_billing_bundle_remaining: 'Remaining',
    customer360_billing_bundle_priority: 'Priority',
    customer360_billing_bundle_created: 'Created',
    customer360_billing_bundle_status: 'Status',
    customer360_billing_credits_used: 'used',
    customer360_billing_purchase_credits: 'Purchase Credits',
    customer360_billing_purchase_back: 'Back to Credits & Quota',
    customer360_billing_purchase_load_failed: 'Failed to open purchase view',
    customer360_billing_no_quota: 'No quota data available.',
    customer360_billing_usage_card_title: 'Usage',
    customer360_billing_usage_last_7: 'Last 7 days',
    customer360_billing_usage_last_30: 'Last 30 days',
    customer360_billing_usage_avg_daily: '{count, plural, one {about # credit/day on average} other {about # credits/day on average}}',
    customer360_billing_analytics_title: 'Usage analytics',
    customer360_billing_analytics_avg_daily: 'Average daily usage',
    customer360_billing_analytics_credits_per_day: 'credits/day',
    customer360_billing_analytics_total_30: 'Total (30 days)',
    customer360_billing_analytics_credits: 'credits',
    customer360_billing_analytics_depletion: 'Estimated depletion',
    customer360_billing_analytics_at_current_rate: 'at current rate',
    customer360_billing_analytics_not_available: '—',
    customer360_billing_plan_card_current: 'Current',
    customer360_billing_plan_card_billed_monthly: 'Billed monthly',
    customer360_billing_plan_card_billed_annually: 'Billed annually',
    customer360_billing_plan_card_per_month: '/mo',
    customer360_billing_plan_card_per_year: '/yr',
    customer360_billing_plan_card_save_percent: 'Save {percent}%',
    customer360_billing_plan_card_effective: 'Effectively {amount}/mo',
    customer360_billing_cancel_modal_title: 'Cancel Subscription',
    customer360_billing_cancel_modal_subtitle: 'Tell us why and pick when. You can re-subscribe any time.',
    customer360_billing_cancel_when_label: 'When should the cancellation take effect?',
    customer360_billing_cancel_when_end_title: 'At the end of the current billing period',
    customer360_billing_cancel_when_end_help: 'Keep using the subscription until the period you\'ve already paid for ends. Reversible until then.',
    customer360_billing_cancel_when_now_title: 'Cancel immediately',
    customer360_billing_cancel_when_now_help: 'Access ends now and the grace period begins. A prorated refund may be issued for unused time.',
    customer360_billing_cancel_reason_label: 'Why are you canceling?',
    customer360_billing_cancel_reason_select: 'Select a reason...',
    customer360_billing_cancel_reason_too_expensive: 'Too expensive',
    customer360_billing_cancel_reason_missing_features: 'Missing features',
    customer360_billing_cancel_reason_switched: 'Switched to a competitor',
    customer360_billing_cancel_reason_not_using: 'Not using the product',
    customer360_billing_cancel_reason_temporary: 'Temporary pause',
    customer360_billing_cancel_reason_other: 'Other',
    customer360_billing_cancel_reasontext_label: 'Additional details (optional)',
    customer360_billing_cancel_reasontext_placeholder: 'Tell us more...',
    customer360_billing_cancel_confirm: 'Confirm Cancellation',
    customer360_billing_cancel_success: 'Subscription canceled.',
    customer360_billing_cancel_failed: 'Failed to cancel',
    customer360_billing_cancel_review_button: 'Review',
    customer360_billing_cancel_review_intro: 'Here\'s what will happen if you cancel. Please review before confirming.',
    customer360_billing_cancel_review_charge_today: 'Charge today',
    customer360_billing_cancel_review_charge_none: 'No charge today. No future charges will be made.',
    customer360_billing_cancel_review_access: 'Access',
    customer360_billing_cancel_review_access_until: '{count, plural, =0 {Access ends today ({date})} one {Access continues until {date} (# day remaining). You can reactivate anytime before then.} other {Access continues until {date} (# days remaining). You can reactivate anytime before then.}}',
    customer360_billing_cancel_review_access_until_unknown: 'Access continues until the end of the current billing period.',
    customer360_billing_cancel_review_access_now_readonly: 'Access becomes read-only immediately. Uploads, edits, and new work are blocked, but you can keep viewing existing content during the grace period.',
    customer360_billing_cancel_review_forfeit: 'Unused time',
    customer360_billing_cancel_review_forfeit_days: '{count, plural, one {# day of paid service will be forfeited (no automatic refund).} other {# days of paid service will be forfeited (no automatic refund).}}',
    customer360_billing_cancel_review_refund_hint: 'Need a refund? Contact support — refunds for unused time can be issued manually.',
    customer360_billing_change_plan_title: 'Change Subscription Plan',
    customer360_billing_change_plan_subtitle: 'We will prorate the unused time from the current plan as a credit.',
    customer360_billing_start_plan_title: 'Choose a Plan',
    customer360_billing_start_plan_subtitle: 'Pick the plan that fits. You\u2019ll be redirected to secure checkout to complete signup.',
    customer360_billing_start_plan_confirm: 'Continue to checkout',
    customer360_billing_change_plan_label: 'New plan',
    customer360_billing_plan_monthly: 'Monthly',
    customer360_billing_plan_annual: 'Annual',
    customer360_billing_change_plan_confirm: 'Review & confirm',
    customer360_billing_change_plan_same_plan: 'Pick a different plan to continue.',
    customer360_billing_change_plan_success: 'Plan changed successfully.',
    customer360_billing_change_plan_failed: 'Failed to change plan',
    customer360_billing_reactivate_title: 'Reactivate subscription',
    customer360_billing_reactivate_confirm: 'Reactivate this subscription? The scheduled cancellation will be removed.',
    customer360_billing_reactivate_success: 'Subscription reactivated.',
    customer360_billing_reactivate_failed: 'Failed to reactivate',

    // Users & Access tab
    customer360_users_subtab_list: 'User List',
    customer360_users_subtab_sso: 'SSO Configuration',
    customer360_users_list_title: 'Users',
    customer360_users_invite: 'Invite',
    customer360_users_empty: 'No users yet.',
    customer360_users_col_user: 'User',
    customer360_users_col_workspace_role: 'Workspace & Role',
    customer360_users_col_auth: 'Auth',
    customer360_users_col_last_login: 'Last Login',
    customer360_users_scope_all_workspaces: 'All workspaces',
    customer360_users_add_workspace: 'Assign to workspace',
    customer360_users_add_save: 'Grant',
    customer360_users_role_disabled_tooltip: 'This role is currently disabled for new assignments and cannot be selected. It is shown because the user already holds it.',
    customer360_users_revoke_title: 'Revoke this assignment',
    customer360_users_revoke_last_assignment_blocked: 'This is the user\'s only role in this customer. Removing it would orphan them from this list — assign them to another workspace first.',
    customer360_users_revoke_last_assignment_only: 'Only role',
    customer360_users_outranked_assignment_label: 'Higher role',
    customer360_users_outranked_assignment_readonly: 'This role outranks your own. Only an admin at or above this role can change or remove it.',
    // Pending invitation states (METALARK-835)
    customer360_users_pending_label: 'Invited',
    customer360_users_pending_tooltip: 'The user has been invited but hasn\'t accepted yet. They have no access to this role until they accept.',
    customer360_users_pending_role_readonly: 'Pending invitations can\'t be edited inline. Cancel and re-invite to change the offered role.',
    customer360_users_resend_title: 'Resend invitation email',
    customer360_users_resend_success: 'Invitation resent. The previous link is no longer valid.',
    customer360_users_resend_new_expiry: 'New expiry',
    customer360_users_resend_failed: 'Failed to resend invitation',
    customer360_users_cancel_invitation_title: 'Cancel this invitation',
    customer360_users_invite_sent_toast: 'Invitation sent. The user will receive an email to accept.',
    customer360_users_invite_already_in_customer: 'This email already belongs to a member of your customer. Open their profile to add a workspace or change their role.',
    customer360_users_invite_open_existing_profile: 'Open profile',
    customer360_users_invite_existing_lookup_failed: 'Could not load their profile. Find them in the Users tab.',
    // Rich info-modal copy for the cross-customer invite success (METALARK-835)
    customer360_users_invite_sent_title: 'Invitation sent',
    customer360_users_invite_sent_body: 'An invitation email has been sent to {email}. They\'ll appear in your user list with an "Invited" badge until they accept.',
    customer360_users_invite_sent_expires: 'Expires',
    customer360_users_resend_success_title: 'Invitation resent',
    customer360_users_role_confirm_title: 'Confirm role change',
    customer360_users_role_cancel_title: 'Cancel role change',
    customer360_users_invite_title: 'Invite user',
    customer360_users_invite_subtitle: 'Create a user account in this customer and assign them an initial role.',
    customer360_users_invite_credentials_label: 'Account credentials',
    customer360_users_invite_role_customerwide_hint: 'Customer admins are usually also assigned to a specific workspace. Pick a workspace above, or leave "All workspaces" selected to grant a customer-wide admin without a workspace role.',
    customer360_users_invite_role_tenantadmin_hint: 'Tenant admins must be granted per workspace. Pick the specific workspace to admin.',
    customer360_users_invite_email: 'Email',
    customer360_users_invite_email_placeholder: 'name@company.com',
    customer360_users_invite_given_name: 'First name',
    customer360_users_invite_family_name: 'Last name',
    customer360_users_invite_role: 'Role',
    customer360_users_invite_choose_role: 'Choose a role…',
    customer360_users_invite_workspace: 'Workspace',
    customer360_users_invite_workspace_none: 'All workspaces',
    customer360_users_invite_send: 'Send invite',
    customer360_users_sso_providers: 'SSO Providers',
    customer360_users_sso_domains: 'Verified domains',
    customer360_users_custom_signin_domain: 'Custom sign-in domain',
    customer360_users_sso_policy: 'Authentication policy',
    customer360_users_revoke_failed: 'Failed to revoke role',
    customer360_users_role_change_failed: 'Failed to change role',
    customer360_users_add_failed: 'Failed to add role assignment',
    customer360_users_auth_sso: 'SSO',
    customer360_users_auth_password: 'Password',
    customer360_users_auth_passkey: 'Passkey',
    customer360_users_auth_password_pending: 'Password (pending)',
    customer360_users_auth_breakglass: 'Breakglass',
    customer360_users_auth_breakglass_tooltip: 'This user can bypass SSO enforcement for emergency access.',
    customer360_users_export: 'Export',
    customer360_users_filter_empty: 'No users match the current filters.',
    customer360_users_filters_search_placeholder: 'Search users…',
    customer360_users_filters_role_all: 'All Roles',
    customer360_users_filters_workspace_all: 'All Workspaces',
    customer360_users_filters_auth_all: 'Any Auth',
    customer360_users_filters_clearall: 'Clear filters',
    customer360_users_filters_remember: 'Remember Filters',
    customer360_users_filters_remember_tooltip: 'Preserve the current filters across view switches and tab re-entries. Scoped to this customer and stored locally on this device.',
    customer360_users_revoke_modal_title: 'Revoke role assignment',
    customer360_users_revoke_modal_body: 'Revoking this assignment removes the user\'s access to this workspace at the selected role. Other assignments (if any) are unaffected.',
    customer360_users_revoke_modal_user: 'User',
    customer360_users_revoke_modal_workspace: 'Workspace',
    customer360_users_revoke_modal_role: 'Role',
    customer360_users_revoke_modal_note: 'Active sessions using this role continue until the user next logs in.',
    customer360_users_revoke_modal_confirm: 'Revoke',
    customer360_users_csv_email: 'Email',
    customer360_users_csv_name: 'Name',
    customer360_users_csv_assignments: 'Assignments',
    customer360_users_col_actions: 'Actions',
    customer360_users_action_edit: 'Edit',
    customer360_users_action_revoke_sessions: 'Revoke sessions',
    customer360_users_revoke_sessions_title: 'Revoke active sessions',
    customer360_users_revoke_sessions_body: 'All active sessions for this user will be invalidated immediately. They\'ll need to sign in again to regain access. This is an auditable action — a reason is required.',
    customer360_users_revoke_sessions_reason_label: 'Reason',
    customer360_users_revoke_sessions_reason_placeholder: 'e.g. Account compromise suspected',
    customer360_users_revoke_sessions_confirm: 'Revoke sessions',
    customer360_users_revoke_sessions_failed: 'Failed to revoke sessions',
    customer360_users_sso_title: 'SSO Configuration',
    customer360_users_sso_subtitle: 'Identity providers, verified domains, and enforcement policy.',
    customer360_users_sso_placeholder: 'Full SSO configuration UI coming in a follow-up. For now, use the standalone SSO page.',
    customer360_users_sso_open_standalone: 'Open SSO Page',

    // Content tab
    customer360_content_subtab_workspaces: 'Workspaces',
    customer360_content_subtab_repositories: 'Connections',
    customer360_content_subtab_pending: 'Pending Files',
    customer360_content_subtab_rules: 'File Rules',
    customer360_content_admin_only_pill: 'Admin-only — feature flag inactive for this customer',
    customer360_content_subtab_activity: 'Sync Activity',
    customer360_content_workspaces_title: 'Workspaces',
    customer360_content_workspaces_empty: 'No workspaces yet.',
    customer360_content_workspaces_created: 'Created {date}',
    customer360_content_subtab_placeholder: 'Full view coming in a follow-up. For now, open the standalone page.',
    customer360_content_open_standalone: 'Open standalone page',

    // Audit tab
    customer360_audit_subtab_log: 'Audit Log',
    customer360_audit_subtab_email: 'Email Logs',
    customer360_audit_subtab_usage: 'Activity Logs',
    customer360_audit_presets: 'Presets',
    customer360_audit_preset_all: 'All',
    customer360_audit_preset_subscription: 'Subscription changes',
    customer360_audit_preset_login: 'Login activity',
    customer360_audit_preset_users: 'User changes',
    customer360_audit_preset_sso: 'SSO events',
    customer360_audit_empty: 'No audit events match the current filter.',
    customer360_audit_col_time: 'Time',
    customer360_audit_col_event: 'Event',
    customer360_audit_col_actor: 'Actor',
    customer360_audit_col_target: 'Target',
    customer360_audit_email_placeholder: 'Email logs filtered by customer coming in a follow-up.',
    customer360_audit_open_email_logs: 'Open Email Logs',

    // Settings tab
    customer360_settings_info_title: 'Customer Info',
    customer360_settings_edit: 'Edit',
    customer360_settings_save: 'Save',
    customer360_settings_save_success: 'Saved.',
    customer360_settings_save_failed: 'Failed to save',
    customer360_settings_display_name: 'Display Name',
    customer360_settings_description: 'Description',
    customer360_settings_contact_email: 'Contact Email',
    customer360_settings_billing_email: 'Billing Email',
    customer360_settings_tier: 'Subscription Tier',
    customer360_settings_max_workspaces: 'Max Workspaces',
    customer360_settings_id: 'Customer ID',
    customer360_settings_session_title: 'Session Security',
    customer360_settings_session_subtitle: 'Session duration, epoch, and enforcement policy.',
    customer360_settings_session_epoch: 'Session Epoch',
    customer360_settings_sso_enforcement: 'SSO Enforcement',
    customer360_settings_sso_note: 'SSO enforcement and breakglass accounts are managed on the Users & Access tab.',
    customer360_settings_domains_title: 'Custom Sign-In Domains',
    customer360_settings_domains_placeholder: 'Custom sign-in domain management coming in a follow-up.',
    customer360_settings_open_domains: 'Open Domain Settings',

    // Settings tab — new labels for the rebuilt edit surfaces
    customer360_settings_info_subtitle: 'Identity, contact, and subscription context for this customer.',
    customer360_settings_description_placeholder: 'A short note about this organization.',
    customer360_settings_contact_email_placeholder: 'primary-contact@example.com',
    customer360_settings_billing_email_placeholder: 'billing@example.com',
    customer360_settings_logo_title: 'Customer Logo',
    customer360_settings_logo_subtitle: 'Logo surfaces on the login page and in the product header.',
    customer360_settings_logo_none: 'No logo set for this customer.',
    customer360_settings_logo_remove: 'Remove logo',
    customer360_settings_logo_upload_label: 'Upload from file',
    customer360_settings_logo_uploading: 'Uploading…',
    customer360_settings_logo_filetypes: 'JPEG, PNG, SVG, or WebP. Max 4 MB.',
    customer360_settings_logo_importurl_label: 'Or import from URL',
    customer360_settings_logo_importurl_placeholder: 'https://…',
    customer360_settings_logo_import: 'Import',
    customer360_settings_logo_importing: 'Importing…',
    customer360_settings_logo_processing: 'Logo is processing in the background — this takes a few seconds.',
    customer360_settings_logo_invalidurl: 'Logo URL must start with http:// or https://',
    customer360_settings_logo_sizelimit: 'Logo file must be under 4 MB.',
    customer360_settings_logo_formatrequired: 'Logo must be JPEG, PNG, SVG, or WebP.',
    customer360_settings_logo_uploadfailed: 'Failed to upload logo',
    customer360_settings_logo_importfailed: 'Failed to import logo from URL',
    customer360_settings_logo_deleted: 'Logo removed.',
    customer360_settings_logo_deletefailed: 'Failed to remove logo',
    customer360_settings_logo_updated: 'Logo updated.',
    customer360_settings_domains_subtitle: 'Route customers to a branded sign-in URL on your own subdomain.',

    // API tab
    customer360_api_tokens_title: 'API Tokens',
    customer360_api_tokens_subtitle: 'Server-to-server authentication tokens for integrations. Each token is limited to your account\u2019s data.',
    customer360_api_tokens_generate: 'Generate Token',
    customer360_api_tokens_generate_title: 'Generate New API Token',
    customer360_api_tokens_generated_warning: 'Copy this token now. You will not be able to see it again.',
    customer360_api_tokens_generated_token: 'Token',
    customer360_api_tokens_name_label: 'Token name',
    customer360_api_tokens_name_placeholder: 'e.g. Production API',
    customer360_api_tokens_expiration_label: 'Expires after',
    customer360_api_tokens_expiration_30: '30 days',
    customer360_api_tokens_expiration_90: '90 days',
    customer360_api_tokens_expiration_180: '180 days',
    customer360_api_tokens_expiration_365: '365 days',
    customer360_api_tokens_done: 'Done',
    customer360_api_tokens_empty: 'No API tokens generated yet.',
    customer360_api_tokens_col_name: 'Name',
    customer360_api_tokens_col_prefix: 'Prefix',
    customer360_api_tokens_col_scope: 'Scope',
    customer360_api_tokens_col_workspace: 'Workspace',
    customer360_api_tokens_workspace_label: 'Workspace',
    customer360_api_tokens_workspace_hint: 'This token will only have access to the selected workspace.',
    customer360_api_tokens_workspace_required: 'Select a workspace before generating the token.',
    customer360_api_tokens_col_created: 'Created',
    customer360_api_tokens_col_last_used: 'Last Used',
    customer360_api_tokens_col_expires: 'Expires',
    customer360_api_tokens_col_status: 'Status',
    customer360_api_tokens_col_actions: 'Actions',
    customer360_api_tokens_rotate: 'Rotate',
    customer360_api_tokens_revoke: 'Revoke',
    customer360_api_tokens_rotate_confirm: 'Rotate this token? The old one will be revoked immediately and a new token will be generated.',
    customer360_api_tokens_rotate_title: 'Rotate API token',
    customer360_api_tokens_rotate_body: 'A new token is generated and the existing one is revoked immediately. Any service still using the old token will stop working until you deploy the new one.',
    customer360_api_tokens_rotate_confirm_action: 'Rotate token',
    customer360_api_tokens_rotate_failed: 'Failed to rotate token',
    customer360_api_tokens_show_history: 'Show history',
    customer360_api_tokens_purge: 'Delete',
    customer360_api_tokens_purge_title: 'Delete token from list',
    customer360_api_tokens_purge_body: 'This removes the token from your list permanently. The token is already revoked or expired, so no integrations are affected.',
    customer360_api_tokens_purge_audit_note: 'The audit log keeps a record of this token\u2019s creation, rotation, and revocation events, so forensic history is preserved even after the row is removed.',
    customer360_api_tokens_purge_confirm_action: 'Delete',
    customer360_api_tokens_purge_failed: 'Failed to delete token',
    customer360_api_tokens_revoke_title: 'Revoke API Token',
    customer360_api_tokens_revoke_subtitle: 'Revoke "{name}"? Any integration using this token will immediately start receiving 401 errors.',
    customer360_api_tokens_revoke_reason_label: 'Reason (required)',
    customer360_api_tokens_revoke_reason_placeholder: 'Why are you revoking this token?',
    customer360_api_tokens_revoke_confirm: 'Revoke Token',
    customer360_api_tokens_revoke_failed: 'Failed to revoke token',
    customer360_api_tokens_generate_failed: 'Failed to generate token',
    customer360_api_tokens_sso_note: 'API tokens bypass SSO enforcement. Revoke the token to block API access; the SSO enforcement toggle does not affect it.',
    customer360_api_docs_title: 'API Documentation',
    customer360_api_docs_reference_title: 'Interactive Reference',
    customer360_api_docs_reference_desc: 'Swagger/OpenAPI explorer with try-it-out.',
    customer360_api_docs_auth_title: 'Authentication',
    customer360_api_docs_auth_desc: 'Token setup, session auth, and code examples.',
    customer360_api_docs_webhooks_title: 'Webhooks & Events',
    customer360_api_docs_webhooks_desc: 'Event types, payload schemas, retries.',
    customer360_api_docs_open: 'Open',

    // API tab — token risk badges, detail drawer, and admin dev tools
    customer360_api_tokens_risk_expiring: 'Expiring soon',
    customer360_api_tokens_risk_expiring_tooltip: 'This token expires within 14 days. Rotate or regenerate before it stops working.',
    customer360_api_tokens_risk_stale: 'Stale',
    customer360_api_tokens_risk_stale_tooltip: 'No use in the last 90 days. If nothing depends on it, consider revoking.',
    customer360_api_tokens_risk_never: 'Never used',
    customer360_api_tokens_risk_never_tooltip: 'Created over 30 days ago and has never been used. Probably safe to revoke.',
    customer360_api_tokens_detail_open: 'View details',

    customer360_api_detail_access_count: 'Total uses',
    customer360_api_detail_last_ip: 'Last used IP',
    customer360_api_detail_jti: 'JWT ID (jti)',
    customer360_api_detail_revoked_title: 'Revoked',
    customer360_api_detail_revoked_at: 'Revoked at',
    customer360_api_detail_revoked_by: 'Revoked by',
    customer360_api_detail_rotation_title: 'Rotation history',
    customer360_api_chain_position: '{position} of {total}',
    customer360_api_chain_position_more: '{position} of {total}+',
    customer360_api_chain_previous: 'Previous',
    customer360_api_chain_next: 'Next',

    // SYSTEM_ADMIN dashboard overview (METALARK-694)

    // Customer list new columns + urgency signals (METALARK-694)
    customer_list_col_state: 'State',
    customer_list_col_plan: 'Plan',
    customer_list_col_mrr: 'MRR',
    customer_list_col_signal: 'Signal',
    customer_list_signal_healthy: 'Healthy',
    customer_list_signal_trial_ends_today: 'Trial ends today',
    customer_list_signal_trial_ends_tomorrow: 'Trial ends tomorrow',
    customer_list_signal_trial_ends_days: 'Trial ends in {count} days',
    customer_list_signal_trial_days_left: '{count} days left in trial',
    customer_list_signal_days_overdue: '{count, plural, =0 {Past Due} one {# day overdue} other {# days overdue}}',
    customer_list_signal_cancels_on: 'Cancels {date}',
    customer_list_signal_grace_days: 'Grace: {count} days',
    customer_list_signal_deletion_in_days: '{count, plural, one {Deletion in # day} other {Deletion in # days}}',
    customer_list_signal_pending_deletion: 'Pending deletion',
    customer_list_signal_unpaid: 'Payment failed — unpaid',
    customer_list_signal_payment_pending: 'Payment confirming…',
    customer_list_signal_incomplete: 'Signup incomplete',
    customer_list_signal_no_subscription: 'No subscription',
    customer_list_signal_paused: 'Paused',

    // Customer list / Health Dashboard toggle + lanes (METALARK-694)
    customer_list_quick_label: 'Quick Filters',
    customer_list_quick_all: 'All',
    customer_list_quick_active: 'Active',
    customer_list_quick_trial: 'Trial',
    customer_list_quick_attention: 'Needs Attention',
    customer_list_churned_label: 'Churned',
    customer_list_churned_tooltip: 'Customers whose subscription was canceled within the past 30 days.',
    customer_list_col_users: 'Users',
    customer_list_col_created: 'Created',
    customer_list_plan_monthly: 'Monthly',
    customer_list_plan_annual: 'Annual',

    // Customer-facing billing.tag — in-app cancel + reactivate + plan change (METALARK-694)
    billing_cancel_subscription: 'Cancel subscription',
    billing_reactivate: 'Reactivate subscription',
    billing_already_on_plan: 'Already on this plan.',
    billing_cancel_modal_title: 'Cancel your subscription',
    billing_cancel_modal_subtitle: 'We\u2019re sorry to see you go. Please let us know why so we can improve.',
    billing_cancel_modal_now: 'Your subscription will end immediately. You\u2019ll retain access for the grace period.',
    billing_cancel_modal_end: 'Your subscription will cancel at the end of the current billing period. Access continues until then.',
    billing_cancel_reason_label: 'Why are you canceling?',
    billing_cancel_reason_select: 'Select a reason\u2026',
    billing_cancel_reason_too_expensive: 'Too expensive',
    billing_cancel_reason_missing_features: 'Missing features',
    billing_cancel_reason_switched: 'Switched to a competitor',
    billing_cancel_reason_not_using: 'Not using the product',
    billing_cancel_reason_temporary: 'Temporary pause',
    billing_cancel_reason_other: 'Other',
    billing_cancel_reasontext_label: 'Additional details (optional)',
    billing_cancel_reasontext_placeholder: 'Tell us more\u2026',
    billing_cancel_now_label: 'Cancel immediately',
    billing_cancel_confirm: 'Confirm cancellation',
    billing_cancel_failed: 'Failed to cancel subscription',
    billing_reactivate_confirm: 'Reactivate this subscription? The scheduled cancellation will be removed.',
    billing_reactivate_failed: 'Failed to reactivate subscription',

    // Insights UI
    insights_nav_dashboard: 'Dashboard',
    insights_nav_blooms: 'Bloom\'s Taxonomy',
    insights_nav_similarity: 'Similarity',
    insights_nav_brand_consistency: 'Brand Consistency',
    insights_nav_osha_compliance: 'OSHA Compliance',
    insights_nav_extract_quality: 'Extract Quality',
    insights_nav_select_label: 'Select a dashboard view',
    insights_nodata: 'No data available',
    insights_modal_default_title: 'Details',
    insights_modal_rowsperpage: 'Rows per page',
    insights_modal_advancedview: 'Advanced view',
    insights_modal_export_toolarge: 'This list is too large to download.',
    insights_modal_pageinfo: 'Page {page} of {total} ({count} total)',

    insights_appname: 'Insights',

    // Insights UI — Dashboard page
    insights_dashboard_title: 'Dashboard',
    insights_dashboard_totalobjects: 'Total Evaluated Objects',
    insights_dashboard_totalobjects_caption: 'Evaluated objects',
    insights_dashboard_totalobjects_tooltip: 'Total count of all evaluated learning objects (courses and nuggets)',
    insights_dashboard_successrate: 'Extraction Success Rate',
    insights_dashboard_successrate_caption: '{successful} successful / {failed} failed',
    insights_dashboard_successrate_tooltip: 'Percentage of objects successfully extracted. Green above 95%, yellow 85-95%, red below 85%. Caption shows successful and failed counts.',
    insights_dashboard_extractionoutcomes: 'Extraction Outcomes',
    insights_dashboard_resultsbytype: 'Extraction Results by Object Type',
    insights_dashboard_resultsbytype_tooltip: 'Successful vs failed extractions grouped by content media type',
    insights_dashboard_contenttypedist: 'Content Type Distribution',
    insights_dashboard_contenttypedist_tooltip: 'Breakdown of successfully extracted content by media type',
    insights_dashboard_authoringtool: 'Authoring Tool Distribution',
    insights_dashboard_authoringtool_tooltip: 'Content distribution by authoring tool — indicates extraction compatibility',
    insights_dashboard_reviewstatus: 'Review Status',
    insights_dashboard_reviewstatus_tooltip: 'Resources flagged as needing review, broken down by the assigned triage action',
    insights_dashboard_needingreview: 'Needing Review',

    // Insights UI — Shared column labels
    insights_col_objectname: 'Object Name',
    insights_col_status: 'Status',
    insights_col_mediatype: 'Media Type',
    insights_col_authoringtool: 'Authoring Tool',
    insights_col_failurereason: 'Failure Reason',
    insights_col_date: 'Date',
    insights_col_extractionstatus: 'Extraction Status',
    insights_col_externalsourcename: 'External Source Name',
    insights_col_externalsourceid: 'External Source ID',

    // Insights UI — Shared status labels
    insights_status_extracted: 'Extracted',
    insights_status_failed: 'Failed',
    insights_status_successful: 'Successful',
    insights_status_needsreview: 'Needs Review',
    insights_status_ok: 'OK',
    insights_status_none: 'None',
    insights_status_unknown: 'Unknown',

    // Insights UI — Dashboard drill-down modals
    insights_dashboard_drill_totalobjects_subtitle: 'All evaluated learning objects broken down by type and status',
    insights_dashboard_drill_failedextractions_title: 'Failed Extractions',
    insights_dashboard_drill_failedextractions_subtitle: 'Items that failed extraction with failure reasons',
    insights_dashboard_drill_typecategory_title: '{label} — {datasetLabel}',
    insights_dashboard_drill_typecategory_subtitle: '{datasetLabel} items in the {label} category',
    insights_dashboard_drill_typecontent_title: '{label} Content',
    insights_dashboard_drill_typecontent_subtitle: 'Items of type {label}',
    insights_dashboard_drill_authoringcontent_subtitle: 'Items created with {label}',
    insights_dashboard_drill_reviewstatus_title: 'Review Status: {label}',
    insights_dashboard_drill_reviewstatus_subtitle: 'Needs-review resources with action = {label}',

    // Insights UI — Dashboard cache / refresh (phase machine)
    insights_dashboard_lastupdated: 'Last Updated',
    insights_dashboard_refresh: 'Refresh Dashboard',
    insights_dashboard_notgenerated_title: 'Dashboard Not Yet Generated',
    insights_dashboard_notgenerated_desc: 'The metadata coverage cache has not been built for this workspace. Generate it now to see Top Skills, Tags, and Keywords across your content library.',
    insights_dashboard_generate: 'Generate Dashboard',
    insights_launch_dashboard_unavailable: 'This dashboard hasn\'t been generated yet and isn\'t available from a shared view.',
    insights_dashboard_generating_title: 'Generating Dashboard…',
    insights_dashboard_aggregating_default: 'Aggregating metadata coverage across the workspace.',
    insights_dashboard_failure_title: 'Dashboard Generation Failed',
    insights_dashboard_tryagain: 'Try Again',
    insights_dashboard_job_submitting: 'Submitting job…',
    insights_dashboard_job_queued: 'Queued — aggregating metadata…',
    insights_dashboard_job_status: 'Status: {status}…',
    insights_dashboard_error_unable_to_resolve: 'Unable to resolve your workspace. Please sign in again, or append ?sliceId=<slice> to the URL.',
    insights_dashboard_error_unexpectedresponse: 'Unexpected response while loading the dashboard cache.',
    insights_dashboard_error_authredirect: 'Could not verify your access to the dashboard files for this workspace. Reload this tab to try again, or sign out and back in if the problem continues.',
    insights_dashboard_error_failedtoload: 'Failed to load dashboard.',
    insights_dashboard_error_jobfailed: 'The dashboard generation job failed.',
    insights_dashboard_error_jobtimedout: 'Dashboard generation timed out.',
    insights_dashboard_error_refreshfailed: 'Refresh failed.',

    // Insights UI — Dashboard Metadata Coverage graphs
    insights_dashboard_coverage_section: 'Metadata Coverage',
    insights_dashboard_coverage_skills_title: 'Top Skills',
    insights_dashboard_coverage_skills_tooltip: 'Skills linked to the most completed content. Click a bar to see the resources for that skill.',
    insights_dashboard_coverage_tags_title: 'Top Tags',
    insights_dashboard_coverage_tags_tooltip: 'Tags linked to the most completed content. Click a bar to see the resources for that tag.',
    insights_dashboard_coverage_keywords_title: 'Top Keywords',
    insights_dashboard_coverage_keywords_tooltip: 'Keywords appearing across the most completed content. Click a bar to see the resources for that keyword.',
    insights_dashboard_coverage_count_label: 'Linked resources',
    insights_dashboard_coverage_drill_skills_title: 'Skill: {label}',
    insights_dashboard_coverage_drill_tags_title: 'Tag: {label}',
    insights_dashboard_coverage_drill_keywords_title: 'Keyword: {label}',
    insights_dashboard_coverage_drill_subtitle: '{count, plural, one {# linked resource} other {# linked resources}}',

    // Insights UI — Bloom's Levels (shared across Bloom's stack)
    insights_blooms_level_remember: 'Remember',
    insights_blooms_level_understand: 'Understand',
    insights_blooms_level_apply: 'Apply',
    insights_blooms_level_analyze: 'Analyze',
    insights_blooms_level_evaluate: 'Evaluate',
    insights_blooms_level_create: 'Create',

    // Insights UI — Bloom's Level meta (education accordion)
    insights_blooms_level_remember_blurb: 'Recalling facts and basic concepts.',
    insights_blooms_level_remember_desc: 'Involves recognition and recall of information.',
    insights_blooms_level_remember_example: '"Define key terms," "List the steps."',
    insights_blooms_level_understand_blurb: 'Explaining ideas and concepts.',
    insights_blooms_level_understand_desc: 'Constructing meaning and demonstrating comprehension.',
    insights_blooms_level_understand_example: '"Explain in your own words," "Summarize the main idea."',
    insights_blooms_level_apply_blurb: 'Using information in new situations.',
    insights_blooms_level_apply_desc: 'Implementing knowledge in practical contexts.',
    insights_blooms_level_apply_example: '"Use this formula to solve," "Demonstrate the technique."',
    insights_blooms_level_analyze_blurb: 'Drawing connections and examining relationships.',
    insights_blooms_level_analyze_desc: 'Breaking information into parts and exploring relationships.',
    insights_blooms_level_analyze_example: '"Compare and contrast," "Identify patterns."',
    insights_blooms_level_evaluate_blurb: 'Justifying decisions and critical thinking.',
    insights_blooms_level_evaluate_desc: 'Making judgments based on criteria and standards.',
    insights_blooms_level_evaluate_example: '"Critique the approach," "Justify your recommendation."',
    insights_blooms_level_create_blurb: 'Producing original work and new ideas.',
    insights_blooms_level_create_desc: 'Generating new products, ideas, or ways of viewing things.',
    insights_blooms_level_create_example: '"Design a solution," "Develop a new strategy."',

    // Insights UI — Color names
    insights_color_red: 'Red',
    insights_color_orange: 'Orange',
    insights_color_yellow: 'Yellow',
    insights_color_green: 'Green',
    insights_color_blue: 'Blue',
    insights_color_purple: 'Purple',

    // Insights UI — Bloom's education accordion
    insights_blooms_education_title: 'Understanding Bloom\'s Taxonomy & Cognitive Levels',
    insights_blooms_education_what_title: 'What is Bloom\'s Taxonomy?',
    insights_blooms_education_what_desc: 'Bloom\'s Taxonomy is a framework developed by educational psychologist Benjamin Bloom in 1956 (and revised in 2001) that classifies learning objectives and educational goals into levels of complexity. It helps L&D professionals understand and measure <em>how deeply learners engage with content</em> — from basic memorization to creative problem-solving.',
    insights_blooms_education_levels_title: 'The Six Cognitive Levels',
    insights_blooms_education_levels_desc: 'Each level builds upon the previous one, creating a hierarchy of thinking skills from foundational to advanced.',
    insights_blooms_education_example_label: 'Example',
    insights_blooms_education_why_title: 'Why This Matters for L&D',
    insights_blooms_education_quality_label: 'Quality Assessment',
    insights_blooms_education_quality_desc: 'Move beyond "completion rates" to measure true learning depth and cognitive engagement.',
    insights_blooms_education_gaps_label: 'Content Gap Analysis',
    insights_blooms_education_gaps_desc: 'Identify which cognitive skills your content library develops well and where you have gaps.',
    insights_blooms_education_strategic_label: 'Strategic Development',
    insights_blooms_education_strategic_desc: 'Design learning experiences that target specific thinking skills needed for job performance.',
    insights_blooms_education_progression_label: 'Learner Progression',
    insights_blooms_education_progression_desc: 'Build learning paths that scaffold from foundational knowledge to advanced application.',
    insights_blooms_education_roi_label: 'ROI Justification',
    insights_blooms_education_roi_desc: 'Demonstrate the value of training by showing it develops higher-order thinking skills that drive business outcomes.',
    insights_blooms_education_methodology_title: 'How We Score',
    insights_blooms_education_methodology_body: 'Each piece of content is scored by our processes from 0 to 100 on every level, based on what the learner is called to do, explicitly or implicitly. Scores fall into five bands: Absent (0–20) the level isn\'t really there; Weak (20–40) it\'s mentioned or described but not practiced; Moderate (40–60) the learner is nudged toward it but can usually get by with recognition or guesswork; Strong (60–80) the learner genuinely has to think at that level to succeed; Exemplary (80–100) the content pushes the learner deep into that level with real judgment, ambiguity, or original thinking. The important threshold to watch is 60 — at or above it, the learner is genuinely doing the thinking, not just being exposed to it.',

    // Insights UI — Bloom's content detail modal
    insights_blooms_modal_content_label: 'Content: {title}',
    insights_blooms_modal_cognitiveprofile: 'Cognitive Profile',
    insights_blooms_modal_cognitiveprofile_tooltip: 'Larger segments indicate stronger engagement with that cognitive level.',
    insights_blooms_modal_summary: 'Summary',
    insights_blooms_modal_summary_tooltip: 'Exemplary level is where this content focuses most.',
    insights_blooms_modal_exemplarylevel: 'Exemplary Level',
    insights_blooms_modal_profile: 'Profile',
    insights_blooms_modal_audience: 'Audience',
    insights_blooms_modal_readability: 'Readability',
    insights_blooms_modal_readability_tooltip: 'Reading difficulty estimates based on general reading ease and grade level.',
    insights_blooms_modal_gradelevel: 'Grade Level',
    insights_blooms_modal_readingease: 'Reading Ease',
    insights_blooms_modal_viewcontent: 'View Content Item',
    insights_blooms_modal_leveldetails: 'Level Details',
    insights_blooms_modal_leveldetails_tooltip: 'Scores range from 0 to 100. Above 60 indicates strong engagement.',
    insights_blooms_modal_noreasoning: 'No reasoning provided.',
    insights_blooms_modal_viewevidence: 'View Evidence ({count})',
    insights_blooms_modal_easelabel_standard: 'Standard',
    insights_blooms_modal_easelabel_difficult: 'Fairly Difficult',
    insights_blooms_modal_easelabel_verydifficult: 'Very Difficult',
    insights_blooms_modal_notapplicable: 'N/A',

    // Insights UI — Bloom's page
    insights_blooms_page_title: 'Bloom\'s Taxonomy Dashboard',
    insights_blooms_lastupdated: 'Last Updated',
    insights_blooms_refresh: 'Refresh Dashboard',
    insights_blooms_notgenerated_title: 'Dashboard Not Yet Generated',
    insights_blooms_notgenerated_desc: 'The Bloom\'s Taxonomy dashboard cache has not been built for this workspace. Generate it now to see aggregate metrics and drill-ins across your content library.',
    insights_blooms_generate: 'Generate Dashboard',
    insights_blooms_generating_title: 'Generating Dashboard…',
    insights_blooms_aggregating_default: 'Aggregating Bloom\'s Taxonomy scores across the slice.',
    insights_blooms_failure_title: 'Dashboard Generation Failed',
    insights_blooms_tryagain: 'Try Again',
    insights_blooms_somethingwentwrong: 'Something went wrong',
    insights_blooms_dismiss: 'Dismiss',
    insights_blooms_error_unable_to_resolve: 'Unable to resolve your workspace. Please sign in again, or append ?sliceId=<slice> to the URL.',
    insights_blooms_error_unexpectedresponse: 'Unexpected response while loading the dashboard cache.',
    insights_blooms_error_failedtoload: 'Failed to load dashboard.',
    insights_blooms_job_submitting: 'Submitting job…',
    insights_blooms_job_queued: 'Queued — aggregating scores…',
    insights_blooms_job_status: 'Status: {status}…',
    insights_blooms_error_jobfailed: 'The dashboard generation job failed.',
    insights_blooms_error_jobtimedout: 'Dashboard generation timed out.',
    insights_blooms_error_refreshfailed: 'Refresh failed.',
    insights_blooms_error_unexpectederror: 'An unexpected error occurred.',
    insights_blooms_error_couldntload_audience: 'Couldn\'t load "{audience}" audience data',
    insights_blooms_error_couldntopen: 'Couldn\'t open "{title}"',
    insights_blooms_error_couldntopendetail: 'Couldn\'t open content detail',
    insights_blooms_error_noscoresavailable: 'No Bloom\'s scores available for this item',
    insights_blooms_error_notscoredyet: 'This resource hasn\'t been scored yet.',
    insights_blooms_kpi_total: 'Total Content Items',
    insights_blooms_kpi_total_caption: 'Analyzed for cognitive complexity',
    insights_blooms_kpi_total_tooltip: 'Count of resources with Bloom\'s Taxonomy scores in this slice.',
    insights_blooms_kpi_multilevel: 'Multi-Level Engagement',
    insights_blooms_kpi_multilevel_caption: '{pct}% reach Strong on 2+ levels',
    insights_blooms_kpi_multilevel_tooltip: 'Items scoring Strong or higher in 2 or more Bloom\'s Cognitive Levels.',
    insights_blooms_audience_section: 'Audience',
    insights_blooms_kpi_newhires: 'New Hires',
    insights_blooms_kpi_newhires_caption: '{pct}% of library',
    insights_blooms_kpi_newhires_tooltip: 'Resources targeted at new-hire audiences.',
    insights_blooms_kpi_teammembers: 'Team Members',
    insights_blooms_kpi_teammembers_caption: '{pct}% of library',
    insights_blooms_kpi_teammembers_tooltip: 'Resources targeted at team-member audiences.',
    insights_blooms_kpi_management: 'Management',
    insights_blooms_kpi_management_caption: '{pct}% of library',
    insights_blooms_kpi_management_tooltip: 'Resources targeted at management audiences.',
    insights_blooms_audience_unclassified: 'Unclassified',
    insights_blooms_chart_highperforming: 'High-Performing Content by Cognitive Level',
    insights_blooms_chart_highperforming_tooltip: 'Count of items scoring 60 or higher for each Bloom\'s level. Click bars to view items.',
    insights_blooms_chart_portfolio: 'Portfolio Coverage Heatmap',
    insights_blooms_chart_portfolio_tooltip: 'Content distribution by cognitive level strength (Absent 0–20, Weak 20–40, Moderate 40–60, Strong 60–80, Exemplary 80–100). Click cells to view items.',
    insights_blooms_chart_readability: 'Reading Level Distribution',
    insights_blooms_chart_readability_tooltip: 'Count of items by U.S. grade level range. Target: 8th grade or lower. Click bars to view items.',
    insights_blooms_chart_items_above: 'Items ≥ 60',
    insights_blooms_audience_all: 'All Audiences',
    insights_blooms_range_absent: 'Absent',
    insights_blooms_range_weak: 'Weak',
    insights_blooms_range_moderate: 'Moderate',
    insights_blooms_range_strong: 'Strong',
    insights_blooms_range_exemplary: 'Exemplary',
    insights_blooms_readability_elementary_label: 'Elementary',
    insights_blooms_readability_middle_label: 'Middle',
    insights_blooms_readability_high_label: 'High',
    insights_blooms_readability_college_label: 'College',
    insights_blooms_readability_elementary_range: 'grade ≤ 5',
    insights_blooms_readability_middle_range: 'grades 6–8',
    insights_blooms_readability_high_range: 'grades 9–12',
    insights_blooms_readability_college_range: 'grade > 12',
    insights_blooms_drill_multilevel_title: 'Multi-Level Engagement',
    insights_blooms_drill_multilevel_subtitle: 'Items scoring Strong or higher in 2 or more Bloom\'s Cognitive Levels',
    insights_blooms_drill_audience_title: 'Audience: {audience}',
    insights_blooms_drill_audience_subtitle: 'Resources targeted at {audience}',
    insights_blooms_drill_highperforming_title: 'High Performing: {label}',
    insights_blooms_drill_highperforming_subtitle: 'Items scoring 60 or higher on {label}',
    insights_blooms_drill_readability_title: 'Reading Level: {label}',
    insights_blooms_drill_readability_subtitle: 'Items with {rangeText} reading grade level',
    insights_blooms_drill_heatmap_title: '{level} — {range}',
    insights_blooms_drill_heatmap_subtitle: 'Items with {level} score in {range} range',
    insights_blooms_col_scores: 'Bloom\'s Scores',

    // Insights UI — Dashboard chart axis & dataset labels
    insights_dashboard_packagedcontent: 'Packaged Content',
    insights_dashboard_digitalresources: 'Digital Resources',
    insights_dashboard_chart_resources: 'Resources',

    // Insights UI — Extract Quality Dashboard
    insights_extract_quality_page_title: 'Extract Quality',
    insights_widget_error: 'Unable to load this data. Please try again later.',
    insights_eq_needsattention: 'Extracts Needing Attention',
    insights_eq_needsattention_caption: '{review} Needs Review / {blocked} Blocked ({pct}% of total)',
    insights_eq_needsattention_tooltip: 'Resources whose quality pipeline outcome is Needs Review or Blocked',
    insights_eq_pendingupload: 'Pending Upload',
    insights_eq_pendingupload_caption: 'Awaiting asset upload',
    insights_eq_pendingupload_tooltip: 'Resources with an upload job created but no asset uploaded yet',
    insights_eq_qualitybytype: 'Quality Status by Media Type',
    insights_eq_qualitybytype_tooltip: 'Pipeline outcome counts per media type',
    insights_eq_mediatype_other: 'Other',
    insights_eq_pertestfailure: 'Per-Test Failure Rate',
    insights_eq_pertestfailure_tooltip: 'Resources with a Low or Fail result per PEQV quality test',
    insights_eq_extractlengthdist: 'Extract Length Distribution',
    insights_eq_extractlengthdist_tooltip: 'Resources by Extract Length test status character-count band',
    insights_eq_qualityfunnel: 'Content Quality Funnel',
    insights_eq_qualityfunnel_tooltip: 'Resource counts through the extraction and PEQV pipeline stages',
    insights_eq_funnel_total: 'Total Resources',
    insights_eq_funnel_extracted: 'Extracted',
    insights_eq_funnel_complete: 'PEQV Complete',
    insights_eq_funnel_review: 'PEQV Needs Review',
    insights_eq_funnel_quit: 'PEQV Blocked',
    insights_eq_drill_needsattention_subtitle: 'Resources with a Needs Review or Blocked pipeline outcome',
    insights_eq_drill_pendingupload_subtitle: 'Resources awaiting asset upload',
    insights_eq_drill_qualitybytype_title: '{mediaType} — {outcome}',
    insights_eq_drill_qualitybytype_subtitle: 'Resources of this media type with the selected pipeline outcome',
    insights_eq_drill_qualitybytype_other_subtitle: 'Resources across several media types ({types}), each under {percent}% of all charted resources, with the selected pipeline outcome',
    insights_eq_drill_test_title: '{test} — Failing Resources',
    insights_eq_drill_test_subtitle: 'Resources with a Low or Fail result on {test}',
    insights_eq_drill_extractlength_title: 'Extract Length: {status}',
    insights_eq_drill_extractlength_subtitle: 'Resources with this Extract Length test status',
    insights_eq_diagnostic_section: 'Diagnostic Detail',
    insights_eq_diagnostic_section_desc: 'Break down quality results by whether the issue comes from the extraction pipeline or the source content itself.',
    insights_eq_extractquality_title: 'Extract Quality',
    insights_eq_extractquality_tooltip: 'Result distribution across the extraction-pipeline tests (Extract Length, Character Corruption, English Garbled Text)',
    insights_eq_contentquality_title: 'Content Quality',
    insights_eq_contentquality_tooltip: 'Result distribution across the source-content tests (Extract Coverage Quality, Language Detection, Structural Quality)',
    insights_eq_extractvscontent_extractpass: '{pct}% of Extract Quality checks passed',
    insights_eq_extractvscontent_contentpass: '{pct}% of Content Quality checks passed',
    insights_eq_drill_typetest_title: '{type}: {test} — {status}',
    insights_eq_drill_typetest_subtitle: 'Resources where the {test} check scored {status}',

    // Insights UI — Resource Similarity Dashboard
    insights_similarity_page_title: 'Resource Similarity',
    insights_similarity_page_subtitle: 'Surface duplicate, overlapping, and thematically related content across your library',
    insights_similarity_lastupdated: 'Last Updated',
    insights_similarity_refresh: 'Refresh Dashboard',
    insights_similarity_notgenerated_title: 'Dashboard Not Yet Generated',
    insights_similarity_notgenerated_desc: 'The content similarity dashboard cache has not been built for this workspace. Generate it now to surface duplicate, overlapping, and thematically related content across your library.',
    insights_similarity_generate: 'Generate Dashboard',
    insights_similarity_generating_title: 'Generating Dashboard…',
    insights_similarity_aggregating_default: 'Analyzing resources across the slice for similarity, skill overlap, and themes.',
    insights_similarity_failure_title: 'Could not load similarity data',
    insights_similarity_tryagain: 'Try again',
    insights_similarity_error_unable_to_resolve: 'Unable to resolve your workspace. Please sign in again, or append ?sliceId=<slice> to the URL.',
    insights_similarity_error_unexpectedresponse: 'The similarity dashboard returned an unexpected response.',
    insights_similarity_error_failedtoload: 'Failed to load similarity dashboard data.',
    insights_similarity_job_submitting: 'Submitting job…',
    insights_similarity_job_queued: 'Queued — analyzing resources…',
    insights_similarity_job_status: 'Status: {status}…',
    insights_similarity_error_jobfailed: 'The dashboard generation job failed.',
    insights_similarity_error_jobtimedout: 'Dashboard generation timed out.',
    insights_similarity_error_refreshfailed: 'Refresh failed.',

    insights_similarity_section_coverage: 'Coverage',
    insights_similarity_section_skills: 'Skills',
    insights_similarity_section_clusters: 'Similarity Clusters',

    insights_similarity_col_title: 'Title',
    insights_similarity_col_count: 'Count',
    insights_similarity_col_skills: 'Skills',
    insights_similarity_col_theme: 'Theme',
    insights_similarity_col_confidence: 'Confidence',

    insights_similarity_duplicate_title: 'Duplicate Coverage',
    insights_similarity_duplicate_description: 'Share of resources that appear to duplicate other content in the library',
    insights_similarity_duplicate_tooltip: 'Resources flagged as likely duplicates as a percentage of the full library',
    insights_similarity_duplicate_caption: '{duplicates} of {total} resources',

    insights_similarity_titleoverlap_title: 'Title Overlap',
    insights_similarity_titleoverlap_description: 'Distinct titles shared by two or more resources',
    insights_similarity_titleoverlap_tooltip: 'Click any row to see the resources that share that title',

    insights_similarity_overlappingtitles_title: 'Overlapping Titles',
    insights_similarity_overlappingtitles_description: 'Distinct titles shared by two or more resources. Click to view the full list.',
    insights_similarity_overlappingtitles_tooltip: 'Click the card to open the full list of overlapping titles',
    insights_similarity_overlappingtitles_caption: 'top: {title} ({count}×)',
    insights_similarity_overlappingtitles_drill_title: 'Overlapping titles',
    insights_similarity_overlappingtitles_drill_subtitle: '{count} distinct titles shared across resources',

    insights_similarity_skilldist_title: 'Skill Distribution',
    insights_similarity_skilldist_description: 'How resources are distributed across the skills they teach',
    insights_similarity_skilldist_tooltip: 'Click a bar to see the resources mapped to that skill',

    insights_similarity_skillsoverlap_title: 'Skills Overlap Clusters',
    insights_similarity_skillsoverlap_description: 'Groups of resources that teach overlapping sets of skills',
    insights_similarity_skillsoverlap_tooltip: 'Click a bubble to see a heat map of which resources cover which skills',

    insights_similarity_resourcesim_title: 'Resource Similarity Clusters',
    insights_similarity_resourcesim_description: 'Resources that are mathematically similar based on content, skills, and metadata',
    insights_similarity_resourcesim_tooltip: 'Click a bubble to see the resources in the cluster',
    insights_similarity_resourcesim_bubblelabel: '{count, plural, one {# Item} other {# Items}}',

    insights_similarity_themes_title: 'Thematic Clusters',
    insights_similarity_themes_description: 'Resources grouped by inferred theme; opacity reflects clustering confidence',
    insights_similarity_themes_tooltip: 'Click a bubble to see the resources in the theme',
    insights_similarity_themes_confidence_high: 'High confidence',
    insights_similarity_themes_confidence_medium: 'Medium confidence',
    insights_similarity_themes_confidence_low: 'Low confidence',

    insights_similarity_drill_title_title: 'Resources sharing title: "{title}"',
    insights_similarity_drill_title_subtitle: '{count} resources share this title',
    insights_similarity_drill_skill_title: 'Resources mapped to skill: {skill}',
    insights_similarity_drill_skill_subtitle: '{count} resources cover this skill',
    insights_similarity_drill_cluster_title: 'Cluster {n}',
    insights_similarity_drill_cluster_subtitle: '{count} similar resources in this cluster',
    insights_similarity_drill_theme_title: 'Theme: {theme}',
    insights_similarity_drill_theme_subtitle: '{count} resources · confidence: {confidence}',
    insights_similarity_drill_skillsoverlap_title: 'Skills overlap: {skills}',
    insights_similarity_drill_skillsoverlap_subtitle: '{count} resources in this cluster',

    insights_similarity_heatmap_legend_present: 'Resource has skill',
    insights_similarity_heatmap_legend_absent: 'Skill not assigned',
    insights_similarity_heatmap_legend_core: 'Core cluster skill',

    admin_customerdeletions_action_extend_title: 'Extend deletion deadline',
    admin_customerdeletions_action_cancel_title: 'Cancel deletion',
    admin_customerdeletions_action_view_title: 'View details',
    admin_webhookdeliveries_view_payload: 'View payload',
    admin_webhooksubscriptions_test_fire: 'Send test event',
    admin_webhooksubscriptions_rotate_url: 'Rotate URL',
    // Friendly create-flow copy (METALARK-928). These live in the shared
    // locale set (not the system-admin chunk) so CUSTOMER_ADMIN sessions can
    // load them — t() has no cross-language fallback, so a missing key renders
    // raw. The full migration of the remaining customer-facing webhook keys
    // is handled alongside this.
    admin_webhooksubscriptions_create_intro: 'Get notified the moment things happen in MetaLark — send events to your team\'s chat or your own service.',
    admin_webhooksubscriptions_create_name_label: 'What should we call it?',
    admin_webhooksubscriptions_create_name_placeholder: 'e.g. Ops alerts — #incidents',
    admin_webhooksubscriptions_create_channel_prompt: 'Where should we send events?',
    admin_webhooksubscriptions_channel_google_chat_name: 'Google Chat',
    admin_webhooksubscriptions_channel_google_chat_desc: 'Post to a Google Chat space',
    admin_webhooksubscriptions_channel_slack_name: 'Slack',
    admin_webhooksubscriptions_channel_slack_desc: 'Post to a Slack channel',
    admin_webhooksubscriptions_channel_customer_webhook_name: 'Your endpoint',
    admin_webhooksubscriptions_channel_customer_webhook_desc: 'Signed HTTP POST to your service',
    admin_webhooksubscriptions_section_advanced: 'Advanced (system options)',
    admin_webhooksubscriptions_url_label_google_chat: 'Google Chat incoming webhook URL',
    admin_webhooksubscriptions_url_label_slack: 'Slack incoming webhook URL',
    admin_webhooksubscriptions_url_label_customer_webhook: 'Your endpoint URL',
    admin_webhooksubscriptions_url_help_google_chat: 'Paste the webhook URL from your Google Chat space settings.',
    admin_webhooksubscriptions_url_help_slack: 'Paste the URL from your Slack app\'s Incoming Webhooks.',
    admin_webhooksubscriptions_url_help_customer_webhook: 'We\'ll POST signed JSON here. Verify the X-MetaLark-Signature header to confirm it\'s from us.',
    admin_webhooksubscriptions_rule_event_label: 'Event',
    admin_webhooksubscriptions_rule_event_choose: 'Choose an event…',
    admin_webhooksubscriptions_rule_event_custom: 'Custom pattern…',
    admin_webhooksubscriptions_rule_source_any: 'Any source',
    admin_webhooksubscriptions_event_job_failed: 'Job failed',
    admin_webhooksubscriptions_event_job_failed_desc: 'Fires when an AI-generation job fails after exhausting retries.',
    admin_webhooksubscriptions_event_job_completed: 'Job completed',
    admin_webhooksubscriptions_event_job_completed_desc: 'Fires when a job finishes successfully.',
    admin_webhooksubscriptions_event_user_activated: 'User activated',
    admin_webhooksubscriptions_event_user_activated_desc: 'Fires when a user first becomes active — password set or SSO sign-in.',
    admin_webhooksubscriptions_event_customer_provisioned: 'Customer provisioned',
    admin_webhooksubscriptions_event_customer_provisioned_desc: 'Fires when a new customer is created.',
    admin_webhooksubscriptions_source_password_set: 'Password set (signup completed)',
    admin_webhooksubscriptions_source_jit_provisioned: 'JIT / SSO provisioned',
    admin_webhooksubscriptions_source_trial: 'Trial signup',
    admin_webhooksubscriptions_source_stripe_checkout: 'Stripe checkout',
    admin_webhooksubscriptions_source_admin: 'Admin wizard',
    admin_webhooksubscriptions_title: 'Webhook Subscriptions',
    admin_webhooksubscriptions_description: 'Configure webhook destinations and event-routing rules. URL changes for system-owned destinations rotate the SSM parameter automatically; customer-owned destinations write the URL directly with HMAC signing.',
    admin_webhooksubscriptions_no_entries: 'No webhook subscriptions yet — create one to start routing lifecycle events.',
    admin_webhooksubscriptions_new: 'New Subscription',
    admin_webhooksubscriptions_create: 'Create',
    admin_webhooksubscriptions_creating: 'Creating…',
    admin_webhooksubscriptions_modal_create_title: 'Create Webhook Subscription',
    admin_webhooksubscriptions_field_name: 'Display Name',
    admin_webhooksubscriptions_field_url: 'URL',
    admin_webhooksubscriptions_field_url_hint_customer: 'Customer-owned URL is stored directly. Use Rotate URL later to change it.',
    admin_webhooksubscriptions_field_url_hint: 'URL hint',
    admin_webhooksubscriptions_field_last_rotated: 'Last rotated',
    admin_webhooksubscriptions_label_customer: 'Customer',
    admin_webhooksubscriptions_rotate_target_db: 'Will update the URL stored on this subscription record (HMAC signing remains unchanged).',
    admin_webhooksubscriptions_health_heading: 'Health',
    admin_webhooksubscriptions_health_healthy_long: 'Healthy — no recent failures',
    admin_webhooksubscriptions_health_consecutive_failures: 'consecutive failures',
    admin_webhooksubscriptions_health_auto_disabled_long: 'Auto-disabled — too many consecutive failures',
    admin_webhooksubscriptions_health_view_deliveries: 'View deliveries',
    admin_webhooksubscriptions_health_view_failures: 'View failures',
    admin_webhooksubscriptions_health_reset_hint: 'Set Status to "active" and save to clear the failure count and re-enable delivery. Rotating the URL also resets the count automatically.',
    admin_webhooksubscriptions_field_dispatch_mode: 'Dispatch mode',
    admin_webhooksubscriptions_mode_bus: 'API Bus',
    admin_webhooksubscriptions_mode_bus_long: 'API Bus (in-process)',
    admin_webhooksubscriptions_url_heading: 'Webhook URL',
    admin_webhooksubscriptions_url_current_label: 'Current URL (last 4 chars)',
    admin_webhooksubscriptions_url_not_set: 'Not set yet',
    admin_webhooksubscriptions_url_set_initial: 'Set URL',
    admin_webhooksubscriptions_url_storage_db: 'Stored on the subscription record (customer-managed).',
    admin_webhooksubscriptions_hmac_label: 'HMAC signing secret',
    admin_webhooksubscriptions_hmac_not_set: 'No secret set yet — outbound POSTs will not include X-MetaLark-Signature',
    admin_webhooksubscriptions_hmac_explainer: 'We sign every outbound POST with this secret so the customer can verify the request really came from us. Plaintext is returned only once at generation/rotation; we store only a hash on our side.',
    admin_webhooksubscriptions_hmac_generate: 'Generate Secret',
    admin_webhooksubscriptions_hmac_rotate: 'Rotate Secret',
    admin_webhooksubscriptions_hmac_confirm_generate: 'Generate a new HMAC signing secret? The plaintext will be shown ONCE — save it immediately.',
    admin_webhooksubscriptions_hmac_confirm_rotate: 'Rotate HMAC secret? The previous secret stops working immediately. The new plaintext will be shown ONCE — save it immediately.',
    admin_webhooksubscriptions_hmac_show_once: 'New HMAC secret (save this now — it will not be shown again):\n\n{secret}',
    admin_webhooksubscriptions_hmac_rotate_failed: 'Failed to rotate HMAC secret',
    admin_webhooksubscriptions_meta_heading: 'Configuration',
    admin_webhooksubscriptions_field_status: 'Status',
    admin_webhooksubscriptions_field_max_retries: 'Max Retries',
    admin_webhooksubscriptions_field_auto_disable_threshold: 'Auto-Disable After (failures)',
    admin_webhooksubscriptions_url_unset: 'not set',
    admin_webhooksubscriptions_status_active: 'Active',
    admin_webhooksubscriptions_status_disabled: 'Disabled',
    admin_webhooksubscriptions_status_auto_disabled: 'Auto-disabled (failure threshold reached)',
    admin_webhooksubscriptions_health_healthy: 'Healthy',
    admin_webhooksubscriptions_col_status: 'Status',
    admin_webhooksubscriptions_col_name: 'Name',
    admin_webhooksubscriptions_col_channel: 'Channel',
    admin_webhooksubscriptions_col_owner: 'Owner',
    admin_webhooksubscriptions_col_url_hint: 'URL',
    admin_webhooksubscriptions_col_health: 'Health',
    admin_webhooksubscriptions_rotate_url_hint: 'The new URL will replace the existing one and update the masked hint. The full URL is never displayed again after save — use Test fire to verify.',
    admin_webhooksubscriptions_rotate_current_hint: 'Current hint: {hint}',
    admin_webhooksubscriptions_rotate_warning: 'Rotation is logged in the audit trail. The previous URL is overwritten — make sure the new URL is correct before saving.',
    admin_webhooksubscriptions_rotating: 'Rotating…',
    admin_webhooksubscriptions_test_result: 'Test fire status: {status} (HTTP {httpStatus})',
    admin_webhooksubscriptions_rules_heading: 'Routing Rules',
    admin_webhooksubscriptions_rules_empty: 'No rules — this subscription will not receive any events until you add at least one.',
    admin_webhooksubscriptions_rule_add: 'Add Rule',
    admin_webhooksubscriptions_rule_pattern: 'Event Pattern',
    admin_webhooksubscriptions_rule_source: 'Source',
    admin_webhooksubscriptions_rule_any_source: 'any',
    admin_webhooksubscriptions_rule_enabled: 'Enabled',
    admin_webhooksubscriptions_rule_sample_rate: 'Sample Rate',
    admin_webhooksubscriptions_rule_rate_limit: 'Rate / hr',
    admin_webhooksubscriptions_rule_unlimited: 'unlimited',
    admin_webhooksubscriptions_adding: 'Adding…',
    admin_webhooksubscriptions_confirm_delete: 'Delete subscription "{name}"? This also removes its rules.',
    admin_webhooksubscriptions_confirm_delete_ssm_note: 'The SSM parameter (if any) is left in place.',
    admin_webhooksubscriptions_confirm_delete_rule: 'Delete this rule?',
    admin_webhooksubscriptions_alert_load_failed: 'Failed to load webhook subscriptions',
    admin_webhooksubscriptions_alert_save_failed: 'Failed to save changes',
    admin_webhooksubscriptions_alert_create_failed: 'Failed to create subscription',
    admin_webhooksubscriptions_alert_delete_failed: 'Failed to delete subscription',
    admin_webhooksubscriptions_alert_rotate_failed: 'Failed to rotate URL',
    admin_webhooksubscriptions_alert_test_failed: 'Test fire failed',
    admin_webhooksubscriptions_alert_rule_create_failed: 'Failed to create rule',
    admin_webhooksubscriptions_alert_rule_update_failed: 'Failed to update rule',
    admin_webhooksubscriptions_alert_rule_delete_failed: 'Failed to delete rule',
    admin_campaigns_domain_remove: 'Remove domain',
    partner_nav_dashboard: 'Dashboard',
    partner_nav_customers: 'Customers',
    partner_nav_webhooks: 'Webhooks',
    partner_nav_portal: 'Partner Portal',
    partner_access_denied: 'This page is only available to partner administrators.',
    partner_enter: 'Enter',
    partner_entering: 'Entering…',
    partner_enter_customer: 'Enter customer',
    partner_enter_customer_aria: 'Enter customer {name}',
    partner_enter_failed: 'Failed to enter customer: {message}',
    partner_dashboard_loading: 'Loading your dashboard…',
    partner_dashboard_portal_label: 'Partner portal',
    partner_dashboard_default_title: 'Your partnership',
    partner_dashboard_under_management: '{count, plural, one {customer under management} other {customers under management}}',
    partner_dashboard_view_customers: 'View customers',
    partner_dashboard_your_customers: 'Your customers',
    partner_dashboard_view_all: 'View all',
    partner_dashboard_load_failed: 'Failed to load your dashboard. Please try again.',
    partner_no_customers: 'No customers are assigned to your partner yet.',
    partner_tier: 'Tier',
    partner_tier_value: '{tier} tier',
    partner_primary_contact: 'Primary contact',
    partner_customer_id: 'Customer ID',
    partner_customers_search: 'Search customers',
    partner_customers_refresh_aria: 'Refresh customer list',
    partner_customers_loading: 'Loading your customers…',
    partner_customers_subtitle_empty: 'Customers your partner owns. Enter one to manage it as its administrator.',
    partner_customers_subtitle: '{count, plural, one {# customer} other {# customers}} under your management. Click a row for detail, or Enter to administer.',
    partner_customers_no_match: 'No customers match "{query}".',
    partner_customers_load_failed: 'Failed to load your customers. Please try again.',
    partner_customers_close_aria: 'Close customer detail',
    partner_workspaces: 'Workspaces',
    partner_workspaces_none: 'None yet',
    partner_workspaces_count: '{count, plural, one {# workspace} other {# workspaces}}',
    partner_workspaces_resolving: 'Resolving workspaces…',
    partner_enter_hint_single: 'Entering drops you straight into this customer’s workspace as its administrator.',
    partner_enter_hint_none: 'This customer has no workspace yet — Enter lands on its Customer 360 admin view.',
    partner_enter_hint_multi: 'This customer has multiple workspaces — Enter lands on Customer 360, where you can pick one.',
    customer360_partner_managed_by: 'Managed by {name}',
    customer360_partner_managed: 'Partner-managed',
    // Format-first New Target wizard and format targets (METALARK-1237)
    migration_wizard_intro: 'Pick what you are publishing to, then where the output should be delivered.',
    migration_wizard_steps: 'New target steps',
    migration_wizard_step_format: 'Format',
    migration_wizard_step_destination: 'Destination',
    migration_wizard_step_location: 'Location',
    migration_wizard_step_name: 'Name',
    migration_wizard_step_done: '{label}, complete. Go back to this step.',
    migration_wizard_format_question: 'What are you publishing to?',
    migration_wizard_format_loading: 'Loading formats…',
    migration_wizard_format_failed: 'The format list could not be loaded. Close this dialog and try again.',
    migration_wizard_default_name: 'Files + metadata',
    migration_wizard_default_tagline: 'Manifest CSV beside the files, on any file store',
    migration_wizard_platform_tagline: 'Direct to the platform',
    migration_wizard_filestores_ready: '{count, plural, =0 {No file store connected} one {# file store ready} other {# file stores ready}}',
    migration_wizard_showmore: 'More options',
    migration_wizard_showmore_sub: '{count, plural, one {# more platform or format} other {# more platforms and formats}}',
    migration_wizard_showfewer: 'Fewer options',
    migration_wizard_showfewer_sub: 'Hide the ones not set up here',
    migration_wizard_connect_title: 'Connect {platform}',
    migration_wizard_connect_intro: 'Set it up here and the wizard picks up where you left off.',
    migration_wizard_loadtype: 'Load type',
    migration_wizard_destination_question_file: 'Where should the file be delivered?',
    migration_wizard_destination_question: 'Where should the content go?',
    migration_wizard_destination_hint: 'Connections that {format} output can ride on. One tile per configured connection.',
    migration_wizard_nodestinations: '{format} output needs a file store connection.',
    migration_wizard_nodestinations_hint: 'Connect {stores}, then return here.',
    migration_wizard_chosenforyou: 'Chosen for you',
    migration_wizard_onlyconnection: 'the only connection that can carry it',
    migration_wizard_change: 'Change',
    migration_wizard_filename: 'File written each push',
    migration_wizard_filename_hint: 'Overwritten in place on every push. An import feed is configured against this exact name, so change it only before that feed exists.',
    migration_wizard_filename_invalid: 'Enter a plain file name, without folders.',
    migration_wizard_placementnote_file: 'One file is written into the chosen folder. No content files are copied.',
    migration_wizard_review: 'Review and name the target.',
    migration_wizard_summary_format: 'Format',
    migration_wizard_summary_destination: 'Destination',
    migration_wizard_summary_file: 'File',
    migration_wizard_summary_nocontent: 'Metadata only. No content files are sent.',
    migration_wizard_summary_default: 'Files, with a MetaLark manifest beside them',
    migration_wizard_summary_platform: 'Created directly in {platform}',
    migration_wizard_noduplicate: 'No existing target uses this folder',
    migration_wizard_nextconfigure: 'Next: Configure fields',
    migration_wizard_nextconfigure_sub: 'Opens with the load type\'s columns listed: the identifier choice, the standard columns with their defaults, and custom columns typed from your portal\'s Template Guide.',
    migration_wizard_continue: 'Continue',
    migration_fieldmap_format_intro: 'Choose what fills each column of the {format} file. Standard columns come with a default; the identifier must map to something every resource carries, in practice an attribute.',
    migration_fieldmap_format_identifier: 'Identifier',
    migration_fieldmap_format_identifier_sub: 'How each row is matched to a record in {platform}. Its column becomes required.',
    migration_fieldmap_format_filename: 'File name',
    migration_fieldmap_format_filename_hint: 'Written into the destination folder and overwritten on every push.',
    migration_fieldmap_format_columns: 'Standard columns',
    migration_fieldmap_format_columns_sub: 'In the order the importer expects them. A column with a default needs no setting unless you want something else.',
    migration_fieldmap_format_col_into: 'Into column',
    migration_fieldmap_format_default: 'Default',
    migration_fieldmap_format_default_is: 'Default: {source}',
    migration_fieldmap_format_empty: 'Left empty',
    migration_fieldmap_format_required: 'Required',
    migration_fieldmap_format_identifier_badge: 'Identifier',
    migration_fieldmap_source_metadata: 'Approved metadata',
    migration_fieldmap_format_custom: 'Custom columns',
    migration_fieldmap_format_custom_sub: 'Columns your portal defines beyond the standard ones. Type each header exactly as the Template Guide shows it.',
    migration_fieldmap_format_custom_none: 'No custom columns yet.',
    migration_fieldmap_format_custom_add: 'Add column',
    migration_fieldmap_format_custom_header: 'Column header',
    migration_fieldmap_format_custom_header_for: 'Header for custom column {index}',
    migration_fieldmap_format_custom_remove: 'Remove column {header}',
    migration_fieldmap_format_custom_nth: 'custom column {index}',
    migration_fieldmap_format_count: '{count} of {max} columns',
    migration_fieldmap_format_provisional: 'Column headers not yet confirmed',
    migration_fieldmap_format_err_identifier: 'Choose which identifier keys the rows.',
    migration_fieldmap_format_err_required: '{header} is required: choose what fills it.',
    migration_fieldmap_format_err_literal: '{header}: enter the value to send.',
    migration_fieldmap_format_err_nokey: '{header}: choose what to send.',
    migration_fieldmap_format_err_header: 'Custom column {index}: enter its header.',
    migration_fieldmap_format_err_collides: '{header} is a standard column. Set it in the list above instead.',
    migration_fieldmap_format_err_repeated: '{header} is listed twice.',
    migration_fieldmap_format_err_toomany: 'More than the {max} columns this format allows.',
    migration_fieldmap_format_err_filename: 'Enter a plain file name, without folders.',
    migration_fieldmap_format_preview_empty_note: 'A dash marks a column that is mapped but has nothing for that resource. A required column left empty fails that resource at push time.',
    migration_status_pushed_metadata_only: 'Pushed (metadata)',
    migration_target_format_nocontent: 'metadata only, no content files',
    admin_repositories_lms_setuptarget: 'Set up target',
    admin_repositories_lms_format_note: '{name} is a metadata format, not a connection: its file is delivered through a file store you already connect. Setting it up opens New Target with {name} chosen.',
    migration_push_desc_format: 'Write {count, plural, one {# item} other {# items}} from {name} into the {format} file. No content files are copied.',
    migration_push_metadataonly: 'Metadata only',
    migration_push_metadataonly_sub: 'No content files are copied. Each item becomes a row in the file.',
    migration_push_formatfile: '{format} file',
    migration_push_formatfile_sub: 'Written as {name} in the destination folder and overwritten each push. Its columns are set on Configure fields.',
  },
} satisfies LocaleFile;
